// Every word the dashboard shows, in every language it speaks.
//
// The table is keyed by the English string the code already writes, so a screen reads the same
// either way and a missing translation falls back to English rather than to a key nobody can read.
// A key can carry a context after a bar, "All|sites", because a Spanish adjective agrees with the
// noun it describes and one English word can need two Spanish ones. The fallback is the text before
// the bar, so a missing entry still reads.
//
// Spanish is written as \u escapes: every file under src/ is plain ASCII, and the accented text as
// the screen draws it lives in translation/dashboard_words.csv, which is what a translator reads
// and edits. The audit fails the run if the two ever disagree.
//
// A third language is one more key in each entry and one more id in LOCALES.
"use strict";

export const LOCALES = ["en", "es"];

// What a formatter is given. es-US rather than plain es: the company writes 1,234,567.50, and plain
// es writes 1.234.567,50.
export const LOCALE_TAGS = { en: "en-US", es: "es-US" };

// Each name is written in its own language and is never translated.
export const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "es", label: "Espa\u00f1ol" },
];

export const WORDS = {
  "${0}/unit": { es: "${0}/unidad" },
  "(All Sites)": { es: "(todos los sitios)" },
  "(arriving with templates)": { es: "(llega con las plantillas)" },
  "(use form default)": { es: "(usar la del formulario)" },
  "(via {0})": { es: "(por {0})" },
  "({0} enabled)": { es: "({0} habilitados)" },
  "({0} new)": { es: "({0} nuevos)" },
  "+ Add": { es: "+ Agregar" },
  "+ Add Document": { es: "+ Agregar documento" },
  "+ Add Other": { es: "+ Agregar otro" },
  "+ Add Training": { es: "+ Agregar capacitaci\u00f3n" },
  "+ Add Value": { es: "+ Agregar valor" },
  "+ Custom Step": { es: "+ Paso personalizado" },
  ", resolved {0} failure record(s)": { es: ", se resolvieron {0} registro(s) de fallo" },
  ", resolved {0} prior failure(s)": { es: ", se resolvieron {0} fallo(s) anterior(es)" },
  "10 digits": { es: "10 d\u00edgitos" },
  "48 hour reminder to the team": { es: "Recordatorio de 48 horas al equipo" },
  "72 hour reminder to the team": { es: "Recordatorio de 72 horas al equipo" },
  "< Prev": { es: "< Anterior" },
  "A Speak Up report is filed": { es: "Se presenta un informe en Confianza" },
  "A form is submitted": { es: "Se env\u00eda un formulario" },
  "A problem is reported": { es: "Se informa una incidencia" },
  "A reason for reassignment is required": { es: "Se requiere un motivo para reasignar" },
  "A report name is required": { es: "El nombre del informe es obligatorio" },
  "A role": { es: "Un rol" },
  "A staff member is requesting to drop this shift.": { es: "Una persona solicita darse de baja de este turno." },
  "A staff member is requesting to drop this shift. Approve to open it for pickup, deny to keep the original assignment, or reassign directly.": { es: "Una persona solicita darse de baja de este turno. Apruebe para abrirlo a quien quiera tomarlo, deniegue para dejar la asignaci\u00f3n original, o reasigne directamente." },
  "A staff member is requesting to drop this shift. Approve to open it for pickup, or deny to keep it assigned.": { es: "Una persona solicita darse de baja de este turno. Apruebe para abrirlo a quien quiera tomarlo, o deniegue para dejarlo asignado." },
  "A supervisor completes this part at a desk. The app cannot fill it in yet.": { es: "Un supervisor completa esta parte en un escritorio. La aplicaci\u00f3n todav\u00eda no puede completarla." },
  "A supply request is made": { es: "Se hace una solicitud de suministros" },
  "A training name and a type are required": { es: "El nombre de la capacitaci\u00f3n y el tipo son obligatorios" },
  "A unique QR code will be generated automatically.": { es: "Se generar\u00e1 autom\u00e1ticamente un c\u00f3digo QR \u00fanico." },
  "A worker cannot resolve an assigned problem": { es: "Un trabajador no puede resolver una incidencia asignada" },
  "Add photos": { es: "Agregar fotos" },
  "ADP payroll export": { es: "Exportaci\u00f3n de n\u00f3mina a ADP" },
  "Alias deactivated.": { es: "Alias desactivado." },
  "All|sources": { es: "Todos" },
  "An admin account holds these by role. There is nothing to change here.": { es: "Una cuenta de administrador los tiene por su rol. Aqu\u00ed no hay nada que cambiar." },
  "Announcement sent.": { es: "Anuncio enviado." },
  "Announcements": { es: "Anuncios" },
  "Answered|form": { es: "Respondida" },
  "API Connection": { es: "Conexi\u00f3n con la API" },
  "Access each role has in the platform today, by area. Manage means full access, including create, edit, and remove. View means read access. Other labels describe a scoped or limited form of access. This is a reference and does not change access.": { es: "El acceso que cada rol tiene hoy en la plataforma, por \u00e1rea. Administrar significa acceso completo, incluidos crear, editar y quitar. Ver significa acceso de lectura. Las dem\u00e1s etiquetas describen un acceso acotado o limitado. Esta es una referencia y no cambia el acceso." },
  "Access log": { es: "Registro de accesos" },
  "Access reference": { es: "Referencia de acceso" },
  "Access reference, generated {0}": { es: "Referencia de acceso, generada el {0}" },
  "Account Capability Diagnostic": { es: "Diagn\u00f3stico de capacidades de la cuenta" },
  "Account Type": { es: "Tipo de cuenta" },
  "Action": { es: "Acci\u00f3n" },
  "Actions": { es: "Acciones" },
  "Activate": { es: "Activar" },
  "Active": { es: "Activo" },
  "Active Sites": { es: "Sitios activos" },
  "Active only": { es: "Solo activas" },
  "Activity Description": { es: "Descripci\u00f3n de la actividad" },
  "Activity Timeline": { es: "Historial de actividad" },
  "Activity Timeline ({0} total)": { es: "Historial de actividad ({0} en total)" },
  "Activity log": { es: "Registro de actividad" },
  "Add Alias": { es: "Agregar un alias" },
  "Add failed": { es: "No se pudo agregar" },
  "Add row": { es: "Agregar fila" },
  "Add the environment variable JOTFORM_API_KEY in Railway, then reload this page. Generate a Full Access key from jotform.com under Settings then API.": { es: "Agregue la variable de entorno JOTFORM_API_KEY en Railway y vuelva a cargar esta p\u00e1gina. Genere una clave de acceso completo en jotform.com, en Settings y luego API." },
  "Add": { es: "Agregar" },
  "Add Building": { es: "Agregar edificio" },
  "Add Category": { es: "Agregar categor\u00eda" },
  "Add Certification": { es: "Agregar certificaci\u00f3n" },
  "Add Custom Onboarding Step": { es: "Agregar un paso de incorporaci\u00f3n personalizado" },
  "Add Document": { es: "Agregar documento" },
  "Add Evaluation": { es: "Agregar una evaluaci\u00f3n" },
  "Add Floor": { es: "Agregar piso" },
  "Add Item": { es: "Agregar el elemento" },
  "Add Line Item": { es: "Agregar un elemento" },
  "Add New Staff": { es: "Agregar a una persona" },
  "Add Service": { es: "Agregar servicio" },
  "Add Site": { es: "Agregar sitio" },
  "Add Staff": { es: "Agregar persona" },
  "Add Step": { es: "Agregar el paso" },
  "Add Supply": { es: "Agregar suministro" },
  "Add Supply to Site": { es: "Agregar un suministro al sitio" },
  "Add Task": { es: "Agregar tarea" },
  "Add Training Record": { es: "Agregar un registro de capacitaci\u00f3n" },
  "Add Value": { es: "Agregar valor" },
  "Add Value to {0}": { es: "Agregar valor a {0}" },
  "Add Vendor": { es: "Agregar proveedor" },
  "Add Zone": { es: "Agregar zona" },
  "Add a note saying why": { es: "Agregue una nota explicando por qu\u00e9" },
  "Add a note...": { es: "Agregue una nota..." },
  "Add a person": { es: "Agregar una persona" },
  "Add a person to {0}": { es: "Agregar una persona a {0}" },
  "Add a photo": { es: "Agregar una foto" },
  "Add an email address": { es: "Agregar un correo" },
  "Add an email address to {0}": { es: "Agregar un correo a {0}" },
  "Add {0} to your home screen": { es: "Agregue {0} a su pantalla de inicio" },
  "Added By": { es: "Agregado por" },
  "Added": { es: "Agregado" },
  "Adding...": { es: "Agregando..." },
  "Additional details about this shift...": { es: "Detalles adicionales sobre este turno..." },
  "Address": { es: "Direcci\u00f3n" },
  "Address *": { es: "Direcci\u00f3n *" },
  "Address Line 1": { es: "Direcci\u00f3n, l\u00ednea 1" },
  "Address Line 2": { es: "Direcci\u00f3n, l\u00ednea 2" },
  "Admin": { es: "Administrador" },
  "Admin Dashboard": { es: "Panel de administraci\u00f3n" },
  "Admin access required": { es: "Se requiere acceso de administrador" },
  "Administered By": { es: "Impartida por" },
  "Administration": { es: "Administraci\u00f3n" },
  "Administrator": { es: "Administrador" },
  "Age": { es: "Antig\u00fcedad" },
  "Alaska Time (Anchorage)": { es: "Hora de Alaska (Anchorage)" },
  "Alias added.": { es: "Alias agregado." },
  "Alias value is required": { es: "El valor del alias es obligatorio" },
  "Aliases": { es: "Alias" },
  "All ({0})": { es: "Todos ({0})" },
  "All Buildings": { es: "Todos los edificios" },
  "All Employees": { es: "Todo el personal" },
  "All Floors": { es: "Todos los pisos" },
  "All Reasons": { es: "Todos los motivos" },
  "All Sites": { es: "Todos los sitios" },
  "All Staff": { es: "Todo el personal" },
  "All Status": { es: "Todos los estados" },
  "All Zones": { es: "Todas las zonas" },
  "All actions": { es: "Todas las acciones" },
  "All categories": { es: "Todas las categor\u00edas" },
  "All current": { es: "Todo al d\u00eda" },
  "All day": { es: "Todo el d\u00eda" },
  "All forms": { es: "Todos los formularios" },
  "All people": { es: "Todas las personas" },
  "All roles": { es: "Todos los roles" },
  "All severities": { es: "Todas las severidades" },
  "All sites": { es: "Todos los sitios" },
  "All statuses": { es: "Todos los estados" },
  "All supplies are already assigned to this site": { es: "Todos los suministros ya est\u00e1n asignados a este sitio" },
  "Allow": { es: "Permitir" },
  "Allow pop-ups to export the PDF": { es: "Permita las ventanas emergentes para exportar el PDF" },
  "Allow pop-ups to print the sheet": { es: "Permita las ventanas emergentes para imprimir la hoja" },
  "Already Synced?": { es: "\u00bfYa sincronizado?" },
  "Already logged, not sent again": { es: "Ya ten\u00edan este registro, no se enviaron de nuevo" },
  "Ambiguous: {0}": { es: "Ambiguos: {0}" },
  "Analytics": { es: "An\u00e1lisis" },
  "Annual renewal required": { es: "Requiere renovaci\u00f3n anual" },
  "Annual": { es: "Anual" },
  "Answered": { es: "Respondidas" },
  "Any result": { es: "Cualquier resultado" },
  "App form": { es: "Formulario de la app" },
  "App forms": { es: "Formularios de la app" },
  "Applies to both the on-screen view and the PDF export.": { es: "Se aplica a la vista en pantalla y a la exportaci\u00f3n a PDF." },
  "Approval Status": { es: "Estado de aprobaci\u00f3n" },
  "Approve": { es: "Aprobar" },
  "Approve Drop": { es: "Aprobar la baja" },
  "Approve Request": { es: "Aprobar la solicitud" },
  "Approve and deny time off": { es: "Aprobar y denegar tiempo libre" },
  "Approved": { es: "Aprobada" },
  "Approved Shift": { es: "Turno aprobado" },
  "Approved Vendor List exported": { es: "Lista de proveedores aprobados exportada" },
  "Apt, suite, etc.": { es: "Apto., oficina, etc." },
  "Archive": { es: "Archivar" },
  "Archived": { es: "Archivado" },
  "Areas Needing Improvement": { es: "\u00c1reas que necesitan mejorar" },
  "Asked": { es: "Solicitado" },
  "Assign": { es: "Asignar" },
  "Assign Issue as Task": { es: "Asignar la incidencia como tarea" },
  "Assign Task": { es: "Asignar la tarea" },
  "Assign To": { es: "Asignar a" },
  "Assign To *": { es: "Asignar a *" },
  "Assign as Task": { es: "Asignar como tarea" },
  "Assign to Site": { es: "Asignar a un sitio" },
  "Assigned Task": { es: "Tarea asignada" },
  "Assigned": { es: "Asignado" },
  "Assigned Staff ({0})": { es: "Personal asignado ({0})" },
  "Assigned Supervisor": { es: "Supervisor asignado" },
  "Assigned Tasks": { es: "Tareas asignadas" },
  "Assigned To": { es: "Asignada a" },
  "Assigned to: {0}": { es: "Asignada a: {0}" },
  "Assignment email": { es: "Correo de asignaci\u00f3n" },
  "Assignments": { es: "Asignaciones" },
  "Attach the filled report as a PDF": { es: "Adjuntar el informe completado en PDF" },
  "Attached": { es: "Adjunto" },
  "Attached Photo": { es: "Foto adjunta" },
  "Attached Video": { es: "Video adjunto" },
  "Attempted": { es: "Intentado" },
  "Attendance sheet": { es: "Hoja de asistencia" },
  "Auto-link complete.": { es: "Vinculaci\u00f3n autom\u00e1tica terminada." },
  "Auto-link failed: {0}": { es: "La vinculaci\u00f3n autom\u00e1tica fall\u00f3: {0}" },
  "Available Metadata": { es: "Metadatos disponibles" },
  "Average": { es: "Promedio" },
  "Average score by site": { es: "Puntaje promedio por sitio" },
  "Average score percent over time": { es: "Porcentaje de puntaje promedio a lo largo del tiempo" },
  "Average score percent, lowest first": { es: "Porcentaje de puntaje promedio, el m\u00e1s bajo primero" },
  "Avg (min)": { es: "Prom. (min)" },
  "Avg Fill Time": { es: "Tiempo medio de cobertura" },
  "Avg Score": { es: "Puntaje promedio" },
  "Avg Score (30d)": { es: "Puntaje promedio (30 d)" },
  "Avg score": { es: "Puntaje promedio" },
  "Avg score %": { es: "Puntaje promedio %" },
  "Back": { es: "Volver" },
  "Back to Dashboard": { es: "Volver al panel" },
  "Back to Employees": { es: "Volver al personal" },
  "Back to Sites": { es: "Volver a Sitios" },
  "Back to Staff": { es: "Volver al personal" },
  "Back to reports": { es: "Volver a los informes" },
  "Backfill complete.": { es: "Relleno terminado." },
  "Backfill failed: {0}": { es: "El relleno fall\u00f3: {0}" },
  "Backfilling...": { es: "Rellenando..." },
  "Back|form": { es: "Atr\u00e1s" },
  "Badge": { es: "N\u00famero de empleado" },
  "Badge number": { es: "N\u00famero de empleado" },
  "Based on {0}": { es: "Seg\u00fan {0}" },
  "Benefits / Payroll": { es: "Beneficios / N\u00f3mina" },
  "Bi-Weekly": { es: "Quincenal" },
  "Billing": { es: "Facturaci\u00f3n" },
  "Biohazard Incident and Exposure Report": { es: "Informe de Incidente y Exposici\u00f3n a Riesgo Biol\u00f3gico" },
  "Birthday": { es: "Fecha de nacimiento" },
  "Bldg: {0}": { es: "Edificio: {0}" },
  "Branding": { es: "Marca" },
  "Building": { es: "Edificio" },
  "Buildings": { es: "Edificios" },
  "Bulk Upload PDFs": { es: "Subir PDF en bloque" },
  "Bulk Upload PDFs\n\nSelect up to 50 PDF files downloaded from Jotform's Submissions > Download as PDFs feature.\n\nFiles must use Jotform's default naming pattern (the submission ID at the start of each filename) so we can match them to submission records. Submissions that already have a PDF will be skipped.": { es: "Subir PDF en bloque\n\nSeleccione hasta 50 archivos PDF descargados con la funci\u00f3n Submissions > Download as PDFs de Jotform.\n\nLos archivos deben conservar el nombre que Jotform les da (el ID del env\u00edo al inicio de cada nombre) para poder asociarlos a los registros de env\u00edo. Los env\u00edos que ya tengan un PDF se omitir\u00e1n." },
  "Bulk resolve failed: {0}": { es: "La resoluci\u00f3n en bloque fall\u00f3: {0}" },
  "Bulk upload complete.": { es: "Subida en bloque terminada." },
  "Bulk upload failed: {0}": { es: "La subida en bloque fall\u00f3: {0}" },
  "Bulk-resolve all {0} unresolved failure(s) for this form": { es: "Resolver en bloque los {0} fallos sin resolver de este formulario" },
  "By": { es: "Por" },
  "By person": { es: "Por persona" },
  "By site": { es: "Por sitio" },
  "By {0}": { es: "Por {0}" },
  "By: {0}": { es: "Por: {0}" },
  "Cancel shift": { es: "Cancelar turno" },
  "Cancel this scheduled shift?": { es: "\u00bfCancelar este turno programado?" },
  "Change photo": { es: "Cambiar foto" },
  "Channels": { es: "Canales" },
  "Chat messages": { es: "Mensajes del chat" },
  "Check this answer": { es: "Revise esta respuesta" },
  "CLAIMED": { es: "TOMADO" },
  "Chosen people": { es: "Personas elegidas" },
  "Cleaning": { es: "Limpieza" },
  "Continue": { es: "Continuar" },
  "CSV": { es: "CSV" },
  "CSV exported": { es: "CSV exportado" },
  "Callout": { es: "Ausencia" },
  "Callout Patterns": { es: "Patrones de ausencias" },
  "Callouts": { es: "Ausencias" },
  "Cancel": { es: "Cancelar" },
  "Cancel Inspection": { es: "Cancelar la inspecci\u00f3n" },
  "Cancel ending this pattern": { es: "Cancelar el fin de este patr\u00f3n" },
  "Cancel removing {0}": { es: "Cancelar y no quitar a {0}" },
  "Cancel this date": { es: "Cancelar esta fecha" },
  "Cancel this inspection?": { es: "\u00bfCancelar esta inspecci\u00f3n?" },
  "Cancel this open shift? It will no longer be available for pickup.": { es: "\u00bfCancelar este turno abierto? Ya nadie podr\u00e1 tomarlo." },
  "Cancel this shift? The pattern will not add it again.": { es: "\u00bfCancelar este turno? El patr\u00f3n no lo volver\u00e1 a agregar." },
  "Cancelled": { es: "Cancelado" },
  "Cancelled by the person who asked": { es: "Cancelada por quien la solicit\u00f3" },
  "Capability": { es: "Permiso" },
  "Case": { es: "Caso" },
  "Case updated": { es: "Caso actualizado" },
  "Cases": { es: "Casos" },
  "Categories ({0})": { es: "Categor\u00edas ({0})" },
  "Category": { es: "Categor\u00eda" },
  "Category *": { es: "Categor\u00eda *" },
  "Category Breakdown": { es: "Desglose por categor\u00eda" },
  "Category activated": { es: "Categor\u00eda activada" },
  "Category created": { es: "Categor\u00eda creada" },
  "Category deactivated": { es: "Categor\u00eda desactivada" },
  "Category is required": { es: "La categor\u00eda es obligatoria" },
  "Category updated": { es: "Categor\u00eda actualizada" },
  "Central Time (Chicago)": { es: "Hora del Centro (Chicago)" },
  "Certification Name *": { es: "Nombre de la certificaci\u00f3n *" },
  "Certification Status": { es: "Estado de certificaci\u00f3n" },
  "Certification added": { es: "Certificaci\u00f3n agregada" },
  "Certifications": { es: "Certificaciones" },
  "Certifications ({0})": { es: "Certificaciones ({0})" },
  "Certifications staff must hold...": { es: "Certificaciones que el personal debe tener..." },
  "Change": { es: "Cambiar" },
  "Change admin accounts (role, status, PIN)": { es: "Cambiar cuentas de administrador (rol, estado, PIN)" },
  "Changes start on": { es: "Los cambios comienzan el" },
  "Changing the type will reclassify this value. For example, changing from \"Building\" to \"Zone\" moves it between categories.": { es: "Si cambia el tipo, este valor pasa a otra categor\u00eda. Por ejemplo, al cambiarlo de \"Edificio\" a \"Zona\" pasa de una categor\u00eda a la otra." },
  "Channel: {0}": { es: "Canal: {0}" },
  "Chat": { es: "Chat" },
  "Chemical": { es: "Qu\u00edmico" },
  "Chemical Usage": { es: "Uso de qu\u00edmicos" },
  "Choose a person": { es: "Elija a una persona" },
  "Choose who hears about each kind of report. People get an email and a notice in the app. Outside addresses get email only. When nobody is set, every admin gets a notice in the app.": { es: "Elija qui\u00e9n recibe avisos de cada tipo de informe. Las personas reciben un correo y un aviso en la aplicaci\u00f3n. Las direcciones externas reciben solo el correo. Si no hay nadie, cada administrador recibe un aviso en la aplicaci\u00f3n." },
  "City": { es: "Ciudad" },
  "Claimed": { es: "Tomado" },
  "Claimed By": { es: "Tomado por" },
  "Claimed Pickup Shift": { es: "Turno disponible tomado" },
  "Claims": { es: "Turnos tomados" },
  "Clear": { es: "Borrar" },
  "Clear selection": { es: "Quitar la selecci\u00f3n" },
  "Click Cancel to abort.": { es: "Presione Cancelar para no continuar." },
  "Click Refresh Diagnostic to load.": { es: "Presione Actualizar el diagn\u00f3stico para cargar." },
  "Click \"Add Supply\" to start.": { es: "Haga clic en \"Agregar suministro\" para empezar." },
  "Click \"Create Task\" to assign one.": { es: "Haga clic en \"Crear tarea\" para asignar una." },
  "Click photo to open full size": { es: "Haga clic en la foto para verla en tama\u00f1o completo" },
  "Client": { es: "Cliente" },
  "Client Contact": { es: "Contacto del cliente" },
  "Client Forms": { es: "Formularios del cliente" },
  "Client Name": { es: "Nombre del cliente" },
  "Clock": { es: "Asistencia" },
  "Clock in and out": { es: "Registro de entrada y salida" },
  "Close": { es: "Cerrar" },
  "Closed, no response": { es: "Cerrado sin respuesta" },
  "Collapse sidebar": { es: "Contraer el panel lateral" },
  "Color": { es: "Color" },
  "Color (optional)": { es: "Color (opcional)" },
  "Coming soon": { es: "Pr\u00f3ximamente" },
  "Communication": { es: "Comunicaci\u00f3n" },
  "Company": { es: "Empresa" },
  "Company Identity": { es: "Identidad de la empresa" },
  "Company settings and branding": { es: "Configuraci\u00f3n y marca de la empresa" },
  "Company settings saved": { es: "Configuraci\u00f3n de la empresa guardada" },
  "Completed": { es: "Completado" },
  "Completed At": { es: "Fecha de finalizaci\u00f3n" },
  "Completed By": { es: "Completada por" },
  "Completed Date": { es: "Fecha de finalizaci\u00f3n" },
  "Completed {0}": { es: "Completado el {0}" },
  "Completed: {0}": { es: "Completada: {0}" },
  "Compliance": { es: "Cumplimiento" },
  "Compliance percent, higher is better": { es: "Porcentaje de cumplimiento, m\u00e1s es mejor" },
  "Confidential Communication Record": { es: "Registro confidencial de comunicaciones" },
  "Confidential Employee Record": { es: "Registro confidencial de la persona" },
  "Confidential Record": { es: "Registro confidencial" },
  "Confidential Site Record": { es: "Registro confidencial del sitio" },
  "Config load failed: {0}": { es: "No se pudo cargar la configuraci\u00f3n: {0}" },
  "Confirm Convert": { es: "Confirmar la conversi\u00f3n" },
  "Confirm ending this pattern": { es: "Confirmar el fin de este patr\u00f3n" },
  "Confirm on the next screen.": { es: "Confirme en la siguiente pantalla." },
  "Connected to Live API": { es: "Conectado a la API en vivo" },
  "Contact": { es: "Contacto" },
  "Contact Email": { es: "Correo del contacto" },
  "Contact Information": { es: "Datos de contacto" },
  "Contact Name": { es: "Nombre del contacto" },
  "Contact Phone": { es: "Tel\u00e9fono del contacto" },
  "Contract": { es: "Contrato" },
  "Contract Dates": { es: "Fechas del contrato" },
  "Contract Details": { es: "Detalles del contrato" },
  "Contract Terms": { es: "Condiciones del contrato" },
  "Contract Type": { es: "Tipo de contrato" },
  "Contractor": { es: "Contratista" },
  "Conversation": { es: "Conversaci\u00f3n" },
  "Convert": { es: "Convertir" },
  "Convert Callout": { es: "Convertir la ausencia" },
  "Convert Scheduled Shift to Open Pickup": { es: "Convertir un turno programado en turno abierto" },
  "Convert to Open Pickup": { es: "Convertir en turno abierto" },
  "Copied": { es: "Copiado" },
  "Copy": { es: "Copiar" },
  "Copy the address below and paste it there.": { es: "Copie la direcci\u00f3n de abajo y p\u00e9guela all\u00ed." },
  "Corrective Action Report": { es: "Reporte de acci\u00f3n correctiva" },
  "Cost by site": { es: "Costo por sitio" },
  "Cost is estimated using each supply current cost per unit. Supplies without a price contribute zero cost.": { es: "El costo se estima con el costo actual por unidad de cada suministro. Los suministros sin precio no suman costo." },
  "Cost is estimated using its current cost per unit. Supplies without a price contribute zero cost.": { es: "El costo se estima con su costo actual por unidad. Los suministros sin precio no suman costo." },
  "Cost trend": { es: "Tendencia del costo" },
  "Cost/Unit": { es: "Costo por unidad" },
  "Could not fetch live content: {0}": { es: "No se pudo obtener el contenido en vivo: {0}" },
  "Could not load folder.": { es: "No se pudo cargar la carpeta." },
  "Could not load permissions: {0}": { es: "No se pudieron cargar los permisos: {0}" },
  "Could not load report: {0}": { es: "No se pudo cargar el informe: {0}" },
  "Could not load reports: {0}": { es: "No se pudieron cargar los informes: {0}" },
  "Could not load staff.": { es: "No se pudo cargar el personal." },
  "Could not load team: {0}": { es: "No se pudo cargar el equipo: {0}" },
  "Could not save: {0}": { es: "No se pudo guardar: {0}" },
  "Coverage by Site": { es: "Cobertura por sitio" },
  "Create": { es: "Crear" },
  "Create Assigned Task": { es: "Crear una tarea asignada" },
  "Create Category": { es: "Crear categor\u00eda" },
  "Create Task": { es: "Crear tarea" },
  "Create Template": { es: "Crear la plantilla" },
  "Create and Assign": { es: "Crear y asignar" },
  "Create report": { es: "Crear el informe" },
  "Create, edit and delete scheduled shifts": { es: "Crear, editar y eliminar turnos programados" },
  "Created": { es: "Fecha de creaci\u00f3n" },
  "Created By": { es: "Creada por" },
  "Currently assigned to: {0}. They will be notified of the change.": { es: "Asignada ahora a: {0}. Recibir\u00e1 un aviso del cambio." },
  "Currently open": { es: "Abiertas en este momento" },
  "Currently: Allowed (admins always allowed)": { es: "Ahora: Permitido (los administradores siempre lo tienen)" },
  "Currently: Allowed (override)": { es: "Ahora: Permitido (cambio para esta persona)" },
  "Currently: Allowed (role default)": { es: "Ahora: Permitido (predeterminado del rol)" },
  "Currently: Blocked (override)": { es: "Ahora: Bloqueado (cambio para esta persona)" },
  "Currently: Blocked (role default)": { es: "Ahora: Bloqueado (predeterminado del rol)" },
  "Custodial Laborer": { es: "Auxiliar de limpieza" },
  "Custodial Lead": { es: "L\u00edder de limpieza" },
  "Custom": { es: "Personalizado" },
  "Customer Complaint Log": { es: "Registro de quejas de clientes" },
  "DROP REQ": { es: "SOL. DE BAJA" },
  "Daily": { es: "Diario" },
  "Daily Checklist": { es: "Lista diaria" },
  "Daily Service Log": { es: "Registro diario de servicio" },
  "Damage Report": { es: "Reporte de da\u00f1os" },
  "Dark Mode": { es: "Modo oscuro" },
  "Dark mode": { es: "Modo oscuro" },
  "Dashboard": { es: "Panel" },
  "Date": { es: "Fecha" },
  "Date *": { es: "Fecha *" },
  "Date is required": { es: "La fecha es obligatoria" },
  "Date range:": { es: "Rango de fechas:" },
  "Dates": { es: "Fechas" },
  "Day Porter": { es: "Conserje de d\u00eda" },
  "Days": { es: "D\u00edas" },
  "Deactivate": { es: "Desactivar" },
  "Deactivate \"{0}\"?": { es: "\u00bfDesactivar \"{0}\"?" },
  "Deactivate alias {0}?": { es: "\u00bfDesactivar el alias {0}?" },
  "Deactivate this line item?": { es: "\u00bfDesactivar este elemento?" },
  "Deactivate this site?": { es: "\u00bfDesactivar este sitio?" },
  "Deactivate this template?": { es: "\u00bfDesactivar esta plantilla?" },
  "Decided": { es: "Decidido" },
  "Decided by": { es: "Decidido por" },
  "Deciding this request leaves these shifts as they are. Change or cover them on the schedule.": { es: "Decidir esta solicitud deja estos turnos como est\u00e1n. C\u00e1mbielos o c\u00fabralos en el horario." },
  "Default range": { es: "Rango predeterminado" },
  "Default severity": { es: "Severidad predeterminada" },
  "Default site": { es: "Sitio predeterminado" },
  "Defaults": { es: "Valores predeterminados" },
  "Delete failed: {0}": { es: "No se pudo eliminar: {0}" },
  "Delete": { es: "Eliminar" },
  "Delta": { es: "Diferencia" },
  "Denied": { es: "Denegada" },
  "Deny": { es: "Denegar" },
  "Deny Request": { es: "Denegar la solicitud" },
  "Describe the category": { es: "Describa la categor\u00eda" },
  "Describe what happened": { es: "Describa qu\u00e9 pas\u00f3" },
  "Describe what this service covers...": { es: "Describa lo que cubre este servicio..." },
  "Describe what this vendor supplies...": { es: "Describa lo que suministra este proveedor..." },
  "Description": { es: "Descripci\u00f3n" },
  "Description *": { es: "Descripci\u00f3n *" },
  "Detail load failed: {0}": { es: "No se pudo cargar el detalle: {0}" },
  "Detailed Instructions": { es: "Instrucciones detalladas" },
  "Detailed Instructions (optional)": { es: "Instrucciones detalladas (opcional)" },
  "Diagnostic complete": { es: "Diagn\u00f3stico completado" },
  "Diagnostic failed: {0}": { es: "El diagn\u00f3stico fall\u00f3: {0}" },
  "Diagnostic load failed: {0}": { es: "No se pudo cargar el diagn\u00f3stico: {0}" },
  "Direct message inbox overview": { es: "Resumen de la bandeja de mensajes directos" },
  "Disabled only": { es: "Solo deshabilitados" },
  "Disabled": { es: "Deshabilitado" },
  "Dismiss": { es: "Descartar" },
  "Display name": { es: "Nombre para mostrar" },
  "Do not escalate": { es: "No escalar" },
  "Document": { es: "Documento" },
  "Document added": { es: "Documento agregado" },
  "Document not found": { es: "No se encontr\u00f3 el documento" },
  "Document the scope of work for this site...": { es: "Documente el alcance del trabajo de este sitio..." },
  "Document updated": { es: "Documento actualizado" },
  "Documents": { es: "Documentos" },
  "Documents ({0})": { es: "Documentos ({0})" },
  "Don't show again": { es: "No volver a mostrar" },
  "Done": { es: "Listo" },
  "Download CSV files for audits and clients.": { es: "Descargue archivos CSV para auditor\u00edas y clientes." },
  "Download PDF": { es: "Descargar el PDF" },
  "Download started": { es: "Descarga iniciada" },
  "Download": { es: "Descargar" },
  "Downloaded": { es: "Descargado" },
  "Downloading...": { es: "Descargando..." },
  "Drop Req": { es: "Sol. de baja" },
  "Drop Request": { es: "Solicitud de baja" },
  "Drop Requested": { es: "Baja solicitada" },
  "Drop Requests": { es: "Solicitudes de baja" },
  "Drop approved": { es: "Baja aprobada" },
  "Drop approved, shift is now open": { es: "Baja aprobada, el turno queda abierto" },
  "Drop approved, shift is open": { es: "Baja aprobada, el turno queda abierto" },
  "Drop request denied": { es: "Solicitud de baja denegada" },
  "Dropdown Options": { es: "Opciones de las listas" },
  "Dropdown and site lookups": { es: "Opciones de las listas y listas por sitio" },
  "Due Date": { es: "Fecha l\u00edmite" },
  "Due Time": { es: "Hora l\u00edmite" },
  "Due": { es: "Vence" },
  "Due: {0}": { es: "Vence: {0}" },
  "Duplicate": { es: "Duplicar" },
  "Duration": { es: "Duraci\u00f3n" },
  "EIN / Tax ID": { es: "EIN / N\u00famero de identificaci\u00f3n fiscal" },
  "EIN {0}": { es: "EIN {0}" },
  "EPA Reg #": { es: "N.\u00ba de registro de la EPA" },
  "Eastern Time (New York)": { es: "Hora del Este (Nueva York)" },
  "Edit Form": { es: "Editar el formulario" },
  "Edit": { es: "Editar" },
  "Edit Category": { es: "Editar categor\u00eda" },
  "Edit Details": { es: "Editar los detalles" },
  "Edit Document": { es: "Editar el documento" },
  "Edit Scheduled Inspection": { es: "Editar la inspecci\u00f3n programada" },
  "Edit Scheduled Shift": { es: "Editar el turno programado" },
  "Edit Service": { es: "Editar el servicio" },
  "Edit Site Details": { es: "Editar los detalles del sitio" },
  "Edit Site Lookup": { es: "Editar opci\u00f3n del sitio" },
  "Edit Staff Info": { es: "Editar los datos de la persona" },
  "Edit Supply": { es: "Editar el suministro" },
  "Edit Task": { es: "Editar la tarea" },
  "Edit Training Record": { es: "Editar el registro de capacitaci\u00f3n" },
  "Edit Value": { es: "Editar valor" },
  "Edit Vendor": { es: "Editar el proveedor" },
  "Edit report": { es: "Editar el informe" },
  "Email": { es: "Correo" },
  "Email *": { es: "Correo *" },
  "Email only": { es: "Solo correo" },
  "Emergency Contact": { es: "Contacto de emergencia" },
  "Emergency Phone": { es: "Tel\u00e9fono de emergencia" },
  "Employee": { es: "Persona" },
  "Employee ID": { es: "ID de la persona" },
  "Employee Information": { es: "Datos de la persona" },
  "Employee Report": { es: "Informe de la persona" },
  "Employee is required": { es: "La persona es obligatoria" },
  "Employee, name, and type are required": { es: "La persona, el nombre y el tipo son obligatorios" },
  "Employees": { es: "Personal" },
  "Employment": { es: "Empleo" },
  "Employment Type": { es: "Tipo de empleo" },
  "Empty": { es: "Vac\u00edo" },
  "Enabled (included in sync)": { es: "Habilitado (incluido en la sincronizaci\u00f3n)" },
  "Enabled only": { es: "Solo habilitados" },
  "Enabled": { es: "Habilitado" },
  "Enabled: Any": { es: "Habilitados: todos" },
  "End": { es: "Fin" },
  "End Date": { es: "Fecha de fin" },
  "End Time": { es: "Hora de fin" },
  "End Time *": { es: "Hora de fin *" },
  "End pattern": { es: "Terminar el patr\u00f3n" },
  "End this pattern after": { es: "Terminar este patr\u00f3n despu\u00e9s del" },
  "End this pattern after {0}? Future shifts it added are removed, except ones changed by hand.": { es: "\u00bfTerminar este patr\u00f3n despu\u00e9s del {0}? Los turnos futuros que agreg\u00f3 se eliminan, salvo los que se cambiaron a mano." },
  "End time": { es: "Hora de fin" },
  "Ended": { es: "Terminado" },
  "Ends": { es: "Termina" },
  "Enforced": { es: "Se aplica" },
  "Enforced capabilities take effect immediately. Rolling out capabilities are saved against the person now and begin enforcing as each area is wired.": { es: "Los permisos que se aplican tienen efecto de inmediato. Los que est\u00e1n en implementaci\u00f3n se guardan ahora para la persona y empiezan a aplicarse a medida que se conecta cada \u00e1rea." },
  "English": { es: "Ingl\u00e9s" },
  "Enter": { es: "Ingrese" },
  "Enter a new 4-digit PIN for this staff member.": { es: "Escriba un PIN nuevo de 4 d\u00edgitos para esta persona." },
  "Entity Type": { es: "Tipo de registro" },
  "Environmental Compliance Audit": { es: "Auditor\u00eda de cumplimiento ambiental" },
  "Equipment": { es: "Equipo" },
  "Error": { es: "Error" },
  "Errors: {0}": { es: "Errores: {0}" },
  "Escalate to": { es: "Escalar a" },
  "Escalated to": { es: "Escalado a" },
  "Escalating sends that person an email. The email carries no case text.": { es: "Al escalar, se env\u00eda un correo a esa persona. El correo no incluye el texto del caso." },
  "Escalation email": { es: "Correo de escalamiento" },
  "Estimated cost": { es: "Costo estimado" },
  "Estimated cost (USD)": { es: "Costo estimado (USD)" },
  "Estimated cost by period": { es: "Costo estimado por periodo" },
  "Estimated cost by site": { es: "Costo estimado por sitio" },
  "Estimated cost from logged usage at current prices": { es: "Costo estimado a partir del uso registrado, a los precios actuales" },
  "Estimated cost split": { es: "Reparto del costo estimado" },
  "Estimated cost trend": { es: "Tendencia del costo estimado" },
  "Estimated cost, top 10": { es: "Costo estimado, los 10 principales" },
  "Estimated supply cost per {0}": { es: "Costo estimado de suministros por {0}" },
  "Evaluation saved": { es: "Evaluaci\u00f3n guardada" },
  "Evaluations": { es: "Evaluaciones" },
  "Events": { es: "Registros" },
  "Every email about this form carries everything the report says, to every person and address on these lists.": { es: "Cada correo sobre este formulario lleva todo lo que dice el informe, a cada persona y direcci\u00f3n de estas listas." },
  "Every form": { es: "Todos los formularios" },
  "Every message": { es: "Cada mensaje" },
  "Every view, download, and print of an original Jotform PDF is recorded here with user, timestamp, IP, and success status. This log is append-only and survives submission deletion via text snapshots.": { es: "Cada vista, descarga e impresi\u00f3n de un PDF original de Jotform queda registrada aqu\u00ed con la persona, la fecha y hora, la IP y el resultado. Este registro solo crece y se conserva aunque el env\u00edo se elimine, gracias a copias en texto." },
  "Every view, download, print and email of a form's PDF, from Jotform or from the app, is recorded here with the person, the time, the address it came from and whether it succeeded. This log is append-only and survives a submission's deletion through text snapshots.": { es: "Cada vista, descarga, impresi\u00f3n y correo del PDF de un formulario, de Jotform o de la app, queda registrado aqu\u00ed con la persona, la hora, la direcci\u00f3n desde la que se hizo y si tuvo \u00e9xito. Este registro solo agrega y sobrevive a la eliminaci\u00f3n de un env\u00edo mediante copias de texto." },
  "Everyone": { es: "Todos" },
  "Everyone active": { es: "Todas las personas activas" },
  "Exp: {0}": { es: "Vence: {0}" },
  "Expand sidebar": { es: "Ampliar el panel lateral" },
  "Expired Docs": { es: "Docs. vencidos" },
  "Expired Documents": { es: "Documentos vencidos" },
  "Expired Items": { es: "Elementos vencidos" },
  "Expired Training": { es: "Capacitaciones vencidas" },
  "Expires in {0}d": { es: "Vence en {0} d" },
  "Expires {0}d": { es: "Vence en {0} d" },
  "Expiring (30 days)": { es: "Por vencer (30 d\u00edas)" },
  "Expiring (30d)": { es: "Por vencer (30 d)" },
  "Expiring Within 30 Days": { es: "Vencen en los pr\u00f3ximos 30 d\u00edas" },
  "Expiry in days (optional)": { es: "Vencimiento en d\u00edas (opcional)" },
  "Expiry": { es: "Vencimiento" },
  "Expiry Date": { es: "Fecha de vencimiento" },
  "Expiry Date (optional)": { es: "Fecha de vencimiento (opcional)" },
  "Explain why this is being reassigned...": { es: "Explique por qu\u00e9 se reasigna..." },
  "Explain why this task is being reassigned...": { es: "Explique por qu\u00e9 se reasigna esta tarea..." },
  "Export": { es: "Exportar" },
  "Export AVL": { es: "Exportar la lista de aprobados" },
  "Export CSV": { es: "Exportar CSV" },
  "Export PDF": { es: "Exportar PDF" },
  "Export data (CSV)": { es: "Exportar datos (CSV)" },
  "Extra Coverage": { es: "Cobertura adicional" },
  "Extra large": { es: "Muy grande" },
  "Failed only": { es: "Solo fallidos" },
  "Failed retry": { es: "Reintento de fallos" },
  "Failed to load aliases: {0}": { es: "No se pudieron cargar los alias: {0}" },
  "Failed to load analytics: {0}": { es: "No se pudieron cargar los an\u00e1lisis: {0}" },
  "Failed to load supplies": { es: "No se pudieron cargar los suministros" },
  "Failure marked resolved.": { es: "Fallo marcado como resuelto." },
  "Failures load failed: {0}": { es: "No se pudieron cargar los fallos: {0}" },
  "Failures": { es: "Fallos" },
  "Fastest": { es: "M\u00e1s r\u00e1pido" },
  "Fetch error: {0}": { es: "Error al obtener: {0}" },
  "Fetching from Jotform. This may take a moment for forms with many submissions...": { es: "Obteniendo de Jotform. Puede tardar un momento en formularios con muchos env\u00edos..." },
  "Fetching...": { es: "Obteniendo..." },
  "File": { es: "Archivo" },
  "File fetch failed ({0}): {1}": { es: "No se pudo obtener el archivo ({0}): {1}" },
  "File must be under 50MB": { es: "El archivo debe pesar menos de 50 MB" },
  "Filed by {0}": { es: "Presentado por {0}" },
  "Filed by": { es: "Presentado por" },
  "Filed forms": { es: "Formularios presentados" },
  "Filed forms ({0})": { es: "Formularios presentados ({0})" },
  "Filed {0}": { es: "Presentado el {0}" },
  "Filed": { es: "Presentado" },
  "Filing email to the team": { es: "Correo de presentaci\u00f3n al equipo" },
  "Fill Rate": { es: "Tasa de cobertura" },
  "Fill Rate by Reason": { es: "Tasa de cobertura por motivo" },
  "Fill this in at your desk. Answers are saved when you press Save, and a sign-off is made with its own button.": { es: "Complete esta secci\u00f3n en su escritorio. Las respuestas se guardan al presionar Guardar, y la firma se hace con su propio bot\u00f3n." },
  "Filled through {0}": { es: "Cubierto hasta el {0}" },
  "Filter: {0}": { es: "Filtro: {0}" },
  "First Name": { es: "Nombre" },
  "First Name *": { es: "Nombre *" },
  "First Name A-Z": { es: "Nombre A-Z" },
  "First response (h)": { es: "Primera respuesta (h)" },
  "First response (h, median)": { es: "Primera respuesta (h, mediana)" },
  "Fl {0}": { es: "Piso {0}" },
  "Floor": { es: "Piso" },
  "Floor Plans ({0})": { es: "Planos ({0})" },
  "Floor plan removed": { es: "Plano quitado" },
  "Floor plan uploaded": { es: "Plano subido" },
  "Floor {0}": { es: "Piso {0}" },
  "Floor: {0}": { es: "Piso: {0}" },
  "Floors": { es: "Pisos" },
  "Folders API": { es: "API de carpetas" },
  "Folders found": { es: "Carpetas encontradas" },
  "For HR documents, you can also link from the HR Records page by selecting a synced submission in the Add Document modal.": { es: "Para documentos de personal, tambi\u00e9n puede vincular desde Expedientes de personal eligiendo un env\u00edo sincronizado en la ventana Agregar documento." },
  "For": { es: "Por" },
  "Force Fetch": { es: "Forzar descarga" },
  "Force-fetch failed: {0}": { es: "La descarga forzada fall\u00f3: {0}" },
  "Force-fetched {0} ({1}){2}": { es: "Se forz\u00f3 la descarga de {0} ({1}){2}" },
  "Forced fetch": { es: "Descarga forzada" },
  "Form Detail": { es: "Detalle del formulario" },
  "Form sent. The people who handle these forms have been told.": { es: "Formulario enviado. Ya se avis\u00f3 a las personas que atienden estos formularios." },
  "Form Title": { es: "T\u00edtulo del formulario" },
  "Form catalog sync pulls the list of Jotform forms. Submissions sync pulls only new or updated submissions for enabled forms. Full Refresh bypasses the incremental filter and re-pulls every submission for every enabled form (use sparingly, see warning).": { es: "La sincronizaci\u00f3n del cat\u00e1logo trae la lista de formularios de Jotform. La sincronizaci\u00f3n de env\u00edos trae solo los env\u00edos nuevos o actualizados de los formularios habilitados. La actualizaci\u00f3n completa omite el filtro incremental y vuelve a traer cada env\u00edo de cada formulario habilitado (\u00fasela con moderaci\u00f3n, vea la advertencia)." },
  "Form default: {0}": { es: "Del formulario: {0}" },
  "Form updated": { es: "Formulario actualizado" },
  "Form": { es: "Formulario" },
  "Forms & Jotform Integration": { es: "Formularios e integraci\u00f3n con Jotform" },
  "Forms filed": { es: "Formularios presentados" },
  "Forms load failed: {0}": { es: "No se pudieron cargar los formularios: {0}" },
  "Forms with Gaps": { es: "Formularios con faltantes" },
  "Forms": { es: "Formularios" },
  "Forms:": { es: "Formularios:" },
  "French": { es: "Franc\u00e9s" },
  "Fri": { es: "Vie" },
  "Friday": { es: "Viernes" },
  "From a customer": { es: "De un cliente" },
  "From the app": { es: "Desde la aplicaci\u00f3n" },
  "From the dashboard": { es: "Desde el panel" },
  "From: {0}": { es: "Desde: {0}" },
  "Full Refresh": { es: "Actualizaci\u00f3n completa" },
  "Full Refresh. Read Before Running.": { es: "Actualizaci\u00f3n completa. Lea esto antes de ejecutarla." },
  "Full Time": { es: "Tiempo completo" },
  "Full name": { es: "Nombre completo" },
  "Full refresh complete. Processed {0} submissions ({1} new, {2} updated) across {3} form(s)": { es: "Actualizaci\u00f3n completa terminada. Se procesaron {0} env\u00edos ({1} nuevos, {2} actualizados) en {3} formulario(s)" },
  "Full refresh failed: {0}": { es: "La actualizaci\u00f3n completa fall\u00f3: {0}" },
  "F{0}": { es: "P{0}" },
  "General": { es: "General" },
  "General Info": { es: "Informaci\u00f3n general" },
  "General chat": { es: "Chat general" },
  "Generate badge number": { es: "Generar n\u00famero de empleado" },
  "Generated by {0} Operations Platform": { es: "Generado por la plataforma de operaciones de {0}" },
  "Generated {0}": { es: "Generado el {0}" },
  "Given in English": { es: "Impartida en ingl\u00e9s" },
  "Given in French": { es: "Impartida en franc\u00e9s" },
  "Given in Spanish": { es: "Impartida en espa\u00f1ol" },
  "Green": { es: "Ecol\u00f3gico" },
  "Green Cert Type": { es: "Tipo de certificaci\u00f3n ecol\u00f3gica" },
  "Green Certified Product": { es: "Producto con certificaci\u00f3n ecol\u00f3gica" },
  "Green certified": { es: "Con certificaci\u00f3n ecol\u00f3gica" },
  "Green cleaning": { es: "Limpieza ecol\u00f3gica" },
  "Green-certified share": { es: "Proporci\u00f3n con certificaci\u00f3n ecol\u00f3gica" },
  "Green-certified share of cost": { es: "Proporci\u00f3n del costo con certificaci\u00f3n ecol\u00f3gica" },
  "Green-certified share of estimated cost: {0} of {1}": { es: "Proporci\u00f3n del costo estimado con certificaci\u00f3n ecol\u00f3gica: {0} de {1}" },
  "HR - Onboarding": { es: "RR. HH. - Incorporaci\u00f3n" },
  "HR - Ongoing / Annual": { es: "RR. HH. - Continuo / Anual" },
  "HR Document": { es: "Documento de personal" },
  "HR Files": { es: "Archivos de personal" },
  "HR Onboarding Step": { es: "Paso de incorporaci\u00f3n" },
  "HR Records": { es: "Expedientes de personal" },
  "HR Training Record": { es: "Registro de capacitaci\u00f3n" },
  "Hand over": { es: "Entregar" },
  "Hand to": { es: "Entregar a" },
  "Has details": { es: "Tiene detalles" },
  "Has original PDF (show PDF buttons)": { es: "Tiene PDF original (mostrar los botones de PDF)" },
  "Hawaii Time (Honolulu)": { es: "Hora de Haw\u00e1i (Honolulu)" },
  "Held by": { es: "A cargo de" },
  "Help": { es: "Ayuda" },
  "Hide": { es: "Ocultar" },
  "Hide manual entry": { es: "Ocultar la entrada manual" },
  "Hide test accounts": { es: "Ocultar las cuentas de prueba" },
  "High": { es: "Alta" },
  "Higher means more spend": { es: "M\u00e1s alto significa m\u00e1s gasto" },
  "Hire Date": { es: "Fecha de contrataci\u00f3n" },
  "Hire Date (newest)": { es: "Fecha de contrataci\u00f3n (m\u00e1s reciente)" },
  "Hire Date (oldest)": { es: "Fecha de contrataci\u00f3n (m\u00e1s antigua)" },
  "Hired {0}": { es: "Contrataci\u00f3n: {0}" },
  "Hourly Rate": { es: "Tarifa por hora" },
  "Hours": { es: "Horas" },
  "Hours, lower is better": { es: "Horas, menos es mejor" },
  "How aliases work.": { es: "C\u00f3mo funcionan los alias." },
  "How is this service priced?": { es: "\u00bfC\u00f3mo se cobra este servicio?" },
  "How to read this.": { es: "C\u00f3mo leer esto." },
  "ID {0}": { es: "ID {0}" },
  "Inbox": { es: "Bandeja de entrada" },
  "IP": { es: "IP" },
  "In Progress": { es: "En curso" },
  "In app": { es: "En la aplicaci\u00f3n" },
  "Inactive only": { es: "Solo inactivas" },
  "Inheriting form's category. Pick a value to override.": { es: "Usa la categor\u00eda del formulario. Elija un valor para cambiarla." },
  "Initialize Onboarding": { es: "Iniciar la incorporaci\u00f3n" },
  "Inspection": { es: "Inspecci\u00f3n" },
  "Inspection Analytics Report": { es: "Informe de an\u00e1lisis de inspecciones" },
  "Inspection Details": { es: "Detalles de la inspecci\u00f3n" },
  "Inspection Report": { es: "Informe de inspecci\u00f3n" },
  "Inspection Scores and Quality": { es: "Puntajes de inspecci\u00f3n y calidad" },
  "Inspection Templates": { es: "Plantillas de inspecci\u00f3n" },
  "Inspection and Quality": { es: "Inspecciones y calidad" },
  "Inspection cancelled": { es: "Inspecci\u00f3n cancelada" },
  "Inspection photo": { es: "Foto de la inspecci\u00f3n" },
  "Inspection report settings": { es: "Configuraci\u00f3n del informe de inspecciones" },
  "Inspection rescheduled": { es: "Inspecci\u00f3n reprogramada" },
  "Inspection results over the selected period": { es: "Resultados de las inspecciones en el periodo seleccionado" },
  "Inspection scheduled": { es: "Inspecci\u00f3n programada" },
  "Inspection score percent": { es: "Porcentaje de puntaje de inspecci\u00f3n" },
  "Inspection score trend": { es: "Tendencia del puntaje de inspecci\u00f3n" },
  "Inspection scores by site": { es: "Puntajes de inspecci\u00f3n por sitio" },
  "Inspection templates and scheduling": { es: "Plantillas y programaci\u00f3n de inspecciones" },
  "Inspection updated": { es: "Inspecci\u00f3n actualizada" },
  "Inspections": { es: "Inspecciones" },
  "Inspections (Last 30 Days)": { es: "Inspecciones (\u00faltimos 30 d\u00edas)" },
  "Inspections in period": { es: "Inspecciones en el periodo" },
  "Inspections: templates and scheduling": { es: "Inspecciones: plantillas y programaci\u00f3n" },
  "Install": { es: "Instalar" },
  "Integrations": { es: "Integraciones" },
  "Integrations and forms": { es: "Integraciones y formularios" },
  "Internal notes about this employee...": { es: "Notas internas sobre esta persona..." },
  "Inventory": { es: "Inventario" },
  "Inventory ({0})": { es: "Inventario ({0})" },
  "Inventory usage and requests": { es: "Uso del inventario y solicitudes" },
  "Invite sent.": { es: "Invitaci\u00f3n enviada." },
  "Issue": { es: "Incidencia" },
  "Issue Detail": { es: "Detalle de la incidencia" },
  "Issue Response and Resolution": { es: "Respuesta y resoluci\u00f3n de incidencias" },
  "Issue Tracker": { es: "Registro de incidencias" },
  "Issue report settings": { es: "Configuraci\u00f3n del informe de incidencias" },
  "Issue to task assignment": { es: "Asignaci\u00f3n de incidencias a tareas" },
  "Issued Date": { es: "Fecha de emisi\u00f3n" },
  "Issuer": { es: "Entidad emisora" },
  "Issues": { es: "Incidencias" },
  "Issues Report": { es: "Informe de incidencias" },
  "Issues Summary": { es: "Resumen de incidencias" },
  "Issues: report and track": { es: "Incidencias: informar y dar seguimiento" },
  "Issues|report category": { es: "Problemas" },
  "Issuing Body": { es: "Entidad emisora" },
  "It opens like an app, one tap from your home screen.": { es: "Se abre como una aplicaci\u00f3n, a un toque desde su pantalla de inicio." },
  "Item": { es: "Elemento" },
  "Item Label *": { es: "Nombre del elemento *" },
  "Item added": { es: "Elemento agregado" },
  "Item deactivated": { es: "Elemento desactivado" },
  "Item label": { es: "Nombre del elemento" },
  "Item saved": { es: "Elemento guardado" },
  "Items Scored": { es: "Elementos calificados" },
  "JOTFORM_API_KEY not configured": { es: "JOTFORM_API_KEY no est\u00e1 configurada" },
  "Jotform API limits vary by account tier. Free accounts allow 1,000 calls per day. Each enabled form costs one API call, plus one per 1,000 submissions. If you have many forms with many submissions, a full refresh could consume a large portion of your daily quota.": { es: "Los l\u00edmites de la API de Jotform dependen del tipo de cuenta. Las cuentas gratuitas permiten 1,000 llamadas por d\u00eda. Cada formulario habilitado cuesta una llamada, m\u00e1s una por cada 1,000 env\u00edos. Si tiene muchos formularios con muchos env\u00edos, una actualizaci\u00f3n completa puede consumir gran parte de su cuota diaria." },
  "Jotform Account": { es: "Cuenta de Jotform" },
  "Jotform Form ID": { es: "ID del formulario en Jotform" },
  "Jotform Form": { es: "Formulario de Jotform" },
  "Jotform Reference": { es: "Referencia de Jotform" },
  "Jotform Submission (optional)": { es: "Env\u00edo de Jotform (opcional)" },
  "Jotform Submission ID": { es: "ID del env\u00edo en Jotform" },
  "Jotform Submissions": { es: "Env\u00edos en Jotform" },
  "Jotform submission ID or URL": { es: "ID o enlace del env\u00edo de Jotform" },
  "Jotform": { es: "Jotform" },
  "Jotform:": { es: "Jotform:" },
  "Keep at least one of Email or In app on.": { es: "Deje activado al menos Correo o En la aplicaci\u00f3n." },
  "Keep filling": { es: "Seguir llenando" },
  "Key Status": { es: "Estado de la clave" },
  "Key:": { es: "Clave:" },
  "Leave": { es: "Salir" },
  "Leave this form? Your saved answers stay, and you can continue from Filed forms.": { es: "\u00bfSalir de este formulario? Sus respuestas guardadas se quedan y puede continuar desde Formularios presentados." },
  "LIVE": { es: "EN VIVO" },
  "Label": { es: "Nombre" },
  "Label (displayed)": { es: "Nombre (se muestra)" },
  "Label (displayed) *": { es: "Nombre (se muestra) *" },
  "Label *": { es: "Nombre *" },
  "Label and slug required": { es: "El nombre y el identificador son obligatorios" },
  "Label and zone required": { es: "La descripci\u00f3n y la zona son obligatorias" },
  "Labels API": { es: "API de etiquetas" },
  "Labels found": { es: "Etiquetas encontradas" },
  "Labor reports": { es: "Informes de mano de obra" },
  "Language": { es: "Idioma" },
  "Language it was given in": { es: "Idioma en que se imparti\u00f3" },
  "Large": { es: "Grande" },
  "Largest": { es: "M\u00e1ximo" },
  "Last 30 Days": { es: "\u00daltimos 30 d\u00edas" },
  "Last 60 Days": { es: "\u00daltimos 60 d\u00edas" },
  "Last 90 Days": { es: "\u00daltimos 90 d\u00edas" },
  "Last Inspection": { es: "\u00daltima inspecci\u00f3n" },
  "Last Matched": { es: "\u00daltima coincidencia" },
  "Last Name": { es: "Apellido" },
  "Last Name A-Z": { es: "Apellido A-Z" },
  "Last Name Z-A": { es: "Apellido Z-A" },
  "Last Review Date": { es: "Fecha de la \u00faltima revisi\u00f3n" },
  "Last Sync": { es: "\u00daltima sincronizaci\u00f3n" },
  "Last Sync:": { es: "\u00daltima sincronizaci\u00f3n:" },
  "Last Synced": { es: "\u00daltima sincronizaci\u00f3n" },
  "Last Week": { es: "La semana pasada" },
  "Last activity": { es: "\u00daltima actividad" },
  "Last day": { es: "\u00daltimo d\u00eda" },
  "Last refreshed: {0}": { es: "\u00daltima actualizaci\u00f3n: {0}" },
  "Last updated": { es: "\u00daltima actualizaci\u00f3n" },
  "Last {0} to {1}": { es: "Del {0} al {1}" },
  "Latest": { es: "M\u00e1s reciente" },
  "Latest Date": { es: "\u00daltima fecha" },
  "Latest Score": { es: "\u00daltimo puntaje" },
  "Latest: {0}": { es: "\u00daltima: {0}" },
  "Lead Time (days)": { es: "Tiempo de entrega (d\u00edas)" },
  "Legal / Consent": { es: "Legal / Consentimiento" },
  "Legal name": { es: "Nombre legal" },
  "Light Mode": { es: "Modo claro" },
  "Light mode": { es: "Modo claro" },
  "Link Site": { es: "Vincular un sitio" },
  "Link Site to Service": { es: "Vincular un sitio al servicio" },
  "Link Submission to Record": { es: "Vincular el env\u00edo a un registro" },
  "Link Supply": { es: "Vincular un suministro" },
  "Link Supply to Vendor": { es: "Vincular un suministro al proveedor" },
  "Link failed: {0}": { es: "No se pudo vincular: {0}" },
  "Link to Record": { es: "Vincular a un registro" },
  "Link to the app": { es: "Enlace a la aplicaci\u00f3n" },
  "Link to user...": { es: "Vincular a una persona..." },
  "Link": { es: "V\u00ednculo" },
  "Link: Any": { es: "V\u00ednculo: todos" },
  "Linked Supplies": { es: "Suministros vinculados" },
  "Linked only": { es: "Solo vinculados" },
  "Linked to user": { es: "Vinculado a la persona" },
  "Linked to {0}": { es: "Vinculado a {0}" },
  "Linked": { es: "Vinculado" },
  "Linked. New {0} alias recorded.": { es: "Vinculado. Se registr\u00f3 un nuevo alias de {0}." },
  "Linked. Warning: {0}. Review the Aliases tab to resolve.": { es: "Vinculado. Aviso: {0}. Revise la pesta\u00f1a Alias para resolverlo." },
  "Linking...": { es: "Vinculando..." },
  "Live Answers (fetched from Jotform)": { es: "Respuestas en vivo (obtenidas de Jotform)" },
  "Live Operations": { es: "Operaciones en vivo" },
  "Live Ops": { es: "Operaciones en vivo" },
  "Load More": { es: "Ver m\u00e1s" },
  "Load more": { es: "Ver m\u00e1s" },
  "Loading HR files...": { es: "Cargando los archivos de personal..." },
  "Loading aliases...": { es: "Cargando los alias..." },
  "Loading analytics...": { es: "Cargando los an\u00e1lisis..." },
  "Loading buildings...": { es: "Cargando los edificios..." },
  "Loading cases...": { es: "Cargando los casos..." },
  "Loading company settings...": { es: "Cargando la configuraci\u00f3n de la empresa..." },
  "Loading folder...": { es: "Cargando la carpeta..." },
  "Loading from Jotform...": { es: "Cargando desde Jotform..." },
  "Loading patterns...": { es: "Cargando los patrones..." },
  "Loading permissions...": { es: "Cargando los permisos..." },
  "Loading record details...": { es: "Cargando los datos del registro..." },
  "Loading reports...": { es: "Cargando los informes..." },
  "Loading schedule...": { es: "Cargando el horario..." },
  "Loading settings...": { es: "Cargando la configuraci\u00f3n..." },
  "Loading submissions...": { es: "Cargando los env\u00edos..." },
  "Loading team...": { es: "Cargando el equipo..." },
  "Loading time off...": { es: "Cargando el tiempo libre..." },
  "Loading timeline...": { es: "Cargando el historial..." },
  "Loading...": { es: "Cargando..." },
  "Locked on": { es: "Siempre activo" },
  "Log training for several people": { es: "Registrar capacitaci\u00f3n de varias personas" },
  "Logo": { es: "Logotipo" },
  "Logo must be an image file": { es: "El logotipo debe ser un archivo de imagen" },
  "Logo must be under 10MB": { es: "El logotipo debe pesar menos de 10 MB" },
  "Logo uploaded. Click Save to keep it.": { es: "Logotipo subido. Haga clic en Guardar para conservarlo." },
  "Logout": { es: "Cerrar sesi\u00f3n" },
  "Low": { es: "Baja" },
  "Low Stock": { es: "Existencias bajas" },
  "Low Threshold": { es: "Existencias m\u00ednimas" },
  "Lowest Scoring Items": { es: "Elementos con el puntaje m\u00e1s bajo" },
  "Lowest-scoring items": { es: "Elementos con el puntaje m\u00e1s bajo" },
  "Maintenance": { es: "Mantenimiento" },
  "Manage roles and permissions": { es: "Administrar roles y permisos" },
  "Management": { es: "Gesti\u00f3n" },
  "Manual time entry and shift edits": { es: "Registro manual de horas y cambios de turnos" },
  "Manually linking a submission via the Submissions tab automatically records aliases. You can also add them above.": { es: "Vincular a mano un env\u00edo desde la pesta\u00f1a Env\u00edos registra alias autom\u00e1ticamente. Tambi\u00e9n puede agregarlos arriba." },
  "Manufacturer": { es: "Fabricante" },
  "Mark Reviewed": { es: "Marcar como revisado" },
  "Mark all read": { es: "Marcar todo como le\u00eddo" },
  "Mark as preferred vendor for this supply": { es: "Marcar como proveedor preferido de este suministro" },
  "Marketplace": { es: "Tablero" },
  "Marketplace Coverage (Last 30 Days)": { es: "Cobertura en el tablero (\u00faltimos 30 d\u00edas)" },
  "Matched Employee": { es: "Persona asociada" },
  "Matched: {0}": { es: "Coincidentes: {0}" },
  "Matches": { es: "Coincidencias" },
  "Max Score (points)": { es: "Puntaje m\u00e1ximo (puntos)" },
  "Max score": { es: "Puntaje m\u00e1ximo" },
  "Median": { es: "Mediana" },
  "Median (min)": { es: "Mediana (min)" },
  "Median first response": { es: "Mediana de primera respuesta" },
  "Median hours per {0}": { es: "Mediana de horas por {0}" },
  "Median resolution": { es: "Mediana de resoluci\u00f3n" },
  "Median resolution (h)": { es: "Mediana de resoluci\u00f3n (h)" },
  "Median resolution by site": { es: "Mediana de resoluci\u00f3n por sitio" },
  "Medium": { es: "Media" },
  "Menu": { es: "Men\u00fa" },
  "Message": { es: "Mensaje" },
  "Messages": { es: "Mensajes" },
  "Messages did not load.": { es: "Los mensajes no se cargaron." },
  "Min order, availability notes...": { es: "Pedido m\u00ednimo, notas de disponibilidad..." },
  "Min: {0}": { es: "M\u00edn.: {0}" },
  "Missing IDs load failed: {0}": { es: "No se pudieron cargar los ID faltantes: {0}" },
  "Missing:": { es: "Faltantes:" },
  "Mon": { es: "Lun" },
  "Monday": { es: "Lunes" },
  "Month": { es: "Mes" },
  "Monthly": { es: "Mensual" },
  "Monthly Totals": { es: "Totales del mes" },
  "Monthly Value": { es: "Valor mensual" },
  "Monthly Value ($)": { es: "Valor mensual ($)" },
  "More": { es: "M\u00e1s" },
  "Most Recent Activity": { es: "Actividad m\u00e1s reciente" },
  "Most recent inspection per site": { es: "Inspecci\u00f3n m\u00e1s reciente por sitio" },
  "Mountain Time (Denver)": { es: "Hora de la Monta\u00f1a (Denver)" },
  "Mountain Time, no daylight saving (Phoenix)": { es: "Hora de la Monta\u00f1a, sin horario de verano (Phoenix)" },
  "My alerts": { es: "Mis alertas" },
  "N/A": { es: "N/D" },
  "Name": { es: "Nombre" },
  "Name *": { es: "Nombre *" },
  "Name and address required": { es: "El nombre y la direcci\u00f3n son obligatorios" },
  "Name and category are required": { es: "El nombre y la categor\u00eda son obligatorios" },
  "Name required": { es: "El nombre es obligatorio" },
  "Name, category, and unit required": { es: "El nombre, la categor\u00eda y la unidad son obligatorios" },
  "Name, phone, and email required": { es: "El nombre, el tel\u00e9fono y el correo son obligatorios" },
  "Names already used": { es: "Nombres ya usados" },
  "Needs response": { es: "Necesitan respuesta" },
  "New Gear Request": { es: "Solicitud de equipo nuevo" },
  "New Inspection Template": { es: "Nueva plantilla de inspecci\u00f3n" },
  "New PIN (4 digits)": { es: "PIN nuevo (4 d\u00edgitos)" },
  "New Shift": { es: "Turno nuevo" },
  "New Shifts": { es: "Turnos nuevos" },
  "New Supply Request": { es: "Solicitud de suministro nuevo" },
  "New Template": { es: "Nueva plantilla" },
  "New announcement": { es: "Nuevo anuncio" },
  "New report": { es: "Nuevo informe" },
  "Next": { es: "Siguiente" },
  "Next >": { es: "Siguiente >" },
  "Next Week": { es: "La pr\u00f3xima semana" },
  "No announcements yet.": { es: "Todav\u00eda no hay anuncios." },
  "No form to start.": { es: "No hay formularios para iniciar." },
  "No forms found. Press Sync Form Catalog under Maintenance to pull your account's forms.": { es: "No se encontraron formularios. Presione Sincronizar el cat\u00e1logo de formularios en Mantenimiento para traer los formularios de su cuenta." },
  "No PDF access events recorded yet. Events appear here as soon as anyone views, downloads, or prints a submission PDF.": { es: "Todav\u00eda no hay accesos a PDF registrados. Aparecer\u00e1n aqu\u00ed en cuanto alguien vea, descargue o imprima el PDF de un env\u00edo." },
  "No aliases recorded yet.": { es: "Todav\u00eda no hay alias registrados." },
  "No enabled forms to diagnose.": { es: "No hay formularios habilitados para diagnosticar." },
  "No issues in this state.": { es: "No hay problemas en este estado." },
  "No match: {0}": { es: "Sin coincidencias: {0}" },
  "No missing submissions. This form is in sync.": { es: "No faltan env\u00edos. Este formulario est\u00e1 sincronizado." },
  "No one else can read this chat.": { es: "Nadie m\u00e1s puede leer este chat." },
  "No photos": { es: "Sin fotos" },
  "No records found.": { es: "No se encontraron registros." },
  "No reports filed yet.": { es: "Todav\u00eda no se ha presentado ning\u00fan informe." },
  "No site": { es: "Sin sitio" },
  "No staff to show for this filter.": { es: "No hay personal para mostrar con este filtro." },
  "No submissions found. Click \"Sync All Submissions\" above to pull the latest.": { es: "No se encontraron env\u00edos. Presione \"Sincronizar todos los env\u00edos\" arriba para traer los m\u00e1s recientes." },
  "No submissions found. Press Sync All Submissions under Maintenance to pull the latest.": { es: "No se encontraron env\u00edos. Presione Sincronizar todos los env\u00edos en Mantenimiento para traer los m\u00e1s recientes." },
  "No supervisor questions on this form.": { es: "Este formulario no tiene preguntas para el supervisor." },
  "No sync operations yet.": { es: "Todav\u00eda no hay sincronizaciones." },
  "No time off is waiting for a decision.": { es: "No hay tiempo libre pendiente de decisi\u00f3n." },
  "No time off requests to show.": { es: "No hay solicitudes de tiempo libre para mostrar." },
  "No unfinished reports.": { es: "No hay informes sin terminar." },
  "No unresolved failures. Sync is healthy.": { es: "No hay fallos sin resolver. La sincronizaci\u00f3n est\u00e1 en orden." },
  "No": { es: "No" },
  "No HR records on file for this employee yet.": { es: "Todav\u00eda no hay expedientes de personal para esta persona." },
  "No Show": { es: "No se present\u00f3" },
  "No activity found for this filter.": { es: "No se encontr\u00f3 actividad con este filtro." },
  "No activity recorded for this site": { es: "No hay actividad registrada para este sitio" },
  "No approved vendors to export": { es: "No hay proveedores aprobados para exportar" },
  "No assigned tasks found.": { es: "No se encontraron tareas asignadas." },
  "No buildings configured": { es: "No hay edificios configurados" },
  "No buildings defined for this site yet.": { es: "Este sitio todav\u00eda no tiene edificios." },
  "No cases.": { es: "No hay casos." },
  "No certifications on file": { es: "No hay certificaciones archivadas" },
  "No checklist initialized": { es: "No se ha iniciado la lista" },
  "No completed inspections in the selected date range. Complete some inspections to see analytics here.": { es: "No hay inspecciones completadas en el rango de fechas elegido. Complete algunas inspecciones para ver aqu\u00ed los an\u00e1lisis." },
  "No completed inspections in this range yet.": { es: "Todav\u00eda no hay inspecciones completadas en este rango." },
  "No completed inspections yet.": { es: "Todav\u00eda no hay inspecciones completadas." },
  "No conversations.": { es: "No hay conversaciones." },
  "No data to export": { es: "No hay datos para exportar" },
  "No data to print": { es: "No hay datos para imprimir" },
  "No data yet.": { es: "Todav\u00eda no hay datos." },
  "No description": { es: "Sin descripci\u00f3n" },
  "No documents found. Use Add Document to upload.": { es: "No se encontraron documentos. Use Agregar documento para subir uno." },
  "No documents match this search.": { es: "Ning\u00fan documento coincide con la b\u00fasqueda." },
  "No documents on file": { es: "No hay documentos archivados" },
  "No employees match your filters.": { es: "Ninguna persona coincide con los filtros." },
  "No end": { es: "Sin fin" },
  "No end date": { es: "Sin fecha de fin" },
  "No entries yet.": { es: "Todav\u00eda no hay entradas." },
  "No evaluations on file": { es: "No hay evaluaciones registradas" },
  "No file": { es: "Sin archivo" },
  "No floors defined for this site yet.": { es: "Este sitio todav\u00eda no tiene pisos." },
  "No inspections match these filters.": { es: "Ninguna inspecci\u00f3n coincide con estos filtros." },
  "No inspections match this search.": { es: "Ninguna inspecci\u00f3n coincide con la b\u00fasqueda." },
  "No inspections recorded yet.": { es: "Todav\u00eda no hay inspecciones registradas." },
  "No issue activity in this range yet.": { es: "Todav\u00eda no hay actividad de incidencias en este rango." },
  "No items filed under Other. Use Add Other to upload.": { es: "No hay nada archivado en Otros. Use Agregar otro para subir algo." },
  "No items in this category.": { es: "No hay elementos en esta categor\u00eda." },
  "No items match this search.": { es: "Ning\u00fan elemento coincide con la b\u00fasqueda." },
  "No items yet. Add your first line item below.": { es: "Todav\u00eda no hay elementos. Agregue el primero abajo." },
  "No logo": { es: "Sin logotipo" },
  "No matching pages": { es: "Ninguna p\u00e1gina coincide" },
  "No messages in this site channel": { es: "No hay mensajes en el canal de este sitio" },
  "No messages to print": { es: "No hay mensajes para imprimir" },
  "No messages yet": { es: "Todav\u00eda no hay mensajes" },
  "No messages yet.": { es: "Todav\u00eda no hay mensajes." },
  "No note": { es: "Sin nota" },
  "No patterns yet. Turn on Repeat when adding a shift to create one.": { es: "Todav\u00eda no hay patrones. Active Repetir al agregar un turno para crear uno." },
  "No pending inspections.": { es: "No hay inspecciones pendientes." },
  "No photo attached for this item.": { es: "Este elemento no tiene una foto adjunta." },
  "No pickup activity found in this period.": { es: "No hay actividad de turnos disponibles en este periodo." },
  "No problems reported yet.": { es: "Todav\u00eda no se ha reportado ning\u00fan problema." },
  "No reason given": { es: "Sin motivo" },
  "No records match this search.": { es: "Ning\u00fan registro coincide con la b\u00fasqueda." },
  "No role": { es: "Sin rol" },
  "No saved reports yet. Use New report to create one.": { es: "Todav\u00eda no hay informes guardados. Use Nuevo informe para crear uno." },
  "No scope of work documented yet.": { es: "Todav\u00eda no se ha documentado el alcance del trabajo." },
  "No services to export": { es: "No hay servicios para exportar" },
  "No services yet.": { es: "Todav\u00eda no hay servicios." },
  "No shift": { es: "Sin turno" },
  "No shift started on {0} ({1})": { es: "Sin turno iniciado el {0} ({1})" },
  "No shift started today ({0})": { es: "Sin turno iniciado hoy ({0})" },
  "No shifts found for this period and filter.": { es: "No hay turnos en este periodo con este filtro." },
  "No shifts on these days.": { es: "No hay turnos en estos d\u00edas." },
  "No shifts started on {0}.": { es: "Ning\u00fan turno iniciado el {0}." },
  "No shifts started yet today.": { es: "Todav\u00eda no ha iniciado ning\u00fan turno hoy." },
  "No sites": { es: "Sin sitios" },
  "No sites assigned": { es: "No tiene sitios asignados" },
  "No sites found.": { es: "No se encontraron sitios." },
  "No sites linked yet": { es: "Todav\u00eda no hay sitios vinculados" },
  "No specific service": { es: "Ning\u00fan servicio en particular" },
  "No staff assigned": { es: "No hay personal asignado" },
  "No staff match that search": { es: "Ninguna persona coincide con esa b\u00fasqueda" },
  "No staff match these filters.": { es: "Ninguna persona coincide con estos filtros." },
  "No supplies assigned to this site": { es: "No hay suministros asignados a este sitio" },
  "No supplies configured.": { es: "No hay suministros configurados." },
  "No supplies linked yet": { es: "Todav\u00eda no hay suministros vinculados" },
  "No supply requests yet.": { es: "Todav\u00eda no hay solicitudes de suministros." },
  "No supply usage in this range yet.": { es: "Todav\u00eda no hay uso de suministros en este rango." },
  "No tasks configured for this site": { es: "No hay tareas configuradas para este sitio" },
  "No team member selected.": { es: "No hay ninguna persona del equipo seleccionada." },
  "No templates yet. Create one to get started.": { es: "Todav\u00eda no hay plantillas. Cree una para empezar." },
  "No training records": { es: "No hay registros de capacitaci\u00f3n" },
  "No training records found.": { es: "No se encontraron registros de capacitaci\u00f3n." },
  "No unlinked Jotform submissions for this employee. Sync the Forms page if needed, or use manual entry.": { es: "Esta persona no tiene env\u00edos de Jotform sin vincular. Sincronice la p\u00e1gina Formularios si hace falta, o use la entrada manual." },
  "No upcoming scheduled shifts found.": { es: "No hay turnos programados pr\u00f3ximos." },
  "No upcoming shifts": { es: "No hay pr\u00f3ximos turnos" },
  "No values yet. Click \"+ Add Value\" to add one.": { es: "Todav\u00eda no hay valores. Haga clic en \"+ Agregar valor\" para agregar uno." },
  "No vendors match these filters.": { es: "Ning\u00fan proveedor coincide con estos filtros." },
  "No vendors yet. Use Add Vendor to start.": { es: "Todav\u00eda no hay proveedores. Use Agregar proveedor para empezar." },
  "No zones defined for this site yet.": { es: "Este sitio todav\u00eda no tiene zonas." },
  "No-Show": { es: "No se present\u00f3" },
  "No-Shows": { es: "No se presentaron" },
  "Nobody else can read a Speak Up report": { es: "Nadie m\u00e1s puede leer un informe de Confianza" },
  "Nobody set. Every admin gets a notice in the app.": { es: "No hay nadie. Cada administrador recibe un aviso en la aplicaci\u00f3n." },
  "Nobody yet": { es: "Nadie todav\u00eda" },
  "Non-onboarding": { es: "No de incorporaci\u00f3n" },
  "Normal": { es: "Normal" },
  "Not answered": { es: "Sin responder" },
  "Not available": { es: "No disponible" },
  "Not enough resolved data to chart a trend yet.": { es: "Todav\u00eda no hay suficientes datos resueltos para trazar una tendencia." },
  "Not enough resolved data to chart compliance yet.": { es: "Todav\u00eda no hay suficientes datos resueltos para trazar el cumplimiento." },
  "Not given": { es: "No indicado" },
  "Not now": { es: "Ahora no" },
  "Not saved": { es: "Sin guardar" },
  "Not saved yet. Check your connection and try again.": { es: "Todav\u00eda no se guard\u00f3. Revise su conexi\u00f3n e intente de nuevo." },
  "Not sent yet. Check your connection and try again.": { es: "Todav\u00eda no se envi\u00f3. Revise su conexi\u00f3n e intente de nuevo." },
  "Not sent.": { es: "No se envi\u00f3." },
  "Not set": { es: "Sin indicar" },
  "Not signed": { es: "Sin firmar" },
  "Not signed yet. Check your connection and try again.": { es: "Todav\u00eda no se firm\u00f3. Revise su conexi\u00f3n e intente de nuevo." },
  "Not yet loaded": { es: "Todav\u00eda no cargado" },
  "Not yet": { es: "Todav\u00eda no" },
  "Note": { es: "Nota" },
  "Note to previous assignee (optional)": { es: "Nota para quien la ten\u00eda asignada (opcional)" },
  "Notes (admin only)": { es: "Notas (solo administradores)" },
  "Notes": { es: "Notas" },
  "Notes (optional)": { es: "Notas (opcional)" },
  "Nothing changed yet.": { es: "Nada ha cambiado todav\u00eda." },
  "Nothing reported yet.": { es: "Todav\u00eda no se ha informado nada." },
  "Nothing to update": { es: "No hay nada que actualizar" },
  "Nothing was added.": { es: "No se agreg\u00f3 nada." },
  "Nothing yet.": { es: "Nada todav\u00eda." },
  "Notifications": { es: "Avisos" },
  "Notifications did not load.": { es: "Los avisos no se cargaron." },
  "OPEN": { es: "ABIERTO" },
  "Off|alerts": { es: "Apagado" },
  "One site's people": { es: "El personal de un sitio" },
  "Only someone who holds this can grant it.": { es: "Solo quien la tiene puede otorgarla." },
  "Only when I'm tagged": { es: "Solo cuando me etiquetan" },
  "Open photo {0}": { es: "Abrir la foto {0}" },
  "Opening...": { es: "Abriendo..." },
  "OT Risk": { es: "Riesgo de horas extra" },
  "Occurrences": { es: "Veces" },
  "Oldest Activity": { es: "Actividad m\u00e1s antigua" },
  "On Probation": { es: "A prueba" },
  "Onboarding only": { es: "Solo de incorporaci\u00f3n" },
  "Onboarding": { es: "Incorporaci\u00f3n" },
  "Onboarding Progress": { es: "Avance de la incorporaci\u00f3n" },
  "Onboarding Step": { es: "Paso de incorporaci\u00f3n" },
  "Onboarding Steps": { es: "Pasos de incorporaci\u00f3n" },
  "Onboarding incomplete": { es: "Incorporaci\u00f3n incompleta" },
  "Onboarding initialized": { es: "Incorporaci\u00f3n iniciada" },
  "Onboarding {0}/{1}": { es: "Incorporaci\u00f3n {0}/{1}" },
  "Onboarding: Any": { es: "Incorporaci\u00f3n: todos" },
  "One-Off Assigned": { es: "Asignada una sola vez" },
  "Open in Jotform": { es: "Abrir en Jotform" },
  "Open": { es: "Abierto" },
  "Open|verb": { es: "Abrir" },
  "Open Issues": { es: "Incidencias abiertas" },
  "Open Marketplace Shift": { es: "Turno abierto en el tablero" },
  "Open Now": { es: "Abiertos ahora" },
  "Open by severity": { es: "Abiertas por severidad" },
  "Open file": { es: "Abrir el archivo" },
  "Open issues by severity": { es: "Incidencias abiertas por severidad" },
  "Open now": { es: "Abiertas ahora" },
  "Open shift posted": { es: "Turno abierto publicado" },
  "Open that pattern": { es: "Abrir ese patr\u00f3n" },
  "Open the browser menu (the three dots).": { es: "Abra el men\u00fa del navegador (los tres puntos)." },
  "Open the pattern": { es: "Abrir el patr\u00f3n" },
  "Open this page in Safari or Chrome first.": { es: "Abra esta p\u00e1gina primero en Safari o Chrome." },
  "Operational": { es: "Operativo" },
  "Operations": { es: "Operaciones" },
  "Operations overview.": { es: "Resumen de operaciones." },
  "Optional description": { es: "Descripci\u00f3n opcional" },
  "Optional note explaining why (e.g. 'all duplicates', 'all deleted in Jotform', 'fixed manually'). Leave blank to dismiss without a reason.": { es: "Nota opcional que explique el motivo (p. ej. 'todos duplicados', 'todos eliminados en Jotform', 'corregido a mano'). D\u00e9jela en blanco para descartarlos sin motivo." },
  "Optional note explaining why this is being resolved manually (e.g. 'duplicate', 'deleted in Jotform', 'intentional skip'). Leave blank if you just want to dismiss without a reason.": { es: "Nota opcional que explique por qu\u00e9 se resuelve a mano (p. ej. 'duplicado', 'eliminado en Jotform', 'omisi\u00f3n intencional'). D\u00e9jela en blanco si solo quiere descartarlo sin motivo." },
  "Optional notes": { es: "Notas opcionales" },
  "Optional when approving. Required when denying.": { es: "Opcional al aprobar. Obligatoria al denegar." },
  "Optional: describe what this template covers": { es: "Opcional: describa lo que cubre esta plantilla" },
  "Or enter manually": { es: "O escr\u00edbala a mano" },
  "Original": { es: "Original" },
  "Originally Assigned": { es: "Asignado originalmente" },
  "Orphan (in our DB but not Jotform): {0}": { es: "Hu\u00e9rfanos (en nuestra base y no en Jotform): {0}" },
  "Other": { es: "Otro" },
  "Our Submissions": { es: "Env\u00edos nuestros" },
  "Ours": { es: "Nuestros" },
  "Ours:": { es: "Nuestros:" },
  "Output and export": { es: "Resultado y exportaci\u00f3n" },
  "Overall Notes": { es: "Notas generales" },
  "Overall Score": { es: "Puntaje general" },
  "Override active. Pick blank to revert to form default.": { es: "Categor\u00eda cambiada a mano. Elija la opci\u00f3n en blanco para volver a la del formulario." },
  "Overtime risk: claiming this shift may push the worker past 40 weekly hours.": { es: "Riesgo de horas extra: tomar este turno puede llevar a la persona a m\u00e1s de 40 horas semanales." },
  "Overview": { es: "Resumen" },
  "PDF Access Log": { es: "Registro de acceso a PDF" },
  "PDF access log": { es: "Registro de acceso a PDF" },
  "PDF fetch failed (status {0})": { es: "No se pudo obtener el PDF (estado {0})" },
  "PDF fetch failed ({0}): {1}": { es: "No se pudo obtener el PDF ({0}): {1}" },
  "PDF is being captured, check back in a few minutes.": { es: "Se est\u00e1 capturando el PDF. Vuelva a revisar en unos minutos." },
  "PDF log load failed: {0}": { es: "No se pudo cargar el registro de PDF: {0}" },
  "PIN reset for {0}": { es: "PIN restablecido para {0}" },
  "Phone or email address": { es: "Tel\u00e9fono o correo electr\u00f3nico" },
  "Photo could not be loaded": { es: "No se pudo cargar la foto" },
  "Pick a form to start": { es: "Elija un formulario para iniciar" },
  "PIN": { es: "PIN" },
  "PIN must be 4 digits": { es: "El PIN debe tener 4 d\u00edgitos" },
  "PPE": { es: "EPP" },
  "PPE Compliance Log, monthly check": { es: "Registro de cumplimiento de EPP, revisi\u00f3n mensual" },
  "PPE Hazard Assessment Written Verification": { es: "Verificaci\u00f3n escrita de la evaluaci\u00f3n de riesgos para EPP" },
  "Pacific Time (Los Angeles)": { es: "Hora del Pac\u00edfico (Los \u00c1ngeles)" },
  "Page {0}": { es: "P\u00e1gina {0}" },
  "Page {0} / {1}": { es: "P\u00e1gina {0} / {1}" },
  "Part Time": { es: "Tiempo parcial" },
  "Part of a weekly pattern. Changes here apply to this date only.": { es: "Parte de un patr\u00f3n semanal. Los cambios aqu\u00ed se aplican solo a esta fecha." },
  "Past due {0}": { es: "Vencido el {0}" },
  "Paste a URL or upload below": { es: "Pegue un enlace o suba un archivo abajo" },
  "Paste the UUID of the target record": { es: "Pegue el UUID del registro de destino" },
  "Pattern saved. {0} shifts added through {1}.": { es: "Patr\u00f3n guardado. {0} turnos agregados hasta el {1}." },
  "Patterns": { es: "Patrones" },
  "Pay period start day": { es: "D\u00eda en que empieza el periodo de pago" },
  "Payment terms, minimum order, pricing structure...": { es: "Condiciones de pago, pedido m\u00ednimo, estructura de precios..." },
  "Pending": { es: "Pendiente" },
  "Pending Review": { es: "Pendiente de revisi\u00f3n" },
  "Per-Form Gap Analysis": { es: "Faltantes por formulario" },
  "Per-person permissions": { es: "Permisos por persona" },
  "Per-site breakdown": { es: "Desglose por sitio" },
  "Performance notes, delivery quality, responsiveness...": { es: "Notas de desempe\u00f1o, calidad de las entregas, capacidad de respuesta..." },
  "Period": { es: "Periodo" },
  "Permissions saved": { es: "Permisos guardados" },
  "Person": { es: "Persona" },
  "Phone": { es: "Tel\u00e9fono" },
  "Phone *": { es: "Tel\u00e9fono *" },
  "Phone number": { es: "N\u00famero de tel\u00e9fono" },
  "Phone or Email": { es: "Tel\u00e9fono o correo" },
  "Photo": { es: "Foto" },
  "Photo Added": { es: "Foto agregada" },
  "Photo Attached": { es: "Foto adjunta" },
  "Photo attached": { es: "Foto adjunta" },
  "Photo must be under 20MB": { es: "La foto debe pesar menos de 20 MB" },
  "Photo updated": { es: "Foto actualizada" },
  "Photo {0}": { es: "Foto {0}" },
  "Photo/Video": { es: "Foto o video" },
  "Photo/Video (optional)": { es: "Foto o video (opcional)" },
  "Photos ({0})": { es: "Fotos ({0})" },
  "Pick a conversation on the left to start.": { es: "Elija una conversaci\u00f3n a la izquierda para empezar." },
  "Pick a team member, then set each capability to Default, Allow, or Deny. Default follows the person role. An admin can grant the manage permissions capability to let someone else open this screen.": { es: "Elija a una persona del equipo y defina cada permiso como Predeterminado, Permitir o Denegar. Predeterminado sigue el rol de la persona. Un administrador puede conceder el permiso de administrar permisos para que otra persona abra esta pantalla." },
  "Pick a training...": { es: "Elija una capacitaci\u00f3n..." },
  "Pick a user first": { es: "Primero elija a una persona" },
  "Pick the date it was given": { es: "Elija la fecha en que se imparti\u00f3" },
  "Pick the language it was given in": { es: "Elija el idioma en que se imparti\u00f3" },
  "Pickup": { es: "Turno disponible" },
  "Pickup Shift": { es: "Turno disponible" },
  "Please choose a file to upload": { es: "Elija un archivo para subir" },
  "Points": { es: "Puntos" },
  "Popup blocked. Allow popups to print PDFs.": { es: "Ventana emergente bloqueada. Permita las ventanas emergentes para imprimir los PDF." },
  "Popup blocked. Allow popups to view PDFs.": { es: "Ventana emergente bloqueada. Permita las ventanas emergentes para ver los PDF." },
  "Post Open Shift": { es: "Publicar turno abierto" },
  "Post Shift": { es: "Publicar turno" },
  "Preferred": { es: "Preferido" },
  "Preferred Language": { es: "Idioma preferido" },
  "Prev": { es: "Anterior" },
  "Primary color": { es: "Color principal" },
  "Prime Contractor": { es: "Contratista principal" },
  "Print": { es: "Imprimir" },
  "Print Report": { es: "Imprimir el informe" },
  "Print attendance sheet": { es: "Imprimir la hoja de asistencia" },
  "Print this QR code and attach it to the supply container": { es: "Imprima este c\u00f3digo QR y p\u00e9guelo en el envase del suministro" },
  "Priority": { es: "Prioridad" },
  "Privacy note.": { es: "Nota de privacidad." },
  "Private conversations": { es: "Conversaciones privadas" },
  "Private message": { es: "Mensaje privado" },
  "Probes the Jotform API to determine which filtering mechanism this account supports: labels, folders, or keyword-based fallback. The recommendation drives how this business's forms are told apart from the other businesses' forms on the same account.": { es: "Consulta la API de Jotform para determinar qu\u00e9 mecanismo de filtrado admite esta cuenta: etiquetas, carpetas o palabras clave como alternativa. La recomendaci\u00f3n decide c\u00f3mo se distinguen los formularios de este negocio de los de los otros negocios en la misma cuenta." },
  "Probing...": { es: "Consultando..." },
  "Problems reported": { es: "Problemas reportados" },
  "Processed": { es: "Procesados" },
  "Processed: {0}": { es: "Procesados: {0}" },
  "Products / Services": { es: "Productos / servicios" },
  "Products and Services": { es: "Productos y servicios" },
  "Profile": { es: "Perfil" },
  "Profile updated": { es: "Perfil actualizado" },
  "Proof of who opened each PDF.": { es: "Constancia de qui\u00e9n abri\u00f3 cada PDF." },
  "Proof of who opened or was sent each PDF.": { es: "Prueba de qui\u00e9n abri\u00f3 o recibi\u00f3 cada PDF." },
  "Pull new submissions for ALL enabled forms? This may take a minute.": { es: "\u00bfTraer los env\u00edos nuevos de TODOS los formularios habilitados? Puede tardar un minuto." },
  "Pull new submissions for this form?": { es: "\u00bfTraer los env\u00edos nuevos de este formulario?" },
  "Pull": { es: "Traer" },
  "Pull the latest forms from Jotform. Only forms whose title starts with {0} are imported. Forms in the app that no longer match are switched off and kept, with their submissions. Continue?": { es: "Descargar los formularios m\u00e1s recientes de Jotform. Solo se importan los formularios cuyo t\u00edtulo empieza con {0}. Los formularios de la aplicaci\u00f3n que ya no coinciden se apagan y se conservan, con sus env\u00edos. \u00bfContinuar?" },
  "Pulls every submission from Jotform for every enabled form, ignoring the incremental filter. Existing cached submissions are re-fetched and their metadata is refreshed (submitter name, email, matched employee, expiry). Admin-set fields (status, notes, manual expiry, entity links) are preserved.": { es: "Trae cada env\u00edo de Jotform de cada formulario habilitado, sin el filtro incremental. Los env\u00edos ya guardados se vuelven a obtener y se actualizan sus metadatos (nombre del remitente, correo, persona asociada, vencimiento). Los campos fijados por un administrador (estado, notas, vencimiento manual, v\u00ednculos a registros) se conservan." },
  "QR": { es: "QR" },
  "QR: {0}": { es: "QR: {0}" },
  "Quality": { es: "Calidad" },
  "Quality checks": { es: "Controles de calidad" },
  "Quantity": { es: "Cantidad" },
  "Quarterly": { es: "Trimestral" },
  "Queued retry: {0}": { es: "Reintentos en cola: {0}" },
  "Quick snapshots": { es: "Res\u00famenes r\u00e1pidos" },
  "Raised the case": { es: "Present\u00f3 el caso" },
  "Rate Structure": { es: "Estructura de tarifas" },
  "Rate limit note": { es: "Nota sobre el l\u00edmite de llamadas" },
  "Rating": { es: "Calificaci\u00f3n" },
  "Rating *": { es: "Calificaci\u00f3n *" },
  "Rating required": { es: "La calificaci\u00f3n es obligatoria" },
  "Raw result": { es: "Resultado sin procesar" },
  "Re-run Auto-Link": { es: "Volver a vincular autom\u00e1ticamente" },
  "Re-run Auto-Link?\n\nThis scans every unlinked submission and matches it against the users table by employee ID, email, or exact full name. Ambiguous matches (multiple users) are left unlinked for manual review. Submitters that don't exist in the users table will also remain unlinked.\n\nThis action is safe to run any number of times.": { es: "\u00bfVolver a ejecutar la vinculaci\u00f3n autom\u00e1tica?\n\nRevisa cada env\u00edo sin vincular y lo compara con la tabla de usuarios por n\u00famero de empleado, correo o nombre completo exacto. Las coincidencias ambiguas (varios usuarios) se dejan sin vincular para revisarlas a mano. Quienes no existen en la tabla de usuarios tambi\u00e9n quedan sin vincular.\n\nEsta acci\u00f3n se puede ejecutar las veces que haga falta." },
  "Reaches {0} people, {1} with phone alerts on.": { es: "Llega a {0} personas, {1} con alertas en el tel\u00e9fono." },
  "Reactivate": { es: "Reactivar" },
  "Read filed incident reports": { es: "Leer los informes de incidentes presentados" },
  "Read the case": { es: "Ley\u00f3 el caso" },
  "Read this log": { es: "Ley\u00f3 este registro" },
  "Reason": { es: "Motivo" },
  "Reason Breakdown": { es: "Desglose por motivo" },
  "Reason for Reassignment *": { es: "Motivo de la reasignaci\u00f3n *" },
  "Reassign": { es: "Reasignar" },
  "Reassign Issue": { es: "Reasignar la incidencia" },
  "Reassign Task": { es: "Reasignar la tarea" },
  "Reassign To": { es: "Reasignar a" },
  "Reassign To *": { es: "Reasignar a *" },
  "Reassigned": { es: "Reasignada" },
  "Received": { es: "Recibido" },
  "Received {0}": { es: "Recibido: {0}" },
  "Recent Sync Log": { es: "Registro de sincronizaciones recientes" },
  "Recommendation": { es: "Recomendaci\u00f3n" },
  "Record Detail": { es: "Detalle del registro" },
  "Record Detail: {0}": { es: "Detalle del registro: {0}" },
  "Record Details": { es: "Datos del registro" },
  "Record Fields": { es: "Campos del registro" },
  "Record ID (UUID)": { es: "ID del registro (UUID)" },
  "Recorded when the person started the shift in the portal. There is no end time and no hours here.": { es: "Registrado cuando la persona inici\u00f3 el turno en el portal. Aqu\u00ed no hay hora de fin ni horas." },
  "Records": { es: "Registros" },
  "Refill Request": { es: "Solicitud de reposici\u00f3n" },
  "Refresh Diagnostic": { es: "Actualizar el diagn\u00f3stico" },
  "Refresh": { es: "Actualizar" },
  "Refreshing...": { es: "Actualizando..." },
  "Related Items ({0})": { es: "Elementos relacionados ({0})" },
  "Release": { es: "Liberar" },
  "Release this shift back to the open pool?": { es: "\u00bfLiberar este turno y devolverlo a los turnos abiertos?" },
  "Released": { es: "Liberado" },
  "Reliability": { es: "Cumplimiento" },
  "Reload": { es: "Volver a cargar" },
  "Remove from this list": { es: "Quitar de esta lista" },
  "Remove photo|form": { es: "Quitar foto" },
  "Remove row": { es: "Quitar fila" },
  "Remove the link for this submission?": { es: "\u00bfQuitar el v\u00ednculo de este env\u00edo?" },
  "Remove": { es: "Quitar" },
  "Remove photo": { es: "Quitar la foto" },
  "Remove this certification?": { es: "\u00bfQuitar esta certificaci\u00f3n?" },
  "Remove this document from this list?": { es: "\u00bfQuitar este documento de esta lista?" },
  "Remove this floor plan from this list?": { es: "\u00bfQuitar este plano de esta lista?" },
  "Remove this list and its values from the lists shown?": { es: "\u00bfQuitar esta lista y sus valores de las listas que se muestran?" },
  "Remove this service?": { es: "\u00bfQuitar este servicio?" },
  "Remove this site assignment?": { es: "\u00bfQuitar esta asignaci\u00f3n de sitio?" },
  "Remove this step from this list?": { es: "\u00bfQuitar este paso de esta lista?" },
  "Remove this supply from this list?": { es: "\u00bfQuitar este suministro de esta lista?" },
  "Remove this training record from this list?": { es: "\u00bfQuitar este registro de capacitaci\u00f3n de esta lista?" },
  "Remove this value from this list?": { es: "\u00bfQuitar este valor de esta lista?" },
  "Remove this vendor?": { es: "\u00bfQuitar este proveedor?" },
  "Remove {0}": { es: "Quitar a {0}" },
  "Removed from this list": { es: "Quitado de esta lista" },
  "Removing...": { es: "Quitando..." },
  "Repeat on": { es: "Repetir los" },
  "Repeat this shift": { es: "Repetir este turno" },
  "Repeats": { es: "Se repite" },
  "Replaces an earlier pattern": { es: "Reemplaza a un patr\u00f3n anterior" },
  "Report": { es: "Informe" },
  "Report created": { es: "Informe creado" },
  "Report deactivated": { es: "Informe desactivado" },
  "Report in progress": { es: "Informe en curso" },
  "Report submitted.": { es: "Informe enviado." },
  "Report updated": { es: "Informe actualizado" },
  "Reported": { es: "Reportada" },
  "Reported At": { es: "Fecha del reporte" },
  "Reported By": { es: "Reportada por" },
  "Reported by": { es: "Informado por" },
  "Reporting": { es: "Informes" },
  "Reports": { es: "Informes" },
  "Reports and report builder": { es: "Informes y creador de informes" },
  "Reports, labor, and scheduling": { es: "Informes, mano de obra y horarios" },
  "Request approved": { es: "Solicitud aprobada" },
  "Request denied": { es: "Solicitud denegada" },
  "Request failed": { es: "La solicitud fall\u00f3" },
  "Requested": { es: "Solicitado" },
  "Requested By": { es: "Solicitado por" },
  "Requests": { es: "Solicitudes" },
  "Required": { es: "Obligatorio" },
  "Required Certifications": { es: "Certificaciones requeridas" },
  "Required for new hire onboarding": { es: "Obligatorio en la incorporaci\u00f3n de nuevas contrataciones" },
  "Requires signature": { es: "Requiere firma" },
  "Reschedule": { es: "Reprogramar" },
  "Reset": { es: "Restablecer" },
  "Reset PIN": { es: "Restablecer el PIN" },
  "Reset link sent.": { es: "Enlace enviado." },
  "Resolution": { es: "Resoluci\u00f3n" },
  "Resolution (h)": { es: "Resoluci\u00f3n (h)" },
  "Resolution (h, median)": { es: "Resoluci\u00f3n (h, mediana)" },
  "Resolution Photo": { es: "Foto de la resoluci\u00f3n" },
  "Resolution SLA": { es: "SLA de resoluci\u00f3n" },
  "Resolution SLA %": { es: "SLA de resoluci\u00f3n %" },
  "Resolution SLA by site": { es: "SLA de resoluci\u00f3n por sitio" },
  "Resolution and response compliance percent per {0}": { es: "Porcentaje de cumplimiento de resoluci\u00f3n y respuesta por {0}" },
  "Resolution and response trend": { es: "Tendencia de resoluci\u00f3n y respuesta" },
  "Resolution breaches": { es: "Incumplimientos de resoluci\u00f3n" },
  "Resolution notes": { es: "Notas de resoluci\u00f3n" },
  "Resolution: {0}": { es: "Resoluci\u00f3n: {0}" },
  "Resolve all": { es: "Resolver todos" },
  "Resolve failed: {0}": { es: "No se pudo resolver: {0}" },
  "Resolve": { es: "Resolver" },
  "Resolved": { es: "Resuelta" },
  "Resolved At": { es: "Fecha de resoluci\u00f3n" },
  "Resolving...": { es: "Resolviendo..." },
  "Responded {0}": { es: "Respondido: {0}" },
  "Response": { es: "Respuesta" },
  "Response SLA": { es: "SLA de respuesta" },
  "Response SLA %": { es: "SLA de respuesta %" },
  "Response Time": { es: "Tiempo de respuesta" },
  "Response Time Summary": { es: "Resumen del tiempo de respuesta" },
  "Response Time by Site": { es: "Tiempo de respuesta por sitio" },
  "Response Time by Urgency": { es: "Tiempo de respuesta por urgencia" },
  "Response breaches": { es: "Incumplimientos de respuesta" },
  "Response time, resolution time, and service level compliance": { es: "Tiempo de respuesta, tiempo de resoluci\u00f3n y cumplimiento del nivel de servicio" },
  "Result": { es: "Resultado" },
  "Resume": { es: "Continuar" },
  "Retried ({0}){1}": { es: "Reintentado ({0}){1}" },
  "Retry failed: {0}": { es: "El reintento fall\u00f3: {0}" },
  "Retry": { es: "Reintentar" },
  "Retrying...": { es: "Reintentando..." },
  "Review": { es: "Revisar" },
  "Reviewed {0}": { es: "Revisado el {0}" },
  "Reviewed": { es: "Revisado" },
  "Role": { es: "Rol" },
  "Role at Site": { es: "Rol en el sitio" },
  "Role reference": { es: "Referencia de roles" },
  "Role: {0}. Default follows this role until you override it.": { es: "Rol: {0}. Predeterminado sigue este rol hasta que usted lo cambie." },
  "Roles and Permissions": { es: "Roles y permisos" },
  "Roles and permissions reference": { es: "Referencia de roles y permisos" },
  "Role|customer": { es: "Puesto" },
  "Rolling out": { es: "En implementaci\u00f3n" },
  "Row {0}": { es: "Fila {0}" },
  "Run Diagnostic": { es: "Ejecutar el diagn\u00f3stico" },
  "Run Full Refresh": { es: "Ejecutar la actualizaci\u00f3n completa" },
  "Run PDF Backfill": { es: "Ejecutar el relleno de PDF" },
  "Run": { es: "Ejecutar" },
  "Run PDF backfill?\n\nThis sweeps the entire {0} inbox and ingests any PDFs that have not yet been captured. May take a few minutes. Run again if the result shows the max was reached.": { es: "\u00bfEjecutar el relleno de PDF?\n\nRecorre toda la bandeja {0} e incorpora los PDF que a\u00fan no se hayan capturado. Puede tardar unos minutos. Ejec\u00fatelo de nuevo si el resultado muestra que se alcanz\u00f3 el m\u00e1ximo." },
  "Running...": { es: "Ejecutando..." },
  "Safety": { es: "Seguridad" },
  "Schedule and time off": { es: "Horario y tiempo libre" },
  "Section {0}": { es: "Secci\u00f3n {0}" },
  "Section {0} of {1}": { es: "Secci\u00f3n {0} de {1}" },
  "Send a PIN reset link": { es: "Enviar un enlace para restablecer el PIN" },
  "Send activation invite": { es: "Enviar invitaci\u00f3n de activaci\u00f3n" },
  "Send announcement": { es: "Enviar anuncio" },
  "Send this form? You cannot change it after it is sent.": { es: "\u00bfEnviar este formulario? No lo podr\u00e1 cambiar despu\u00e9s de enviarlo." },
  "Send to": { es: "Enviar a" },
  "Sent": { es: "Enviados" },
  "Shift pickups and drops": { es: "Turnos libres y turnos soltados" },
  "Sign with your mouse or finger": { es: "Firme con el mouse o el dedo" },
  "SLA compliance and breaches": { es: "Cumplimiento e incumplimientos del SLA" },
  "SLA compliance by period": { es: "Cumplimiento del SLA por periodo" },
  "SLA compliance trend": { es: "Tendencia del cumplimiento del SLA" },
  "Safety / Incident": { es: "Seguridad / Incidente" },
  "Safety Committee Minutes and Attendance": { es: "Acta y asistencia del comit\u00e9 de seguridad" },
  "Safety Incident Report": { es: "Informe de Incidente de Seguridad" },
  "Safety Inspection Checklist": { es: "Lista de inspecci\u00f3n de seguridad" },
  "Sampled: {0}": { es: "Muestra: {0}" },
  "Sat": { es: "S\u00e1b" },
  "Saturday": { es: "S\u00e1bado" },
  "Save": { es: "Guardar" },
  "Save Changes": { es: "Guardar los cambios" },
  "Save Company Settings": { es: "Guardar la configuraci\u00f3n de la empresa" },
  "Save Evaluation": { es: "Guardar la evaluaci\u00f3n" },
  "Save Scope": { es: "Guardar el alcance" },
  "Save changes": { es: "Guardar los cambios" },
  "Saved": { es: "Guardado" },
  "Saving...": { es: "Guardando..." },
  "Saw it in the list": { es: "Lo vio en la lista" },
  "Schedule": { es: "Horario" },
  "Schedule All": { es: "Programar todo" },
  "Schedule Inspection": { es: "Programar una inspecci\u00f3n" },
  "Schedule Shift": { es: "Programar turno" },
  "Schedule and shift pickups": { es: "Horario y turnos disponibles" },
  "Schedule updated": { es: "Horario actualizado" },
  "Scheduled Shift": { es: "Turno programado" },
  "Scheduled": { es: "Programado" },
  "Scheduled Date": { es: "Fecha programada" },
  "Scheduled Date *": { es: "Fecha programada *" },
  "Scheduling for": { es: "Programando para" },
  "Scope of Work": { es: "Alcance del trabajo" },
  "Score": { es: "Puntaje" },
  "Score Trend": { es: "Tendencia del puntaje" },
  "Score by Service Category": { es: "Puntaje por categor\u00eda de servicio" },
  "Score trend": { es: "Tendencia del puntaje" },
  "Score: {0}": { es: "Puntuaci\u00f3n: {0}" },
  "Scroll down and tap Add to Home Screen.": { es: "Despl\u00e1cese hacia abajo y toque Agregar a pantalla de inicio." },
  "Search by name": { es: "Buscar por nombre" },
  "Search employee, category, file, uploaded by": { es: "Buscar persona, categor\u00eda, archivo o qui\u00e9n lo subi\u00f3" },
  "Search employee, file, notes": { es: "Buscar persona, archivo o notas" },
  "Search employee, training, type, administered by": { es: "Buscar persona, capacitaci\u00f3n, tipo o qui\u00e9n la imparti\u00f3" },
  "Search name, ID, phone, role": { es: "Buscar nombre, ID, tel\u00e9fono o rol" },
  "Search name, email, form...": { es: "Buscar nombre, correo, formulario..." },
  "Search name, email, or role...": { es: "Buscar por nombre, correo o rol..." },
  "Search pages": { es: "Buscar p\u00e1ginas" },
  "Search site, address, contract": { es: "Buscar sitio, direcci\u00f3n o contrato" },
  "Search site, service, staff, notes": { es: "Buscar por sitio, servicio, personal o notas" },
  "Search staff": { es: "Buscar personal" },
  "Search staff...": { es: "Buscar personal..." },
  "Search template, site, assignee": { es: "Buscar plantilla, sitio o persona asignada" },
  "Search titles or notes...": { es: "Buscar t\u00edtulos o notas..." },
  "Search vendor, contact, phone, email, services": { es: "Buscar proveedor, contacto, tel\u00e9fono, correo o servicios" },
  "Secondary color": { es: "Color secundario" },
  "Select (optional)": { es: "Seleccione (opcional)" },
  "Select a scheduled shift below. The original shift will be cancelled and replaced with an open pickup that eligible staff can claim.": { es: "Seleccione un turno programado abajo. El turno original se cancela y se reemplaza por un turno abierto que el personal elegible puede tomar." },
  "Select a site": { es: "Seleccione un sitio" },
  "Select a site...": { es: "Seleccione un sitio..." },
  "Select a staff member": { es: "Seleccione a una persona" },
  "Select a staff member...": { es: "Seleccione a una persona..." },
  "Select a supply": { es: "Seleccione un suministro" },
  "Select a supply...": { es: "Seleccione un suministro..." },
  "Select a synced submission...": { es: "Seleccione un env\u00edo sincronizado..." },
  "Select a team member...": { es: "Seleccione a una persona del equipo..." },
  "Select all": { es: "Seleccionar todo" },
  "Select an employee first": { es: "Primero seleccione a una persona" },
  "Select an employee first to see matching Jotform submissions.": { es: "Primero seleccione a una persona para ver sus env\u00edos de Jotform." },
  "Select an employee to view their onboarding checklist.": { es: "Seleccione a una persona para ver su lista de incorporaci\u00f3n." },
  "Select building...": { es: "Seleccione un edificio..." },
  "Select category...": { es: "Seleccione una categor\u00eda..." },
  "Select employee...": { es: "Seleccione a una persona..." },
  "Select entity type and ID": { es: "Seleccione el tipo de registro y el ID" },
  "Select floor...": { es: "Seleccione un piso..." },
  "Select role...": { es: "Seleccione un rol..." },
  "Select shift...": { es: "Seleccione un turno..." },
  "Select site...": { es: "Seleccione un sitio..." },
  "Select staff member...": { es: "Seleccione a la persona..." },
  "Select staff...": { es: "Seleccione personal..." },
  "Select template...": { es: "Seleccione una plantilla..." },
  "Select type...": { es: "Seleccione un tipo..." },
  "Select user...": { es: "Seleccione a una persona..." },
  "Select...": { es: "Seleccione..." },
  "Selected site": { es: "Sitio seleccionado" },
  "Send again": { es: "Enviar de nuevo" },
  "Send it": { es: "Enviarlo" },
  "Send this report again to everyone set for this form?": { es: "\u00bfEnviar este informe de nuevo a todas las personas configuradas para este formulario?" },
  "Send": { es: "Enviar" },
  "Sending...": { es: "Enviando..." },
  "Sent again: {0} and {1}, {2}.": { es: "Enviado de nuevo: {0} y {1}, {2}." },
  "Service": { es: "Servicio" },
  "Service Catalog": { es: "Cat\u00e1logo de servicios" },
  "Service Category": { es: "Categor\u00eda de servicio" },
  "Service Details": { es: "Detalles del servicio" },
  "Service Name *": { es: "Nombre del servicio *" },
  "Service added": { es: "Servicio agregado" },
  "Service catalog exported": { es: "Cat\u00e1logo de servicios exportado" },
  "Service category": { es: "Categor\u00eda de servicio" },
  "Service level targets (hours)": { es: "Objetivos de nivel de servicio (horas)" },
  "Service name required": { es: "El nombre del servicio es obligatorio" },
  "Service removed": { es: "Servicio quitado" },
  "Service updated": { es: "Servicio actualizado" },
  "Services": { es: "Servicios" },
  "Services: create, edit, remove, link": { es: "Servicios: crear, editar, quitar y vincular" },
  "Services: view": { es: "Servicios: ver" },
  "Session expired": { es: "La sesi\u00f3n expir\u00f3" },
  "Sessions": { es: "Sesiones" },
  "Settings": { es: "Configuraci\u00f3n" },
  "Share button": { es: "bot\u00f3n Compartir" },
  "Shift": { es: "Turno" },
  "Shift Details": { es: "Detalles del turno" },
  "Shift Drop Request": { es: "Solicitud de baja de turno" },
  "Shift Pickup": { es: "Turnos disponibles" },
  "Shift Pickup Board": { es: "Tablero de turnos disponibles" },
  "Shift approved": { es: "Turno aprobado" },
  "Shift assigned": { es: "Turno asignado" },
  "Shift cancelled": { es: "Turno cancelado" },
  "Shift converted to open pickup": { es: "Turno convertido en turno abierto" },
  "Shift reassigned": { es: "Turno reasignado" },
  "Shift released": { es: "Turno liberado" },
  "Shift scheduled": { es: "Turno programado" },
  "Shift updated": { es: "Turno actualizado" },
  "Shifts": { es: "Turnos" },
  "Shifts & Schedule": { es: "Turnos y horario" },
  "Shifts before this date stay as they are. Shifts someone changed or cancelled by hand are kept.": { es: "Los turnos anteriores a esta fecha quedan como est\u00e1n. Los turnos que alguien cambi\u00f3 o cancel\u00f3 a mano se conservan." },
  "Shifts by Day of Week": { es: "Turnos por d\u00eda de la semana" },
  "Shifts on these days": { es: "Turnos en estos d\u00edas" },
  "Show Missing IDs": { es: "Mostrar los ID faltantes" },
  "Show": { es: "Mostrar" },
  "Show \"Other\" text input": { es: "Mostrar un cuadro de texto para \"Otro\"" },
  "Show EIN on report exports by default": { es: "Mostrar el EIN en los informes exportados de forma predeterminada" },
  "Show on report": { es: "Mostrar en el informe" },
  "Show test accounts": { es: "Mostrar las cuentas de prueba" },
  "Showing the first {0}. Choose a person to narrow the list.": { es: "Mostrando los primeros {0}. Elija una persona para acotar la lista." },
  "Showing {0} of {1}": { es: "Se muestran {0} de {1}" },
  "Showing {0} to {1} of {2}": { es: "Mostrando {0} a {1} de {2}" },
  "Shown as: {0}": { es: "Se muestra como: {0}" },
  "Sign In": { es: "Iniciar sesi\u00f3n" },
  "Sign Out": { es: "Cerrar sesi\u00f3n" },
  "Sign": { es: "Firmar" },
  "Signature": { es: "Firma" },
  "Signed by {0} on {1} at {2}": { es: "Firmado por {0} el {1} a las {2}" },
  "Signing in...": { es: "Iniciando sesi\u00f3n..." },
  "Signing...": { es: "Firmando..." },
  "Since midnight": { es: "Desde la medianoche" },
  "Site": { es: "Sitio" },
  "Site *": { es: "Sitio *" },
  "Site Assignments": { es: "Asignaciones de sitio" },
  "Site Assignments ({0})": { es: "Asignaciones de sitio ({0})" },
  "Site Channel Messages": { es: "Mensajes del canal del sitio" },
  "Site Comparison": { es: "Comparaci\u00f3n de sitios" },
  "Site Lookups": { es: "Listas por sitio" },
  "Site Name": { es: "Nombre del sitio" },
  "Site Notes": { es: "Notas del sitio" },
  "Site Performance": { es: "Desempe\u00f1o por sitio" },
  "Site Record": { es: "Registro del sitio" },
  "Site channel": { es: "Canal del sitio" },
  "Site created": { es: "Sitio creado" },
  "Site deactivated": { es: "Sitio desactivado" },
  "Site linked": { es: "Sitio vinculado" },
  "Site reactivated": { es: "Sitio reactivado" },
  "Site records and floor plans": { es: "Registros de sitios y planos" },
  "Site unlinked": { es: "Sitio desvinculado" },
  "Site updated": { es: "Sitio actualizado" },
  "Site x Day Heatmap": { es: "Mapa de calor de sitio por d\u00eda" },
  "Site, date, and times are required": { es: "El sitio, la fecha y las horas son obligatorios" },
  "Site, description, zone, and assignee are required": { es: "El sitio, la descripci\u00f3n, la zona y la persona asignada son obligatorios" },
  "Sites": { es: "Sitios" },
  "Skipped: {0}": { es: "Omitidos: {0}" },
  "Slowest": { es: "M\u00e1s lento" },
  "Slug (auto-generated)": { es: "Identificador (se genera solo)" },
  "Some remove actions within Inspections and Sites are reserved to Admin.": { es: "Algunas acciones de quitar en Inspecciones y Sitios est\u00e1n reservadas al administrador." },
  "Someone asks to drop a shift": { es: "Alguien pide dejar un turno" },
  "Someone else has to decide your own request.": { es: "Otra persona tiene que decidir su propia solicitud." },
  "Someone picks up an open shift": { es: "Alguien toma un turno disponible" },
  "Someone registers for an account": { es: "Alguien se registra para tener una cuenta" },
  "Someone requests time off": { es: "Alguien solicita tiempo libre" },
  "Something went wrong and the page needs reloading.": { es: "Algo sali\u00f3 mal y hay que volver a cargar la p\u00e1gina." },
  "Soonest first. Deciding a request leaves the schedule as it is.": { es: "Primero las m\u00e1s pr\u00f3ximas. Decidir una solicitud deja el horario como est\u00e1." },
  "Source": { es: "Fuente" },
  "Source record not found. The original data may have been deleted.": { es: "No se encontr\u00f3 el registro de origen. Es posible que los datos originales se hayan eliminado." },
  "Source|pdf": { es: "Origen" },
  "Spanish": { es: "Espa\u00f1ol" },
  "Specify Category": { es: "Especifique la categor\u00eda" },
  "Staff": { es: "Personal" },
  "Staff Management": { es: "Gesti\u00f3n de personal" },
  "Staff Member": { es: "Persona" },
  "Staff Member *": { es: "Persona *" },
  "Staff Reliability": { es: "Cumplimiento del personal" },
  "Staff Reliability Metrics": { es: "Indicadores de cumplimiento del personal" },
  "Staff Summary": { es: "Resumen del personal" },
  "Staff accounts and approvals": { es: "Cuentas del personal y aprobaciones" },
  "Staff accounts, approvals, PIN resets": { es: "Cuentas del personal, aprobaciones y cambios de PIN" },
  "Staff and training": { es: "Personal y capacitaci\u00f3n" },
  "Staff at this site...": { es: "Personal de este sitio..." },
  "Staff covers custodial and porter roles, and client contacts, who use the staff portal. This shows their access to platform data.": { es: "Personal incluye los roles de limpieza y conserjer\u00eda, y los contactos del cliente, que usan el portal del personal. Aqu\u00ed se muestra su acceso a los datos de la plataforma." },
  "Staff with Onboarding": { es: "Personal en incorporaci\u00f3n" },
  "Staff, site, start time, and end time are required": { es: "La persona, el sitio, la hora de inicio y la hora de fin son obligatorios" },
  "Stage": { es: "Etapa" },
  "Standard": { es: "Est\u00e1ndar" },
  "Start": { es: "Inicio" },
  "Start a form": { es: "Iniciar un formulario" },
  "Start Date": { es: "Fecha de inicio" },
  "Start Time": { es: "Hora de inicio" },
  "Start Time *": { es: "Hora de inicio *" },
  "Start Work": { es: "Iniciar el trabajo" },
  "Start time": { es: "Hora de inicio" },
  "Started by": { es: "Iniciado por" },
  "Started {0}": { es: "Iniciado el {0}" },
  "Started": { es: "Iniciado" },
  "Started Shift": { es: "Turno iniciado" },
  "Started on {0}": { es: "Iniciados el {0}" },
  "Started today": { es: "Iniciados hoy" },
  "Starts": { es: "Comienza" },
  "State": { es: "Estado" },
  "Status updated": { es: "Estado actualizado" },
  "Status": { es: "Estado" },
  "Status Changed": { es: "Cambio de estado" },
  "Status: {0}.": { es: "Estado: {0}." },
  "Step Name": { es: "Nombre del paso" },
  "Step added": { es: "Paso agregado" },
  "Step-by-step instructions or notes...": { es: "Instrucciones paso a paso o notas..." },
  "Step-by-step instructions, tips, or notes for the cleaner...": { es: "Instrucciones paso a paso, consejos o notas para quien limpia..." },
  "Step-by-step instructions, tips, or notes...": { es: "Instrucciones paso a paso, consejos o notas..." },
  "Still needed before you can submit:": { es: "Falta esto antes de poder enviarlo:" },
  "Still needed in the supervisor section": { es: "Pendiente en la secci\u00f3n del supervisor" },
  "Stock": { es: "Existencias" },
  "Stock: {0}": { es: "Existencias: {0}" },
  "Stop telling {0} about this?": { es: "\u00bfDejar de avisar a {0} sobre esto?" },
  "Street address": { es: "Calle y n\u00famero" },
  "Subject named": { es: "Persona mencionada" },
  "Submission Detail": { es: "Detalle del env\u00edo" },
  "Submission ID": { es: "ID del env\u00edo" },
  "Submission content (SSN, bank info, dates of birth) is stored only in Jotform. The dashboard caches metadata only. Opening a submission detail below fetches the full answers from Jotform in real time. Close the modal when done.": { es: "El contenido de los env\u00edos (SSN, datos bancarios, fechas de nacimiento) se guarda solo en Jotform. El panel guarda solo metadatos. Al abrir el detalle de un env\u00edo se traen las respuestas completas de Jotform en tiempo real. Cierre la ventana al terminar." },
  "Submissions load failed: {0}": { es: "No se pudieron cargar los env\u00edos: {0}" },
  "Submissions": { es: "Env\u00edos" },
  "Submissions:": { es: "Env\u00edos:" },
  "Submit report": { es: "Enviar el informe" },
  "Submitted": { es: "Enviado" },
  "Submitter Name": { es: "Nombre del remitente" },
  "Submitter": { es: "Remitente" },
  "Submitting...": { es: "Enviando..." },
  "Success only": { es: "Solo correctos" },
  "Summary": { es: "Resumen" },
  "Sun": { es: "Dom" },
  "Sunday": { es: "Domingo" },
  "Supervisor section": { es: "Secci\u00f3n del supervisor" },
  "Supervisor": { es: "Supervisor" },
  "Supervisors are scoped to their assigned sites for site-level actions.": { es: "En las acciones de cada sitio, los supervisores solo act\u00faan en sus sitios asignados." },
  "Supplemental": { es: "Suplementario" },
  "Supplies": { es: "Suministros" },
  "Supplies & Inventory": { es: "Suministros e inventario" },
  "Supplies and Inventory": { es: "Suministros e inventario" },
  "Supplies and vendors": { es: "Suministros y proveedores" },
  "Supplies at This Site ({0})": { es: "Suministros en este sitio ({0})" },
  "Supplies used": { es: "Suministros usados" },
  "Supply": { es: "Suministro" },
  "Supply *": { es: "Suministro *" },
  "Supply Usage and Cost": { es: "Uso y costo de suministros" },
  "Supply assigned": { es: "Suministro asignado" },
  "Supply catalog": { es: "Cat\u00e1logo de suministros" },
  "Supply catalog: create, edit, remove": { es: "Cat\u00e1logo de suministros: crear, editar y quitar" },
  "Supply linked": { es: "Suministro vinculado" },
  "Supply removed": { es: "Suministro quitado" },
  "Supply report settings": { es: "Configuraci\u00f3n del informe de suministros" },
  "Supply requests": { es: "Pedidos de suministros" },
  "Supply unlinked": { es: "Suministro desvinculado" },
  "Supply updated": { es: "Suministro actualizado" },
  "Sync Actions": { es: "Acciones de sincronizaci\u00f3n" },
  "Sync All Submissions": { es: "Sincronizar todos los env\u00edos" },
  "Sync Diagnostic": { es: "Diagn\u00f3stico de sincronizaci\u00f3n" },
  "Sync Form Catalog": { es: "Sincronizar el cat\u00e1logo de formularios" },
  "Sync failed: {0}": { es: "La sincronizaci\u00f3n fall\u00f3: {0}" },
  "Synced {0} forms. {1}.": { es: "Se sincronizaron {0} formularios. {1}." },
  "Synced {0} submissions ({1} new, {2} updated) across {3} form(s)": { es: "Se sincronizaron {0} env\u00edos ({1} nuevos, {2} actualizados) en {3} formulario(s)" },
  "Syncing...": { es: "Sincronizando..." },
  "System": { es: "Sistema" },
  "System category": { es: "Categor\u00eda del sistema" },
  "TEST": { es: "PRUEBA" },
  "Tag someone": { es: "Etiquetar a alguien" },
  "Take this case": { es: "Tomar este caso" },
  "Tap Add or Install.": { es: "Toque Agregar o Instalar." },
  "Tap Add to Home Screen.": { es: "Toque Agregar a pantalla de inicio." },
  "Tap Add to Home screen or Install app.": { es: "Toque Agregar a pantalla de inicio o Instalar aplicaci\u00f3n." },
  "Tap Add.": { es: "Toque Agregar." },
  "Tap Install below.": { es: "Toque Instalar abajo." },
  "Tap the Share button at the bottom of the screen.": { es: "Toque el bot\u00f3n Compartir en la parte inferior de la pantalla." },
  "Tap the Share button in the address bar.": { es: "Toque el bot\u00f3n Compartir en la barra de direcciones." },
  "Target Role (optional)": { es: "Rol destinatario (opcional)" },
  "Targets, response then resolution. High {0} and {1}. Medium {2} and {3}. Low {4} and {5}. Aging means open past its resolution target.": { es: "Objetivos, primero respuesta y luego resoluci\u00f3n. Alta {0} y {1}. Media {2} y {3}. Baja {4} y {5}. Una incidencia atrasada sigue abierta despu\u00e9s de su objetivo de resoluci\u00f3n." },
  "Task": { es: "Tarea" },
  "Task Completion": { es: "Tareas completadas" },
  "Task Description *": { es: "Descripci\u00f3n de la tarea *" },
  "Task Detail": { es: "Detalle de la tarea" },
  "Task Name": { es: "Nombre de la tarea" },
  "Task Type": { es: "Tipo de tarea" },
  "Task created": { es: "Tarea creada" },
  "Task created and assigned": { es: "Tarea creada y asignada" },
  "Task progress": { es: "Avance de tareas" },
  "Task reassigned": { es: "Tarea reasignada" },
  "Task reference": { es: "Referencia de la tarea" },
  "Task updated": { es: "Tarea actualizada" },
  "Tasks": { es: "Tareas" },
  "Tasks ({0})": { es: "Tareas ({0})" },
  "Tasks and assignments": { es: "Tareas y asignaciones" },
  "Tasks: create, assign, reassign": { es: "Tareas: crear, asignar y reasignar" },
  "Tax Forms": { es: "Formularios de impuestos" },
  "Tell me what happened and I will tell you what to do.": { es: "Cu\u00e9nteme qu\u00e9 pas\u00f3 y le digo qu\u00e9 hacer." },
  "Template": { es: "Plantilla" },
  "Template *": { es: "Plantilla *" },
  "Template Name *": { es: "Nombre de la plantilla *" },
  "Template created": { es: "Plantilla creada" },
  "Template deactivated": { es: "Plantilla desactivada" },
  "Template, site, and date are required": { es: "La plantilla, el sitio y la fecha son obligatorios" },
  "Templates": { es: "Plantillas" },
  "Temporary PIN": { es: "PIN temporal" },
  "Text size": { es: "Tama\u00f1o del texto" },
  "That employee ID is already in use.": { es: "Ese n\u00famero de identificaci\u00f3n de empleado ya est\u00e1 en uso." },
  "That one is for admins. Ask an admin to take a look.": { es: "Ese es para administradores. P\u00eddale a un administrador que lo revise." },
  "The Jotform Count is what Jotform's API reports for each form. The Our Count is what we have stored. A positive Delta means Jotform has submissions we never picked up. Click \"Show Missing IDs\" to see which specific submissions are missing and Force Fetch them one at a time. Unresolved Failures are submissions the sync attempted to insert but errored on. Use the Failures table below to retry or dismiss them.": { es: "El conteo de Jotform es lo que informa la API de Jotform para cada formulario. El conteo nuestro es lo que tenemos guardado. Una diferencia positiva significa que Jotform tiene env\u00edos que nunca recogimos. Presione \"Mostrar los ID faltantes\" para ver cu\u00e1les faltan y forzar su descarga uno por uno. Los fallos sin resolver son env\u00edos que la sincronizaci\u00f3n intent\u00f3 insertar y dieron error. Use la tabla de fallos de abajo para reintentarlos o descartarlos." },
  "The case goes back to the team.": { es: "El caso vuelve al equipo." },
  "The connection dropped. Your answer is saved.": { es: "Se perdi\u00f3 la conexi\u00f3n. La respuesta qued\u00f3 guardada." },
  "The forms connection is not set up. Ask your administrator.": { es: "La conexi\u00f3n de formularios no est\u00e1 configurada. P\u00eddale ayuda a su administrador." },
  "The person reads your note in the app. Their notice shows only the dates.": { es: "La persona lee su nota en la aplicaci\u00f3n. Su aviso muestra solo las fechas." },
  "The schedule fills 8 weeks ahead and keeps extending until the pattern is ended.": { es: "El horario se llena con 8 semanas de anticipaci\u00f3n y sigue extendi\u00e9ndose hasta que el patr\u00f3n termine." },
  "The scheduled shift will be cancelled and posted as an open shift for eligible staff to claim.": { es: "El turno programado se cancela y se publica como turno abierto para que el personal elegible lo tome." },
  "The source record could not be found. It may have been deleted or the entry was logged with a temporary reference.": { es: "No se encontr\u00f3 el registro de origen. Es posible que se haya eliminado o que la entrada se registrara con una referencia temporal." },
  "The task will appear in the staff member's \"Assigned Tasks\" tab where they can mark it as in progress, resolved, or unable to resolve.": { es: "La tarea aparecer\u00e1 en la pesta\u00f1a \"Tareas asignadas\" de la persona, donde podr\u00e1 marcarla como en curso, resuelta o imposible de resolver." },
  "The team promised a response within 72 hours of filing.": { es: "El equipo se comprometi\u00f3 a responder en un plazo de 72 horas desde la presentaci\u00f3n." },
  "Then add it to your home screen from that browser.": { es: "Luego agr\u00e9guela a su pantalla de inicio desde ese navegador." },
  "These people are told about this form as well as anyone under Every form.": { es: "Estas personas reciben avisos de este formulario, igual que quienes est\u00e1n en Todos los formularios." },
  "These still need an answer": { es: "Estas todav\u00eda necesitan respuesta" },
  "They will get an email. The email carries no case text.": { es: "Recibir\u00e1 un correo. El correo no incluye el texto del caso." },
  "This did not load.": { es: "Esto no se carg\u00f3." },
  "This form could not be read. Close the window and try again.": { es: "No se pudo leer este formulario. Cierre la ventana e intente de nuevo." },
  "This form was already sent.": { es: "Este formulario ya se envi\u00f3." },
  "This Month": { es: "Este mes" },
  "This question cannot be answered here yet. Your supervisor will finish it.": { es: "Esta pregunta todav\u00eda no se puede responder aqu\u00ed. Su supervisor la completar\u00e1." },
  "This question is full.": { es: "Esta pregunta est\u00e1 llena." },
  "This Week": { es: "Esta semana" },
  "This affects future auto-matching. Existing linked submissions are not changed.": { es: "Esto afecta las futuras vinculaciones autom\u00e1ticas. Los env\u00edos ya vinculados no cambian." },
  "This case is not available.": { es: "Este caso no est\u00e1 disponible." },
  "This issue is currently assigned to someone. Selecting a new person will remove the previous assignment.": { es: "Esta incidencia ya est\u00e1 asignada a alguien. Elegir a otra persona quita la asignaci\u00f3n anterior." },
  "This item was not scored (inspection not yet completed).": { es: "Este elemento no se calific\u00f3 (la inspecci\u00f3n a\u00fan no se completa)." },
  "This page is for admins.": { es: "Esta p\u00e1gina es para administradores." },
  "This photo could not be read here. Choose a JPEG or PNG, or take a screenshot of it.": { es: "No se pudo leer esta foto aqu\u00ed. Elija un JPEG o un PNG, o t\u00f3mele una captura de pantalla." },
  "This report could not be found, or your account cannot open it.": { es: "No se encontr\u00f3 este informe, o su cuenta no puede abrirlo." },
  "This report's data source arrives with the report templates workstream. It will run here once that ships.": { es: "La fuente de datos de este informe llegar\u00e1 con las plantillas de informes. Se ejecutar\u00e1 aqu\u00ed cuando est\u00e9 disponible." },
  "This shift is open in the marketplace and available for staff to claim.": { es: "Este turno est\u00e1 abierto en el tablero y cualquier persona elegible puede tomarlo." },
  "This shift runs past midnight. Pick the day it starts.": { es: "Este turno pasa de la medianoche. Elija el d\u00eda en que comienza." },
  "This source arrives with the report templates workstream. You can save the report now, and it will run once its data source ships.": { es: "Esta fuente llegar\u00e1 con las plantillas de informes. Puede guardar el informe ahora y se ejecutar\u00e1 cuando su fuente de datos est\u00e9 disponible." },
  "This table is full.": { es: "Esta tabla est\u00e1 llena." },
  "Thu": { es: "Jue" },
  "Thursday": { es: "Jueves" },
  "Tick at least one person": { es: "Marque al menos a una persona" },
  "Time": { es: "Hora" },
  "Time off": { es: "Tiempo libre" },
  "Time off ({0})": { es: "Tiempo libre ({0})" },
  "Time off approved. They get a notice in the app.": { es: "Tiempo libre aprobado. La persona recibe un aviso en la aplicaci\u00f3n." },
  "Time off denied. They get a notice in the app.": { es: "Tiempo libre denegado. La persona recibe un aviso en la aplicaci\u00f3n." },
  "Time off request": { es: "Solicitud de tiempo libre" },
  "Timeline": { es: "Historial" },
  "Times Scored": { es: "Veces calificado" },
  "Times checked": { es: "Veces revisado" },
  "Timesheets": { es: "Hojas de horas" },
  "Timezone": { es: "Zona horaria" },
  "Title": { es: "T\u00edtulo" },
  "To: {0}": { es: "Hasta: {0}" },
  "Today": { es: "Hoy" },
  "Told when everyone else on the team is named in a report.": { es: "Recibe el aviso cuando todas las dem\u00e1s personas del equipo aparecen en un informe." },
  "Too many files. Maximum 50 per upload, you selected {0}.": { es: "Demasiados archivos. El m\u00e1ximo es 50 por subida y seleccion\u00f3 {0}." },
  "Top supplies by cost": { es: "Suministros con mayor costo" },
  "Total Forms": { es: "Formularios en total" },
  "Total": { es: "Total" },
  "Total Claimed": { es: "Total tomados" },
  "Total Pickups": { es: "Turnos tomados" },
  "Training": { es: "Capacitaci\u00f3n" },
  "Training Name": { es: "Nombre de la capacitaci\u00f3n" },
  "Training Records ({0})": { es: "Registros de capacitaci\u00f3n ({0})" },
  "Training Type": { es: "Tipo de capacitaci\u00f3n" },
  "Training record added": { es: "Registro de capacitaci\u00f3n agregado" },
  "Training record not found": { es: "No se encontr\u00f3 el registro de capacitaci\u00f3n" },
  "Training record updated": { es: "Registro de capacitaci\u00f3n actualizado" },
  "Trend bucket": { es: "Agrupaci\u00f3n de la tendencia" },
  "Trend by {0}": { es: "Tendencia por {0}" },
  "Triggered By": { es: "Iniciado por" },
  "Try again": { es: "Intentar de nuevo" },
  "Try clearing filters.": { es: "Pruebe a quitar los filtros." },
  "Tue": { es: "Mar" },
  "Tuesday": { es: "Martes" },
  "Type": { es: "Tipo" },
  "Type a message": { es: "Escriba un mensaje" },
  "Type {0} to confirm.": { es: "Escriba {0} para confirmar." },
  "Typical run time is 1 to 5 minutes depending on volume. Do not close this page while it runs. You can monitor progress in the Sync Log table below after it completes.": { es: "Suele tardar de 1 a 5 minutos seg\u00fan el volumen. No cierre esta p\u00e1gina mientras se ejecuta. Puede seguir el avance en la tabla del registro de sincronizaciones cuando termine." },
  "Untitled form": { es: "Formulario sin t\u00edtulo" },
  "URGENT": { es: "URGENTE" },
  "Unable to Resolve": { es: "No se pudo resolver" },
  "Unassigned": { es: "Sin asignar" },
  "Uncategorized": { es: "Sin categor\u00eda" },
  "Unfinished reports": { es: "Informes sin terminar" },
  "Unfinished": { es: "Sin terminar" },
  "Unheld": { es: "Sin responsable" },
  "Unit": { es: "Unidad" },
  "Unit *": { es: "Unidad *" },
  "Unit Cost": { es: "Costo unitario" },
  "Unknown": { es: "Desconocido" },
  "Unlink": { es: "Desvincular" },
  "Unlinked only": { es: "Solo sin vincular" },
  "Unlinked": { es: "Desvinculado" },
  "Unmatched": { es: "Sin asociar" },
  "Unmatched: {0}": { es: "Sin coincidencia: {0}" },
  "Unresolved Failures": { es: "Fallos sin resolver" },
  "Unresolved Submission Failures": { es: "Fallos de env\u00edo sin resolver" },
  "Unspecified": { es: "Sin especificar" },
  "Until": { es: "Hasta" },
  "Upcoming": { es: "Pr\u00f3ximos" },
  "Upcoming Shifts (Next 7 Days)": { es: "Pr\u00f3ximos turnos (siguientes 7 d\u00edas)" },
  "Updated": { es: "Actualizado" },
  "Updated the case": { es: "Actualiz\u00f3 el caso" },
  "Upload File": { es: "Subir un archivo" },
  "Upload Logo": { es: "Subir logotipo" },
  "Upload a photo or video (up to 50MB), or paste a YouTube link above": { es: "Suba una foto o un video (hasta 50 MB), o pegue arriba un enlace de YouTube" },
  "Upload failed with status {0}": { es: "La subida fall\u00f3 con el estado {0}" },
  "Upload failed": { es: "No se pudo subir el archivo" },
  "Upload failed ({0}): {1}": { es: "No se pudo subir el archivo ({0}): {1}" },
  "Upload failed: {0}": { es: "No se pudo subir el archivo: {0}" },
  "Uploaded": { es: "Archivo subido" },
  "Uploaded By": { es: "Subido por" },
  "Uploaded: {0}": { es: "Subidos: {0}" },
  "Uploading...": { es: "Subiendo..." },
  "Urgency": { es: "Urgencia" },
  "Urgent": { es: "Urgente" },
  "Usage events": { es: "Registros de uso" },
  "Use company branding on export": { es: "Usar la marca de la empresa al exportar" },
  "Use this as a reference. Change what one person may do under By person.": { es: "\u00daselo como referencia. Lo que una persona puede hacer se cambia en Por persona." },
  "Use this only when you have a specific reason, such as: you have made changes in Jotform that are not appearing here, the field-extraction logic has changed, or you suspect the cache is out of sync. For normal daily operations, the regular \"Sync All Submissions\" button is sufficient.": { es: "\u00dasela solo con un motivo concreto, por ejemplo: hizo cambios en Jotform que no aparecen aqu\u00ed, cambi\u00f3 la l\u00f3gica de extracci\u00f3n de campos, o sospecha que la copia local est\u00e1 desactualizada. Para el trabajo diario basta con el bot\u00f3n \"Sincronizar todos los env\u00edos\"." },
  "Used by Speak Up. People only, never outside addresses. Nothing uses this until the Speak Up update.": { es: "Lo usa Confianza. Solo personas, nunca direcciones externas. Nada lo usa hasta la actualizaci\u00f3n de Confianza." },
  "User {0}": { es: "Usuario {0}" },
  "User": { es: "Persona" },
  "Username": { es: "Nombre de usuario" },
  "Valid": { es: "Vigente" },
  "Value (lowercased automatically)": { es: "Valor (se pasa a min\u00fasculas autom\u00e1ticamente)" },
  "Value": { es: "Valor" },
  "Value (stored)": { es: "Valor (se guarda)" },
  "Value (stored) *": { es: "Valor (se guarda) *" },
  "Value *": { es: "Valor *" },
  "Value added": { es: "Valor agregado" },
  "Value and label required": { es: "El valor y el nombre son obligatorios" },
  "Value updated": { es: "Valor actualizado" },
  "Vendor": { es: "Proveedor" },
  "Vendor / W-9": { es: "Proveedor / W-9" },
  "Vendor Name *": { es: "Nombre del proveedor *" },
  "Vendor Registry": { es: "Registro de proveedores" },
  "Vendor added": { es: "Proveedor agregado" },
  "Vendor name required": { es: "El nombre del proveedor es obligatorio" },
  "Vendor removed": { es: "Proveedor quitado" },
  "Vendor updated": { es: "Proveedor actualizado" },
  "Vendors": { es: "Proveedores" },
  "Vendors and services": { es: "Proveedores y servicios" },
  "Vendors: create, edit, remove, link": { es: "Proveedores: crear, editar, quitar y vincular" },
  "Vendors: evaluate and export": { es: "Proveedores: evaluar y exportar" },
  "Vendors: view": { es: "Proveedores: ver" },
  "View Original PDF": { es: "Ver el PDF original" },
  "View": { es: "Ver" },
  "View All": { es: "Ver todo" },
  "View PDF": { es: "Ver el PDF" },
  "View Site": { es: "Ver el sitio web" },
  "View all": { es: "Ver todo" },
  "View file": { es: "Ver el archivo" },
  "View inspection": { es: "Ver la inspecci\u00f3n" },
  "View profile": { es: "Ver el perfil" },
  "View shift": { es: "Ver el turno" },
  "View site": { es: "Ver el sitio" },
  "View vendor": { es: "Ver el proveedor" },
  "Void": { es: "Anular" },
  "Void report": { es: "Anular reporte" },
  "Void|status": { es: "Anulado" },
  "Voluntary Drop": { es: "Baja voluntaria" },
  "Voluntary Drops": { es: "Bajas voluntarias" },
  "Website": { es: "Sitio web" },
  "Wed": { es: "Mi\u00e9" },
  "Wednesday": { es: "Mi\u00e9rcoles" },
  "Week": { es: "Semana" },
  "Weekly": { es: "Semanal" },
  "Weekly Trend": { es: "Tendencia semanal" },
  "Weekly pattern": { es: "Patr\u00f3n semanal" },
  "Welcome back, {0}": { es: "Bienvenido de nuevo, {0}" },
  "Welcome, {0}": { es: "Bienvenido, {0}" },
  "What this does": { es: "Qu\u00e9 hace" },
  "What this report covers": { es: "Lo que cubre este informe" },
  "What was reported": { es: "Lo que se inform\u00f3" },
  "When a submitter's name or email does not exactly match a user record, auto-link falls back to the alias table. Aliases are added in two ways: (a) automatically whenever an admin manually links a submission to a user (source = manual_link), and (b) explicitly in this tab (source = admin_added). The five-tier matching order is: employee ID exact, email exact, email alias, name exact, name alias. Each alias's last_matched_at and match_count show when and how often it has helped.": { es: "Cuando el nombre o el correo de un remitente no coincide exactamente con un registro de usuario, la vinculaci\u00f3n autom\u00e1tica recurre a la tabla de alias. Los alias se agregan de dos formas: (a) autom\u00e1ticamente cada vez que un administrador vincula a mano un env\u00edo con una persona (source = manual_link), y (b) de forma expl\u00edcita en esta pesta\u00f1a (source = admin_added). El orden de coincidencia, en cinco niveles, es: ID de empleado exacto, correo exacto, alias de correo, nombre exacto, alias de nombre. Los campos last_matched_at y match_count de cada alias muestran cu\u00e1ndo y cu\u00e1ntas veces ha servido." },
  "When set, each submission will auto-compute an expiry date.": { es: "Si se indica, cada env\u00edo calcular\u00e1 su fecha de vencimiento autom\u00e1ticamente." },
  "When to use this": { es: "Cu\u00e1ndo usarla" },
  "When": { es: "Cu\u00e1ndo" },
  "Who attended": { es: "Qui\u00e9nes asistieron" },
  "Who gets told": { es: "Qui\u00e9n recibe avisos" },
  "Who has no record": { es: "Qui\u00e9n no tiene registro" },
  "Why is this report being voided?": { es: "\u00bfPor qu\u00e9 se anula este reporte?" },
  "Work Started": { es: "Trabajo iniciado" },
  "Worked": { es: "Trabajados" },
  "Working from the written procedure only right now.": { es: "Por ahora trabajo solo con el procedimiento escrito." },
  "Working...": { es: "Procesando..." },
  "Works": { es: "Funciona" },
  "Written once; each person gets it in their language.": { es: "Se escribe una vez; cada persona lo recibe en su idioma." },
  "Yes": { es: "S\u00ed" },
  "You can send up to 3 photos with one message.": { es: "Puede enviar hasta 3 fotos con un mensaje." },
  "YouTube link attached": { es: "Enlace de YouTube adjunto" },
  "Your account cannot read incident reports.": { es: "Su cuenta no puede leer los informes de incidentes." },
  "Your message did not send.": { es: "Su mensaje no se envi\u00f3." },
  "Your messages": { es: "Sus mensajes" },
  "Your own capabilities are set by another admin.": { es: "Sus propias capacidades las define otro administrador." },
  "Your settings did not save.": { es: "Sus ajustes no se guardaron." },
  "ZIP": { es: "C\u00f3digo postal" },
  "Zip": { es: "C\u00f3digo postal" },
  "Zone": { es: "Zona" },
  "Zone *": { es: "Zona *" },
  "Zones": { es: "Zonas" },
  "across {0}": { es: "de {0}" },
  "already scheduled at that time": { es: "ya programado a esa hora" },
  "annual": { es: "anual" },
  "at {0}": { es: "en {0}" },
  "biweekly": { es: "quincenal" },
  "by {0}": { es: "por {0}" },
  "changed by hand": { es: "cambiado a mano" },
  "clock in": { es: "entrada registrada" },
  "critical": { es: "cr\u00edtica" },
  "deleted user": { es: "persona eliminada" },
  "e.g. #24A4F4": { es: "p. ej. #24A4F4" },
  "e.g. 1": { es: "p. ej. 1" },
  "e.g. 1, 2, B": { es: "p. ej. 1, 2, B" },
  "e.g. 365 for annual expiration": { es: "p. ej. 365 para vencimiento anual" },
  "e.g. 95% or Pass": { es: "p. ej. 95% o Aprobado" },
  "e.g. All-Purpose Cleaner": { es: "p. ej. Limpiador multiusos" },
  "e.g. Called out": { es: "p. ej. se report\u00f3 ausente" },
  "e.g. Called out sick": { es: "p. ej. se report\u00f3 enfermo" },
  "e.g. Clean window blinds in conference room": { es: "p. ej. Limpiar las persianas de la sala de reuniones" },
  "e.g. Complete bloodborne pathogens training": { es: "p. ej. Completar la capacitaci\u00f3n sobre pat\u00f3genos transmitidos por la sangre" },
  "e.g. Equipment Types": { es: "p. ej. Tipos de equipo" },
  "e.g. Floor Technician": { es: "p. ej. T\u00e9cnico de pisos" },
  "e.g. General Cleaning Training": { es: "p. ej. Capacitaci\u00f3n general de limpieza" },
  "e.g. Green Cleaning Fundamentals": { es: "p. ej. Fundamentos de limpieza ecol\u00f3gica" },
  "e.g. Green cleaning only, monthly frequency": { es: "p. ej. Solo limpieza ecol\u00f3gica, frecuencia mensual" },
  "e.g. Gymnasium": { es: "p. ej. Gimnasio" },
  "e.g. High severity weekly": { es: "p. ej. Severidad alta, semanal" },
  "e.g. ISSA, OSHA": { es: "p. ej. ISSA, OSHA" },
  "e.g. Issues": { es: "p. ej. Problemas" },
  "e.g. Main": { es: "p. ej. Principal" },
  "e.g. Office Cleaning": { es: "p. ej. Limpieza de oficinas" },
  "e.g. Offices": { es: "p. ej. Oficinas" },
  "e.g. Restrooms": { es: "p. ej. Ba\u00f1os" },
  "e.g. Site supervisor": { es: "p. ej. Supervisor del sitio" },
  "e.g. Standard Office Cleaning": { es: "p. ej. Limpieza est\u00e1ndar de oficinas" },
  "e.g. Toilets scrubbed and sanitized": { es: "p. ej. Inodoros tallados y desinfectados" },
  "e.g. Vacuum carpets": { es: "p. ej. Aspirar las alfombras" },
  "e.g. cleaner, supervisor, admin": { es: "p. ej. cleaner, supervisor, admin" },
  "e.g. equipment_types": { es: "p. ej. tipos_de_equipo" },
  "e.g. floor_tech": { es: "p. ej. tecnico_de_pisos" },
  "e.g. {0}": { es: "p. ej. {0}" },
  "email alias already mapped to a different user": { es: "el alias de correo ya corresponde a otra persona" },
  "email {0}": { es: "correo {0}" },
  "email": { es: "correo" },
  "email-alias {0}": { es: "alias de correo {0}" },
  "ends next day": { es: "termina al d\u00eda siguiente" },
  "ends {0}": { es: "termina el {0}" },
  "failed": { es: "fallido" },
  "fetch|stage": { es: "descarga" },
  "first last as they submitted": { es: "nombre apellido tal como lo enviaron" },
  "general cleaning guidance": { es: "una gu\u00eda general de limpieza" },
  "generated {0}": { es: "generado el {0}" },
  "high": { es: "alta" },
  "in progress": { es: "en curso" },
  "issue reported": { es: "incidencia reportada" },
  "jdoe.personal@gmail.com": { es: "jperez.personal@gmail.com" },
  "logo": { es: "logotipo" },
  "low": { es: "baja" },
  "max {0} pts": { es: "m\u00e1x. {0} pts" },
  "medium": { es: "media" },
  "message sent": { es: "mensaje enviado" },
  "monthly": { es: "mensual" },
  "n/a": { es: "n/d" },
  "name alias already mapped to a different user": { es: "el alias de nombre ya corresponde a otra persona" },
  "name {0}": { es: "nombre {0}" },
  "name": { es: "nombre" },
  "name-alias {0}": { es: "alias de nombre {0}" },
  "name@email.com": { es: "nombre@correo.com" },
  "never": { es: "nunca" },
  "no email": { es: "sin correo" },
  "normal": { es: "normal" },
  "note: {0}": { es: "nota: {0}" },
  "of {0} active": { es: "de {0} activos" },
  "on {0}": { es: "el {0}" },
  "parse|stage": { es: "lectura" },
  "partial|sync": { es: "parcial" },
  "pending": { es: "pendiente" },
  "posted as an open shift": { es: "publicado como turno abierto" },
  "prompts text input": { es: "muestra un cuadro de texto" },
  "quarterly": { es: "trimestral" },
  "running|sync": { es: "en curso" },
  "shift created": { es: "turno creado" },
  "shift deleted": { es: "turno eliminado" },
  "shift session": { es: "sesi\u00f3n de turno" },
  "shift updated": { es: "turno actualizado" },
  "site assigned": { es: "sitio asignado" },
  "site unassigned": { es: "sitio desasignado" },
  "someone": { es: "alguien" },
  "staff site assignment": { es: "asignaci\u00f3n de sitio" },
  "standard": { es: "est\u00e1ndar" },
  "started|pattern": { es: "iniciado" },
  "store|stage": { es: "guardado" },
  "success": { es: "correcto" },
  "supply logged": { es: "suministro registrado" },
  "supply usage": { es: "uso de suministros" },
  "task completed": { es: "tarea completada" },
  "the ADP guide": { es: "la gu\u00eda de ADP" },
  "the app guide": { es: "la gu\u00eda de la aplicaci\u00f3n" },
  "to {0}": { es: "a {0}" },
  "today": { es: "hoy" },
  "unable to resolve": { es: "no se pudo resolver" },
  "unknown": { es: "desconocido" },
  "urgent": { es: "urgente" },
  "weekly": { es: "semanal" },
  "weeks": { es: "semanas" },
  "why is this an alias": { es: "por qu\u00e9 es un alias" },
  "with a link to the app": { es: "con un enlace a la aplicaci\u00f3n" },
  "with the PDF attached": { es: "con el PDF adjunto" },
  "yesterday": { es: "ayer" },
  "{0} (copy)": { es: "{0} (copia)" },
  "{0} (no dashes needed)": { es: "{0} (sin guiones)" },
  "{0} - Activity Timeline": { es: "{0} - Historial de actividad" },
  "{0} - Chat History": { es: "{0} - Historial del chat" },
  "{0} - Employee Report": { es: "{0} - Informe de la persona" },
  "{0} - Site Timeline": { es: "{0} - Historial del sitio" },
  "{0} Admin": { es: "{0} Administraci\u00f3n" },
  "{0} answered": { es: "{0} respondidas" },
  "{0} photo|count": {
    en: { one: "{0} photo", other: "{0} photos" },
    es: { one: "{0} foto", other: "{0} fotos" },
  },
  "{0} Supervisor": { es: "{0} Supervisi\u00f3n" },
  "{0} added, {1} removed, {2} kept": { es: "{0} agregados, {1} quitados, {2} conservados" },
  "{0} added, {1} removed, {2} total": { es: "{0} agregados, {1} eliminados, {2} en total" },
  "{0} answered, {1} to go": { es: "{0} respondidas, {1} pendientes" },
  "{0} at {1}": { es: "{0} en {1}" },
  "{0} cases need attention": { es: "{0} casos necesitan atenci\u00f3n" },
  "{0} claimed": { es: "{0} tomados" },
  "{0} claimed with overtime risk": { es: "{0} tomados con riesgo de horas extra" },
  "{0} claims": { es: "{0} tomados" },
  "{0} dates were skipped because this person is already scheduled at that time:": { es: "Se omitieron {0} fechas porque esta persona ya tiene un turno a esa hora:" },
  "{0} days ago": { es: "hace {0} d\u00edas" },
  "{0} folders found": { es: "{0} carpetas encontradas" },
  "{0} for {1}": { es: "{0} para {1}" },
  "{0} labels returned": { es: "{0} etiquetas devueltas" },
  "{0} new": { es: "{0} nuevos" },
  "{0} of {1}": { es: "{0} de {1}" },
  "{0} of {1} answered": { es: "{0} de {1} respondidas" },
  "{0} of {1} tasks": { es: "{0} de {1} tareas" },
  "{0} open": { es: "{0} abiertos" },
  "{0} open issues": { es: "{0} incidencias abiertas" },
  "{0} pending": { es: "{0} pendientes" },
  "{0} purged ({1} submissions removed)": { es: "{0} depurados ({1} env\u00edos eliminados)" },
  "{0} scheduled": { es: "{0} programados" },
  "{0} skipped (wrong prefix)": { es: "{0} omitidos (prefijo incorrecto)" },
  "{0} staff": { es: "{0} personas" },
  "{0} to {1}": { es: "{0} a {1}" },
  "{0} total, {1} callouts": { es: "{0} en total, {1} ausencias" },
  "{0} unread messages": { es: "{0} mensajes sin leer" },
  "{0} unread notifications": { es: "{0} avisos sin leer" },
  "{0} updated": { es: "{0} actualizados" },
  "{0}% callouts": { es: "{0}% de ausencias" },
  "{0}% filled": { es: "{0}% cubierto" },
  "{0}/{1} filled": { es: "{0}/{1} cubiertos" },
  "{0}/{1} pts": { es: "{0}/{1} pts" },
  "{0}: {1}% on {2}": { es: "{0}: {1}% el {2}" },
  "{0}d": { es: "{0}d" },
  "{0}d lead": { es: "{0} d de entrega" },
  "{0}h": { es: "{0}h" },
  "{0}h past": { es: "{0} h de retraso" },
  "{0}m": { es: "{0}m" },
  "{0}w ago": { es: "hace {0} sem." },
  // One English word, two Spanish ones. The context after the bar names the noun the Spanish
  // adjective has to agree with.
  "Active|pattern": { es: "Activo" },
  "Active|people": { es: "Activas" },
  "Active|person": { es: "Activa" },
  "Active|sites": { es: "Activos" },
  "Aging|issue count": { es: "Atrasadas" },
  "All Areas|zone": { es: "Todas las \u00e1reas" },
  "All staff|access": { es: "Todo el personal" },
  "All|cases": { es: "Todos" },
  "All|inspections": { es: "Todas" },
  "All|patterns": { es: "Todos" },
  "All|people": { es: "Todas" },
  "All|requests": { es: "Todas" },
  "All|shifts": { es: "Todos" },
  "All|sites": { es: "Todos" },
  "All|timeline": { es: "Todo" },
  "All|vendors": { es: "Todos" },
  "Approved|request": { es: "Aprobada" },
  "Approved|shift": { es: "Aprobado" },
  "Approved|vendor": { es: "Aprobado" },
  "Approved|vendors": { es: "Aprobados" },
  "Assigned sites|access": { es: "Sitios asignados" },
  "Assigned|access": { es: "Asignados" },
  "Assigned|inspection": { es: "Asignada a" },
  "Assigned|issue": { es: "Asignada" },
  "Assigned|shift": { es: "Asignado" },
  "Assigned|task": { es: "Asignada" },
  "Cancelled|inspection": { es: "Cancelada" },
  "Cancelled|request": { es: "Cancelada" },
  "Cancelled|shift": { es: "Cancelado" },
  "Claimed|shift": { es: "Tomado" },
  "Closed|case": { es: "Cerrado" },
  "Closed|shift": { es: "Cerrado" },
  "Common Areas|zone": { es: "\u00c1reas comunes" },
  "Complete assigned|access": { es: "Completar las asignadas" },
  "Completed|inspection": { es: "Completada" },
  "Completed|inspections": { es: "Completadas" },
  "Completed|shift": { es: "Completado" },
  "DOC|source": { es: "DOC" },
  "Default|permission": { es: "Predeterminado" },
  "Denied|request": { es: "Denegada" },
  "Due soon|case": { es: "Vence pronto" },
  "Ended|pattern": { es: "Terminado" },
  "Escalated|case": { es: "Escalado" },
  "Expired|filter": { es: "Vencidos" },
  "Expired|item": { es: "Vencido" },
  "Expired|shift": { es: "Vencido" },
  "Exterior|zone": { es: "Exterior" },
  "Filled|shift": { es: "Cubierto" },
  "General|zone": { es: "General" },
  "In review|case": { es: "En revisi\u00f3n" },
  "Inactive|people": { es: "Inactivas" },
  "Inactive|sites": { es: "Inactivos" },
  "Inactive|vendor": { es: "Inactivo" },
  "Inactive|vendors": { es: "Inactivos" },
  "Invalid|key": { es: "No v\u00e1lida" },
  "JF|source": { es: "JF" },
  "Kitchen/Break Room|zone": { es: "Cocina / sala de descanso" },
  "Link|button": { es: "Vincular" },
  "Lobby|zone": { es: "Vest\u00edbulo" },
  "Log and request|access": { es: "Registrar y solicitar" },
  "Manage|access": { es: "Administrar" },
  "New|records": { es: "Nuevos" },
  "New|request": { es: "Nueva" },
  "New|shift": { es: "Nuevo" },
  "New|submission": { es: "Nuevo" },
  "None|access": { es: "Sin acceso" },
  "None|activity": { es: "Ninguna" },
  "None|inspection": { es: "Ninguna" },
  "None|note": { es: "Ninguna" },
  "None|site": { es: "Ninguno" },
  "None|shifts": { es: "Ninguno" },
  "Not set|key": { es: "Sin configurar" },
  "ONB|source": { es: "INC" },
  "Offices|zone": { es: "Oficinas" },
  "Off|value": { es: "Desactivar" },
  "On time|case": { es: "A tiempo" },
  "On|value": { es: "Activar" },
  "Open|case": { es: "Abierto" },
  "Open|issue": { es: "Abierta" },
  "Open|issue count": { es: "Abiertas" },
  "Open|shift": { es: "Abierto" },
  "Other|items": { es: "Otros" },
  "Overdue|case": { es: "Atrasado" },
  "Own tasks|access": { es: "Sus tareas" },
  "Parking|zone": { es: "Estacionamiento" },
  "Pending|people": { es: "Pendientes" },
  "Pending|pickups": { es: "Pendientes" },
  "Pending|vendors": { es: "Pendientes" },
  "Probation|vendors": { es: "A prueba" },
  "Released|shift": { es: "Liberado" },
  "Removed|assignment": { es: "Asignaci\u00f3n quitada" },
  "Removed|certification": { es: "Certificaci\u00f3n quitada" },
  "Removed|task": { es: "Tarea quitada" },
  "Reported|issue count": { es: "Reportadas" },
  "Report|access": { es: "Informar" },
  "Requested|request": { es: "Solicitada" },
  "Request|access": { es: "Solicitar" },
  "Resolved|case": { es: "Resuelto" },
  "Resolved|issue count": { es: "Resueltas" },
  "Responded|case": { es: "Respondido" },
  "Restrooms|zone": { es: "Ba\u00f1os" },
  "Scheduled|date": { es: "Programada para" },
  "Scheduled|inspection": { es: "Programada" },
  "Scheduled|inspections": { es: "Programadas" },
  "Scheduled|shift": { es: "Programado" },
  "Schedule|verb": { es: "Programar" },
  "Self|access": { es: "Uno mismo" },
  "Start|work": { es: "Iniciar" },
  "TR|source": { es: "CAP" },
  "Time|section": { es: "Horarios" },
  "Updated|records": { es: "Actualizados" },
  "Valid|key": { es: "V\u00e1lida" },
  "View|access": { es: "Ver" },
  "active|person": { es: "activa" },
  "active|site": { es: "activo" },
  "all|issues": { es: "todas" },
  "approved|request": { es: "aprobada" },
  "approved|vendor": { es: "aprobado" },
  "assigned|task": { es: "asignada" },
  "cancelled|inspection": { es: "cancelada" },
  "certification|record": { es: "certificaci\u00f3n" },
  "clock|record": { es: "asistencia" },
  "closed|issue": { es: "cerrada" },
  "completed at|field": { es: "fecha de finalizaci\u00f3n" },
  "completed|inspection": { es: "completada" },
  "completed|item": { es: "completado" },
  "completed|training": { es: "completada" },
  "created at|field": { es: "fecha de creaci\u00f3n" },
  "custom|report category": { es: "personalizados" },
  "day|bucket": { es: "d\u00eda" },
  "denied|request": { es: "denegada" },
  "description|field": { es: "descripci\u00f3n" },
  "document|record": { es: "documento" },
  "escalated|issue": { es: "escalada" },
  "escalated|issues": { es: "escaladas" },
  "failed|training": { es: "no aprobada" },
  "floor|field": { es: "piso" },
  "form|record": { es: "formulario" },
  "fulfilled|request": { es: "entregada" },
  "inactive|person": { es: "inactiva" },
  "inactive|site": { es: "inactivo" },
  "inactive|vendor": { es: "inactivo" },
  "inspection|record": { es: "inspecci\u00f3n" },
  "issue|record": { es: "incidencia" },
  "kind|field": { es: "tipo" },
  "label|field": { es: "nombre" },
  "labor|report category": { es: "mano de obra" },
  "lookup|record": { es: "lista de opciones" },
  "message|record": { es: "mensaje" },
  "month|bucket": { es: "mes" },
  "notes|field": { es: "notas" },
  "off|category": { es: "inactiva" },
  "onboarding|record": { es: "incorporaci\u00f3n" },
  "open|issue": { es: "abierta" },
  "open|issues": { es: "abiertas" },
  "other|report category": { es: "otros" },
  "pickup|record": { es: "turno disponible" },
  "priority|field": { es: "prioridad" },
  "probation|vendor": { es: "a prueba" },
  "quality|report category": { es: "calidad" },
  "reported at|field": { es: "fecha del reporte" },
  "resolved|issue": { es: "resuelta" },
  "resolved|issues": { es: "resueltas" },
  "resolved|task": { es: "resuelta" },
  "scheduled|inspection": { es: "programada" },
  "schedule|record": { es: "horario" },
  "service|record": { es: "servicio" },
  "severity|field": { es: "gravedad" },
  "shift|record": { es: "turno" },
  "site name|field": { es: "nombre del sitio" },
  "site|field": { es: "sitio" },
  "status|field": { es: "estado" },
  "submitted|item": { es: "enviado" },
  "supplies|report category": { es: "suministros" },
  "supply|record": { es: "suministro" },
  "task|record": { es: "tarea" },
  "terminated|person": { es: "dada de baja" },
  "title|field": { es: "t\u00edtulo" },
  "to|between two dates": { es: "a" },
  "training|record": { es: "capacitaci\u00f3n" },
  "updated at|field": { es: "fecha de actualizaci\u00f3n" },
  "user|record": { es: "persona" },
  "vendor|record": { es: "proveedor" },
  "week|bucket": { es: "semana" },
  "zone|field": { es: "zona" },
  // A count choice, one entry per form the language has. English has two; Spanish has two; a
  // language with more picks its own through Intl.PluralRules.
  "{0} hour|count": {
    en: { one: "{0} hour", other: "{0} hours" },
    es: { one: "{0} hora", other: "{0} horas" },
  },
  "{0} inspection|count": {
    en: { one: "{0} inspection", other: "{0} inspections" },
    es: { one: "{0} inspecci\u00f3n", other: "{0} inspecciones" },
  },
  "{0} shift claimed with overtime risk|count": {
    en: { one: "{0} shift claimed with overtime risk", other: "{0} shifts claimed with overtime risk" },
    es: { one: "{0} turno tomado con riesgo de horas extra", other: "{0} turnos tomados con riesgo de horas extra" },
  },
  "{0} started today|count": {
    en: { one: "{0} started today", other: "{0} started today" },
    es: { one: "{0} iniciado hoy", other: "{0} iniciados hoy" },
  },
  "{0} started on {1}|count": {
    en: { one: "{0} started on {1}", other: "{0} started on {1}" },
    es: { one: "{0} iniciado el {1}", other: "{0} iniciados el {1}" },
  },
  "{0} started|count": {
    en: { one: "{0} started", other: "{0} started" },
    es: { one: "{0} iniciado", other: "{0} iniciados" },
  },
  "{0} no-show|count": {
    en: { one: "{0} no-show", other: "{0} no-shows" },
    es: { one: "{0} no se present\u00f3", other: "{0} no se presentaron" },
  },
  "{0} message|count": {
    en: { one: "{0} message", other: "{0} messages" },
    es: { one: "{0} mensaje", other: "{0} mensajes" },
  },
  "{0} entry|count": {
    en: { one: "{0} entry", other: "{0} entries" },
    es: { one: "{0} registro", other: "{0} registros" },
  },
  "{0} staff|count": {
    en: { one: "{0} staff", other: "{0} staff" },
    es: { one: "{0} persona", other: "{0} personas" },
  },
  "{0} task|count": {
    en: { one: "{0} task", other: "{0} tasks" },
    es: { one: "{0} tarea", other: "{0} tareas" },
  },
  "{0}mo ago|count": {
    en: { one: "{0}mo ago", other: "{0}mo ago" },
    es: { one: "hace {0} mes", other: "hace {0} meses" },
  },
  "{0}y ago|count": {
    en: { one: "{0}y ago", other: "{0}y ago" },
    es: { one: "hace {0} a\u00f1o", other: "hace {0} a\u00f1os" },
  },
  "{0} employee|count": {
    en: { one: "{0} employee", other: "{0} employees" },
    es: { one: "{0} persona", other: "{0} personas" },
  },
  "{0} expired|count": {
    en: { one: "{0} expired", other: "{0} expired" },
    es: { one: "{0} vencido", other: "{0} vencidos" },
  },
  "{0} expiring|count": {
    en: { one: "{0} expiring", other: "{0} expiring" },
    es: { one: "{0} por vencer", other: "{0} por vencer" },
  },
  "{0} document|count": {
    en: { one: "{0} document", other: "{0} documents" },
    es: { one: "{0} documento", other: "{0} documentos" },
  },
  "{0} record|count": {
    en: { one: "{0} record", other: "{0} records" },
    es: { one: "{0} registro", other: "{0} registros" },
  },
  "{1} of {0} steps complete|count": {
    en: { one: "{1} of {0} step complete", other: "{1} of {0} steps complete" },
    es: { one: "{1} de {0} paso completado", other: "{1} de {0} pasos completados" },
  },
  "{0} item|count": {
    en: { one: "{0} item", other: "{0} items" },
    es: { one: "{0} elemento", other: "{0} elementos" },
  },
  "({0} pending)|count": {
    en: { one: "({0} pending)", other: "({0} pending)" },
    es: { one: "({0} pendiente)", other: "({0} pendientes)" },
  },
  "{0} site|count": {
    en: { one: "{0} site", other: "{0} sites" },
    es: { one: "{0} sitio", other: "{0} sitios" },
  },
  "{0} linked|count": {
    en: { one: "{0} linked", other: "{0} linked" },
    es: { one: "{0} vinculado", other: "{0} vinculados" },
  },
  "Exported {0} row|count": {
    en: { one: "Exported {0} row", other: "Exported {0} rows" },
    es: { one: "{0} fila exportada", other: "{0} filas exportadas" },
  },
  "{0} line item|count": {
    en: { one: "{0} line item", other: "{0} line items" },
    es: { one: "{0} elemento", other: "{0} elementos" },
  },
  "{0} inspections, avg {1}%|count": {
    en: { one: "{0} inspection, avg {1}%", other: "{0} inspections, avg {1}%" },
    es: { one: "{0} inspecci\u00f3n, promedio {1}%", other: "{0} inspecciones, promedio {1}%" },
  },
  "{0} items scored, {1}/{2} pts|count": {
    en: { one: "{0} item scored, {1}/{2} pts", other: "{0} items scored, {1}/{2} pts" },
    es: { one: "{0} elemento calificado, {1}/{2} pts", other: "{0} elementos calificados, {1}/{2} pts" },
  },
  "{1} of {0} entry|count": {
    en: { one: "{1} of {0} entry", other: "{1} of {0} entries" },
    es: { one: "{1} de {0} registro", other: "{1} de {0} registros" },
  },
  "{0} done|count": {
    en: { one: "{0} done", other: "{0} done" },
    es: { one: "{0} completada", other: "{0} completadas" },
  },
  "{0} aging|count": {
    en: { one: "{0} aging", other: "{0} aging" },
    es: { one: "{0} atrasada", other: "{0} atrasadas" },
  },
  "SLA breaches in range|count": {
    en: { one: "SLA breach in range", other: "SLA breaches in range" },
    es: { one: "incumplimiento del SLA en el rango", other: "incumplimientos del SLA en el rango" },
  },
  "Activity Timeline ({1} of {0} entry{2})|count": {
    en: { one: "Activity Timeline ({1} of {0} entry{2})", other: "Activity Timeline ({1} of {0} entries{2})" },
    es: { one: "Historial de actividad ({1} de {0} registro{2})", other: "Historial de actividad ({1} de {0} registros{2})" },
  },
  "Load More ({0} remaining)|count": {
    en: { one: "Load More ({0} remaining)", other: "Load More ({0} remaining)" },
    es: { one: "Cargar m\u00e1s ({0} restante)", other: "Cargar m\u00e1s ({0} restantes)" },
  },
  "{0}h left|count": {
    en: { one: "{0}h left", other: "{0}h left" },
    es: { one: "{0} h restante", other: "{0} h restantes" },
  },
  "{0} values|count": {
    en: { one: "{0} value", other: "{0} values" },
    es: { one: "{0} valor", other: "{0} valores" },
  },
  "{0} person selected|count": {
    en: { one: "{0} person selected", other: "{0} people selected" },
    es: { one: "{0} persona seleccionada", other: "{0} personas seleccionadas" },
  },
  "{1} of {0} saved|count": {
    en: { one: "{1} of {0} saved", other: "{1} of {0} saved" },
    es: { one: "{1} de {0} guardado", other: "{1} de {0} guardados" },
  },
  "{0} of {1} have no record|count": {
    en: { one: "{0} of {1} has no record", other: "{0} of {1} have no record" },
    es: { one: "{0} de {1} no tiene registro", other: "{0} de {1} no tienen registro" },
  },
  "{0} person logged|count": {
    en: { one: "{0} person logged", other: "{0} people logged" },
    es: { one: "{0} persona registrada", other: "{0} personas registradas" },
  },
  "Bulk-resolve {0} unresolved failure for form \"{1}\".|count": {
    en: { one: "Bulk-resolve {0} unresolved failure for form \"{1}\".", other: "Bulk-resolve {0} unresolved failures for form \"{1}\"." },
    es: { one: "Resolver en bloque {0} fallo sin resolver del formulario \"{1}\".", other: "Resolver en bloque {0} fallos sin resolver del formulario \"{1}\"." },
  },
  "Bulk-resolved {0} failure.|count": {
    en: { one: "Bulk-resolved {0} failure.", other: "Bulk-resolved {0} failures." },
    es: { one: "Se resolvi\u00f3 en bloque {0} fallo.", other: "Se resolvieron en bloque {0} fallos." },
  },
  "{0} days|count": {
    en: { one: "{0} day", other: "{0} days" },
    es: { one: "{0} d\u00eda", other: "{0} d\u00edas" },
  },
  "{0} failure|count": {
    en: { one: "{0} failure", other: "{0} failures" },
    es: { one: "{0} fallo", other: "{0} fallos" },
  },
  "{0} alias|count": {
    en: { one: "{0} alias", other: "{0} aliases" },
    es: { one: "{0} alias", other: "{0} alias" },
  },
  "{0} user|count": {
    en: { one: "{0} user", other: "{0} users" },
    es: { one: "{0} persona", other: "{0} personas" },
  },
  "{0} email|count": {
    en: { one: "{0} email", other: "{0} emails" },
    es: { one: "{0} correo", other: "{0} correos" },
  },
  "{0} app notice|count": {
    en: { one: "{0} app notice", other: "{0} app notices" },
    es: { one: "{0} aviso en la aplicaci\u00f3n", other: "{0} avisos en la aplicaci\u00f3n" },
  },  // Step 169: customer links and their QR codes, a customer's filings, and the signature box on a
  // dashboard filing.
  "Customer links": { es: "Enlaces para clientes" },
  "Show QR code": { es: "Ver c\u00f3digo QR" },
  "Copy link": { es: "Copiar enlace" },
  "Turn off": { es: "Apagar" },
  "Turn on": { es: "Encender" },
  "Off|link": { es: "Apagado" },
  "Expired|link": { es: "Vencido" },
  "Uses": { es: "Usos" },
  "Last used": { es: "\u00daltimo uso" },
  "No customer links yet.": { es: "Todav\u00eda no hay enlaces para clientes." },
  "QR code": { es: "C\u00f3digo QR" },
  "Customer link": { es: "Enlace para clientes" },
  "The address could not be copied.": { es: "No se pudo copiar la direcci\u00f3n." },
  "Scan to tell {0} how the building is being kept.": { es: "Escanee para decirle a {0} c\u00f3mo se mantiene el edificio." },
  "Scan to tell {0} how we are doing.": { es: "Escanee para decirle a {0} c\u00f3mo lo estamos haciendo." },
  "Facility Cleanliness Evaluation Checklist": { es: "Lista de evaluaci\u00f3n de limpieza del edificio" },
  "Client Satisfaction Survey": { es: "Encuesta de satisfacci\u00f3n del cliente" },
  "Customer": { es: "Cliente" },
  "Section averages": { es: "Promedios por secci\u00f3n" },
  "Overall": { es: "General" },
  "Save signature": { es: "Guardar firma" },
  // Step 185: Help insights, rating an answer, Spanish corrections and the leftovers.
  "Help insights": { es: "Estad\u00edsticas de la Ayuda" },
  "Questions": { es: "Preguntas" },
  "People asking": { es: "Personas que preguntan" },
  "Missed": { es: "Sin respuesta" },
  "Rated helpful": { es: "\u00datiles" },
  "Rated not helpful": { es: "No \u00fatiles" },
  "Typical reply time": { es: "Tiempo t\u00edpico de respuesta" },
  "Questions by day": { es: "Preguntas por d\u00eda" },
  "What people ask about": { es: "De qu\u00e9 preguntan" },
  "Questions Help could not answer": { es: "Preguntas que la Ayuda no pudo responder" },
  "What was missing": { es: "Lo que falt\u00f3" },
  "Everyone who asked": { es: "Todos los que preguntaron" },
  "Last asked": { es: "\u00daltima pregunta" },
  "Their questions": { es: "Sus preguntas" },
  "Opening this is recorded.": { es: "Abrir esto queda registrado." },
  "No questions in this range.": { es: "No hay preguntas en este periodo." },
  "Was this helpful?": { es: "\u00bfLe sirvi\u00f3?" },
  "What was missing?": { es: "\u00bfQu\u00e9 falt\u00f3?" },
  "Thanks. This helps Help get better.": { es: "Gracias. Esto ayuda a mejorar la Ayuda." },
  "Test account": { es: "Cuenta de prueba" },
  "Shown in Spanish as": { es: "Se muestra en espa\u00f1ol como" },
  "Saved. People see this wording from now on.": { es: "Guardado. El personal ve este texto desde ahora." },
  "Rename": { es: "Cambiar nombre" },
  "Removed supplies": { es: "Suministros quitados" },
  "Certifications expiring soon": { es: "Certificaciones por vencer" },
  "Expired certifications": { es: "Certificaciones vencidas" },
  "Only people given time off approval see requests here. Ask an admin to grant it under Roles and Permissions.": { es: "Solo quienes pueden aprobar tiempo libre ven las solicitudes aqu\u00ed. Pida a un administrador que lo otorgue en Roles y permisos." },
  "See Help insights": { es: "Ver estad\u00edsticas de la Ayuda" },
  "Portal": { es: "Portal" },
  "All languages": { es: "Todos los idiomas" },
  "All apps": { es: "Todas las aplicaciones" },
  "App": { es: "Aplicaci\u00f3n" },
  "{0} sec": { es: "{0} s" },
  "{0} min": { es: "{0} min" },
  "{0}% missed": { es: "{0}% sin respuesta" },
  "{0} missed": { es: "{0} sin respuesta" },
  "By language": { es: "Por idioma" },
  "By app": { es: "Por aplicaci\u00f3n" },
  "Topic": { es: "Tema" },
  "Section": { es: "Secci\u00f3n" },
  "Topics": { es: "Temas" },
  "Question": { es: "Pregunta" },
  "Form builder": { es: "Creador de formularios" },
  "New form": { es: "Nuevo formulario" },
  "Version {0}": { es: "Versi\u00f3n {0}" },
  "Published": { es: "Publicado" },
  "Draft in progress": { es: "Borrador en curso" },
  "Retired": { es: "Retirado" },
  "From the code": { es: "Del c\u00f3digo" },
  "Made with the builder": { es: "Hecho con el creador" },
  "Retire form": { es: "Retirar formulario" },
  "Why is this form being retired?": { es: "\u00bfPor qu\u00e9 se retira este formulario?" },
  "Version history": { es: "Historial de versiones" },
  "Published as version {0}.": { es: "Publicado como versi\u00f3n {0}." },
  "Make and change forms": { es: "Crear y cambiar formularios" },
  "Code": { es: "C\u00f3digo" },
  "Version": { es: "Versi\u00f3n" },
  "No version published yet.": { es: "Todav\u00eda no se ha publicado ninguna versi\u00f3n." },
  "A retired form leaves every list. Reports already filed still open.": { es: "Un formulario retirado sale de todas las listas. Los reportes ya presentados se siguen abriendo." },
  "Every form the apps offer, with its latest version. Edit opens the form's draft in the builder, or starts one. A row opens its version history.": { es: "Cada formulario que ofrecen las aplicaciones, con su \u00faltima versi\u00f3n. Editar abre el borrador del formulario en el creador, o empieza uno. Una fila abre su historial de versiones." },
  "No forms yet. New form starts one.": { es: "Todav\u00eda no hay formularios. Nuevo formulario empieza uno." },
  "Describe the form you need. The builder asks what it needs to know.": { es: "Describa el formulario que necesita. El creador le pregunta lo que necesita saber." },
  "Staff app": { es: "Aplicaci\u00f3n del personal" },
  "PDF": { es: "PDF" },
  "Try it": { es: "Probarlo" },
  "Needs fixing before it can be published": { es: "Hay que corregir esto antes de publicarlo" },
  "Who gets the filled report": { es: "Qui\u00e9n recibe el reporte lleno" },
  "Filled PDF attached": { es: "Con el PDF lleno adjunto" },
  "What changed": { es: "Qu\u00e9 cambi\u00f3" },
  "Publish": { es: "Publicar" },
  "Only an admin can publish. Your draft is saved for one to review.": { es: "Solo un administrador puede publicar. Su borrador queda guardado para que uno lo revise." },
  "Discard draft": { es: "Descartar borrador" },
  "This question asks for sensitive information.": { es: "Esta pregunta pide informaci\u00f3n delicada." },
  "The builder is working...": { es: "El creador est\u00e1 trabajando..." },
  "Write to the builder": { es: "Escriba al creador" },
  "You": { es: "Usted" },
  "The builder": { es: "El creador" },
  "Start over": { es: "Empezar de nuevo" },
  "A test filling. Nothing is saved or sent.": { es: "Un llenado de prueba. No se guarda ni se env\u00eda nada." },
  "This was a test. Nothing was saved or sent.": { es: "Esto fue una prueba. No se guard\u00f3 ni se envi\u00f3 nada." },
  "Photos are added when the form is filled for real.": { es: "Las fotos se agregan cuando el formulario se llena de verdad." },
  "The customer signs here.": { es: "El cliente firma aqu\u00ed." },
  "Question {0} of {1}": { es: "Pregunta {0} de {1}" },
  "Nothing to show yet. Describe the form and the preview draws it.": { es: "Todav\u00eda no hay nada que mostrar. Describa el formulario y la vista previa lo dibuja." },
  "Loading the PDF...": { es: "Cargando el PDF..." },
  "No problems. This draft can be published.": { es: "Sin problemas. Este borrador se puede publicar." },
  "Discard this draft? It leaves the list and cannot be reopened.": { es: "\u00bfDescartar este borrador? Sale de la lista y no se puede volver a abrir." },
  "Publishing makes this the version every app offers.": { es: "Al publicar, esta pasa a ser la versi\u00f3n que ofrecen todas las aplicaciones." },
  "Fix the problems above before publishing.": { es: "Corrija los problemas de arriba antes de publicar." },
  "Publishing...": { es: "Publicando..." },
  "Filed form": { es: "Formulario presentado" },
  "FRM|source": { es: "FRM" },
  "No one matches.": { es: "Nadie coincide." },
  "No filed forms": { es: "No hay formularios presentados" },
  "Client reports": { es: "Informes para clientes" },
  "Client ratings": { es: "Calificaciones de los clientes" },
  "How clients rated each site, out of 10.": { es: "C\u00f3mo calificaron los clientes cada sitio, sobre 10." },
  "An answer given on the old 1 to 5 scale is doubled, so a 4 counts as 8.": { es: "Una respuesta dada en la escala anterior del 1 al 5 se multiplica por dos, as\u00ed que un 4 cuenta como 8." },
  "Not rated": { es: "Sin calificar" },
  "Responses": { es: "Respuestas" },
  "No response": { es: "Sin respuesta" },
  "No response in {0}.": { es: "Sin respuesta en {0}." },
  "Last response {0}": { es: "\u00daltima respuesta: {0}" },
  "No response yet": { es: "Todav\u00eda no hay respuestas" },
  "Each question": { es: "Cada pregunta" },
  "Overall by month": { es: "General por mes" },
  "Overall by site": { es: "General por sitio" },
  "Comments": { es: "Comentarios" },
  "From|comment": { es: "De" },
  "No comments in this range.": { es: "No hay comentarios en este periodo." },
  "No ratings in this range.": { es: "No hay calificaciones en este periodo." },
  "Client survey": { es: "Encuesta del cliente" },
  "Client survey schedule": { es: "Calendario de la encuesta del cliente" },
  "Survey schedule": { es: "Calendario de la encuesta" },
  "The Client Satisfaction Survey is emailed to each contact below on the day set, with the site's survey link.": { es: "La encuesta de satisfacci\u00f3n del cliente se env\u00eda por correo a cada contacto de abajo el d\u00eda indicado, con el enlace de la encuesta del sitio." },
  "Contacts": { es: "Contactos" },
  "No contacts yet.": { es: "Todav\u00eda no hay contactos." },
  "Check this email": { es: "Revise este correo" },
  "Add contact": { es: "Agregar contacto" },
  "How often": { es: "Con qu\u00e9 frecuencia" },
  "Every month": { es: "Cada mes" },
  "Every three months": { es: "Cada tres meses" },
  "Off|survey": { es: "Desactivado" },
  "Day of the month": { es: "D\u00eda del mes" },
  "Last sent": { es: "\u00daltimo env\u00edo" },
  "Not sent yet": { es: "Todav\u00eda no se ha enviado" },
  "Next send": { es: "Pr\u00f3ximo env\u00edo" },
  "Not scheduled": { es: "Sin programar" },
  "Schedule saved.": { es: "Calendario guardado." },
  "Monthly client reports": { es: "Informes mensuales para clientes" },
  "Each site's month, written up for its client, sent with the PDF and acknowledged by the client.": { es: "El mes de cada sitio, redactado para su cliente, enviado con el PDF y confirmado por el cliente." },
  "New monthly report": { es: "Nuevo informe mensual" },
  "The figures are filled in from the records. You can correct any of them and write the rest before it is signed.": { es: "Las cifras se completan con los registros. Puede corregir cualquiera y escribir el resto antes de firmarlo." },
  "First day": { es: "Primer d\u00eda" },
  "Make report": { es: "Crear informe" },
  "No monthly reports in this range.": { es: "No hay informes mensuales en este periodo." },
  "Sent to": { es: "Enviado a" },
  "Acknowledged by": { es: "Confirmado por" },
  "Acknowledged by {0} on {1}": { es: "Confirmado por {0} el {1}" },
  "Sent {0} and not acknowledged yet.": { es: "Enviado el {0} y todav\u00eda sin confirmar." },
  "Draft|monthly report": { es: "Borrador" },
  "Sent|monthly report": { es: "Enviado" },
  "Acknowledged": { es: "Confirmado" },
  "Overdue|monthly report": { es: "Atrasado" },
  "Send to the client": { es: "Enviar al cliente" },
  "The email carries the report as a PDF, a link to acknowledge it, and the site's rating survey.": { es: "El correo lleva el informe en PDF, un enlace para confirmarlo y la encuesta de calificaci\u00f3n del sitio." },
  "Recipients": { es: "Destinatarios" },
  "The site's survey contacts": { es: "Los contactos de la encuesta del sitio" },
  "Add an address": { es: "Agregar una direcci\u00f3n" },
  "Sent to the client.": { es: "Enviado al cliente." },
  "Bring back": { es: "Restaurar" },
  "Supply brought back": { es: "Suministro restaurado" },
  "Shift names": { es: "Nombres de turnos" },
  "Each shift's blocks, in the order the checklist groups tasks under them.": { es: "Los bloques de cada turno, en el orden en que la lista agrupa las tareas." },
  "No shift names at this site yet.": { es: "Todav\u00eda no hay nombres de turnos en este sitio." },
  "Add a block": { es: "Agregar un bloque" },
  "Move up": { es: "Subir" },
  "Move down": { es: "Bajar" },
  "Shift name": { es: "Nombre del turno" },
  "Block name": { es: "Nombre del bloque" },
  "Starts at": { es: "Empieza a las" },
  "A new shift name is given to every block of the shift.": { es: "Un nombre de turno nuevo se da a todos los bloques del turno." },
  "Remove this block from this list?": { es: "\u00bfQuitar este bloque de esta lista?" },
  "Block removed": { es: "Bloque quitado" },
  "Copying is blocked here. The PIN is selected, so copy it with your keyboard.": { es: "Aqu\u00ed no se permite copiar. El PIN est\u00e1 seleccionado, as\u00ed que c\u00f3pielo con el teclado." },
  "Could not remove this. {0}": { es: "No se pudo quitar. {0}" },
  "Linked by hand": { es: "Vinculado a mano" },
  "Added by an admin": { es: "Agregado por un administrador" },
  "Learned from a match": { es: "Aprendido de una coincidencia" },
  "No void reports.": { es: "No hay informes anulados." },
  "action|field": { es: "acci\u00f3n" },
  "active tasks still linked|field": { es: "tareas activas a\u00fan vinculadas" },
  "admin notes|field": { es: "notas del administrador" },
  "after|field": { es: "despu\u00e9s" },
  "alias id|field": { es: "id del alias" },
  "alias type|field": { es: "tipo de alias" },
  "alias value|field": { es: "valor del alias" },
  "aliases recorded|field": { es: "alias registrados" },
  "anchor time|field": { es: "hora de referencia" },
  "approval status|field": { es: "estado de aprobaci\u00f3n" },
  "assigned name|field": { es: "nombre asignado" },
  "assigned to|field": { es: "asignado a" },
  "assignee name|field": { es: "nombre de la persona asignada" },
  "badge number|field": { es: "n\u00famero de empleado" },
  "badge source|field": { es: "origen del n\u00famero de empleado" },
  "before|field": { es: "antes" },
  "block id|field": { es: "id del bloque" },
  "block label|field": { es: "nombre del bloque" },
  "blocks updated|field": { es: "bloques actualizados" },
  "bulk|field": { es: "en bloque" },
  "category|field": { es: "categor\u00eda" },
  "category id|field": { es: "id de la categor\u00eda" },
  "cert name|field": { es: "nombre de la certificaci\u00f3n" },
  "cert type|field": { es: "tipo de certificaci\u00f3n" },
  "changed|field": { es: "cambiado" },
  "changes|field": { es: "cambios" },
  "channel id|field": { es: "id del canal" },
  "channel name|field": { es: "nombre del canal" },
  "city|field": { es: "ciudad" },
  "claimed by|field": { es: "tomado por" },
  "clock in|field": { es: "entrada" },
  "clock out|field": { es: "salida" },
  "contract type|field": { es: "tipo de contrato" },
  "count|field": { es: "cantidad" },
  "created|field": { es: "creados" },
  "days|field": { es: "d\u00edas" },
  "decision|field": { es: "decisi\u00f3n" },
  "deleted shift|field": { es: "turno quitado" },
  "disabled|field": { es: "desactivado" },
  "disabled form ids|field": { es: "ids de formularios desactivados" },
  "duration minutes|field": { es: "minutos de duraci\u00f3n" },
  "effective from|field": { es: "vigente desde" },
  "employee id|field": { es: "id de empleado" },
  "end time|field": { es: "hora de fin" },
  "ended by|field": { es: "terminado por" },
  "ended by self|field": { es: "terminado por la misma persona" },
  "ends on|field": { es: "termina el" },
  "entity id|field": { es: "id del registro" },
  "entity type|field": { es: "tipo de registro" },
  "evaluation id|field": { es: "id de la evaluaci\u00f3n" },
  "failed|field": { es: "fallidos" },
  "failure id|field": { es: "id del fallo" },
  "field|field": { es: "campo" },
  "fields changed|field": { es: "campos cambiados" },
  "floor plan id|field": { es: "id del plano" },
  "form code|field": { es: "c\u00f3digo del formulario" },
  "form id|field": { es: "id del formulario" },
  "form title|field": { es: "t\u00edtulo del formulario" },
  "forms|field": { es: "formularios" },
  "forms with fetch errors|field": { es: "formularios con errores de descarga" },
  "from|field": { es: "de" },
  "generated through|field": { es: "generado hasta" },
  "gps verified|field": { es: "GPS verificado" },
  "hours|field": { es: "horas" },
  "is assigned site|field": { es: "es un sitio asignado" },
  "is completed|field": { es: "est\u00e1 completado" },
  "is preferred|field": { es: "es preferido" },
  "issue id|field": { es: "id de la incidencia" },
  "issue title|field": { es: "t\u00edtulo de la incidencia" },
  "issuing body|field": { es: "entidad emisora" },
  "item name|field": { es: "nombre del art\u00edculo" },
  "jotform submission id|field": { es: "id del env\u00edo de Jotform" },
  "kept|field": { es: "conservados" },
  "last date|field": { es: "\u00faltima fecha" },
  "leave type|field": { es: "tipo de ausencia" },
  "locale|field": { es: "idioma" },
  "lookup type|field": { es: "tipo de lista" },
  "max possible score|field": { es: "puntaje m\u00e1ximo posible" },
  "message text|field": { es: "texto del mensaje" },
  "name|field": { es: "nombre" },
  "new name|field": { es: "nombre nuevo" },
  "new role|field": { es: "rol nuevo" },
  "new status|field": { es: "estado nuevo" },
  "new user|field": { es: "persona nueva" },
  "note|field": { es: "nota" },
  "old role|field": { es: "rol anterior" },
  "old status|field": { es: "estado anterior" },
  "old user|field": { es: "persona anterior" },
  "origin|field": { es: "procedencia" },
  "ot warning|field": { es: "aviso de horas extra" },
  "part day|field": { es: "parte del d\u00eda" },
  "pct score|field": { es: "puntaje en porcentaje" },
  "phone|field": { es: "tel\u00e9fono" },
  "previous claimer|field": { es: "quien lo hab\u00eda tomado" },
  "processed|field": { es: "procesados" },
  "quantity|field": { es: "cantidad" },
  "rating|field": { es: "calificaci\u00f3n" },
  "reason|field": { es: "motivo" },
  "reassigned from|field": { es: "reasignado de" },
  "recipient name|field": { es: "nombre del destinatario" },
  "recipient user id|field": { es: "id del destinatario" },
  "removed|field": { es: "quitados" },
  "replaces pattern id|field": { es: "id del patr\u00f3n que reemplaza" },
  "request id|field": { es: "id de la solicitud" },
  "request type|field": { es: "tipo de solicitud" },
  "requested|field": { es: "solicitado" },
  "resolved|field": { es: "resueltos" },
  "resolved failures|field": { es: "fallos resueltos" },
  "result id|field": { es: "id del resultado" },
  "role|field": { es: "rol" },
  "role at site|field": { es: "rol en el sitio" },
  "scan method|field": { es: "m\u00e9todo de escaneo" },
  "scheduled date|field": { es: "fecha programada" },
  "scheduled inspection id|field": { es: "id de la inspecci\u00f3n programada" },
  "scheduled shift id|field": { es: "id del turno programado" },
  "score|field": { es: "puntaje" },
  "sent message id|field": { es: "id del mensaje enviado" },
  "service id|field": { es: "id del servicio" },
  "session date|field": { es: "fecha de la sesi\u00f3n" },
  "shift duration|field": { es: "duraci\u00f3n del turno" },
  "shift id|field": { es: "id del turno" },
  "shift label|field": { es: "nombre del turno" },
  "site id|field": { es: "id del sitio" },
  "site session id|field": { es: "id de la sesi\u00f3n en el sitio" },
  "skipped|field": { es: "omitidos" },
  "skipped deleted|field": { es: "omitidos por quitados" },
  "skipped wrong prefix|field": { es: "omitidos por prefijo incorrecto" },
  "slug|field": { es: "identificador" },
  "sort order|field": { es: "orden" },
  "source|field": { es: "fuente" },
  "source id|field": { es: "id de la fuente" },
  "source name|field": { es: "nombre de la fuente" },
  "split|field": { es: "dividido" },
  "start time|field": { es: "hora de inicio" },
  "starts on|field": { es: "empieza el" },
  "step category|field": { es: "categor\u00eda del paso" },
  "step name|field": { es: "nombre del paso" },
  "steps|field": { es: "pasos" },
  "submission id|field": { es: "id del env\u00edo" },
  "supply id|field": { es: "id del suministro" },
  "supply name|field": { es: "nombre del suministro" },
  "target role|field": { es: "rol de destino" },
  "target user id|field": { es: "id de la persona de destino" },
  "task id|field": { es: "id de la tarea" },
  "task label|field": { es: "nombre de la tarea" },
  "task type|field": { es: "tipo de tarea" },
  "template id|field": { es: "id de la plantilla" },
  "template name|field": { es: "nombre de la plantilla" },
  "to|field": { es: "a" },
  "total|field": { es: "total" },
  "total score|field": { es: "puntaje total" },
  "training name|field": { es: "nombre de la capacitaci\u00f3n" },
  "training type|field": { es: "tipo de capacitaci\u00f3n" },
  "unit|field": { es: "unidad" },
  "updated|field": { es: "actualizados" },
  "urgency|field": { es: "urgencia" },
  "user id|field": { es: "id de la persona" },
  "value|field": { es: "valor" },
  "vendor id|field": { es: "id del proveedor" },
  "weekly minutes|field": { es: "minutos semanales" },
  "Pre-Service Site Assessment": { es: "Evaluaci\u00f3n del sitio antes del servicio" },
  "Monthly Client Performance Report": { es: "Informe mensual de desempe\u00f1o para el cliente" },
  "Employee Performance Evaluation": { es: "Evaluaci\u00f3n del desempe\u00f1o del empleado" },
  "Call Intake and Communication Log": { es: "Registro de llamadas y comunicaciones" },
  "Change of Service Request": { es: "Solicitud de cambio de servicio" },
  "Site-Specific Orientation Checklist": { es: "Lista de orientaci\u00f3n espec\u00edfica del sitio" },
  "insert|stage": { es: "inserci\u00f3n" },
  "update|stage": { es: "actualizaci\u00f3n" },
  "person not active": { es: "persona no activa" },
  "{0} shift scheduled|count": {
    en: { one: "{0} shift scheduled", other: "{0} shifts scheduled" },
    es: { one: "{0} turno programado", other: "{0} turnos programados" },
  },
  "Send announcements to staff": { es: "Enviar anuncios al personal" },
  "Keep the injury log": { es: "Llevar el registro de lesiones" },
  "Only someone who keeps the injury log can change these answers.": { es: "Solo quien lleva el registro de lesiones puede cambiar estas respuestas." },
  "Your account cannot change these answers.": { es: "Su cuenta no puede cambiar estas respuestas." },
  "Safety records": { es: "Registros de seguridad" },
  "Injury log": { es: "Registro de lesiones" },
  "Every recordable injury and illness of a year, with its totals.": { es: "Cada lesi\u00f3n y enfermedad registrable de un a\u00f1o, con sus totales." },
  "Year": { es: "A\u00f1o" },
  "Job title": { es: "Puesto" },
  "Where|place": { es: "Lugar" },
  "Outcome": { es: "Resultado" },
  "Days away": { es: "D\u00edas de ausencia" },
  "Days restricted": { es: "D\u00edas restringidos" },
  "Privacy case": { es: "Caso de privacidad" },
  "No recordable cases this year.": { es: "No hay casos registrables este a\u00f1o." },
  "Totals for {0}": { es: "Totales de {0}" },
  "Death": { es: "Muerte" },
  "Days away from work": { es: "D\u00edas de ausencia del trabajo" },
  "Job transfer or restriction": { es: "Traslado de puesto o restricci\u00f3n" },
  "Other recordable case": { es: "Otro caso registrable" },
  "Injury": { es: "Lesi\u00f3n" },
  "Skin disorder": { es: "Trastorno de la piel" },
  "Respiratory condition": { es: "Afecci\u00f3n respiratoria" },
  "Poisoning": { es: "Envenenamiento" },
  "Hearing loss": { es: "P\u00e9rdida de audici\u00f3n" },
  "All other illnesses": { es: "Todas las dem\u00e1s enfermedades" },
  "Number of cases": { es: "N\u00famero de casos" },
  "Days of job transfer or restriction": { es: "D\u00edas con traslado de puesto o restricci\u00f3n" },
  "Injury and illness types": { es: "Tipos de lesiones y enfermedades" },
  "Deaths": { es: "Muertes" },
  "Cases with days away from work": { es: "Casos con d\u00edas de ausencia del trabajo" },
  "Cases with job transfer or restriction": { es: "Casos con traslado de puesto o restricci\u00f3n" },
  "Other recordable cases": { es: "Otros casos registrables" },
  "Total days away from work": { es: "Total de d\u00edas de ausencia del trabajo" },
  "Total days of job transfer or restriction": { es: "Total de d\u00edas con traslado de puesto o restricci\u00f3n" },
  "Injuries": { es: "Lesiones" },
  "Skin disorders": { es: "Trastornos de la piel" },
  "Respiratory conditions": { es: "Afecciones respiratorias" },
  "Poisonings": { es: "Envenenamientos" },
  "Annual summary": { es: "Resumen anual" },
  "The year's totals and their certification, posted from February 1 to April 30.": { es: "Los totales del a\u00f1o y su certificaci\u00f3n, que se publican del 1 de febrero al 30 de abril." },
  "Post the {0} summary from February 1 to April 30.": { es: "Publique el resumen de {0} del 1 de febrero al 30 de abril." },
  "Establishment": { es: "Establecimiento" },
  "Annual average number of employees": { es: "Promedio anual de empleados" },
  "Total hours worked by all employees last year": { es: "Total de horas trabajadas por todos los empleados el a\u00f1o pasado" },
  "To check against: {0} active staff accounts on average, counted on the first of each month.": { es: "Para comparar: {0} cuentas activas del personal en promedio, contadas el primer d\u00eda de cada mes." },
  "Certification": { es: "Certificaci\u00f3n" },
  "Certified by {0}, {1}, on {2}": { es: "Certificado por {0}, {1}, el {2}" },
  "Not certified yet.": { es: "Todav\u00eda no est\u00e1 certificado." },
  "The injury log changed after this summary was certified. It needs certifying again.": { es: "El registro de lesiones cambi\u00f3 despu\u00e9s de que se certific\u00f3 este resumen. Hay que certificarlo de nuevo." },
  "Company executive": { es: "Ejecutivo de la empresa" },
  "Title|job": { es: "Cargo" },
  "I certify that I have examined the injury log and that to the best of my knowledge this annual summary is correct and complete.": { es: "Certifico que he examinado el registro de lesiones y que, a mi leal saber y entender, este resumen anual es correcto y completo." },
  "Certify": { es: "Certificar" },
  "Certifying...": { es: "Certificando..." },
  "Kind": { es: "Tipo" },
  "Supervisor inspection": { es: "Inspecci\u00f3n de supervisor" },
  "Audit inspection": { es: "Inspecci\u00f3n de auditor\u00eda" },
  "Contract reference": { es: "Referencia del contrato" },
  "Service lines": { es: "L\u00edneas de servicio" },
  "Office|service line": { es: "Oficinas" },
  "Schools|service line": { es: "Escuelas" },
  "Laboratory|service line": { es: "Laboratorio" },
  "Industrial|service line": { es: "Industrial" },
  "Disinfection|service line": { es: "Desinfecci\u00f3n" },
  "Post-construction|service line": { es: "Despu\u00e9s de obra" },
  "Day porter|service line": { es: "Conserje de d\u00eda" },
  "Landscaping|service line": { es: "Jardiner\u00eda" },
  "This question is still being answered. The answer will show here.": { es: "Esta pregunta todav\u00eda se est\u00e1 respondiendo. La respuesta aparecer\u00e1 aqu\u00ed." },
  "Which site is this for?": { es: "\u00bfPara qu\u00e9 sitio es este reporte?" },
  "No particular site": { es: "Ning\u00fan sitio en particular" },
  "No private chat yet": { es: "Todav\u00eda no hay chat privado" },
  "No private chat with {0} yet. It starts the first time they open their messages in the staff app.": { es: "Todav\u00eda no hay un chat privado con {0}. Empieza la primera vez que abra sus mensajes en la aplicaci\u00f3n del personal." },
  "Quotes": { es: "Cotizaciones" },
  "Build quotes": { es: "Preparar cotizaciones" },
  "New quote": { es: "Nueva cotizaci\u00f3n" },
  "Every quote, newest first. A row opens the quote.": { es: "Todas las cotizaciones, de la m\u00e1s reciente a la m\u00e1s antigua. Una fila abre la cotizaci\u00f3n." },
  "Search number, client, site": { es: "Buscar n\u00famero, cliente o sitio" },
  "Number": { es: "N\u00famero" },
  "Monthly price": { es: "Precio mensual" },
  "Valid until|quote": { es: "V\u00e1lida hasta" },
  "Sent|quote": { es: "Enviada" },
  "Draft|quote": { es: "Borrador" },
  "Accepted|quote": { es: "Aceptada" },
  "Declined|quote": { es: "Rechazada" },
  "Void|quote": { es: "Anulada" },
  "Revision {0}": { es: "Revisi\u00f3n {0}" },
  "No quotes match.": { es: "Ninguna cotizaci\u00f3n coincide." },
  "No quotes yet. New quote starts one.": { es: "Todav\u00eda no hay cotizaciones. Nueva cotizaci\u00f3n empieza una." },
  "Back to quotes": { es: "Volver a las cotizaciones" },
  "sq ft": { es: "pies cuadrados" },
  "days|unit": { es: "d\u00edas" },
  "hours|unit": { es: "horas" },
  "months|unit": { es: "meses" },
  "{0} per hour": { es: "{0} por hora" },
  "{0} per sq ft": { es: "{0} por pie cuadrado" },
  "per hour": { es: "por hora" },
  "per sq ft": { es: "por pie cuadrado" },
  "Follows the service days": { es: "Sigue los d\u00edas de servicio" },
  "Follows the school days": { es: "Sigue los d\u00edas de clases" },
  "Half the service days": { es: "La mitad de los d\u00edas de servicio" },
  "Frequencies": { es: "Frecuencias" },
  "Frequency": { es: "Frecuencia" },
  "Times per month": { es: "Veces al mes" },
  "Times per month follows the frequency until you type a count over it. Clear the count to follow the frequency again.": { es: "Las veces al mes siguen la frecuencia hasta que escriba una cantidad encima. Borre la cantidad para volver a seguir la frecuencia." },
  "Rate per hour": { es: "Tasa por hora" },
  "Hours each time": { es: "Horas cada vez" },
  "Hours per month": { es: "Horas al mes" },
  "No rows.": { es: "No hay filas." },
  "Figures": { es: "Cifras" },
  "Working out the figures...": { es: "Calculando las cifras..." },
  "These figures are from before the last change.": { es: "Estas cifras son de antes del \u00faltimo cambio." },
  "Checks": { es: "Verificaciones" },
  "A check that is not met is a warning. It never stops a save or a send.": { es: "Una verificaci\u00f3n que no se cumple es una advertencia. Nunca impide guardar ni enviar." },
  "Changes not saved yet": { es: "Cambios todav\u00eda sin guardar" },
  "Quote saved.": { es: "Cotizaci\u00f3n guardada." },
  "Leave without saving your changes?": { es: "\u00bfSalir sin guardar los cambios?" },
  "Keep editing": { es: "Seguir editando" },
  "Client and site": { es: "Cliente y sitio" },
  "Site on file": { es: "Sitio registrado" },
  "No site on file": { es: "Ning\u00fan sitio registrado" },
  "Site address": { es: "Direcci\u00f3n del sitio" },
  "Left empty, the client, the site name and the address are filled from the site on file when you save.": { es: "Si se dejan vac\u00edos, el cliente, el nombre del sitio y la direcci\u00f3n se llenan con el sitio registrado al guardar." },
  "Step {0} of {1}": { es: "Paso {0} de {1}" },
  "Preview client PDF": { es: "Ver el PDF del cliente" },
  "Internal worksheet": { es: "Hoja de trabajo interna" },
  "Client PDF": { es: "PDF del cliente" },
  "Every input, task line, figure and check, with the wages, burden and margin. It stays inside the company.": { es: "Cada dato, l\u00ednea de tarea, cifra y verificaci\u00f3n, con los salarios, las cargas y el margen. Se queda dentro de la empresa." },
  "What the client receives: the scope, how often each task is done, the price and the terms. It shows no hour, rate, cost or margin.": { es: "Lo que recibe el cliente: el alcance, con qu\u00e9 frecuencia se hace cada tarea, el precio y los t\u00e9rminos. No muestra horas, tasas, costos ni margen." },
  "The PDF shows the quote as last saved.": { es: "El PDF muestra la cotizaci\u00f3n como se guard\u00f3 por \u00faltima vez." },
  "The mail carries the client's PDF only: the scope, how often each task is done, and the price. It is written in English and then Spanish, with your message above both.": { es: "El correo lleva solo el PDF del cliente: el alcance, con qu\u00e9 frecuencia se hace cada tarea y el precio. Est\u00e1 escrito en ingl\u00e9s y luego en espa\u00f1ol, con su mensaje arriba de ambos." },
  "Language of the PDF": { es: "Idioma del PDF" },
  "Mailed to {0} of {1}.": { es: "Se envi\u00f3 a {0} de {1}." },
  "The quote changed while it was mailing, so it was not marked sent.": { es: "La cotizaci\u00f3n cambi\u00f3 mientras se enviaba, as\u00ed que no se marc\u00f3 como enviada." },
  "Mark accepted": { es: "Marcar como aceptada" },
  "Mark declined": { es: "Marcar como rechazada" },
  "Mark this quote accepted? Once it is, the quote reads only.": { es: "\u00bfMarcar esta cotizaci\u00f3n como aceptada? Despu\u00e9s solo se puede leer." },
  "Mark this quote declined? Once it is, the quote reads only.": { es: "\u00bfMarcar esta cotizaci\u00f3n como rechazada? Despu\u00e9s solo se puede leer." },
  "Void this quote? Once it is, the quote reads only.": { es: "\u00bfAnular esta cotizaci\u00f3n? Despu\u00e9s solo se puede leer." },
  "Quote marked {0}.": { es: "Cotizaci\u00f3n marcada como {0}." },
  "This quote is {0}, so it reads only.": { es: "Esta cotizaci\u00f3n est\u00e1 {0}, as\u00ed que solo se puede leer." },
  "Save your changes before sending or closing the quote.": { es: "Guarde los cambios antes de enviar o cerrar la cotizaci\u00f3n." },
  "Sent on {0} to {1}.": { es: "Se envi\u00f3 el {0} a {1}." },
  "Saving a change makes this quote a draft again, as revision {0}.": { es: "Al guardar un cambio, esta cotizaci\u00f3n vuelve a ser borrador, como la revisi\u00f3n {0}." },
  "Quote defaults": { es: "Valores iniciales de las cotizaciones" },
  "The starting values every new quote takes. A box left empty takes the model's own value, shown greyed. A quote already made keeps its own values.": { es: "Los valores con los que empieza cada cotizaci\u00f3n nueva. Una casilla vac\u00eda toma el valor propio del modelo, que se ve en gris. Una cotizaci\u00f3n ya hecha conserva sus valores." },
  "Last saved by {0} on {1}.": { es: "Guardado por \u00faltima vez por {0} el {1}." },
  "Last saved on {0}.": { es: "Guardado por \u00faltima vez el {0}." },
  "Days a quote stays valid": { es: "D\u00edas que una cotizaci\u00f3n es v\u00e1lida" },
  "Terms in English": { es: "T\u00e9rminos en ingl\u00e9s" },
  "Terms in Spanish": { es: "T\u00e9rminos en espa\u00f1ol" },
  "The terms print on the client's PDF. A Spanish PDF prints the English terms while no Spanish is saved.": { es: "Los t\u00e9rminos se imprimen en el PDF del cliente. Un PDF en espa\u00f1ol imprime los t\u00e9rminos en ingl\u00e9s mientras no haya t\u00e9rminos en espa\u00f1ol guardados." },
  "Quote defaults saved.": { es: "Valores iniciales de las cotizaciones guardados." },
  "{0} address could not be reached.|count": {
    en: { one: "{0} address could not be reached.", other: "{0} addresses could not be reached." },
    es: { one: "No se pudo entregar a {0} direcci\u00f3n.", other: "No se pudo entregar a {0} direcciones." },
  },
  "New message": { es: "Nuevo mensaje" },
  "Picking someone in the office opens your direct chat with them. Picking a staff member opens their private chat with the office.": { es: "Al elegir a alguien de la oficina se abre su chat directo con esa persona. Al elegir a alguien del personal se abre su chat privado con la oficina." },
  "Search by name or badge": { es: "Buscar por nombre o n\u00famero de empleado" },
  "Office|people": { es: "Oficina" },
  "Search chats and people": { es: "Buscar chats y personas" },
  "People": { es: "Personas" },
  "Message {0}": { es: "Escribir a {0}" },
  "Direct message": { es: "Mensaje directo" },
  "Direct messages": { es: "Mensajes directos" },
  "No direct messages yet. New message starts one.": { es: "Todav\u00eda no hay mensajes directos. Nuevo mensaje empieza uno." },
  "Act 34 PA State Police check": { es: "Verificaci\u00f3n de la Polic\u00eda Estatal (Act 34)" },
  "Act 151 child abuse clearance": { es: "Verificaci\u00f3n de antecedentes de abuso infantil (Act 151)" },
  "FBI fingerprint check": { es: "Verificaci\u00f3n de huellas del FBI" },
  "Act 168 employment history review": { es: "Revisi\u00f3n del historial laboral (Act 168)" },
  "Current|clearance": { es: "Vigente" },
  "Expiring|clearance": { es: "Por vencer" },
  "Expired|clearance": { es: "Vencida" },
  "Missing|clearance": { es: "Falta" },
  "Done|review": { es: "Hecha" },
  "Clearances": { es: "Autorizaciones escolares" },
  "Needed to work at a school site. Each is renewed every 60 months from its own date.": { es: "Necesarias para trabajar en un sitio escolar. Cada una se renueva cada 60 meses a partir de su propia fecha." },
  "Issued {0}, expires {1}": { es: "Emitida el {0}, vence el {1}" },
  "None on file": { es: "Ninguna registrada" },
  "Add or renew": { es: "Agregar o renovar" },
  "Correct a date": { es: "Corregir una fecha" },
  "History ({0})": { es: "Historial ({0})" },
  "Corrected on {0} from {1} to {2}: {3}": { es: "Corregida el {0} de {1} a {2}: {3}" },
  "Completed on {0}. Disclosure: {1}.": { es: "Hecha el {0}. Divulgaci\u00f3n: {1}." },
  "Record the review": { es: "Registrar la revisi\u00f3n" },
  "For a date entered wrongly. The clearance keeps its place, its expiry is worked out again from the right date, and the correction is recorded with your reason.": { es: "Para una fecha anotada por error. La autorizaci\u00f3n conserva su lugar, su vencimiento se calcula de nuevo con la fecha correcta y la correcci\u00f3n queda registrada con su motivo." },
  "The employment history review the school can ask to see.": { es: "La revisi\u00f3n del historial laboral que la escuela puede pedir ver." },
  "A clearance is renewed every 60 months from the date on it. The one there now stays in the history.": { es: "Una autorizaci\u00f3n se renueva cada 60 meses a partir de la fecha que tiene. La que hay ahora queda en el historial." },
  "Entered as {0}.": { es: "Anotada como {0}." },
  "The right date": { es: "La fecha correcta" },
  "Completed on": { es: "Hecha el" },
  "Issued on": { es: "Emitida el" },
  "Anything disclosed?": { es: "\u00bfSe divulg\u00f3 algo?" },
  "No document": { es: "Sin documento" },
  "From this person's HR documents. Optional.": { es: "De los documentos de recursos humanos de esta persona. Opcional." },
  "Every active person": { es: "Todas las personas activas" },
  "Everyone, inactive and terminated included": { es: "Todos, incluidas las personas inactivas y dadas de baja" },
  "Everyone's school clearances, the soonest expiry first. A row opens the person's record.": { es: "Las autorizaciones escolares de todos, primero las que vencen antes. Una fila abre el expediente de la persona." },
  "School site": { es: "Sitio escolar" },
  "Pick a site to export it for the school.": { es: "Elija un sitio para exportarlo para la escuela." },
  "Export for a school": { es: "Exportar para una escuela" },
  "Act 34": { es: "Act 34" },
  "Act 151": { es: "Act 151" },
  "FBI": { es: "FBI" },
  "Act 168": { es: "Act 168" },
  "Clearances missing": { es: "Faltan autorizaciones escolares" },
  "{0} cannot be placed at this school site.": { es: "No se puede asignar a {0} a este sitio escolar." },
  "This person cannot be placed at this school site.": { es: "No se puede asignar a esta persona a este sitio escolar." },
  "Missing or out of date:": { es: "Faltan o est\u00e1n vencidas:" },
  "Nobody works at a school site without current clearances. If a date was entered wrongly, correct it on their Clearances, then try again.": { es: "Nadie trabaja en un sitio escolar sin autorizaciones vigentes. Si una fecha se anot\u00f3 por error, corr\u00edjala en sus autorizaciones escolares y vuelva a intentarlo." },
  "Open the clearances of {0}": { es: "Abrir las autorizaciones de {0}" },
  "Open their clearances": { es: "Abrir sus autorizaciones" },
  "Industry description": { es: "Descripci\u00f3n de la industria" },
  "NAICS code": { es: "C\u00f3digo NAICS" },
  "Six digits": { es: "Seis d\u00edgitos" },
  "Executive name": { es: "Nombre del ejecutivo" },
  "Executive title": { es: "Cargo del ejecutivo" },
  "Executive phone": { es: "Tel\u00e9fono del ejecutivo" },
  "OSHA 300A": { es: "OSHA 300A" },
  "The annual summary of injuries prints these. A blank one stays a line to fill in by hand.": { es: "El resumen anual de lesiones imprime estos datos. Uno en blanco queda como una l\u00ednea para llenar a mano." },
  "Download for OSHA": { es: "Descargar para OSHA" },
  "Posting": { es: "Publicaci\u00f3n" },
  "The day the summary went up where employees can read it. It stays up from February 1 to April 30.": { es: "El d\u00eda en que el resumen se coloc\u00f3 donde el personal puede leerlo. Se queda colocado del 1 de febrero al 30 de abril." },
  "Posted on": { es: "Publicado el" },
  "The posting date did not save. Try again.": { es: "La fecha de publicaci\u00f3n no se guard\u00f3. Intente de nuevo." },
  "Download for OSHA saves the summary as the file OSHA's Injury Tracking Application takes.": { es: "Descargar para OSHA guarda el resumen como el archivo que acepta la aplicaci\u00f3n de seguimiento de lesiones de OSHA." },
  "Workload plan": { es: "Plan de carga de trabajo" },
  "Hours a month": { es: "Horas al mes" },
  "Hours a service day": { es: "Horas por d\u00eda de servicio" },
  "Staff recommended": { es: "Personal recomendado" },
  "Staff minimum": { es: "Personal m\u00ednimo" },
  "Cleaners assigned": { es: "Limpiadores asignados" },
  "No workload plan yet.": { es: "Todav\u00eda no hay plan de carga de trabajo." },
  "Print the plan": { es: "Imprimir el plan" },
  "The hours this building needs, task by task, and the staff that follows, from the quote used as its plan. It shows no price.": { es: "Las horas que necesita este edificio, tarea por tarea, y el personal que resulta, de la cotizaci\u00f3n usada como su plan. No muestra ning\u00fan precio." },
  "The quote changed after this plan took it. Use the quote again to update the plan.": { es: "La cotizaci\u00f3n cambi\u00f3 despu\u00e9s de que este plan la tom\u00f3. Vuelva a usar la cotizaci\u00f3n para actualizar el plan." },
  "From quote {0}, revision {1}, taken on {2} by {3}.": { es: "De la cotizaci\u00f3n {0}, revisi\u00f3n {1}, tomado el {2} por {3}." },
  "From quote {0}, revision {1}, taken on {2}.": { es: "De la cotizaci\u00f3n {0}, revisi\u00f3n {1}, tomado el {2}." },
  "The plan already has this quote as it was saved. Nothing changed.": { es: "El plan ya tiene esta cotizaci\u00f3n tal como se guard\u00f3. No cambi\u00f3 nada." },
  "Open the quote": { es: "Abrir la cotizaci\u00f3n" },
  "Update the plan": { es: "Actualizar el plan" },
  "End the plan": { es: "Terminar el plan" },
  "End this site's workload plan? It moves to the earlier plans, and the site has no plan until a quote is used again.": { es: "\u00bfTerminar el plan de carga de trabajo de este sitio? Pasa a los planes anteriores, y el sitio no tiene plan hasta que se vuelva a usar una cotizaci\u00f3n." },
  "Make a workload plan": { es: "Hacer un plan de carga de trabajo" },
  "Use a saved quote": { es: "Usar una cotizaci\u00f3n guardada" },
  "This site's saved quotes. Declined and void quotes are left out.": { es: "Las cotizaciones guardadas de este sitio. Se omiten las rechazadas y las anuladas." },
  "Optional. It shows with the plan.": { es: "Opcional. Se muestra con el plan." },
  "No saved quote names this site yet.": { es: "Todav\u00eda ninguna cotizaci\u00f3n guardada nombra este sitio." },
  "Use this quote": { es: "Usar esta cotizaci\u00f3n" },
  "Typed by hand": { es: "Escrito a mano" },
  "Earlier plans": { es: "Planes anteriores" },
  "Quote {0}, revision {1}": { es: "Cotizaci\u00f3n {0}, revisi\u00f3n {1}" },
  "Taken on {0} by {1}": { es: "Tomado el {0} por {1}" },
  "Taken on {0}": { es: "Tomado el {0}" },
  "Ended on {0} by {1}": { es: "Terminado el {0} por {1}" },
  "Ended on {0}": { es: "Terminado el {0}" },
  "Replaced by a newer plan": { es: "Reemplazado por un plan m\u00e1s reciente" },
  "Reason: {0}": { es: "Motivo: {0}" },
  "Workload plan saved.": { es: "Plan de carga de trabajo guardado." },
  "Workload plan ended.": { es: "Plan de carga de trabajo terminado." },
  "The plan as the site reads it: the hours, the staffing and the task table. It shows no price.": { es: "El plan como lo lee el sitio: las horas, el personal y la tabla de tareas. No muestra ning\u00fan precio." },
  "Use as this site's workload plan": { es: "Usar como plan de carga de trabajo de este sitio" },
  "Save first": { es: "Guarde primero" },
  "Workload plan for {0}": { es: "Plan de carga de trabajo de {0}" },
  "Was the workload plan for {0}": { es: "Fue el plan de carga de trabajo de {0}" },
  "Changed since": { es: "Cambi\u00f3 desde entonces" },
  "Saving does not change the site's plan. Use the quote again to update it.": { es: "Guardar no cambia el plan del sitio. Vuelva a usar la cotizaci\u00f3n para actualizarlo." },
  "Makes this quote the site's workload plan: its hours, staffing and tasks, with no price. A save after that does not change the plan.": { es: "Hace de esta cotizaci\u00f3n el plan de carga de trabajo del sitio: sus horas, su personal y sus tareas, sin precio. Un cambio guardado despu\u00e9s no cambia el plan." },
  "Workload plans": { es: "Planes de carga de trabajo" },
  "Plan": { es: "Plan" },
  "No plan yet": { es: "Todav\u00eda sin plan" },
  "Which active sites have a workload plan, with its hours and staffing. A row opens the site's plan.": { es: "Qu\u00e9 sitios activos tienen un plan de carga de trabajo, con sus horas y su personal. Una fila abre el plan del sitio." },
  "Photos of the whole inspection": { es: "Fotos de toda la inspecci\u00f3n" },
  "Signed by {0}, {1}": { es: "Firmado por {0}, {1}" },
  "No rows yet. Add a row to price it.": { es: "Todav\u00eda no hay filas. Agregue una para cotizarla." },
  "The equipment, supplies and other direct costs lists start every new quote with the rows set here, or with none.": { es: "Las listas de equipo, suministros y otros costos directos empiezan cada cotizaci\u00f3n nueva con las filas que se fijen aqu\u00ed, o sin ninguna." },
  "End employment": { es: "Terminar el empleo" },
  "Put on leave": { es: "Poner en licencia" },
  "Return from leave": { es: "Regresar de la licencia" },
  "Rehire": { es: "Recontratar" },
  "On leave": { es: "En licencia" },
  "No reason recorded": { es: "No hay motivo registrado" },
  "Record the reason": { es: "Registrar el motivo" },
  "Eligible for rehire?": { es: "\u00bfElegible para recontrataci\u00f3n?" },
  "Eligible for rehire": { es: "Elegible para recontrataci\u00f3n" },
  "Not eligible for rehire": { es: "No elegible para recontrataci\u00f3n" },
  "Marked not eligible for rehire when they left.": { es: "Se marc\u00f3 como no elegible para recontrataci\u00f3n al salir." },
  "Employment ended": { es: "Fin del empleo" },
  "Returned from leave": { es: "Regreso de la licencia" },
  "Rehired": { es: "Recontrataci\u00f3n" },
  "Status changed": { es: "Cambio de estado" },
  "Confirmed still working": { es: "Confirmaci\u00f3n de que sigue trabajando" },
  "Employment updated.": { es: "Empleo actualizado." },
  "Reason recorded.": { es: "Motivo registrado." },
  "End the employment of {0}? Every site they are assigned to ends with it.": { es: "\u00bfTerminar el empleo de {0}? Tambi\u00e9n terminan todas sus asignaciones a sitios." },
  "Put {0} on leave? They cannot sign in while on leave, and they keep their sites.": { es: "\u00bfPoner a {0} en licencia? No podr\u00e1 iniciar sesi\u00f3n mientras est\u00e9 en licencia y conserva sus sitios." },
  "Bring {0} back from leave? They can sign in again.": { es: "\u00bfRegresar a {0} de la licencia? Podr\u00e1 iniciar sesi\u00f3n de nuevo." },
  "Rehire {0}? They can sign in again, and their history stays.": { es: "\u00bfRecontratar a {0}? Podr\u00e1 iniciar sesi\u00f3n de nuevo y su historial se conserva." },
  "This records the reason {0} left. Their status and sites do not change.": { es: "Esto registra el motivo por el que {0} se fue. Su estado y sus sitios no cambian." },
  "This records the reason {0} is on leave. Their status and sites do not change.": { es: "Esto registra el motivo por el que {0} est\u00e1 en licencia. Su estado y sus sitios no cambian." },
  "Record the termination letter in HR Records, under Disciplinary.": { es: "Registre la carta de despido en Expedientes de personal, en Disciplinario." },
  "Open in HR Records": { es: "Abrir en Expedientes de personal" },
  "Open in Staff Management": { es: "Abrir en Gesti\u00f3n de personal" },
  "Choose a reason": { es: "Elija un motivo" },
  "Choose": { es: "Elija" },
  "Expected return": { es: "Regreso previsto" },
  "Expected back {0}": { es: "Regreso previsto: {0}" },
  "Last day {0}": { es: "\u00daltimo d\u00eda: {0}" },
  "New hire date": { es: "Nueva fecha de contrataci\u00f3n" },
  "Optional.": { es: "Opcional." },
  "Required for Other.": { es: "Obligatorio para Otro." },
  "Recorded by {0} on {1}": { es: "Registrado por {0} el {1}" },
  "Recorded on {0}": { es: "Registrado el {0}" },
  "this person": { es: "esta persona" },
  "Roster check": { es: "Revisi\u00f3n del personal" },
  "Still working": { es: "Sigue trabajando" },
  "Go through everyone: confirm who is still working and where, then put their records right.": { es: "Revise a todos: confirme qui\u00e9n sigue trabajando y d\u00f3nde, y luego corrija sus registros." },
  "{0} of {1} confirmed in the last 30 days": { es: "{0} de {1} confirmados en los \u00faltimos 30 d\u00edas" },
  "Not confirmed yet": { es: "Sin confirmar todav\u00eda" },
  "Show ended": { es: "Mostrar empleos terminados" },
  "Last shift": { es: "\u00daltimo turno" },
  "Last signed in": { es: "\u00daltimo inicio de sesi\u00f3n" },
  "Last confirmed": { es: "\u00daltima confirmaci\u00f3n" },
  "Confirmed by": { es: "Confirmado por" },
  "Awaiting review": { es: "Pendiente de revisi\u00f3n" },
  "Completed inspections with a review line still to sign. A row opens the inspection.": { es: "Inspecciones completadas con una l\u00ednea de revisi\u00f3n sin firmar. Una fila abre la inspecci\u00f3n." },
  "Nothing is waiting for review.": { es: "No hay nada pendiente de revisi\u00f3n." },
  "Review|inspection": { es: "Revisi\u00f3n" },
  "Waiting for {0}": { es: "Pendiente: {0}" },
  "Issue a warning": { es: "Emitir una advertencia" },
  "Verbal warning": { es: "Advertencia verbal" },
  "Written warning": { es: "Advertencia por escrito" },
  "Final written warning": { es: "Advertencia final por escrito" },
  "Termination": { es: "Despido" },
  "Declined to sign": { es: "Se neg\u00f3 a firmar" },
  "Discipline": { es: "Disciplina" },
  "Open a case": { es: "Abrir un caso" },
  "Disciplinary": { es: "Disciplinario" },
  "Original form": { es: "Formulario original" },
  "Add to the record": { es: "Agregar al registro" },
  "Added to the record.": { es: "Agregada al registro." },
  "Filed on the old form, not on the record yet": { es: "Archivadas en el formulario anterior, a\u00fan sin registrar" },
  "A warning already given on the old form, entered on the record as it was, with no signature or delivery.": { es: "Una advertencia ya dada en el formulario anterior, registrada tal como fue, sin firma ni entrega." },
  "Date given": { es: "Fecha en que se dio" },
  "Step": { es: "Paso" },
  "{0} may begin at any step.": { es: "{0} puede comenzar en cualquier paso." },
  "admins only": { es: "solo administradores" },
  "A verbal warning is a note of the conversation, placed on the record. It takes no signatures.": { es: "Una advertencia verbal es una nota de la conversaci\u00f3n que queda en el registro. No lleva firmas." },
  "Counted over the last 12 months. The handbook sets no lookback.": { es: "Contadas en los \u00faltimos 12 meses. El manual no fija un per\u00edodo de revisi\u00f3n." },
  "Date of the incident": { es: "Fecha del incidente" },
  "What happened": { es: "Qu\u00e9 pas\u00f3" },
  "Policy": { es: "Pol\u00edtica" },
  "Optional, for example a handbook section": { es: "Opcional, por ejemplo una secci\u00f3n del manual" },
  "What must change": { es: "Qu\u00e9 debe cambiar" },
  "By when": { es: "Para cu\u00e1ndo" },
  "Suspension from": { es: "Suspensi\u00f3n desde" },
  "Suspension to": { es: "Suspensi\u00f3n hasta" },
  "Does this follow a complaint or other protected activity by this person?": { es: "\u00bfEsto ocurre despu\u00e9s de una queja u otra actividad protegida de esta persona?" },
  "Discussed with the Controller on": { es: "Consultado con el Contralor el" },
  "Leave out any medical detail.": { es: "No incluya ning\u00fan detalle m\u00e9dico." },
  "If this person is covered by a union agreement, follow that agreement's procedure.": { es: "Si esta persona est\u00e1 cubierta por un convenio sindical, siga el procedimiento de ese convenio." },
  "Language of the warning": { es: "Idioma de la advertencia" },
  "Save as draft": { es: "Guardar como borrador" },
  "Draft saved.": { es: "Borrador guardado." },
  "The issuer signs": { es: "Firma quien la emite" },
  "{0} signs": { es: "Firma {0}" },
  "The person": { es: "La persona" },
  "Signing confirms they received this warning. It does not mean they agree.": { es: "Al firmar confirma que recibi\u00f3 esta advertencia. No significa que est\u00e9 de acuerdo." },
  "Signed": { es: "Firmada" },
  "Sign again": { es: "Firmar de nuevo" },
  "Witness": { es: "Testigo" },
  "Their account": { es: "Su versi\u00f3n" },
  "Optional. What the person says, recorded on the warning.": { es: "Opcional. Lo que dice la persona, registrado en la advertencia." },
  "Warning issued.": { es: "Advertencia emitida." },
  "Preview PDF": { es: "Vista previa del PDF" },
  "The warning as the person receives it, on the company's letterhead.": { es: "La advertencia tal como la recibe la persona, con el membrete de la empresa." },
  "Handed over in person": { es: "Entregada en persona" },
  "Printed": { es: "Impresa" },
  "Mailed": { es: "Enviada por correo postal" },
  "To their email on file, {0}, and any office address added.": { es: "A su correo registrado, {0}, y a cualquier direcci\u00f3n de la oficina que se agregue." },
  "To their email on file, and any office address added.": { es: "A su correo registrado y a cualquier direcci\u00f3n de la oficina que se agregue." },
  "Sent by email.": { es: "Enviada por correo electr\u00f3nico." },
  "Recorded as {0}.": { es: "Registrada como {0}." },
  "Delivered": { es: "Entrega" },
  "Rescind": { es: "Anular" },
  "Why it is rescinded": { es: "Por qu\u00e9 se anula" },
  "The warning stays on the record, marked rescinded.": { es: "La advertencia queda en el registro, marcada como anulada." },
  "Warning rescinded.": { es: "Advertencia anulada." },
  "Rescinded": { es: "Anulada" },
  "Rescinded: {0}": { es: "Anulada: {0}" },
  "Draft|warning": { es: "Borrador" },
  "Issued|warning": { es: "Emitida" },
  "Closed|warning": { es: "Cerrada" },
  "No warnings on record.": { es: "No hay advertencias registradas." },
  "Every warning on record, newest first. A row opens the warning.": { es: "Todas las advertencias registradas, las m\u00e1s recientes primero. Una fila abre la advertencia." },
  "The warnings you issued, newest first. A row opens the warning.": { es: "Las advertencias que usted emiti\u00f3, las m\u00e1s recientes primero. Una fila abre la advertencia." },
  "Every step": { es: "Todos los pasos" },
  "Every category": { es: "Todas las categor\u00edas" },
  "Issued by": { es: "Emitida por" },
  "Issued by anyone": { es: "Emitida por cualquiera" },
  "From": { es: "Desde" },
  "To": { es: "Hasta" },
  "No warnings match.": { es: "Ninguna advertencia coincide." },
  "About whom": { es: "Sobre qui\u00e9n" },
  "Optional. Choose a person": { es: "Opcional. Elija una persona" },
  "On behalf of": { es: "En nombre de" },
  "Optional. A staff member who reported it in person": { es: "Opcional. Un empleado que lo inform\u00f3 en persona" },
  "Is this about someone in management?": { es: "\u00bfSe trata de alguien de la gerencia?" },
  "Assigned to": { es: "Asignado a" },
  "Case opened.": { es: "Caso abierto." },
  "Enter the code we emailed to {0}": { es: "Escriba el c\u00f3digo que enviamos a {0}" },
  "Enter the code we emailed to you.": { es: "Escriba el c\u00f3digo que le enviamos por correo." },
  "Verify": { es: "Verificar" },
  "Checking...": { es: "Verificando..." },
  "Send a new code": { es: "Enviar un c\u00f3digo nuevo" },
  "We sent a new code.": { es: "Enviamos un c\u00f3digo nuevo." },
  "Remember this device for 30 days": { es: "Recordar este dispositivo por 30 d\u00edas" },
  "Trusted devices": { es: "Dispositivos de confianza" },
  "A device you entered a sign-in code on is remembered for 30 days. Any other device asks for a code emailed to you.": { es: "Un dispositivo en el que escribi\u00f3 un c\u00f3digo de inicio de sesi\u00f3n se recuerda por 30 d\u00edas. Cualquier otro dispositivo pide un c\u00f3digo enviado a su correo." },
  "Browser": { es: "Navegador" },
  "First seen": { es: "Visto por primera vez" },
  "Last seen": { es: "Visto por \u00faltima vez" },
  "Remembered until": { es: "Recordado hasta" },
  "{0} on {1}": { es: "{0} en {1}" },
  "Unknown browser": { es: "Navegador desconocido" },
  "No devices are remembered.": { es: "No hay dispositivos recordados." },
  "Forget all my devices": { es: "Olvidar todos mis dispositivos" },
  "Forget all your devices? Each one asks for a code at its next sign-in.": { es: "\u00bfOlvidar todos sus dispositivos? Cada uno pedir\u00e1 un c\u00f3digo la pr\u00f3xima vez que inicie sesi\u00f3n." },
  "Forget this person's devices": { es: "Olvidar los dispositivos de esta persona" },
  "Forget this person's devices? Each one asks for a code at its next sign-in.": { es: "\u00bfOlvidar los dispositivos de esta persona? Cada uno pedir\u00e1 un c\u00f3digo la pr\u00f3xima vez que inicie sesi\u00f3n." },
  "Devices forgotten.": { es: "Dispositivos olvidados." },
  "{0} clearances missing|count": {
    en: { one: "{0} clearance missing", other: "{0} clearances missing" },
    es: { one: "Falta {0} autorizaci\u00f3n escolar", other: "Faltan {0} autorizaciones escolares" },
  },
  "{0} tries left|count": {
    en: { one: "{0} try left", other: "{0} tries left" },
    es: { one: "Le queda {0} intento", other: "Le quedan {0} intentos" },
  },
  "Send a new code in {0} seconds|count": {
    en: { one: "Send a new code in {0} second", other: "Send a new code in {0} seconds" },
    es: { one: "Enviar un c\u00f3digo nuevo en {0} segundo", other: "Enviar un c\u00f3digo nuevo en {0} segundos" },
  },
  "Issue|warning": { es: "Emitir" },
  "Workspace": { es: "Espacio de trabajo" },
  "Message Board": { es: "Tablero de mensajes" },
  "To-dos": { es: "Pendientes" },
  "Docs and Files": { es: "Documentos y archivos" },
  "My assignments": { es: "Mis asignaciones" },
  "New project": { es: "Nuevo proyecto" },
  "Email me copies": { es: "Enviarme copias por correo" },
  "Chat records": { es: "Registros del chat" },
  "Every search here is recorded.": { es: "Cada b\u00fasqueda aqu\u00ed queda registrada." },
  "Each project keeps its posts, to-dos, chat and files in one place.": { es: "Cada proyecto guarda sus publicaciones, pendientes, chat y archivos en un solo lugar." },
  "Projects": { es: "Proyectos" },
  "Archived|projects": { es: "Archivados" },
  "Archived|post": { es: "Archivada" },
  "Archived projects": { es: "Proyectos archivados" },
  "Back to Workspace": { es: "Volver al espacio de trabajo" },
  "Back to {0}": { es: "Volver a {0}" },
  "No projects yet. Start one with New project.": { es: "Todav\u00eda no hay proyectos. Empiece uno con Nuevo proyecto." },
  "No archived projects.": { es: "No hay proyectos archivados." },
  "Last activity {0}": { es: "\u00daltima actividad: {0}" },
  "Edit project": { es: "Editar el proyecto" },
  "Blue": { es: "Azul" },
  "Orange": { es: "Naranja" },
  "Red": { es: "Rojo" },
  "Purple": { es: "Morado" },
  "Teal": { es: "Verde azulado" },
  "Gold": { es: "Dorado" },
  "Gray": { es: "Gris" },
  "Members": { es: "Miembros" },
  "You are the owner of a project you start. Add the office people who work on it.": { es: "Usted es el propietario del proyecto que inicia. Agregue a las personas de la oficina que trabajan en \u00e9l." },
  "Search people": { es: "Buscar personas" },
  "you": { es: "usted" },
  "Owner": { es: "Propietario" },
  "Start the project": { es: "Iniciar el proyecto" },
  "Members read and write everything in the project, and its chat follows them. Owners also rename it, change its members and archive it.": { es: "Los miembros leen y escriben todo en el proyecto, y su chat los sigue. Los propietarios adem\u00e1s le cambian el nombre, cambian sus miembros y lo archivan." },
  "A project needs at least one owner.": { es: "Un proyecto necesita al menos un propietario." },
  "Project started.": { es: "Proyecto iniciado." },
  "Project saved.": { es: "Proyecto guardado." },
  "Members saved.": { es: "Miembros guardados." },
  "Project archived.": { es: "Proyecto archivado." },
  "Project brought back.": { es: "Proyecto restaurado." },
  "Archive this project? It moves to Archived, and nothing in it is deleted.": { es: "\u00bfArchivar este proyecto? Pasa a Archivados y no se borra nada de \u00e9l." },
  "This project is archived. It can be read, and nothing new can be added.": { es: "Este proyecto est\u00e1 archivado. Se puede leer, y no se puede agregar nada nuevo." },
  "Email copies on.": { es: "Copias por correo activadas." },
  "Email copies off.": { es: "Copias por correo desactivadas." },
  "New posts, and comments on posts, also come to your email.": { es: "Las publicaciones nuevas y los comentarios en ellas tambi\u00e9n le llegan por correo." },
  "Activity": { es: "Actividad" },
  "Activity did not load.": { es: "La actividad no se carg\u00f3." },
  "Nothing has happened here yet.": { es: "Todav\u00eda no ha pasado nada aqu\u00ed." },
  "Talk with the project's members. It opens in Messages.": { es: "Hable con los miembros del proyecto. Se abre en Mensajes." },
  "This project's chat did not open.": { es: "El chat de este proyecto no se abri\u00f3." },
  "Project chat": { es: "Chat del proyecto" },
  "No posts yet.": { es: "Todav\u00eda no hay publicaciones." },
  "New post": { es: "Nueva publicaci\u00f3n" },
  "Edit post": { es: "Editar la publicaci\u00f3n" },
  "Plain text. Line breaks are kept, and web addresses become links.": { es: "Texto simple. Se conservan los saltos de l\u00ednea, y las direcciones web se vuelven enlaces." },
  "Pin to the top": { es: "Fijar arriba" },
  "Pinned": { es: "Fijada" },
  "Post": { es: "Publicar" },
  "Posted.": { es: "Publicado." },
  "Post saved.": { es: "Publicaci\u00f3n guardada." },
  "Post archived.": { es: "Publicaci\u00f3n archivada." },
  "Archive this post? It leaves the message board, and nothing is deleted.": { es: "\u00bfArchivar esta publicaci\u00f3n? Sale del tablero de mensajes y no se borra nada." },
  "edited": { es: "editado" },
  "Comment": { es: "Comentario" },
  "Add a comment": { es: "Agregar un comentario" },
  "Add comment": { es: "Agregar comentario" },
  "No one else is in this project.": { es: "No hay nadie m\u00e1s en este proyecto." },
  "Remove this comment?": { es: "\u00bfQuitar este comentario?" },
  "New list": { es: "Nueva lista" },
  "Add the list": { es: "Agregar la lista" },
  "List added.": { es: "Lista agregada." },
  "No lists yet. Start one with New list.": { es: "Todav\u00eda no hay listas. Empiece una con Nueva lista." },
  "Nothing open on this list.": { es: "No hay nada pendiente en esta lista." },
  "Hide the done ones": { es: "Ocultar los completados" },
  "Add a to-do": { es: "Agregar un pendiente" },
  "Edit to-do": { es: "Editar el pendiente" },
  "To-do": { es: "Pendiente" },
  "Add the to-do": { es: "Agregar el pendiente" },
  "To-do added.": { es: "Pendiente agregado." },
  "To-do saved.": { es: "Pendiente guardado." },
  "No to-dos yet.": { es: "Todav\u00eda no hay pendientes." },
  "Due {0}": { es: "Vence el {0}" },
  "Overdue since {0}": { es: "Vencido desde el {0}" },
  "Done by {0}, {1}": { es: "Completado por {0}, {1}" },
  "Done {0}": { es: "Completado: {0}" },
  "No one yet": { es: "Nadie todav\u00eda" },
  "No due date": { es: "Sin fecha l\u00edmite" },
  "Added by": { es: "Agregado por" },
  "This to-do is not in the project.": { es: "Este pendiente no est\u00e1 en el proyecto." },
  "The server does not list a to-do's comments yet. A comment added here is saved, and shows once it does.": { es: "El servidor todav\u00eda no muestra los comentarios de un pendiente. Un comentario agregado aqu\u00ed se guarda y aparece cuando lo haga." },
  "Nothing is assigned to you.": { es: "No tiene nada asignado." },
  "Upload": { es: "Subir" },
  "Documents, images and PDFs, up to 25 MB each.": { es: "Documentos, im\u00e1genes y PDF, de hasta 25 MB cada uno." },
  "{0} is over 25 MB. Files up to 25 MB can be added.": { es: "{0} pasa de 25 MB. Se pueden agregar archivos de hasta 25 MB." },
  "No files yet.": { es: "Todav\u00eda no hay archivos." },
  "File added.": { es: "Archivo agregado." },
  "File removed.": { es: "Archivo quitado." },
  "Remove {0} from the project?": { es: "\u00bfQuitar {0} del proyecto?" },
  "{0} KB": { es: "{0} KB" },
  "{0} MB": { es: "{0} MB" },
  "Channel": { es: "Canal" },
  "Every channel": { es: "Todos los canales" },
  "Words": { es: "Palabras" },
  "Search": { es: "Buscar" },
  "Searching...": { es: "Buscando..." },
  "Preparing...": { es: "Preparando..." },
  "Sender": { es: "Remitente" },
  "No messages match.": { es: "Ning\u00fan mensaje coincide." },
  "Only the first 2,000 are shown. Narrow the search to see the rest.": { es: "Solo se muestran los primeros 2,000. Acote la b\u00fasqueda para ver el resto." },
  "Searches recorded": { es: "B\u00fasquedas registradas" },
  "No searches yet.": { es: "Todav\u00eda no hay b\u00fasquedas." },
  "The record of searches did not load.": { es: "El registro de b\u00fasquedas no se carg\u00f3." },
  "searched": { es: "busc\u00f3" },
  "downloaded a PDF": { es: "descarg\u00f3 un PDF" },
  "Everything": { es: "Todo" },
  "{0} comments|count": {
    en: { one: "{0} comment", other: "{0} comments" },
    es: { one: "{0} comentario", other: "{0} comentarios" },
  },
  "{0} members|count": {
    en: { one: "{0} member", other: "{0} members" },
    es: { one: "{0} miembro", other: "{0} miembros" },
  },
  "{0} messages|count": {
    en: { one: "{0} message", other: "{0} messages" },
    es: { one: "{0} mensaje", other: "{0} mensajes" },
  },
  "{0} open|count": {
    en: { one: "{0} open", other: "{0} open" },
    es: { one: "{0} pendiente", other: "{0} pendientes" },
  },
  "{0} checklist to print|count": {
    en: { one: "{0} checklist to print", other: "{0} checklists to print" },
    es: { one: "{0} lista para imprimir", other: "{0} listas para imprimir" },
  },
  "{0} log to print|count": {
    en: { one: "{0} log to print", other: "{0} logs to print" },
    es: { one: "{0} registro para imprimir", other: "{0} registros para imprimir" },
  },
  "{0} use logged|count": {
    en: { one: "{0} use logged", other: "{0} uses logged" },
    es: { one: "{0} uso registrado", other: "{0} usos registrados" },
  },
  "{0} issue recorded|count": {
    en: { one: "{0} issue recorded", other: "{0} issues recorded" },
    es: { one: "{0} entrega registrada", other: "{0} entregas registradas" },
  },
  "{0} person on the record|count": {
    en: { one: "{0} person on the record", other: "{0} people on the record" },
    es: { one: "{0} persona en el registro", other: "{0} personas en el registro" },
  },
  "{0} inspection to print|count": {
    en: { one: "{0} inspection to print", other: "{0} inspections to print" },
    es: { one: "{0} inspecci\u00f3n para imprimir", other: "{0} inspecciones para imprimir" },
  },
  "{0} session to print|count": {
    en: { one: "{0} session to print", other: "{0} sessions to print" },
    es: { one: "{0} sesi\u00f3n para imprimir", other: "{0} sesiones para imprimir" },
  },
  "{0} done|todos": {
    en: { one: "{0} done", other: "{0} done" },
    es: { one: "{0} completado", other: "{0} completados" },
  },
  "Green|color": { es: "Verde" },
  "Kept records": { es: "Registros conservados" },
  "Master Zone Cleaning Checklist": { es: "Lista de verificaci\u00f3n maestra de limpieza por zona" },
  "Disinfection Coverage Log": { es: "Registro de cobertura de desinfecci\u00f3n" },
  "Chemical Usage Log": { es: "Registro de uso de productos qu\u00edmicos" },
  "PPE Compliance Log": { es: "Registro de cumplimiento de EPP" },
  "School Clearance Tracking Record": { es: "Registro de seguimiento de autorizaciones escolares" },
  "Training Attendance Roster": { es: "Lista de asistencia a la capacitaci\u00f3n" },
  "Supervisor Inspection Report": { es: "Informe de inspecci\u00f3n del supervisor" },
  "Quality Assurance Audit Inspection": { es: "Inspecci\u00f3n de auditor\u00eda de control de calidad" },
  "Each zone's checklist for each day, from the site's checklist.": { es: "La lista de cada zona para cada d\u00eda, tomada de la lista de verificaci\u00f3n del sitio." },
  "The touchpoints on each day's checklist, treated or not.": { es: "Los puntos de contacto frecuente de la lista de cada d\u00eda, tratados o no." },
  "Every chemical logged at the site, a log for each month.": { es: "Cada producto qu\u00edmico registrado en el sitio, un registro por mes." },
  "The equipment issued and signed for, and the site's stock, a log for each month.": { es: "El equipo entregado y firmado, y las existencias del sitio, un registro por mes." },
  "Everyone at the site and their clearances, as of today.": { es: "Todas las personas del sitio y sus autorizaciones, a la fecha de hoy." },
  "Every inspection completed in the range, each on its own page.": { es: "Cada inspecci\u00f3n completada en el rango, cada una en su propia p\u00e1gina." },
  "A roster for each training session at the site in the range.": { es: "Una lista por cada sesi\u00f3n de capacitaci\u00f3n en el sitio dentro del rango." },
  "Pick a site...": { es: "Elija un sitio..." },
  "Pick a site to print its record.": { es: "Elija un sitio para imprimir su registro." },
  "As of {0}": { es: "Al {0}" },
  "At most {0} days at a time.": { es: "Como m\u00e1ximo {0} d\u00edas a la vez." },
  "Nothing to print for this site in this range.": { es: "No hay nada que imprimir para este sitio en este rango." },
  "Allow pop-ups to print": { es: "Permita las ventanas emergentes para imprimir" },
  "Printed on {0}": { es: "Impreso el {0}" },
  "Print the tracking record": { es: "Imprimir el registro de seguimiento" },
  "Zone Details": { es: "Datos de la zona" },
  "Before You Start": { es: "Antes de empezar" },
  "Daily Tasks": { es: "Tareas diarias" },
  "Weekly and Periodic Tasks": { es: "Tareas semanales y peri\u00f3dicas" },
  "Restroom Detail": { es: "Detalle de ba\u00f1os" },
  "Anything Not Done": { es: "Lo que no se hizo" },
  "Found and Reported": { es: "Hallado y reportado" },
  "Close of Zone": { es: "Cierre de la zona" },
  "Sign-Off": { es: "Firmas" },
  "Log Details": { es: "Datos del registro" },
  "Response Level": { es: "Nivel de respuesta" },
  "Critical Touchpoints": { es: "Puntos de contacto cr\u00edticos" },
  "Standard Touchpoints and Shared Surfaces": { es: "Puntos de contacto est\u00e1ndar y superficies compartidas" },
  "Whole-Room Treatment": { es: "Tratamiento de toda la sala" },
  "Items Not Treated": { es: "Elementos no tratados" },
  "Verification": { es: "Verificaci\u00f3n" },
  "Sign-Off and Filing": { es: "Firmas y archivo" },
  "Daily Usage Log": { es: "Registro de uso diario" },
  "Monthly Stock Position": { es: "Situaci\u00f3n mensual de existencias" },
  "Observations": { es: "Observaciones" },
  "Standard List Compliance": { es: "Cumplimiento de la lista est\u00e1ndar" },
  "Site and Period": { es: "Sitio y per\u00edodo" },
  "Equipment Issue Record": { es: "Registro de entrega de equipo" },
  "Stock Check": { es: "Revisi\u00f3n de existencias" },
  "Compliance Observation": { es: "Observaci\u00f3n de cumplimiento" },
  "Shortages, Reactions and Actions": { es: "Faltantes, reacciones y acciones" },
  "Tracking Grid": { es: "Cuadro de seguimiento" },
  "Monthly Review": { es: "Revisi\u00f3n mensual" },
  "Session Details": { es: "Datos de la sesi\u00f3n" },
  "Trainer": { es: "Instructor" },
  "Attendance": { es: "Asistencia" },
  "Trainer Attestation": { es: "Declaraci\u00f3n del instructor" },
  "Date and Shift": { es: "Fecha y turno" },
  "Cleaner": { es: "Limpiador" },
  "Crew Lead": { es: "Jefe de cuadrilla" },
  "Scope of Work Reference": { es: "Referencia del alcance de trabajo" },
  "Check": { es: "Comprobaci\u00f3n" },
  "Area or Room": { es: "\u00c1rea o sala" },
  "Initials": { es: "Iniciales" },
  "Restroom Task": { es: "Tarea de ba\u00f1o" },
  "Area": { es: "\u00c1rea" },
  "Reported To": { es: "Reportado a" },
  "Found": { es: "Hallazgo" },
  "Detail, Location and Who Was Informed": { es: "Detalle, ubicaci\u00f3n y a qui\u00e9n se inform\u00f3" },
  "Product in Use": { es: "Producto en uso" },
  "Registration Number": { es: "N\u00famero de registro" },
  "Label Contact Time": { es: "Tiempo de contacto de la etiqueta" },
  "Site Touchpoint List Reference": { es: "Referencia de la lista de puntos de contacto del sitio" },
  "Number of Items on the List": { es: "N\u00famero de elementos de la lista" },
  "Level Today": { es: "Nivel de hoy" },
  "Level 1, Standard": { es: "Nivel 1, est\u00e1ndar" },
  "Level 2, Heightened": { es: "Nivel 2, elevado" },
  "Level 3, Outbreak": { es: "Nivel 3, brote" },
  "Level Set By": { es: "Nivel fijado por" },
  "Date the Level Was Set": { es: "Fecha en que se fij\u00f3 el nivel" },
  "Affected Areas": { es: "\u00c1reas afectadas" },
  "Touchpoint Item": { es: "Punto de contacto" },
  "Location": { es: "Ubicaci\u00f3n" },
  "Pass 1": { es: "Pasada 1" },
  "Pass 2": { es: "Pasada 2" },
  "Surfaces Treated": { es: "Superficies tratadas" },
  "Contact Time Held": { es: "Tiempo de contacto respetado" },
  "Reason Inaccessible": { es: "Motivo por el que no hubo acceso" },
  "Product Identifier": { es: "Identificador del producto" },
  "Dilution Used": { es: "Diluci\u00f3n usada" },
  "Area or Task": { es: "\u00c1rea o tarea" },
  "Quantity Used": { es: "Cantidad usada" },
  "Month and Year": { es: "Mes y a\u00f1o" },
  "Janitorial Closet Location": { es: "Ubicaci\u00f3n del cuarto de limpieza" },
  "Site Listing Reference": { es: "Referencia de la lista del sitio" },
  "Number of Products on the Site Listing": { es: "N\u00famero de productos en la lista del sitio" },
  "Opening Stock": { es: "Existencia inicial" },
  "Used": { es: "Usado" },
  "Closing Stock": { es: "Existencia final" },
  "Reorder Needed": { es: "Hace falta reordenar" },
  "Physical Count Performed By": { es: "Conteo f\u00edsico hecho por" },
  "Discrepancies Found": { es: "Diferencias halladas" },
  "Reorder Placed": { es: "Pedido hecho" },
  "Observation": { es: "Observaci\u00f3n" },
  "Detail and Action": { es: "Detalle y acci\u00f3n" },
  "Products Used This Month On the Standard List": { es: "Productos de la lista est\u00e1ndar usados este mes" },
  "Products Used Outside the Standard List": { es: "Productos usados fuera de la lista est\u00e1ndar" },
  "Proportion of Total Usage On the Standard List": { es: "Proporci\u00f3n del uso total en la lista est\u00e1ndar" },
  "Substitutions Proposed": { es: "Sustituciones propuestas" },
  "Crew Size This Period": { es: "Tama\u00f1o de la cuadrilla en este per\u00edodo" },
  "Hazard Assessment Reference": { es: "Referencia de la evaluaci\u00f3n de riesgos" },
  "Item Issued": { es: "Art\u00edculo entregado" },
  "Qty": { es: "Cant." },
  "Fit OK": { es: "Le queda bien" },
  "Item and Size": { es: "Art\u00edculo y talla" },
  "On Hand": { es: "En existencia" },
  "Minimum": { es: "M\u00ednimo" },
  "Sizes That Must Be Held": { es: "Tallas que deben tenerse" },
  "Task Observed": { es: "Tarea observada" },
  "Employees Observed": { es: "Empleados observados" },
  "Wearing Required Equipment": { es: "Usan el equipo requerido" },
  "Issue Reported": { es: "Problema reportado" },
  "Action Taken": { es: "Acci\u00f3n tomada" },
  "Closed": { es: "Cerrado" },
  "Work Stopped for Missing Equipment": { es: "Trabajo detenido por falta de equipo" },
  "Equipment Failures": { es: "Fallas del equipo" },
  "Skin Reactions or Discomfort Reported": { es: "Reacciones en la piel o molestias reportadas" },
  "Fit Problems": { es: "Problemas de ajuste" },
  "Referred to Hazard Reassessment": { es: "Enviado a nueva evaluaci\u00f3n de riesgos" },
  "Act 34 Issued": { es: "Act 34, emitida" },
  "Act 34 Expires": { es: "Act 34, vence" },
  "Act 151 Issued": { es: "Act 151, emitida" },
  "Act 151 Expires": { es: "Act 151, vence" },
  "Act 114 Issued": { es: "Act 114, emitida" },
  "Act 114 Expires": { es: "Act 114, vence" },
  "Act 168 Complete": { es: "Act 168, completa" },
  "Training Expires": { es: "Vence la capacitaci\u00f3n" },
  "Meaning": { es: "Significado" },
  "Review month": { es: "Mes de la revisi\u00f3n" },
  "Reviewed by": { es: "Revisado por" },
  "Date reviewed": { es: "Fecha de revisi\u00f3n" },
  "Employees in Notice or worse": { es: "Empleados en Aviso o peor" },
  "Actions taken": { es: "Acciones tomadas" },
  "Training Title or Topic": { es: "T\u00edtulo o tema de la capacitaci\u00f3n" },
  "Related Document No.": { es: "N\u00famero de documento relacionado" },
  "Start Time / End Time": { es: "Hora de inicio / hora de fin" },
  "Site or Location": { es: "Sitio o lugar" },
  "Delivery Method": { es: "Forma de impartirla" },
  "Language(s) Delivered In": { es: "Idioma(s) en que se imparti\u00f3" },
  "Translation Method": { es: "Forma de traducci\u00f3n" },
  "Understanding Verified By": { es: "Comprensi\u00f3n verificada por" },
  "Materials or Equipment Used": { es: "Materiales o equipo usados" },
  "Trainer Name": { es: "Nombre del instructor" },
  "Trainer Role": { es: "Cargo del instructor" },
  "Qualification Held": { es: "Calificaci\u00f3n que tiene" },
  "Trainer Signature": { es: "Firma del instructor" },
  "Employee Name (print)": { es: "Nombre del empleado (en letra de molde)" },
  "Understood": { es: "Entendido" },
  "Total attendees this session": { es: "Total de asistentes en esta sesi\u00f3n" },
  "Treated": { es: "Tratado" },
  "Name and Signature": { es: "Nombre y firma" },
  "Zone complete as recorded above": { es: "Zona completa como se registr\u00f3 arriba" },
  "Received and carried into OCSA-FRM-005": { es: "Recibido y pasado al OCSA-FRM-005" },
  "The worker at the site": { es: "El trabajador en el sitio" },
  "Reviewed By": { es: "Revisado por" },
  "Level 3 Reviewed By": { es: "Nivel 3 revisado por" },
  "Field Lead": { es: "Jefe de campo" },
  "Site supervisor": { es: "Supervisor del sitio" },
  "Filed By": { es: "Archivado por" },
  "Controller": { es: "Contralor" },
  "Every attendee signs their own line. A supervisor may not sign on an employee's behalf.": { es: "Cada asistente firma su propia l\u00ednea. Un supervisor no puede firmar en nombre de un empleado." },
  "I confirm that I delivered the training described in Section 1, that each person listed in Section 3 attended for the full duration, and that the session was delivered in a format and language each attendee was able to understand. I confirm that attendees had the opportunity to ask questions and that questions raised were answered.": { es: "Confirmo que impart\u00ed la capacitaci\u00f3n descrita en la Secci\u00f3n 1, que cada persona de la Secci\u00f3n 3 asisti\u00f3 durante toda la sesi\u00f3n y que la sesi\u00f3n se imparti\u00f3 en un formato y un idioma que cada asistente pod\u00eda entender. Confirmo que los asistentes tuvieron la oportunidad de hacer preguntas y que se respondieron las preguntas que hicieron." },
  "Clear|clearance band": { es: "Al d\u00eda" },
  "Notice|clearance band": { es: "Aviso" },
  "Follow up|clearance band": { es: "Seguimiento" },
  "At risk|clearance band": { es: "En riesgo" },
  "Expired|clearance band": { es: "Vencida" },
  "All clearances current with more than 120 days remaining": { es: "Todas las autorizaciones vigentes con m\u00e1s de 120 d\u00edas restantes" },
  "120 days or fewer to the earliest expiry": { es: "120 d\u00edas o menos para el primer vencimiento" },
  "60 days or fewer": { es: "60 d\u00edas o menos" },
  "30 days or fewer": { es: "30 d\u00edas o menos" },
  "Any clearance past its date": { es: "Cualquier autorizaci\u00f3n vencida" },
  "None": { es: "Ninguna" },
  "Written renewal notice issued to the employee": { es: "Aviso de renovaci\u00f3n por escrito entregado al empleado" },
  "Second notice. Field Lead informed": { es: "Segundo aviso. Se informa al jefe de campo" },
  "Replacement coverage planned for that site": { es: "Se planifica la cobertura de reemplazo para ese sitio" },
  "Employee removed from school sites the same day": { es: "El empleado sale de los sitios escolares el mismo d\u00eda" },
  "Closet unlocked, and secured again behind you": { es: "Cuarto abierto, y cerrado de nuevo al salir" },
  "Cart stocked for the whole zone": { es: "Carrito surtido para toda la zona" },
  "Wet floor signs on the cart": { es: "Letreros de piso mojado en el carrito" },
  "Cloths in the correct color for each area type": { es: "Pa\u00f1os del color correcto para cada tipo de \u00e1rea" },
  "Dilution station working, and containers labeled": { es: "Estaci\u00f3n de diluci\u00f3n funcionando, y envases etiquetados" },
  "Protective equipment for the tasks in this zone": { es: "Equipo de protecci\u00f3n para las tareas de esta zona" },
  "Vacuum or machine checked, cord along its full length": { es: "Aspiradora o m\u00e1quina revisada, el cable en todo su largo" },
  "Zone walked, and anything unusual noted before starting": { es: "Zona recorrida, y todo lo inusual anotado antes de empezar" },
  "Toilets and urinals cleaned and disinfected, contact time held": { es: "Inodoros y urinarios limpios y desinfectados, con el tiempo de contacto respetado" },
  "Sinks, counters and faucets cleaned and disinfected": { es: "Lavamanos, mostradores y llaves limpios y desinfectados" },
  "Mirrors cleaned, no streaks in the light": { es: "Espejos limpios, sin rayas a la luz" },
  "Partitions, doors and latches wiped on both faces": { es: "Divisiones, puertas y pestillos limpiados por ambos lados" },
  "Dispensers restocked, tissue, towels and soap": { es: "Dispensadores surtidos, papel, toallas y jab\u00f3n" },
  "Waste and sanitary units emptied, wiped and relined": { es: "Basureros y recipientes sanitarios vaciados, limpiados y con bolsa nueva" },
  "Floor swept and damp mopped with disinfectant": { es: "Piso barrido y trapeado con desinfectante" },
  "Behind fixtures and in corners checked": { es: "Revisado detr\u00e1s de los aparatos y en las esquinas" },
  "Touchpoints disinfected on the way out": { es: "Puntos de contacto desinfectados al salir" },
  "Wet floor signs left until the floor is dry": { es: "Letreros de piso mojado puestos hasta que se seque el piso" },
  "Damage to the building or its contents": { es: "Da\u00f1os al edificio o a su contenido" },
  "Spill, leak or standing water": { es: "Derrame, fuga o agua estancada" },
  "Sharps or broken glass": { es: "Objetos punzantes o vidrio roto" },
  "Blood or body fluid": { es: "Sangre o fluidos corporales" },
  "Unlabeled or unknown container": { es: "Envase sin etiqueta o desconocido" },
  "Equipment fault": { es: "Falla de un equipo" },
  "Supply running out": { es: "Suministro por agotarse" },
  "Door, window or closet found insecure": { es: "Puerta, ventana o cuarto encontrado sin seguro" },
  "Anything else worth reporting": { es: "Cualquier otra cosa que valga la pena reportar" },
  "Wet floor signs collected once floors were dry": { es: "Letreros de piso mojado recogidos al secarse los pisos" },
  "Waste removed to the collection point, streams kept separate": { es: "Basura llevada al punto de recolecci\u00f3n, con los tipos separados" },
  "Equipment cleaned, emptied and stored": { es: "Equipo limpio, vaciado y guardado" },
  "Mop heads and cloths in the laundry bag": { es: "Cabezas de trapeador y pa\u00f1os en la bolsa de lavander\u00eda" },
  "Cart restocked for the next shift": { es: "Carrito surtido para el siguiente turno" },
  "Closet left clean, orderly and secured": { es: "Cuarto limpio, ordenado y cerrado" },
  "Containers closed and labeled": { es: "Envases cerrados y etiquetados" },
  "Lights, doors and access left as the site requires": { es: "Luces, puertas y accesos como lo pide el sitio" },
  "Method used, surface test or marker check": { es: "M\u00e9todo usado, prueba de superficie o marcador" },
  "Number of touchpoints sampled": { es: "N\u00famero de puntos de contacto revisados" },
  "Number passing": { es: "N\u00famero que pasa" },
  "Items failing, and where": { es: "Elementos que no pasan, y d\u00f3nde" },
  "Retraining raised, with the OCSA-FRM-033 reference": { es: "Nueva capacitaci\u00f3n pedida, con la referencia OCSA-FRM-033" },
  "Repeat check date and result": { es: "Fecha y resultado de la nueva revisi\u00f3n" },
  "Spills or releases this month": { es: "Derrames o escapes este mes" },
  "Reactions, fumes or odors reported": { es: "Reacciones, vapores u olores reportados" },
  "Damaged or missing container labels": { es: "Etiquetas de envases da\u00f1adas o faltantes" },
  "Unlabeled or unknown containers found": { es: "Envases sin etiqueta o desconocidos hallados" },
  "Products used at a dilution other than the label rate": { es: "Productos usados con una diluci\u00f3n distinta a la de la etiqueta" },
  "Products found on site that are absent from the site listing": { es: "Productos hallados en el sitio que no est\u00e1n en la lista del sitio" },
  "Employee complaints about a product": { es: "Quejas de empleados sobre un producto" },
  "Safety data sheets missing from the binder": { es: "Hojas de datos de seguridad que faltan en la carpeta" },
  "Disposable nitrile gloves": { es: "Guantes de nitrilo desechables" },
  "Reusable forearm length gloves": { es: "Guantes reutilizables hasta el antebrazo" },
  "Puncture resistant gloves": { es: "Guantes resistentes a pinchazos" },
  "Safety glasses meeting ANSI Z87.1": { es: "Lentes de seguridad que cumplen ANSI Z87.1" },
  "Chemical splash goggles": { es: "Gafas contra salpicaduras qu\u00edmicas" },
  "Aprons, gowns and coveralls": { es: "Delantales, batas y overoles" },
  "Hearing protection": { es: "Protecci\u00f3n auditiva" },
  "PPE issued": { es: "EPP entregado" },
  "Issue PPE": { es: "Entregar EPP" },
  "Protective equipment this person was given, each signed for when it was handed over.": { es: "El equipo de protecci\u00f3n que se le entreg\u00f3 a esta persona, cada uno firmado al recibirlo." },
  "Protective equipment given out at this site, each signed for when it was handed over.": { es: "El equipo de protecci\u00f3n entregado en este sitio, cada uno firmado al recibirlo." },
  "Nothing issued yet.": { es: "Todav\u00eda no se ha entregado nada." },
  "Fits well": { es: "Le queda bien" },
  "Does not fit": { es: "No le queda bien" },
  "PPE issue saved.": { es: "Entrega de EPP guardada." },
  "Something else, typed": { es: "Otra cosa, escrita" },
  "What was issued": { es: "Qu\u00e9 se entreg\u00f3" },
  "Size": { es: "Talla" },
  "Fits well?": { es: "\u00bfLe queda bien?" },
  "{0} signs for what they received": { es: "{0} firma lo que recibi\u00f3" },
  "The person signs for what they received": { es: "La persona firma lo que recibi\u00f3" },
  "Add equipment": { es: "Agregar equipo" },
  "Every machine and tool on the register, where it is and when it is next serviced. A row opens the item.": { es: "Cada m\u00e1quina y herramienta del registro, d\u00f3nde est\u00e1 y cu\u00e1ndo le toca el pr\u00f3ximo servicio. Una fila abre el art\u00edculo." },
  "Service due": { es: "Servicio pendiente" },
  "Tick items to print their labels.": { es: "Marque art\u00edculos para imprimir sus etiquetas." },
  "Print labels": { es: "Imprimir etiquetas" },
  "Print labels ({0})": { es: "Imprimir etiquetas ({0})" },
  "Tick {0} for labels": { es: "Marcar {0} para etiquetas" },
  "Next service": { es: "Pr\u00f3ximo servicio" },
  "Latest event": { es: "\u00daltimo evento" },
  "Nothing matches these filters.": { es: "Nada coincide con estos filtros." },
  "No equipment on the register yet.": { es: "Todav\u00eda no hay equipo en el registro." },
  "Equipment added.": { es: "Equipo agregado." },
  "In service": { es: "En servicio" },
  "Out of service": { es: "Fuera de servicio" },
  "Retired|equipment": { es: "Dado de baja" },
  "Checked|equipment": { es: "Revisado" },
  "Serviced": { es: "Servicio hecho" },
  "Repaired": { es: "Reparado" },
  "Tagged out": { es: "Retirado de servicio" },
  "Returned to service": { es: "Devuelto al servicio" },
  "Moved|equipment": { es: "Trasladado" },
  "Record service": { es: "Registrar servicio" },
  "Repair": { es: "Reparar" },
  "Tag out": { es: "Retirar de servicio" },
  "Return to service": { es: "Devolver al servicio" },
  "Move": { es: "Trasladar" },
  "Retire": { es: "Dar de baja" },
  "Edit equipment": { es: "Editar equipo" },
  "Floor scrubber 2": { es: "Fregadora de pisos 2" },
  "Auto scrubber": { es: "Fregadora autom\u00e1tica" },
  "Make": { es: "Marca" },
  "Model": { es: "Modelo" },
  "Serial number": { es: "N\u00famero de serie" },
  "Bought on": { es: "Comprado el" },
  "Service every (days)": { es: "Servicio cada (d\u00edas)" },
  "Last serviced on": { es: "\u00daltimo servicio el" },
  "A whole number of days, 1 or more.": { es: "Un n\u00famero entero de d\u00edas, 1 o m\u00e1s." },
  "Back to equipment": { es: "Volver al equipo" },
  "Label code": { es: "C\u00f3digo de la etiqueta" },
  "Retired on": { es: "Dado de baja el" },
  "due": { es: "pendiente" },
  "Nothing recorded yet.": { es: "Todav\u00eda no hay nada registrado." },
  "From {0} to {1}": { es: "De {0} a {1}" },
  "Recorded.": { es: "Registrado." },
  "A tagged out item is out of service until it is returned. The site's supervisors and the admins are told.": { es: "Un art\u00edculo retirado de servicio queda fuera de servicio hasta que se devuelva. Se avisa a los supervisores del sitio y a los administradores." },
  "The next service moves {0} days on from today.": { es: "El pr\u00f3ximo servicio pasa a {0} d\u00edas a partir de hoy." },
  "Move to": { es: "Trasladar a" },
  "What is wrong": { es: "Qu\u00e9 falla" },
  "Required.": { es: "Obligatorio." },
  "Retire {0}? It stays on the register, and nothing more can be recorded against it.": { es: "\u00bfDar de baja {0}? Se queda en el registro y ya no se puede registrar nada m\u00e1s para \u00e9l." },
  "Periodic work": { es: "Trabajo peri\u00f3dico" },
  "The weekly, monthly, quarterly and seasonal work on every site's checklist, and whether it is done this period. A row opens the site's checklist.": { es: "El trabajo semanal, mensual, trimestral y de temporada de la lista de cada sitio, y si est\u00e1 hecho en este per\u00edodo. Una fila abre la lista del sitio." },
  "All|periodic": { es: "Todo" },
  "Overdue|periodic": { es: "Vencido" },
  "Due|periodic": { es: "Pendiente" },
  "Done|periodic": { es: "Hecho" },
  "Last done": { es: "\u00daltima vez" },
  "By|done by": { es: "Por" },
  "Next due": { es: "Pr\u00f3ximo vencimiento" },
  "Nothing matches this filter.": { es: "Nada coincide con este filtro." },
  "No periodic work on any checklist yet.": { es: "Todav\u00eda no hay trabajo peri\u00f3dico en ninguna lista." },
  "Every two weeks": { es: "Cada dos semanas" },
  "Seasonal": { es: "De temporada" },
  "Touchpoint": { es: "Punto de contacto frecuente" },
  "High-touch surfaces disinfected on schedule. They print on the Disinfection Coverage Log.": { es: "Superficies de contacto frecuente desinfectadas seg\u00fan el horario. Se imprimen en el Registro de cobertura de desinfecci\u00f3n." },
  "Word, Excel and PowerPoint documents, CSV and text files, images and PDFs, up to 25 MB each.": { es: "Documentos de Word, Excel y PowerPoint, archivos CSV y de texto, im\u00e1genes y PDF, de hasta 25 MB cada uno." },
  "Level": { es: "Nivel" },
  "Trigger": { es: "Causa" },
  "What This Log Must Show": { es: "Lo que este registro debe mostrar" },
  "Level 1": { es: "Nivel 1" },
  "Level 2": { es: "Nivel 2" },
  "Level 3": { es: "Nivel 3" },
  "Normal conditions": { es: "Condiciones normales" },
  "Critical and standard touchpoints treated daily, recorded item by item in Sections 3 and 4": { es: "Puntos de contacto cr\u00edticos y est\u00e1ndar tratados a diario, registrados uno por uno en las Secciones 3 y 4" },
  "A customer report or a health authority report of raised illness at the site": { es: "Un reporte del cliente o de una autoridad de salud de m\u00e1s enfermedad en el sitio" },
  "Critical touchpoints treated at least twice per service day, with both pass times recorded. Standard touchpoints daily. Affected areas prioritized in the round": { es: "Puntos de contacto cr\u00edticos tratados al menos dos veces por d\u00eda de servicio, con la hora de ambas pasadas registrada. Puntos de contacto est\u00e1ndar a diario. \u00c1reas afectadas primero en la ronda" },
  "A customer or a health authority reports an outbreak, or a health authority directs a response": { es: "El cliente o una autoridad de salud reporta un brote, o una autoridad de salud ordena una respuesta" },
  "Whole-room treatment recorded in Section 5. Critical touchpoints on every round. Dedicated equipment for affected areas. Daily written coverage report to the customer": { es: "Tratamiento de toda la sala registrado en la Secci\u00f3n 5. Puntos de contacto cr\u00edticos en cada ronda. Equipo exclusivo para las \u00e1reas afectadas. Informe escrito diario de cobertura para el cliente" },
  "Due by": { es: "Fecha l\u00edmite" },
  "All but retired": { es: "Todos menos los dados de baja" },
  "Make a link": { es: "Crear un enlace" },
  "Each link opens one form for one site. A site has one link on for each form, and asking again opens that one.": { es: "Cada enlace abre un formulario para un sitio. Un sitio tiene un enlace encendido por formulario, y pedirlo otra vez abre ese mismo." },
  "On|link": { es: "Encendido" },
  "All|links": { es: "Todos" },
  "State|link": { es: "Estado" },
  "Print sheet": { es: "Imprimir la hoja" },
  "Turn off this link? Anyone who scans it is told it is no longer in use.": { es: "\u00bfApagar este enlace? A quien lo escanee se le dir\u00e1 que ya no est\u00e1 en uso." },
  "All links": { es: "Todos los enlaces" },
  "Scan to report a problem to {0}.": { es: "Escanee para informar un problema a {0}." },
  "From a client": { es: "De un cliente" },
  "For the Controller": { es: "Para el Contralor" },
  "Answered {0}": { es: "Respondida el {0}" },
  "Management review": { es: "Revisi\u00f3n por la direcci\u00f3n" },
  "The evidence pack for the monthly review and the quarterly management review, for every site.": { es: "El paquete de evidencias para la revisi\u00f3n mensual y la revisi\u00f3n trimestral por la direcci\u00f3n, para todos los sitios." },
  "The evidence pack for a monthly review or a quarterly management review under OCSA-QMS-018, for every site, in the order of the review's agenda. What the platform does not hold prints as lines to fill in at the review.": { es: "El paquete de evidencias para una revisi\u00f3n mensual o una revisi\u00f3n trimestral por la direcci\u00f3n seg\u00fan OCSA-QMS-018, para todos los sitios, en el orden de la agenda de la revisi\u00f3n. Lo que la plataforma no tiene se imprime como l\u00edneas para completar en la revisi\u00f3n." },
  "Quarter": { es: "Trimestre" },
  "Quarter {0}, {1}": { es: "Trimestre {0} de {1}" },
  "Print the evidence pack": { es: "Imprimir el paquete de evidencias" },
  "Gathering the evidence...": { es: "Reuniendo la evidencia..." },
  "Management review evidence pack": { es: "Paquete de evidencias para la revisi\u00f3n por la direcci\u00f3n" },
  "Management Review Evidence Pack": { es: "Paquete de evidencias para la revisi\u00f3n por la direcci\u00f3n" },
  "All sites ({0})": { es: "Todos los sitios ({0})" },
  "Monthly review": { es: "Revisi\u00f3n mensual" },
  "Quarterly management review": { es: "Revisi\u00f3n trimestral por la direcci\u00f3n" },
  "Compared with": { es: "Comparado con" },
  "Prepared by": { es: "Preparado por" },
  "Circulated on": { es: "Distribuido el" },
  "Review held on": { es: "Revisi\u00f3n realizada el" },
  "The monthly review examines inspection scores by site, complaints, corrective actions, safety findings and overdue items, and assigns follow ups.": { es: "La revisi\u00f3n mensual examina las calificaciones de inspecci\u00f3n por sitio, las quejas, las acciones correctivas, los hallazgos de seguridad y los pendientes vencidos, y asigna seguimientos." },
  "The evidence pack is circulated at least five working days before a quarterly or annual review. Every agenda item is here in its order; what the platform does not hold is left to fill in at the review.": { es: "El paquete de evidencias se distribuye al menos cinco d\u00edas h\u00e1biles antes de una revisi\u00f3n trimestral o anual. Cada punto de la agenda est\u00e1 aqu\u00ed en su orden; lo que la plataforma no tiene queda para completar en la revisi\u00f3n." },
  "The data for this was not available when the pack was printed.": { es: "Estos datos no estaban disponibles al imprimir el paquete." },
  "Status of actions from the previous review": { es: "Estado de las acciones de la revisi\u00f3n anterior" },
  "Each action open or closed, with its evidence.": { es: "Cada acci\u00f3n abierta o cerrada, con su evidencia." },
  "Due date": { es: "Fecha l\u00edmite" },
  "Open or closed": { es: "Abierta o cerrada" },
  "Evidence": { es: "Evidencia" },
  "Changes outside the company": { es: "Cambios fuera de la empresa" },
  "New contracts, lost contracts, regulation, insurance, the labor market and customer requirements.": { es: "Contratos nuevos, contratos perdidos, normativa, seguros, el mercado laboral y los requisitos de los clientes." },
  "Service quality": { es: "Calidad del servicio" },
  "Completed inspections by site in the period, with the average score against the period before.": { es: "Inspecciones completadas por sitio en el per\u00edodo, con la calificaci\u00f3n promedio frente al per\u00edodo anterior." },
  "Average score": { es: "Calificaci\u00f3n promedio" },
  "Period before": { es: "Per\u00edodo anterior" },
  "Trend": { es: "Tendencia" },
  "Up {0} points": { es: "Sube {0} puntos" },
  "Down {0} points": { es: "Baja {0} puntos" },
  "No change|trend": { es: "Sin cambio" },
  "Sites below 80 percent": { es: "Sitios por debajo del 80 por ciento" },
  "Sites in escalation": { es: "Sitios en escalamiento" },
  "Customers": { es: "Clientes" },
  "Complaints logged in the period by site. Answered on time and late are counted where the complaint carries its deadline.": { es: "Quejas registradas en el per\u00edodo por sitio. Las respondidas a tiempo y las tard\u00edas se cuentan donde la queja lleva su fecha l\u00edmite." },
  "Complaints": { es: "Quejas" },
  "From a client link": { es: "Desde un enlace para clientes" },
  "Answered within five working days": { es: "Respondidas en cinco d\u00edas h\u00e1biles" },
  "Late": { es: "Tard\u00edas" },
  "Client survey results": { es: "Resultados de la encuesta a clientes" },
  "Score out of 10": { es: "Calificaci\u00f3n sobre 10" },
  "Response times, retention, and anything raised by a prime contractor": { es: "Tiempos de respuesta, retenci\u00f3n y todo lo planteado por un contratista principal" },
  "Corrective actions": { es: "Acciones correctivas" },
  "Corrective Action Reports (OCSA-FRM-010) opened and closed in the period, and still open at its end.": { es: "Reportes de acci\u00f3n correctiva (OCSA-FRM-010) abiertos y cerrados en el per\u00edodo, y los que siguen abiertos al final." },
  "Opened in the period": { es: "Abiertos en el per\u00edodo" },
  "Closed in the period": { es: "Cerrados en el per\u00edodo" },
  "Still open at the end of the period": { es: "Siguen abiertos al final del per\u00edodo" },
  "Overdue, reopened, and any cause appearing at more than one site": { es: "Vencidos, reabiertos y toda causa que aparezca en m\u00e1s de un sitio" },
  "Health and safety": { es: "Salud y seguridad" },
  "Filed in the period": { es: "Presentados en el per\u00edodo" },
  "Recordable cases on the injury log": { es: "Casos registrables en el registro de lesiones" },
  "Training sessions held": { es: "Sesiones de capacitaci\u00f3n realizadas" },
  "People trained": { es: "Personas capacitadas" },
  "Safety inspections, near misses, and training completion": { es: "Inspecciones de seguridad, casi accidentes y capacitaci\u00f3n completada" },
  "Environmental performance": { es: "Desempe\u00f1o ambiental" },
  "Staff employed at the end of the period": { es: "Personal empleado al final del per\u00edodo" },
  "Left in the period": { es: "Se fueron en el per\u00edodo" },
  "On leave on {0}": { es: "De licencia el {0}" },
  "Why people left": { es: "Por qu\u00e9 se fueron" },
  "Warnings issued, by step": { es: "Amonestaciones emitidas, por paso" },
  "Warnings": { es: "Amonestaciones" },
  "Turnover, absence, and feedback from employees": { es: "Rotaci\u00f3n, ausencias y comentarios de los empleados" },
  "Resources": { es: "Recursos" },
  "Equipment and periodic work as they stand on {0}.": { es: "Equipo y trabajo peri\u00f3dico tal como est\u00e1n al {0}." },
  "Equipment out of service": { es: "Equipo fuera de servicio" },
  "Equipment service overdue": { es: "Servicio de equipo vencido" },
  "Periodic work overdue": { es: "Trabajo peri\u00f3dico vencido" },
  "Supply costs, and anything the field says it does not have": { es: "Costos de suministros y todo lo que el personal de campo dice que no tiene" },
  "Progress against each annual objective": { es: "Avance de cada objetivo anual" },
  "Each objective stated as its current figure.": { es: "Cada objetivo expresado con su cifra actual." },
  "Objective": { es: "Objetivo" },
  "Current figure": { es: "Cifra actual" },
  "Target": { es: "Meta" },
  "On track, at risk, achieved or withdrawn": { es: "En curso, en riesgo, logrado o retirado" },
  "Opportunities for improvement": { es: "Oportunidades de mejora" },
  "Raised by anyone present.": { es: "Planteadas por cualquier persona presente." },
  "Opportunity": { es: "Oportunidad" },
  "Raised by": { es: "Planteada por" },
  "Next step": { es: "Siguiente paso" },
  "What the review must produce": { es: "Lo que la revisi\u00f3n debe producir" },
  "Each decision with a named owner and a due date.": { es: "Cada decisi\u00f3n con un responsable nombrado y una fecha l\u00edmite." },
  "Output": { es: "Resultado" },
  "Decision": { es: "Decisi\u00f3n" },
  "Improvement decisions": { es: "Decisiones de mejora" },
  "Changes to the system": { es: "Cambios al sistema" },
  "Resource decisions": { es: "Decisiones sobre recursos" },
  "Objective status": { es: "Estado de los objetivos" },
  "Risks accepted": { es: "Riesgos aceptados" },
  "Follow ups": { es: "Seguimientos" },
  "The written monthly summary and the follow ups it assigns.": { es: "El resumen mensual por escrito y los seguimientos que asigna." },
  "Follow up": { es: "Seguimiento" },
  "{0} page printed|count": {
    en: { one: "{0} page printed", other: "{0} pages printed" },
    es: { one: "{0} p\u00e1gina impresa", other: "{0} p\u00e1ginas impresas" },
  },
  "Holidays": { es: "D\u00edas festivos" },
  "{0} observes the eleven federal holidays and the two Eids. Federal days follow the handbook. Enter each year's Eid dates here once {0} confirms them, and any other day {0} closes.": { es: "{0} observa los once d\u00edas festivos federales y los dos Eid. Los d\u00edas federales siguen el manual. Ingrese aqu\u00ed las fechas de los Eid de cada a\u00f1o una vez que {0} las confirme, y cualquier otro d\u00eda en que {0} cierre." },
  "Show retired": { es: "Mostrar los dados de baja" },
  "Add a holiday": { es: "Agregar un d\u00eda festivo" },
  "Edit holiday": { es: "Editar el d\u00eda festivo" },
  "Add holiday": { es: "Agregar el d\u00eda festivo" },
  "Holiday": { es: "D\u00eda festivo" },
  "Federal": { es: "Federal" },
  "Office|holiday": { es: "De la oficina" },
  "Set by the handbook": { es: "Fijado por el manual" },
  "Observed for {0}": { es: "Se observa en lugar del {0}" },
  "Retired|holiday": { es: "Dado de baja" },
  "No holidays this year.": { es: "No hay d\u00edas festivos este a\u00f1o." },
  "Holiday added.": { es: "D\u00eda festivo agregado." },
  "Holiday saved.": { es: "D\u00eda festivo guardado." },
  "Holiday retired.": { es: "D\u00eda festivo dado de baja." },
  "Retire {0}? It stops counting as a holiday and stays on record.": { es: "\u00bfDar de baja {0}? Deja de contar como d\u00eda festivo y se queda en el registro." },
  "Critical|touchpoint": { es: "Cr\u00edtico" },
  "A critical touchpoint is treated twice a day at Level 2 and on every round at Level 3.": { es: "Un punto de contacto cr\u00edtico se trata dos veces al d\u00eda en el Nivel 2 y en cada ronda en el Nivel 3." },
  "Sites to restore": { es: "Sitios por restablecer" },
  "Held when they left": { es: "Lo ten\u00eda al irse" },
  "{0} sites will be restored.|count": {
    en: { one: "{0} site will be restored.", other: "{0} sites will be restored." },
    es: { one: "Se restablecer\u00e1 {0} sitio.", other: "Se restablecer\u00e1n {0} sitios." },
  },
  "No sites will be restored.": { es: "No se restablecer\u00e1 ning\u00fan sitio." },
  "Rehired. {0} sites restored.|count": {
    en: { one: "Rehired. {0} site restored.", other: "Rehired. {0} sites restored." },
    es: { one: "Recontrataci\u00f3n registrada. Se restableci\u00f3 {0} sitio.", other: "Recontrataci\u00f3n registrada. Se restablecieron {0} sitios." },
  },
  "Not acknowledged": { es: "Recepci\u00f3n sin confirmar" },
  "Mark acknowledged": { es: "Confirmar la recepci\u00f3n" },
  "How": { es: "C\u00f3mo" },
  "Phone call": { es: "Llamada telef\u00f3nica" },
  "Text message": { es: "Mensaje de texto" },
  "In person": { es: "En persona" },
  "phone call|acknowledged": { es: "llamada telef\u00f3nica" },
  "text message|acknowledged": { es: "mensaje de texto" },
  "email|acknowledged": { es: "correo electr\u00f3nico" },
  "in person|acknowledged": { es: "en persona" },
  "Acknowledged {0} by {1}, {2}.": { es: "Recepci\u00f3n confirmada el {0} por {1}, {2}." },
  "Acknowledged {0} by email receipt.": { es: "Recepci\u00f3n confirmada el {0} con el correo de acuse de recibo." },
  "Acknowledged {0}.": { es: "Recepci\u00f3n confirmada el {0}." },
  // Step 251: request QRs, the Client requests tab, the Issue Tracker's client requests, the
  // timing report's targets, and supply labels.
  "A form": { es: "Un formulario" },
  "A request QR": { es: "Un QR de solicitud" },
  "Kind|link": { es: "Tipo" },
  "Make a request QR": { es: "Crear un QR de solicitud" },
  "Second floor restroom": { es: "Ba\u00f1o del segundo piso" },
  "Blank makes one QR for the whole site, whose page asks where in the building.": { es: "En blanco crea un QR para todo el sitio, cuya p\u00e1gina pregunta en qu\u00e9 parte del edificio." },
  "That area already has a QR. Showing it.": { es: "Esa \u00e1rea ya tiene un QR. Se muestra." },
  "A request QR opens a page where anyone in the building can ask for help in one area of the site, with no sign-in.": { es: "Un QR de solicitud abre una p\u00e1gina donde cualquiera en el edificio puede pedir ayuda en un \u00e1rea del sitio, sin iniciar sesi\u00f3n." },
  "All kinds": { es: "Todos los tipos" },
  "Request QR": { es: "QR de solicitud" },
  "Link|customer": { es: "Enlace" },
  "Request QRs": { es: "QR de solicitud" },
  "Whole site|request QR": { es: "Todo el sitio" },
  "Ask for help here": { es: "Pida ayuda aqu\u00ed" },
  "Print all for this site": { es: "Imprimir todos los de este sitio" },
  "No request QRs yet.": { es: "A\u00fan no hay QR de solicitud." },
  "{0} open|requests": {
    en: { one: "{0} open", other: "{0} open" },
    es: { one: "{0} abierta", other: "{0} abiertas" },
  },
  "Staff issues": { es: "Incidencias del personal" },
  "Client requests": { es: "Solicitudes de clientes" },
  "Client request": { es: "Solicitud de cliente" },
  "Waiting|requests": { es: "En espera" },
  "Open|requests": { es: "Abiertas" },
  "Closed|requests": { es: "Cerradas" },
  "Respond": { es: "Respuesta" },
  "Respond by": { es: "Responder antes de" },
  "Request": { es: "Solicitud" },
  "Reports|requests": { es: "Reportes" },
  "Reference": { es: "Referencia" },
  "No client requests here.": { es: "No hay solicitudes de clientes aqu\u00ed." },
  "Waiting for approval": { es: "En espera de aprobaci\u00f3n" },
  "Open|request": { es: "Abierta" },
  "In progress|request": { es: "En curso" },
  "Done|request": { es: "Hecha" },
  "Closed|request": { es: "Cerrada" },
  "Declined|request": { es: "Rechazada" },
  "Needs someone else": { es: "Necesita a otra persona" },
  "Responded|request": { es: "Respondida" },
  "No target": { es: "Sin meta" },
  "Filed|request": { es: "Presentada" },
  "Attended at filing": { es: "Con personal al presentarse" },
  "First response": { es: "Primera respuesta" },
  "Last asked|request": { es: "\u00daltima vez pedida" },
  "Asked {0} times|count": {
    en: { one: "Asked {0} time", other: "Asked {0} times" },
    es: { one: "Pedida {0} vez", other: "Pedida {0} veces" },
  },
  "Route|request": { es: "Clasificaci\u00f3n" },
  "{0} minutes absorbed|count": {
    en: { one: "{0} minute absorbed", other: "{0} minutes absorbed" },
    es: { one: "{0} minuto absorbido", other: "{0} minutos absorbidos" },
  },
  "Declined by {0}: {1}": { es: "Rechazada por {0}: {1}" },
  "Email: {0}": { es: "Correo: {0}" },
  "No email was left": { es: "No se dej\u00f3 correo" },
  "Open the contact log entry": { es: "Abrir la entrada del registro de contacto" },
  "Approve and assign": { es: "Aprobar y asignar" },
  "Decline": { es: "Rechazar" },
  "Add a note": { es: "Agregar una nota" },
  "Assign to": { es: "Asignar a" },
  "On shift": { es: "En turno" },
  "Nobody is assigned to this site yet.": { es: "Nadie est\u00e1 asignado a este sitio todav\u00eda." },
  "This reason is emailed to the person who asked, if they left an email.": { es: "Este motivo se env\u00eda por correo a quien lo pidi\u00f3, si dej\u00f3 un correo." },
  "Why it needs someone else": { es: "Por qu\u00e9 necesita a otra persona" },
  "Note|request": { es: "Nota" },
  "Send to the person who asked": { es: "Enviar a quien lo pidi\u00f3" },
  "Save note": { es: "Guardar la nota" },
  "Note saved.": { es: "Nota guardada." },
  "Note saved and emailed to the person who asked.": { es: "Nota guardada y enviada por correo a quien la pidi\u00f3." },
  "Sent to the person who asked {0}": { es: "Enviada a quien lo pidi\u00f3 el {0}" },
  "Not routed yet": { es: "Sin clasificar todav\u00eda" },
  "Within scope": { es: "Dentro del alcance" },
  "Minor extra, absorbed": { es: "Extra menor, absorbido" },
  "Chargeable": { es: "Facturable" },
  "Out of scope": { es: "Fuera del alcance" },
  "Change of service": { es: "Cambio de servicio" },
  "Absorbed minutes": { es: "Minutos absorbidos" },
  "Asked again": { es: "Pedida otra vez" },
  "Started|request": { es: "Iniciada" },
  "Routed|request": { es: "Clasificada" },
  "Reopened|request": { es: "Reabierta" },
  "Emailed: {0}": { es: "Correo enviado: {0}" },
  "received|mail": { es: "recibida" },
  "assigned|mail": { es: "asignada" },
  "done|mail": { es: "hecha" },
  "declined|mail": { es: "rechazada" },
  "note|mail": { es: "nota" },
  "joined|mail": { es: "unida" },
  "{0} already {1} this at {2}.": { es: "{0} ya {1} esto el {2}." },
  "approved|decided": { es: "aprob\u00f3" },
  "declined|decided": { es: "rechaz\u00f3" },
  "Someone": { es: "Alguien" },
  "Asked three or more times this quarter": { es: "Pedido tres o m\u00e1s veces este trimestre" },
  "The same request three or more times at one site in a quarter raises a change of service.": { es: "La misma solicitud tres o m\u00e1s veces en un sitio en un trimestre plantea un cambio de servicio." },
  "Nothing yet this quarter.": { es: "Nada todav\u00eda este trimestre." },
  "{0} times|count": {
    en: { one: "{0} time", other: "{0} times" },
    es: { one: "{0} vez", other: "{0} veces" },
  },
  "All sources": { es: "Todos los or\u00edgenes" },
  "Staff|source": { es: "Personal" },
  "Set status": { es: "Cambiar el estado" },
  "Open in Client requests": { es: "Abrir en Solicitudes de clientes" },
  "A request waiting for approval or declined is decided on the Client requests tab.": { es: "Una solicitud en espera de aprobaci\u00f3n o rechazada se decide en la pesta\u00f1a Solicitudes de clientes." },
  "Against their targets": { es: "Frente a sus metas" },
  "Each issue the report judged, response then resolution": { es: "Cada incidencia que el informe evalu\u00f3, respuesta y luego resoluci\u00f3n" },
  "Response|target": { es: "Respuesta" },
  "Resolution|target": { es: "Resoluci\u00f3n" },
  "Met|target": { es: "Cumplida" },
  "Missed|target": { es: "Incumplida" },
  "No issue was judged in this range.": { es: "Ninguna incidencia fue evaluada en este periodo." },
  "No target is set by severity. A client request is judged against its own respond-by and due times; a staff issue carries no target.": { es: "No hay meta por gravedad. Una solicitud de cliente se eval\u00faa frente a sus propios tiempos de respuesta y de vencimiento; una incidencia del personal no tiene meta." },
  "Print label": { es: "Imprimir la etiqueta" },
  "Print labels for a site": { es: "Imprimir las etiquetas de un sitio" },
  "{0} m|minutes": { es: "{0} min" },
  "{0} h {1} m": { es: "{0} h {1} min" },
  "{0} d {1} h": { es: "{0} d {1} h" },
  "Meets standard": { es: "Cumple el est\u00e1ndar" },
  "Below standard": { es: "Por debajo del est\u00e1ndar" },
  "Failed|band": { es: "Reprobada" },
  "Serious failure": { es: "Falla grave" },
  "Inspection|source": { es: "Inspecci\u00f3n" },
  "Time to fixed": { es: "Tiempo hasta la correcci\u00f3n" },
  "From an inspection": { es: "De una inspecci\u00f3n" },
  "completed {0}": { es: "completada el {0}" },
  "Scored {0} percent": { es: "Puntuaci\u00f3n de {0} por ciento" },
  "Item: {0}, scored {1} of {2}": { es: "Punto: {0}, con {1} de {2}" },
  "Corrective action": { es: "Acci\u00f3n correctiva" },
  "OCSA-FRM-010, closed {0}.": { es: "OCSA-FRM-010, cerrada el {0}." },
  "OCSA-FRM-010, still open.": { es: "OCSA-FRM-010, todav\u00eda abierta." },
  "Client told {0}.": { es: "Cliente informado el {0}." },
  "Open the corrective action": { es: "Abrir la acci\u00f3n correctiva" },
  "Not linked to a corrective action yet. Link it from the corrective action's window on Forms.": { es: "Todav\u00eda no est\u00e1 vinculado a una acci\u00f3n correctiva. Vinc\u00falelo desde la ventana de la acci\u00f3n correctiva en Formularios." },
  "Verified {0} by {1}.": { es: "Verificado el {0} por {1}." },
  "Someone other than the person who fixed it checks it.": { es: "Alguien distinto de quien lo corrigi\u00f3 lo revisa." },
  "Verifying closes the finding.": { es: "Al verificar, el hallazgo se cierra." },
  "Verified": { es: "Verificado" },
  "Band": { es: "Banda" },
  "OCSA-QMS-014 5.2": { es: "OCSA-QMS-014 5.2" },
  "Findings": { es: "Hallazgos" },
  "Finding": { es: "Hallazgo" },
  "Owner|finding": { es: "Responsable" },
  "Fixed|finding": { es: "Corregido" },
  "Checked|finding": { es: "Verificado" },
  "No deficient item. No finding was opened.": { es: "Ning\u00fan punto deficiente. No se abri\u00f3 ning\u00fan hallazgo." },
  "A finding opens in the Issue Tracker, where it is verified.": { es: "Un hallazgo se abre en Incidencias, donde se verifica." },
  "A corrective action (OCSA-FRM-010) is required": { es: "Se requiere una acci\u00f3n correctiva (OCSA-FRM-010)" },
  "The site scored under 80 percent. Start the corrective action for the site and link its findings from the report's window.": { es: "El sitio obtuvo menos del 80 por ciento. Inicie la acci\u00f3n correctiva del sitio y vincule sus hallazgos desde la ventana del reporte." },
  "Start a corrective action": { es: "Iniciar una acci\u00f3n correctiva" },
  "This account cannot start a corrective action from here.": { es: "Esta cuenta no puede iniciar una acci\u00f3n correctiva desde aqu\u00ed." },
  "Linked findings": { es: "Hallazgos vinculados" },
  "No finding is linked yet.": { es: "Todav\u00eda no hay ning\u00fan hallazgo vinculado." },
  "Verified {0}": { es: "Verificado el {0}" },
  "Link findings": { es: "Vincular hallazgos" },
  "The open findings at this site. Untick any that this corrective action does not cover.": { es: "Los hallazgos abiertos en este sitio. Desmarque los que esta acci\u00f3n correctiva no cubre." },
  "No open finding at this site is waiting to be linked.": { es: "Ning\u00fan hallazgo abierto en este sitio espera ser vinculado." },
  "Link|findings": { es: "Vincular" },
  "Client told {0} by {1}.": { es: "Cliente informado el {0} por {1}." },
  "Client not told yet": { es: "Cliente sin informar" },
  "Tell the client": { es: "Informar al cliente" },
  "Name no employee. The client hears what {0} did.": { es: "No nombre a ning\u00fan empleado. El cliente se entera de lo que {0} hizo." },
  "(survey contact)": { es: "(contacto de la encuesta)" },
  "(site contact)": { es: "(contacto del sitio)" },
  "What was done": { es: "Qu\u00e9 se hizo" },
  "What will prevent it": { es: "Qu\u00e9 lo evitar\u00e1" },
  "Sent to {0}; {1} did not go.": { es: "Enviado a {0}; {1} no salieron." },
  "Sent to {0}.": { es: "Enviado a {0}." },
  "Inspection findings": { es: "Hallazgos de inspecci\u00f3n" },
  "Findings opened by inspections, the median hours to their first response and to their fix, how many of the fixed were fixed by their due date, and how many were still unfixed at the end of the period, as OCSA-FRM-011 version 2 counts them.": { es: "Hallazgos abiertos por inspecciones, la mediana de horas hasta su primera respuesta y hasta su correcci\u00f3n, cu\u00e1ntos de los corregidos se corrigieron antes de su fecha de vencimiento y cu\u00e1ntos segu\u00edan sin corregir al final del per\u00edodo, tal como los cuenta OCSA-FRM-011 versi\u00f3n 2." },
  "Measure": { es: "Medida" },
  "This period": { es: "Este per\u00edodo" },
  "Year to date": { es: "Acumulado del a\u00f1o" },
  "Findings opened": { es: "Hallazgos abiertos" },
  "Time to fixed (h, median)": { es: "Tiempo hasta la correcci\u00f3n (h, mediana)" },
  "Fixed by due": { es: "Corregidos a tiempo" },
  "Open at the end of the period": { es: "Abiertos al final del per\u00edodo" },
  // Step 257: the training catalog, who needs a topic, gaps, logging a session, the lesson editor,
  // signing off training and the printed training record.
  "Required training ({0})": { es: "Capacitaci\u00f3n requerida ({0})" },
  "Records|training": { es: "Registros" },
  "Gaps|training": { es: "Brechas" },
  "Catalog": { es: "Cat\u00e1logo" },
  "Awaiting sign-off": { es: "Por firmar" },
  "Pick a topic": { es: "Elija un tema" },
  "Pick the trainer or type their name": { es: "Elija al instructor o escriba su nombre" },
  "Pick a topic...": { es: "Elija un tema..." },
  "Safety critical": { es: "Cr\u00edtico para la seguridad" },
  "Type a name": { es: "Escribir un nombre" },
  "Trainer's name": { es: "Nombre del instructor" },
  "Already had it that day": { es: "Ya la ten\u00eda ese d\u00eda" },
  "Retired|topic": { es: "Dado de baja" },
  "When due": { es: "Cu\u00e1ndo se requiere" },
  "Per site": { es: "Por sitio" },
  "Course link": { es: "Enlace del curso" },
  "Open the course": { es: "Abrir el curso" },
  "Who it is for": { es: "Para qui\u00e9n es" },
  "Active topics": { es: "Temas activos" },
  "Retired topics": { es: "Temas dados de baja" },
  "All topics": { es: "Todos los temas" },
  "Add a topic": { es: "Agregar un tema" },
  "No topics yet.": { es: "A\u00fan no hay temas." },
  "No topics here.": { es: "No hay temas aqu\u00ed." },
  "Details": { es: "Detalles" },
  "Who needs it": { es: "Qui\u00e9n la necesita" },
  "Lesson": { es: "Lecci\u00f3n" },
  "Topic restored": { es: "Tema restaurado" },
  "Topic retired": { es: "Tema dado de baja" },
  "Name in English": { es: "Nombre en ingl\u00e9s" },
  "Name in Spanish": { es: "Nombre en espa\u00f1ol" },
  "Not written yet": { es: "A\u00fan sin escribir" },
  "Name in French": { es: "Nombre en franc\u00e9s" },
  "Evidence form": { es: "Formulario de constancia" },
  "Names on older records": { es: "Nombres en registros anteriores" },
  "Restore": { es: "Restaurar" },
  "Retire this topic? Nobody is asked for it while it is retired.": { es: "\u00bfDar de baja este tema? Nadie lo tiene pendiente mientras est\u00e9 dado de baja." },
  "Key": { es: "Clave" },
  "e.g. spill_response": { es: "p. ej. respuesta_derrames" },
  "Lowercase letters, numbers and underscores, starting with a letter. It cannot change later.": { es: "Letras min\u00fasculas, n\u00fameros y guiones bajos, empezando por una letra. No se puede cambiar despu\u00e9s." },
  "e.g. OCSA-HR-016": { es: "p. ej. OCSA-HR-016" },
  "e.g. 5.5": { es: "p. ej. 5.5" },
  "One record for each site the person works at.": { es: "Un registro por cada sitio donde trabaja la persona." },
  "A lesson on this topic needs a trainer to watch a demonstration.": { es: "Una lecci\u00f3n de este tema necesita que un instructor observe una demostraci\u00f3n." },
  "An https address for a course taken outside the app. It opens in a new tab.": { es: "Una direcci\u00f3n https de un curso que se toma fuera de la aplicaci\u00f3n. Se abre en una pesta\u00f1a nueva." },
  "e.g. OCSA-FRM-033": { es: "p. ej. OCSA-FRM-033" },
  "A form filed about the person at the site that counts as a record.": { es: "Un formulario presentado sobre la persona en el sitio que cuenta como registro." },
  "One name a line. A record under one of these names counts for this topic.": { es: "Un nombre por l\u00ednea. Un registro con uno de estos nombres cuenta para este tema." },
  "Add the topic": { es: "Agregar el tema" },
  "Roles": { es: "Roles" },
  "No role yet.": { es: "A\u00fan no hay ning\u00fan rol." },
  "Only at sites with: {0}": { es: "Solo en sitios con: {0}" },
  "At every site": { es: "En todos los sitios" },
  "Named people": { es: "Personas nombradas" },
  "Nobody named.": { es: "Nadie nombrado." },
  "Edit who needs it": { es: "Editar qui\u00e9n la necesita" },
  "Only at sites with these service lines. None ticked means every site.": { es: "Solo en sitios con estas l\u00edneas de servicio. Sin ninguna marcada, en todos los sitios." },
  "Name a person": { es: "Nombrar a una persona" },
  "Name a person...": { es: "Nombrar a una persona..." },
  "Add|person": { es: "Agregar" },
  "{0} of 30 rows": { es: "{0} de 30 filas" },
  "Needs an in-person session": { es: "Necesita una sesi\u00f3n en persona" },
  "Training gaps": { es: "Brechas de capacitaci\u00f3n" },
  "No topic asks anything of these people.": { es: "Ning\u00fan tema les pide nada a estas personas." },
  "Nobody matches these filters.": { es: "Nadie coincide con estos filtros." },
  "No site assigned": { es: "Sin sitio asignado" },
  "No training is asked of this person.": { es: "No se le pide ninguna capacitaci\u00f3n a esta persona." },
  "Expires {0}": { es: "Vence el {0}" },
  "Online lesson: {0} of {1} tries used": { es: "Lecci\u00f3n en l\u00ednea: {0} de {1} intentos usados" },
  "Try {0}": { es: "Intento {0}" },
  "Passed|training": { es: "Aprobado" },
  "Not passed": { es: "No aprobado" },
  "Signed off by {0}, {1}": { es: "Firmado por el instructor {0}, {1}" },
  "Waiting for trainer": { es: "Espera al instructor" },
  "Signed {0}": { es: "Firmado el {0}" },
  "Retire the live lesson? Staff can no longer take it, and the attempts already taken stay.": { es: "\u00bfDar de baja la lecci\u00f3n vigente? El personal ya no puede tomarla, y los intentos ya hechos se conservan." },
  "Lesson retired": { es: "Lecci\u00f3n dada de baja" },
  "Draft|lesson": { es: "Borrador" },
  "A passage this version cites has changed since it was cited.": { es: "Un pasaje que cita esta versi\u00f3n cambi\u00f3 desde que se cit\u00f3." },
  "Stale": { es: "Desactualizada" },
  "Change note": { es: "Nota del cambio" },
  "Attempts": { es: "Intentos" },
  "No lesson is live for this topic.": { es: "Este tema no tiene una lecci\u00f3n vigente." },
  "No lesson written yet.": { es: "A\u00fan no hay ninguna lecci\u00f3n escrita." },
  "Open the draft": { es: "Abrir el borrador" },
  "New draft from the live lesson": { es: "Nuevo borrador a partir de la lecci\u00f3n vigente" },
  "New blank draft": { es: "Nuevo borrador en blanco" },
  "Retire the live lesson": { es: "Dar de baja la lecci\u00f3n vigente" },
  "Draft saved": { es: "Borrador guardado" },
  "The empty Spanish and French are filled. Have them checked.": { es: "Se completaron el espa\u00f1ol y el franc\u00e9s vac\u00edos. H\u00e1galos revisar." },
  "Published as version {0}": { es: "Publicada como versi\u00f3n {0}" },
  "Published|lesson": { es: "Publicada" },
  "Discard this draft? It cannot be published once discarded.": { es: "\u00bfDescartar este borrador? Una vez descartado no se puede publicar." },
  "Draft discarded": { es: "Borrador descartado" },
  "Back to the versions": { es: "Volver a las versiones" },
  "Changes not saved": { es: "Cambios sin guardar" },
  "Saved|draft": { es: "Guardado" },
  "A passage this draft cites has changed since it was cited.": { es: "Un pasaje que cita este borrador cambi\u00f3 desde que se cit\u00f3." },
  "Save the draft to check it again.": { es: "Guarde el borrador para revisarlo de nuevo." },
  "Blocks": { es: "Bloques" },
  "Kind|block": { es: "Tipo" },
  "The line before the list": { es: "La l\u00ednea antes de la lista" },
  "Warning|block": { es: "Advertencia" },
  "Text|block": { es: "Texto" },
  "List|block": { es: "Lista" },
  "Item {0}": { es: "Elemento {0}" },
  "Remove the item": { es: "Quitar el elemento" },
  "Add an item": { es: "Agregar un elemento" },
  "Cites {0}": { es: "Cita {0}" },
  "Cite another passage": { es: "Citar otro pasaje" },
  "Cite a passage": { es: "Citar un pasaje" },
  "Remove the citation": { es: "Quitar la cita" },
  "{0} of 5 to 10": { es: "{0} de 5 a 10" },
  "Question {0}": { es: "Pregunta {0}" },
  "The right answer": { es: "La respuesta correcta" },
  "Answer {0}": { es: "Respuesta {0}" },
  "Remove the answer": { es: "Quitar la respuesta" },
  "Add an answer": { es: "Agregar una respuesta" },
  "Add a question": { es: "Agregar una pregunta" },
  "Acknowledgement": { es: "Declaraci\u00f3n" },
  "The line the person signs under once they pass.": { es: "La l\u00ednea que la persona firma una vez que aprueba." },
  "Taking it": { es: "Al tomarla" },
  "Pass mark (percent)": { es: "Nota para aprobar (porcentaje)" },
  "Tries": { es: "Intentos" },
  "Needs a trainer": { es: "Necesita un instructor" },
  "Safety topics need a trainer to watch a demonstration (OCSA-HR-016 5.5).": { es: "Los temas de seguridad necesitan que un instructor observe una demostraci\u00f3n (OCSA-HR-016 5.5)." },
  "A trainer signs off after watching the person do it.": { es: "Un instructor firma despu\u00e9s de ver a la persona hacerlo." },
  "Languages": { es: "Idiomas" },
  "Translating...": { es: "Traduciendo..." },
  "Translate": { es: "Traducir" },
  "Fills the empty Spanish and French. A language it changes loses its checker's name.": { es: "Completa el espa\u00f1ol y el franc\u00e9s vac\u00edos. Un idioma que cambia pierde el nombre de quien lo revis\u00f3." },
  "Spanish checked by": { es: "Espa\u00f1ol revisado por" },
  "A safety lesson is published only once someone fluent has checked the Spanish.": { es: "Una lecci\u00f3n de seguridad se publica solo despu\u00e9s de que alguien con fluidez revise el espa\u00f1ol." },
  "French checked by": { es: "Franc\u00e9s revisado por" },
  "Staff see the French of a safety lesson only once a French checker is named.": { es: "El personal ve el franc\u00e9s de una lecci\u00f3n de seguridad solo cuando se indica qui\u00e9n revis\u00f3 el franc\u00e9s." },
  "What changed in this version": { es: "Qu\u00e9 cambi\u00f3 en esta versi\u00f3n" },
  "Discard": { es: "Descartar" },
  "Save draft": { es: "Guardar borrador" },
  "Every document": { es: "Todos los documentos" },
  "No passage found.": { es: "No se encontr\u00f3 ning\u00fan pasaje." },
  "Cite": { es: "Citar" },
  "Sign off": { es: "Firmar como instructor" },
  "Nobody is waiting for a sign-off.": { es: "Nadie espera la firma de un instructor." },
  "Sign first.": { es: "Firme primero." },
  "Tick that you watched them do it.": { es: "Marque que vio a la persona hacerlo." },
  "Signed off. The training record is saved.": { es: "Firmado. El registro de capacitaci\u00f3n qued\u00f3 guardado." },
  "Sign off training": { es: "Firmar una capacitaci\u00f3n como instructor" },
  "Your signature, as the trainer": { es: "Su firma, como instructor" },
  "I watched them do it": { es: "Vi a la persona hacerlo" },
  "Required. Tick it only once you have seen the person do it themselves.": { es: "Obligatorio. M\u00e1rquelo solo despu\u00e9s de ver a la persona hacerlo por s\u00ed misma." },
  "What they showed you": { es: "Lo que le mostr\u00f3" },
  "Print the record": { es: "Imprimir el registro" },
  "Training record": { es: "Registro de capacitaci\u00f3n" },
  "Pass mark": { es: "Nota para aprobar" },
  "Try": { es: "Intento" },
  "Signatures": { es: "Firmas" },
  "The trainer": { es: "El instructor" },
  "Watched them do it": { es: "Vio a la persona hacerlo" },
  "Note: {0}": { es: "Nota: {0}" },
  "Waiting for a trainer's sign-off.": { es: "En espera de la firma de un instructor." },
  "Once|training": { es: "Una vez" },
  "Every year": { es: "Cada a\u00f1o" },
  "Every five years": { es: "Cada cinco a\u00f1os" },
  "On change|training": { es: "Cuando cambia" },
  "On the first day": { es: "El primer d\u00eda" },
  "Before the first shift": { es: "Antes del primer turno" },
  "Before working without a supervisor": { es: "Antes de trabajar sin un supervisor" },
  "Before handling any product": { es: "Antes de manipular cualquier producto" },
  "Before doing the task": { es: "Antes de hacer la tarea" },
  "Before being given access": { es: "Antes de recibir acceso" },
  "Before a shift at a school": { es: "Antes de un turno en una escuela" },
  "Before supervising anyone": { es: "Antes de supervisar a alguien" },
  "Missing|training": { es: "Falta" },
  "Expired|training": { es: "Vencida" },
  "Refresher due": { es: "Repaso pendiente" },
  "Due soon|training": { es: "Vence pronto" },
  "In progress|training": { es: "En curso" },
  "Current|training": { es: "Vigente" },
  "Required|training": { es: "Requeridas" },
  "Retired|lesson": { es: "Dada de baja" },
  "Discarded|lesson": { es: "Descartada" },
  "Expires": { es: "Vence" },
  "Showing": { es: "Mostrando" },
  "{0} named person|count": {
    en: { one: "{0} named person", other: "{0} named people" },
    es: { one: "{0} persona nombrada", other: "{0} personas nombradas" },
  },
  "{0} topic|count": {
    en: { one: "{0} topic", other: "{0} topics" },
    es: { one: "{0} tema", other: "{0} temas" },
  },
  "Saved for {0} person|count": {
    en: { one: "Saved for {0} person", other: "Saved for {0} people" },
    es: { one: "Guardado para {0} persona", other: "Guardado para {0} personas" },
  },
  "{0} person|count": {
    en: { one: "{0} person", other: "{0} people" },
    es: { one: "{0} persona", other: "{0} personas" },
  },
  "{0} open|training": {
    en: { one: "{0} open", other: "{0} open" },
    es: { one: "{0} pendiente", other: "{0} pendientes" },
  },
  "{0} problem to fix before it is published|count": {
    en: { one: "{0} problem to fix before it is published", other: "{0} problems to fix before it is published" },
    es: { one: "{0} problema por corregir antes de publicarla", other: "{0} problemas por corregir antes de publicarla" },
  },
  "{0} waiting for a trainer|count": {
    en: { one: "{0} waiting for a trainer", other: "{0} waiting for a trainer" },
    es: { one: "{0} espera al instructor", other: "{0} esperan al instructor" },
  },
  // Step 263: sessions signed on phones, observation checklists, certificates, documents to sign,
  // company property, and voiding an attempt.
  "Documents to sign": { es: "Documentos por firmar" },
  "Start a session": { es: "Iniciar una sesi\u00f3n" },
  "No sessions here.": { es: "No hay sesiones aqu\u00ed." },
  "Day": { es: "D\u00eda" },
  "Open|session": { es: "Abierta" },
  "Closed|session": { es: "Cerrada" },
  "Cancelled|session": { es: "Cancelada" },
  "e.g. Monthly safety talk": { es: "p. ej. Charla mensual de seguridad" },
  "Language it is given in": { es: "Idioma en que se imparte" },
  "Topics, 1 to 10": { es: "Temas, de 1 a 10" },
  "Start the session": { es: "Iniciar la sesi\u00f3n" },
  "Back to the sessions": { es: "Volver a las sesiones" },
  "Trainer: {0}": { es: "Instructor: {0}" },
  "QR code to sign in": { es: "C\u00f3digo QR para registrarse" },
  "Scan the code, or type this code in the staff app": { es: "Escanee el c\u00f3digo o escriba este c\u00f3digo en la aplicaci\u00f3n del personal" },
  "The list is read again every 5 seconds.": { es: "La lista se vuelve a leer cada 5 segundos." },
  "Nobody has signed in yet.": { es: "Nadie se ha registrado todav\u00eda." },
  "Remove this sign-in? The person can sign in again.": { es: "\u00bfQuitar este registro? La persona puede volver a registrarse." },
  "Closing saves a training record for each person and topic. Sign as the trainer.": { es: "Al cerrar se guarda un registro de capacitaci\u00f3n por cada persona y tema. Firme como instructor." },
  "Close the session": { es: "Cerrar la sesi\u00f3n" },
  "Session closed. The training records are saved.": { es: "Sesi\u00f3n cerrada. Los registros de capacitaci\u00f3n est\u00e1n guardados." },
  "Cancel the session": { es: "Cancelar la sesi\u00f3n" },
  "Cancel this session? Nothing is saved for anyone who signed in.": { es: "\u00bfCancelar esta sesi\u00f3n? No se guarda nada para quienes se registraron." },
  "Session cancelled": { es: "Sesi\u00f3n cancelada" },
  "Print the roster": { es: "Imprimir la lista de asistencia" },
  "Signed at": { es: "Firmado el" },
  "In person, each attendee signed in on the app": { es: "En persona, cada asistente se registr\u00f3 en la aplicaci\u00f3n" },
  "Each attendee ticked I understood this training": { es: "Cada asistente marc\u00f3 Entend\u00ed esta capacitaci\u00f3n" },
  "Kind|lesson": { es: "Tipo" },
  "Quiz": { es: "Cuestionario" },
  "Observation checklist": { es: "Lista de observaci\u00f3n" },
  "The person reads the lesson and answers 5 to 10 questions.": { es: "La persona lee la lecci\u00f3n y responde de 5 a 10 preguntas." },
  "A trainer watches the person do each step on the job and ticks it. It has no questions.": { es: "Un instructor observa a la persona hacer cada paso en el trabajo y lo marca. No tiene preguntas." },
  "Steps": { es: "Pasos" },
  "{0} of 1 to 30": { es: "{0} de 1 a 30" },
  "Each step is one thing the trainer watches the person do.": { es: "Cada paso es una cosa que el instructor observa hacer a la persona." },
  "Step {0}": { es: "Paso {0}" },
  "Add a step": { es: "Agregar un paso" },
  "The line the person signs once the trainer has watched every step.": { es: "La l\u00ednea que la persona firma una vez que el instructor ha observado cada paso." },
  "An observation checklist always needs a trainer, who watches the person do each step.": { es: "Una lista de observaci\u00f3n siempre necesita un instructor, que observa a la persona hacer cada paso." },
  "Upload a certificate": { es: "Subir un certificado" },
  "Open the certificate": { es: "Abrir el certificado" },
  "Certificate saved": { es: "Certificado guardado" },
  "Left empty, the expiry is set by how often the topic is taken.": { es: "Si se deja vac\u00eda, el vencimiento se fija seg\u00fan la frecuencia del tema." },
  "This topic is taken at each site. Pick the site this certificate counts for.": { es: "Este tema se toma en cada sitio. Elija el sitio para el que cuenta este certificado." },
  "The certificate, a PDF, JPEG or PNG up to 10 MB": { es: "El certificado, un PDF, JPEG o PNG de hasta 10 MB" },
  "Choose a PDF, JPEG or PNG file.": { es: "Elija un archivo PDF, JPEG o PNG." },
  "The file is over 10 MB.": { es: "El archivo pasa de 10 MB." },
  "Each document people must read and sign, and how many have signed its current version.": { es: "Cada documento que el personal debe leer y firmar, y cu\u00e1ntos han firmado su versi\u00f3n vigente." },
  "No documents to sign yet.": { es: "A\u00fan no hay documentos por firmar." },
  "Must sign": { es: "Deben firmar" },
  "Signed|document": { es: "Firmado" },
  "Signed an older version": { es: "Firm\u00f3 una versi\u00f3n anterior" },
  "Who must sign": { es: "Qui\u00e9n debe firmar" },
  "Change who must sign": { es: "Cambiar qui\u00e9n debe firmar" },
  "The list in force is not sent with this document yet.": { es: "La lista vigente todav\u00eda no se env\u00eda con este documento." },
  "Nobody must sign it yet.": { es: "Todav\u00eda nadie debe firmarlo." },
  "Saving replaces who must sign with what is ticked here. Someone added is told once.": { es: "Al guardar, qui\u00e9n debe firmar se reemplaza por lo marcado aqu\u00ed. A quien se agregue se le avisa una vez." },
  "Nobody must sign this document here.": { es: "Nadie debe firmar este documento aqu\u00ed." },
  "Open the signature": { es: "Abrir la firma" },
  "Print for the assessor": { es: "Imprimir para el auditor" },
  "Document signatures": { es: "Firmas del documento" },
  "Signed the current version": { es: "Firmaron la versi\u00f3n vigente" },
  "Version signed": { es: "Versi\u00f3n firmada" },
  "Company property": { es: "Propiedad de la empresa" },
  "Issue property": { es: "Entregar propiedad" },
  "Uniforms, keys, badges and fobs this person was given, each signed for when it was handed over.": { es: "Uniformes, llaves, credenciales y llaveros electr\u00f3nicos que se le dieron a esta persona, cada uno firmado al entregarse." },
  "Issued {0}": { es: "Entregado el {0}" },
  "Returned {0}": { es: "Devuelto el {0}" },
  "Returned|property": { es: "Devuelto" },
  "Still out": { es: "Sin devolver" },
  "Mark returned": { es: "Marcar como devuelto" },
  "Property issue saved.": { es: "Entrega de propiedad guardada." },
  "Marked returned.": { es: "Marcado como devuelto." },
  "Uniform shirt": { es: "Camisa de uniforme" },
  "Other uniform": { es: "Otro uniforme" },
  "Key|property": { es: "Llave" },
  "Badge|property": { es: "Credencial" },
  "Fob": { es: "Llavero electr\u00f3nico" },
  "Other|property": { es: "Otro" },
  "Description (optional)": { es: "Descripci\u00f3n (opcional)" },
  "Which door or closet": { es: "Qu\u00e9 puerta o armario" },
  "Returned on": { es: "Fecha de devoluci\u00f3n" },
  "Collect before the last day": { es: "Recoger antes del \u00faltimo d\u00eda" },
  "No company property is still out.": { es: "No queda propiedad de la empresa sin devolver." },
  "Void|attempt": { es: "Anular" },
  "Void this attempt": { es: "Anular este intento" },
  "The training record written from it is removed with it. Why is it being voided?": { es: "El registro de capacitaci\u00f3n que gener\u00f3 se quita con \u00e9l. \u00bfPor qu\u00e9 se anula?" },
  "{0} of 500": { es: "{0} de 500" },
  "Attempt voided.": { es: "Intento anulado." },
  "{0} session|count": {
    en: { one: "{0} session", other: "{0} sessions" },
    es: { one: "{0} sesi\u00f3n", other: "{0} sesiones" },
  },
  "{0} topic picked|count": {
    en: { one: "{0} topic picked", other: "{0} topics picked" },
    es: { one: "{0} tema elegido", other: "{0} temas elegidos" },
  },
  "{0} signed in|count": {
    en: { one: "{0} signed in", other: "{0} signed in" },
    es: { one: "{0} registrado", other: "{0} registrados" },
  },
  "{0} training record saved|count": {
    en: { one: "{0} training record saved", other: "{0} training records saved" },
    es: { one: "{0} registro de capacitaci\u00f3n guardado", other: "{0} registros de capacitaci\u00f3n guardados" },
  },
  "{0} must sign|count": {
    en: { one: "{0} must sign", other: "{0} must sign" },
    es: { one: "{0} debe firmar", other: "{0} deben firmar" },
  },
  "{0} not signed|count": {
    en: { one: "{0} not signed", other: "{0} not signed" },
    es: { one: "{0} sin firmar", other: "{0} sin firmar" },
  },
  "{0} still out.|count": {
    en: { one: "{0} still out.", other: "{0} still out." },
    es: { one: "{0} sin devolver.", other: "{0} sin devolver." },
  },
  // Step 268: the catalog by category, pictures in lessons, the Drafts tab, the Spanish checker and
  // the language chips, checklist coverage, and the owner's Training item and Assign training of
  // October 6.
  "Start here": { es: "Para empezar" },
  "Safety at work": { es: "Seguridad en el trabajo" },
  "Chemicals": { es: "Productos qu\u00edmicos" },
  "Cleaning methods": { es: "M\u00e9todos de limpieza" },
  "Floor care": { es: "Cuidado de pisos" },
  "Equipment|category": { es: "Equipo" },
  "Customer service": { es: "Servicio al cliente" },
  "Building security": { es: "Seguridad del edificio" },
  "For supervisors": { es: "Para supervisores" },
  "Other trainings": { es: "Otras capacitaciones" },
  "Order|topic": { es: "Orden" },
  "Up|order": { es: "Subir" },
  "Down|order": { es: "Bajar" },
  "None|category": { es: "Ninguna" },
  "None|checklist": { es: "Ninguna" },
  "Signed off by checklist": { es: "Firmado con la lista de verificaci\u00f3n" },
  "The order is a whole number from 0 to 9999.": { es: "El orden es un n\u00famero entero de 0 a 9999." },
  "Staff see the catalog by category, in this order. A topic with none is listed last under Other trainings.": { es: "El personal ve el cat\u00e1logo por categor\u00eda, en este orden. Un tema sin categor\u00eda aparece al final, en Otras capacitaciones." },
  "A whole number from 0 to 9999. Lower comes first inside the category.": { es: "Un n\u00famero entero de 0 a 9999. El menor va primero dentro de la categor\u00eda." },
  "When a trainer signs that checklist off for a person, this topic is signed off with it.": { es: "Cuando un instructor firma esa lista de verificaci\u00f3n para una persona, este tema queda firmado con ella." },
  "No topic has an observation checklist live yet.": { es: "Ning\u00fan tema tiene todav\u00eda una lista de observaci\u00f3n vigente." },
  "Every topic is assigned automatically to the roles under Who needs it. Use Assign training for anyone else.": { es: "Cada tema se asigna autom\u00e1ticamente a los roles de Qui\u00e9n la necesita. Use Asignar capacitaci\u00f3n para cualquier otra persona." },
  "Assign training": { es: "Asignar capacitaci\u00f3n" },
  "Drafts|lessons": { es: "Borradores" },
  "Covered by {0}": { es: "Cubierto por {0}" },
  "A trainer signing that checklist off for this person signs this off with it.": { es: "Cuando un instructor firma esa lista de verificaci\u00f3n para esta persona, esto queda firmado con ella." },
  "Signing this checklist off also signs off every module of this person that names it.": { es: "Firmar esta lista de verificaci\u00f3n tambi\u00e9n firma cada m\u00f3dulo de esta persona que la nombra." },
  "Also signed: {0} module|count": {
    en: { one: "Also signed: {0} module", other: "Also signed: {0} modules" },
    es: { one: "Tambi\u00e9n firmado: {0} m\u00f3dulo", other: "Tambi\u00e9n firmados: {0} m\u00f3dulos" },
  },
  "{0} module returned to waiting for a trainer.|count": {
    en: { one: "{0} module returned to waiting for a trainer.", other: "{0} modules returned to waiting for a trainer." },
    es: { one: "{0} m\u00f3dulo volvi\u00f3 a esperar al instructor.", other: "{0} m\u00f3dulos volvieron a esperar al instructor." },
  },
  "This returns {0} module to waiting for a trainer.|count": {
    en: { one: "This returns {0} module to waiting for a trainer.", other: "This returns {0} modules to waiting for a trainer." },
    es: { one: "Esto devuelve {0} m\u00f3dulo a la espera del instructor.", other: "Esto devuelve {0} m\u00f3dulos a la espera del instructor." },
  },
  "This returns every module this checklist signed off to waiting for a trainer.": { es: "Esto devuelve a la espera del instructor cada m\u00f3dulo que esta lista de verificaci\u00f3n firm\u00f3." },
  "Image|block": { es: "Imagen" },
  "Choose a JPEG, PNG, WebP or HEIC picture.": { es: "Elija una imagen JPEG, PNG, WebP o HEIC." },
  "The picture is over 5 MB.": { es: "La imagen pasa de 5 MB." },
  "Upload a picture": { es: "Subir una imagen" },
  "A JPEG, PNG, WebP or HEIC picture up to 5 MB.": { es: "Una imagen JPEG, PNG, WebP o HEIC de hasta 5 MB." },
  "Paste a drawing (SVG)": { es: "Pegar un dibujo (SVG)" },
  "Plain text starting with <svg, with xmlns and a viewBox. No scripts, links or outside images.": { es: "Texto sin formato que empieza con <svg, con xmlns y un viewBox. Sin scripts, enlaces ni im\u00e1genes externas." },
  "Picture uploaded": { es: "Imagen subida" },
  "Drawing pasted": { es: "Dibujo pegado" },
  "Remove the picture": { es: "Quitar la imagen" },
  "No picture yet. Upload one or paste a drawing.": { es: "Todav\u00eda no hay imagen. Suba una o pegue un dibujo." },
  "Alt text": { es: "Texto alternativo" },
  "What the picture shows, read aloud to someone who cannot see it. The English is required.": { es: "Lo que muestra la imagen, le\u00eddo en voz alta a quien no puede verla. El ingl\u00e9s es obligatorio." },
  "Caption": { es: "Pie de imagen" },
  "{0} of up to {1} pictures": { es: "{0} de hasta {1} im\u00e1genes" },
  "Add a picture": { es: "Agregar una imagen" },
  "Some of this version's French is not written yet. Staff are offered English and Spanish.": { es: "Parte del franc\u00e9s de esta versi\u00f3n todav\u00eda no est\u00e1 escrito. Al personal se le ofrece ingl\u00e9s y espa\u00f1ol." },
  "French missing": { es: "Falta el franc\u00e9s" },
  "Spanish checker saved.": { es: "Revisor del espa\u00f1ol guardado." },
  "Spanish checker cleared.": { es: "Revisor del espa\u00f1ol borrado." },
  "Version {0} is live.": { es: "La versi\u00f3n {0} est\u00e1 vigente." },
  "The name of whoever checked the Spanish": { es: "El nombre de quien revis\u00f3 el espa\u00f1ol" },
  "Staff are offered the Spanish as soon as it is written. Name whoever checked it once they have, and the chip clears.": { es: "Al personal se le ofrece el espa\u00f1ol en cuanto est\u00e1 escrito. Indique qui\u00e9n lo revis\u00f3 una vez revisado, y la etiqueta desaparece." },
  "The Spanish is offered to staff and no one has checked it yet.": { es: "El espa\u00f1ol se ofrece al personal y nadie lo ha revisado todav\u00eda." },
  "Spanish not checked yet": { es: "Espa\u00f1ol sin revisar" },
  "Notes that do not stop publishing": { es: "Notas que no impiden publicar" },
  "A safety lesson publishes without it, and staff are offered the Spanish as soon as it is written. Name whoever checked it, here or on the published lesson, and the Spanish not checked yet chip clears.": { es: "Una lecci\u00f3n de seguridad se publica sin \u00e9l, y al personal se le ofrece el espa\u00f1ol en cuanto est\u00e1 escrito. Indique qui\u00e9n lo revis\u00f3, aqu\u00ed o en la lecci\u00f3n publicada, y la etiqueta Espa\u00f1ol sin revisar desaparece." },
  "Publish {0} draft? Each is published on its own, and a refusal stops none of the others.|count": {
    en: { one: "Publish {0} draft? Each is published on its own, and a refusal stops none of the others.", other: "Publish {0} drafts? Each is published on its own, and a refusal stops none of the others." },
    es: { one: "\u00bfPublicar {0} borrador? Cada uno se publica por separado, y un rechazo no detiene a los dem\u00e1s.", other: "\u00bfPublicar {0} borradores? Cada uno se publica por separado, y un rechazo no detiene a los dem\u00e1s." },
  },
  "{0} lesson published|count": {
    en: { one: "{0} lesson published", other: "{0} lessons published" },
    es: { one: "{0} lecci\u00f3n publicada", other: "{0} lecciones publicadas" },
  },
  "Nothing was published.": { es: "No se public\u00f3 nada." },
  "Publish {0}": { es: "Publicar {0}" },
  "Fix the problems listed first.": { es: "Corrija primero los problemas de la lista." },
  "Problems": { es: "Problemas" },
  "Ready to publish": { es: "Lista para publicar" },
  "{0} problem|count": {
    en: { one: "{0} problem", other: "{0} problems" },
    es: { one: "{0} problema", other: "{0} problemas" },
  },
  "and {0} more": { es: "y {0} m\u00e1s" },
  "Every lesson draft still open, by category. Read each one in its topic's Lesson tab, then publish the ones that are ready in one pass.": { es: "Cada borrador de lecci\u00f3n todav\u00eda abierto, por categor\u00eda. Lea cada uno en la pesta\u00f1a Lecci\u00f3n de su tema y publique los que est\u00e9n listos de una vez." },
  "{0} draft|count": {
    en: { one: "{0} draft", other: "{0} drafts" },
    es: { one: "{0} borrador", other: "{0} borradores" },
  },
  "{0} ready|count": {
    en: { one: "{0} ready", other: "{0} ready" },
    es: { one: "{0} listo", other: "{0} listos" },
  },
  "Select all ready": { es: "Seleccionar los listos" },
  "Publish selected ({0})|count": {
    en: { one: "Publish selected ({0})", other: "Publish selected ({0})" },
    es: { one: "Publicar seleccionados ({0})", other: "Publicar seleccionados ({0})" },
  },
  "{0} draft refused|count": {
    en: { one: "{0} draft refused", other: "{0} drafts refused" },
    es: { one: "{0} borrador rechazado", other: "{0} borradores rechazados" },
  },
  "No draft is open.": { es: "No hay ning\u00fan borrador abierto." },
  "Pick the topics and the people. Each person is asked for each topic, on top of what their role already asks.": { es: "Elija los temas y las personas. A cada persona se le pide cada tema, adem\u00e1s de lo que su rol ya le pide." },
  "{0} added, {1} already assigned": { es: "{0} agregadas, {1} ya asignadas" },
  // Step 272: send it to their phone to sign.
  "Who signs": { es: "Qui\u00e9n firma" },
  "Send to their phone": { es: "Enviar a su tel\u00e9fono" },
  "Sign here now": { es: "Firmar aqu\u00ed ahora" },
  "{0} is told on their phone and signs there. The record waits for the signature until then.": { es: "{0} recibe el aviso en su tel\u00e9fono y firma ah\u00ed. El registro queda a la espera de la firma hasta entonces." },
  "The person is told on their phone and signs there. The record waits for the signature until then.": { es: "La persona recibe el aviso en su tel\u00e9fono y firma ah\u00ed. El registro queda a la espera de la firma hasta entonces." },
  "The person signs in the box on this screen.": { es: "La persona firma en el recuadro de esta pantalla." },
  "Send to sign": { es: "Enviar a firmar" },
  "Issue and send to sign": { es: "Emitir y enviar a firmar" },
  "Sent to {0}'s phone to sign.": { es: "Enviado al tel\u00e9fono de {0} para firmar." },
  "Sent to their phone to sign.": { es: "Enviado a su tel\u00e9fono para firmar." },
  "Waiting for signature": { es: "A la espera de firma" },
  "Not right": { es: "No es correcto" },
  "Signed|signature": { es: "Firmado" },
  "Declined|signature": { es: "Se neg\u00f3 a firmar" },
  "Cancelled|signature": { es: "Cancelada" },
  "on their phone": { es: "en su tel\u00e9fono" },
  "in the office": { es: "en la oficina" },
  "Remind": { es: "Recordar" },
  "Reminder sent.": { es: "Recordatorio enviado." },
  "Request cancelled.": { es: "Solicitud cancelada." },
  "Cancel this request? Do this when it was signed on paper or entered by mistake.": { es: "\u00bfCancelar esta solicitud? H\u00e1galo cuando se firm\u00f3 en papel o se registr\u00f3 por error." },
  "The person signs in the box, on whatever screen is in front of them.": { es: "La persona firma en el recuadro, en la pantalla que tenga delante." },
  "The person signs": { es: "Firma la persona" },
  "Signed in the office. It is on the record.": { es: "Firmado en la oficina. Qued\u00f3 en el registro." },
  "Waiting for signatures": { es: "A la espera de firmas" },
  "Every record sent to a phone to sign, Not right first and then the oldest. Remind sends the notice again, Cancel closes one signed on paper or entered by mistake, and Sign here now takes the signature on this screen.": { es: "Cada registro enviado a un tel\u00e9fono para firmar, primero los marcados No es correcto y luego los m\u00e1s antiguos. Recordar env\u00eda el aviso de nuevo, Cancelar cierra uno firmado en papel o registrado por error, y Firmar aqu\u00ed ahora toma la firma en esta pantalla." },
  "Property|signature": { es: "Propiedad" },
  "Warning|signature": { es: "Advertencia" },
  "{0} reminder|count": {
    en: { one: "{0} reminder", other: "{0} reminders" },
    es: { one: "{0} recordatorio", other: "{0} recordatorios" },
  },
  "Nothing is waiting for a signature.": { es: "No hay nada a la espera de firma." },
  "No request matches.": { es: "Ninguna solicitud coincide." },
  "From a key on file": { es: "Por una llave registrada" },
  "It is removed by itself when every key, badge and fob of theirs is marked returned.": { es: "Se quita por s\u00ed solo cuando todas sus llaves, credenciales y llaveros electr\u00f3nicos se marcan como devueltos." },
  "Signature request": { es: "Solicitud de firma" },
  "Reminders": { es: "Recordatorios" },
  // Step 273: the owner's dashboard, training time and the training matrix.
  "Owner's dashboard": { es: "Tablero del propietario" },
  "Q{0} {1}": { es: "T{0} {1}" },
  "{0} h|hours": { es: "{0} h" },
  "Needs:": { es: "Necesita:" },
  "Previous period: {0}": { es: "Periodo anterior: {0}" },
  "Previous period: no value": { es: "Periodo anterior: sin valor" },
  "Hide the sites": { es: "Ocultar los sitios" },
  "No value": { es: "Sin valor" },
  "Compared with {0}": { es: "Comparado con {0}" },
  "Nothing to show for this period.": { es: "No hay nada que mostrar para este periodo." },
  "Up|trend": { es: "Subi\u00f3" },
  "Down|trend": { es: "Baj\u00f3" },
  "Same|trend": { es: "Sin cambio" },
  "better|trend": { es: "mejor" },
  "worse|trend": { es: "peor" },
  "Previous period": { es: "Periodo anterior" },
  "Change|trend": { es: "Cambio" },
  "Time|training": { es: "Tiempo" },
  "Download CSV": { es: "Descargar CSV" },
  "Total, {0}": { es: "Total, {0}" },
  "Lessons passed": { es: "Lecciones aprobadas" },
  "Time in lessons": { es: "Tiempo en lecciones" },
  "Sessions signed": { es: "Sesiones firmadas" },
  "Checklists signed": { es: "Listas de verificaci\u00f3n firmadas" },
  "Pick 1 to 62 days, with To on or after From.": { es: "Elija de 1 a 62 d\u00edas, con Hasta igual o posterior a Desde." },
  "Nobody spent time in training in these days.": { es: "Nadie pas\u00f3 tiempo en capacitaci\u00f3n en estos d\u00edas." },
  "Minutes are time in phone lessons, each attempt capped at 60. Session hours are not recorded; add them from the session list.": { es: "Los minutos son el tiempo en lecciones en el tel\u00e9fono, con un m\u00e1ximo de 60 por intento. Las horas de las sesiones no se registran; agr\u00e9guelas desde la lista de sesiones." },
  "Matrix|training": { es: "Matriz" },
  "Training matrix": { es: "Matriz de capacitaci\u00f3n" },
  "All my sites": { es: "Todos mis sitios" },
  "Legend": { es: "Leyenda" },
  "A blank cell is a topic the person does not need.": { es: "Una celda en blanco es un tema que la persona no necesita." },
  "At each site": { es: "En cada sitio" },
  "How it was done": { es: "C\u00f3mo se hizo" },
  "Completed|training": { es: "Completada" },
  "Expires|training": { es: "Vence" },
  "No record yet": { es: "Sin registro todav\u00eda" },
  "Lesson on the phone": { es: "Lecci\u00f3n en el tel\u00e9fono" },
  "Training session": { es: "Sesi\u00f3n de capacitaci\u00f3n" },
  "Certificate from a course": { es: "Certificado de un curso" },
  "Checklist watched by a trainer": { es: "Lista de verificaci\u00f3n observada por un instructor" },
  "Record entered by the office": { es: "Registro ingresado por la oficina" },
  // Step 273: First due after (the API's Step 275) and the keys heads-up.
  "First due {0} months after {1}|count": {
    en: { one: "First due {0} month after {1}", other: "First due {0} months after {1}" },
    es: { one: "Vence por primera vez {0} mes despu\u00e9s de {1}", other: "Vence por primera vez {0} meses despu\u00e9s de {1}" },
  },
  "First due after {0}": { es: "Vence por primera vez despu\u00e9s de {0}" },
  "First due {0}": { es: "Vence por primera vez el {0}" },
  "First due after": { es: "Vence por primera vez despu\u00e9s de" },
  "None|first due": { es: "Ninguno" },
  "With a topic chosen, this one is first due its renewal months after the person first completes that topic, and nobody is asked for it before.": { es: "Con un tema elegido, este vence por primera vez sus meses de renovaci\u00f3n despu\u00e9s de que la persona completa ese tema por primera vez, y no se le pide a nadie antes." },
  "Keys and access is not done yet. OCSA-HR-013 Section 3 asks for it before any key, badge, fob or code is issued. If they already hold this one, record it; the training is assigned from the record.": { es: "Llaves y acceso todav\u00eda no est\u00e1 hecha. La secci\u00f3n 3 de OCSA-HR-013 la pide antes de entregar cualquier llave, credencial, llavero electr\u00f3nico o c\u00f3digo. Si la persona ya tiene esta, reg\u00edstrela; la capacitaci\u00f3n se asigna desde el registro." },
  "{0} items|supply request": {
    en: { one: "{0} item", other: "{0} items" },
    es: { one: "{0} art\u00edculo", other: "{0} art\u00edculos" },
  },
  "Approved {0} of {1}": { es: "Aprobado: {0} de {1}" },
  "Denied|item": { es: "Denegado" },
  "Quantity to approve": { es: "Cantidad a aprobar" },
  "A whole number from 1 to {0}": { es: "Un n\u00famero entero del 1 al {0}" },
  "Note (optional)": { es: "Nota (opcional)" },
  "Approve all": { es: "Aprobar todo" },
  "Deny all": { es: "Denegar todo" },
  "Download for ordering (CSV)": { es: "Descargar para hacer el pedido (CSV)" },
  "The approved items of the requests not yet fulfilled, for the dates and the site chosen.": { es: "Los art\u00edculos aprobados de las solicitudes a\u00fan no entregadas, para las fechas y el sitio elegidos." },
  "Choose a To date on or after the From date.": { es: "Elija una fecha Hasta igual o posterior a la fecha Desde." },
  "Badge Number, Phone or Email": { es: "N\u00famero de empleado, tel\u00e9fono o correo" },
  "9001, 2155550101 or name@email.com": { es: "9001, 2155550101 o nombre@correo.com" },
  "4-digit PIN": { es: "PIN de 4 d\u00edgitos" },
  "Forgot your PIN?": { es: "\u00bfOlvid\u00f3 su PIN?" },
  "Type your badge number, phone or email first.": { es: "Escriba primero su n\u00famero de empleado, tel\u00e9fono o correo." },
  "Request sent.": { es: "Solicitud enviada." },
  "After too many wrong tries, sign-in stops for a while. Ask the office for help.": { es: "Despu\u00e9s de demasiados intentos equivocados, el inicio de sesi\u00f3n se detiene por un tiempo. Pida ayuda a la oficina." },
  "Choose your PIN": { es: "Elija su PIN" },
  "The PIN you were given is known to the office. Choose your own 4-digit PIN to go on.": { es: "La oficina conoce el PIN que le dieron. Elija su propio PIN de 4 d\u00edgitos para continuar." },
  "New PIN again": { es: "PIN nuevo otra vez" },
  "Type the same PIN twice.": { es: "Escriba el mismo PIN dos veces." },
  "Your PIN is saved.": { es: "Su PIN qued\u00f3 guardado." },
  "Locked until {0}": { es: "Bloqueado hasta {0}" },
  "On a given PIN": { es: "Con un PIN asignado" },
  "No working email": { es: "Sin un correo que funcione" },
  "Last signed in {0}": { es: "\u00daltimo inicio de sesi\u00f3n: {0}" },
  "Never signed in": { es: "Nunca ha iniciado sesi\u00f3n" },
  "Unlock": { es: "Desbloquear" },
  "Sign-in unlocked.": { es: "Inicio de sesi\u00f3n desbloqueado." },
  "Reset PIN also unlocks their sign-in and signs them out everywhere. They choose their own PIN at their next sign-in.": { es: "Restablecer el PIN tambi\u00e9n desbloquea su inicio de sesi\u00f3n y cierra su sesi\u00f3n en todas partes. La persona elige su propio PIN la pr\u00f3xima vez que inicie sesi\u00f3n." },
  "Not sent: {0}": { es: "No se envi\u00f3: {0}" },
  "Vacuum": { es: "Aspiradora" },
  "Carpet extractor": { es: "Extractora de alfombras" },
  "Floor machine": { es: "M\u00e1quina de pisos" },
  "Blower or grounds equipment": { es: "Sopladora o equipo de jardiner\u00eda" },
  "Pressure washer": { es: "Hidrolavadora" },
  "Other|equipment": { es: "Otro" },
  "Open HR file": { es: "Abrir el expediente de personal" },
  "The weekly, monthly, quarterly and seasonal work on this site's checklist, and whether it is done this period.": { es: "El trabajo semanal, mensual, trimestral y de temporada de la lista de este sitio, y si est\u00e1 hecho en este per\u00edodo." },
  "Search by name, badge number or employee ID": { es: "Buscar por nombre, n\u00famero de empleado o ID de la persona" },
  "Badge {0}": { es: "N\u00famero de empleado {0}" },
  "Employee ID {0}": { es: "ID de la persona {0}" },
  "No one is on this list.": { es: "No hay nadie en esta lista." },
  "{0} more. Type to narrow the list.": { es: "{0} m\u00e1s. Escriba para acotar la lista." },
  "Assigned to this site": { es: "Asignados a este sitio" },
  "Everyone else": { es: "Todos los dem\u00e1s" },
  "No one matches these filters.": { es: "Nadie coincide con estos filtros." },
  "Tickets": { es: "Tickets" },
  "What people report about the apps from App support and from Help. A bug and a sign-in problem are emailed to the support contact at once; the rest go in one summary each working morning.": { es: "Lo que la gente informa sobre las aplicaciones desde Soporte de la aplicaci\u00f3n y desde Ayuda. Un error y un problema para iniciar sesi\u00f3n se env\u00edan por correo al contacto de soporte de inmediato; el resto va en un resumen cada ma\u00f1ana de d\u00eda laborable." },
  "Something is not working": { es: "Algo no funciona" },
  "An idea": { es: "Una idea" },
  "Wrong information": { es: "Informaci\u00f3n incorrecta" },
  "Help could not answer": { es: "Ayuda no pudo responder" },
  "Cannot sign in": { es: "No puede iniciar sesi\u00f3n" },
  "New|ticket": { es: "Nuevo" },
  "Working on it": { es: "En proceso" },
  "Done|ticket": { es: "Hecho" },
  "Won't do": { es: "No se har\u00e1" },
  "All|tickets": { es: "Todos" },
  "Staff portal": { es: "Portal del personal" },
  "Admin dashboard": { es: "Panel de administraci\u00f3n" },
  "App support form": { es: "Formulario de soporte de la aplicaci\u00f3n" },
  "Kind of ticket": { es: "Tipo de ticket" },
  "Note|ticket": { es: "Nota" },
  "From|ticket": { es: "De" },
  "Sent|ticket": { es: "Enviado" },
  "Sent from": { es: "Enviado desde" },
  "Screen": { es: "Pantalla" },
  "App version": { es: "Versi\u00f3n de la aplicaci\u00f3n" },
  "Device": { es: "Dispositivo" },
  "Where": { es: "D\u00f3nde" },
  "Both apps": { es: "Las dos aplicaciones" },
  "No tickets yet.": { es: "Todav\u00eda no hay tickets." },
  "App support tickets ({0})": { es: "Tickets de soporte de la aplicaci\u00f3n ({0})" },
  "Screenshot": { es: "Captura de pantalla" },
  "A screenshot is attached.": { es: "Hay una captura de pantalla adjunta." },
  "Note to the person who sent it": { es: "Nota para la persona que lo envi\u00f3" },
  "When the status changes, the person who sent it is told in the app, in their language, with this note.": { es: "Cuando cambia el estado, se le avisa en la aplicaci\u00f3n a la persona que lo envi\u00f3, en su idioma, con esta nota." },
  "Saved.": { es: "Guardado." },
  "Saved. The person who sent it is told in their language.": { es: "Guardado. Se le avisa a la persona que lo envi\u00f3 en su idioma." },
  "The tickets shown, as text to paste into a chat.": { es: "Los tickets que se muestran, como texto para pegar en un chat." },
  "Select the text and copy it.": { es: "Seleccione el texto y c\u00f3pielo." },
  "App support contact": { es: "Contacto de soporte de la aplicaci\u00f3n" },
  "The person tickets go to. Help names them, the sign-in screens show the email, and a bug or a sign-in problem is emailed to them at once.": { es: "La persona que recibe los tickets. Ayuda la nombra, las pantallas de inicio de sesi\u00f3n muestran el correo, y un error o un problema para iniciar sesi\u00f3n se le env\u00eda por correo de inmediato." },
  "Can't sign in? Email {0}": { es: "\u00bfNo puede iniciar sesi\u00f3n? Escriba a {0}" },
  "Ticket for app support": { es: "Ticket para el soporte de la aplicaci\u00f3n" },
  "Nothing is sent until you press Send ticket. The screen, this app's version, your browser and your language go with it.": { es: "No se env\u00eda nada hasta que presione Enviar ticket. Con \u00e9l se env\u00edan la pantalla, la versi\u00f3n de esta aplicaci\u00f3n, su navegador y su idioma." },
  "Send ticket": { es: "Enviar ticket" },
  "Ticket sent.": { es: "Ticket enviado." },
  "Signed in {0}": { es: "Idioma de la firma: {0}" },
  "Signed acknowledgment page": { es: "P\u00e1gina de acuse de recibo firmada" },
  "Handbook acknowledgment": { es: "Acuse de recibo del manual" },
  "PTO (paid time off)": { es: "PTO (tiempo libre pagado)" },
  "Paid sick leave": { es: "Licencia por enfermedad pagada" },
  "Unpaid time off": { es: "Tiempo libre sin pago" },
  "Bereavement and personal": { es: "Duelo y asuntos personales" },
  "Jury duty": { es: "Servicio de jurado" },
  "Military leave": { es: "Licencia militar" },
  "Meeting": { es: "Reuni\u00f3n" },
  "Correction": { es: "Correcci\u00f3n" },
  "Status change": { es: "Cambio de estado" },
  "Handoff": { es: "Traspaso" },
  "Warning|case log": { es: "Advertencia" },
  "Closing note": { es: "Nota de cierre" },
  "Notes saved before the case log": { es: "Notas guardadas antes del registro del caso" },
  "{0} to {1}|status": { es: "De {0} a {1}" },
  "Escalated to {0}": { es: "Escalado a {0}" },
  "Released by {0}. The case went back to the team.": { es: "Liberado por {0}. El caso volvi\u00f3 al equipo." },
  "The case went back to the team.": { es: "El caso volvi\u00f3 al equipo." },
  "Taken by {0}": { es: "Tomado por {0}" },
  "Handed from {0} to {1}": { es: "Pasado de {0} a {1}" },
  "Handed to {0}": { es: "Pasado a {0}" },
  "A warning was issued from this case.": { es: "Se emiti\u00f3 una advertencia desde este caso." },
  "This file could not be read.": { es: "No se pudo leer este archivo." },
  "{0} is not a JPEG, PNG, WebP or HEIC picture or a PDF.": { es: "{0} no es una foto JPEG, PNG, WebP o HEIC ni un PDF." },
  "{0} is over 10 MB.": { es: "{0} pesa m\u00e1s de 10 MB." },
  "Happened {0}": { es: "Ocurri\u00f3 el {0}" },
  "With {0}|case log": { es: "Con {0}" },
  "Correction to the entry of {0}": { es: "Correcci\u00f3n de la entrada del {0}" },
  "Correction to an earlier entry": { es: "Correcci\u00f3n de una entrada anterior" },
  "entry {0}": { es: "entrada {0}" },
  "Corrected below": { es: "Corregida m\u00e1s abajo" },
  "Files: {0}": { es: "Archivos: {0}" },
  "Case log": { es: "Registro del caso" },
  "Nothing has been added yet.": { es: "Todav\u00eda no se ha agregado nada." },
  "Up to {0} files.": { es: "Hasta {0} archivos." },
  "Write what was said or done.": { es: "Escriba lo que se dijo o se hizo." },
  "Attach at least one file.": { es: "Adjunte al menos un archivo." },
  "Keep it to {0} characters.": { es: "No pase de {0} caracteres." },
  "Choose when it happened.": { es: "Elija cu\u00e1ndo ocurri\u00f3." },
  "When it happened cannot be later than now.": { es: "La fecha y hora no pueden ser posteriores a ahora." },
  "Added to the case log.": { es: "Se agreg\u00f3 al registro del caso." },
  "Correct this": { es: "Corregir esto" },
  "Add a correction": { es: "Agregar una correcci\u00f3n" },
  "Add an update": { es: "Agregar una actualizaci\u00f3n" },
  "With|case log": { es: "Con" },
  "No one from {0}": { es: "Nadie de {0}" },
  "Or type the name of someone outside {0}": { es: "O escriba el nombre de alguien de fuera de {0}" },
  "Someone outside {0}": { es: "Alguien de fuera de {0}" },
  "When it happened": { es: "Cu\u00e1ndo ocurri\u00f3" },
  "What was said or done": { es: "Qu\u00e9 se dijo o se hizo" },
  "Attach files": { es: "Adjuntar archivos" },
  "Up to 5 pictures or PDFs, 10 MB each.": { es: "Hasta 5 fotos o PDF, de 10 MB cada uno." },
  "Entries cannot be changed once added. To fix one, add a correction.": { es: "Las entradas no se pueden cambiar despu\u00e9s de agregarlas. Para arreglar una, agregue una correcci\u00f3n." },
  "Add to the case log": { es: "Agregar al registro del caso" },
  "Write the closing note.": { es: "Escriba la nota de cierre." },
  "Allow pop-ups to print the case": { es: "Permita las ventanas emergentes para imprimir el caso" },
  "Print case": { es: "Imprimir el caso" },
  "Required. It goes into the case log as the case's last entry.": { es: "Obligatoria. Queda en el registro del caso como su \u00faltima entrada." },
  "Remove {0}|file": { es: "Quitar {0}" },
  "Send welcome email": { es: "Enviar el correo de bienvenida" },
  "Send it again": { es: "Enviarlo de nuevo" },
  "Send a new welcome email? The last link stops working.": { es: "\u00bfEnviar un nuevo correo de bienvenida? El enlace anterior dejar\u00e1 de funcionar." },
  "Welcome email sent to {0}.": { es: "Se envi\u00f3 el correo de bienvenida a {0}." },
  "Welcome email not sent: {0}": { es: "No se envi\u00f3 el correo de bienvenida: {0}" },
  "Welcome email sent {0}": { es: "Correo de bienvenida enviado el {0}" },
  "Welcome email sent.": { es: "Se envi\u00f3 el correo de bienvenida." },
  "no reason was given": { es: "no se dio ning\u00fan motivo" },
  "If the email does not reach them, give them this PIN.": { es: "Si el correo no le llega, dele este PIN." },
  "Employment could not be loaded. Try again.": { es: "No se pudo cargar el empleo. Vuelva a intentarlo." },
  "Library": { es: "Biblioteca" },
  "Every company document. Open one to read it.": { es: "Todos los documentos de la empresa. Abra uno para leerlo." },
  "The library is loading. Check back soon.": { es: "La biblioteca se est\u00e1 cargando. Vuelva a revisar pronto." },
  "Search the library": { es: "Buscar en la biblioteca" },
  "Number, title or words in the text": { es: "N\u00famero, t\u00edtulo o palabras del texto" },
  "Results for {0}": { es: "Resultados de {0}" },
  "No document matches that search.": { es: "Ning\u00fan documento coincide con esa b\u00fasqueda." },
  "All|documents": { es: "Todos" },
  "{0} documents|library": {
    en: { one: "{0} document", other: "{0} documents" },
    es: { one: "{0} documento", other: "{0} documentos" },
  },
  "Also in Spanish": { es: "Tambi\u00e9n en espa\u00f1ol" },
  "Found in {0}": { es: "Encontrado en {0}" },
  "Back to the Library": { es: "Volver a la Biblioteca" },
  "This document did not open. Try again.": { es: "Este documento no se abri\u00f3. Vuelva a intentarlo." },
  "Document number": { es: "N\u00famero de documento" },
  "This document is shown in English.": { es: "Este documento se muestra en ingl\u00e9s." },
  "See the designed version": { es: "Ver la versi\u00f3n dise\u00f1ada" },
  "Contents": { es: "Contenido" },
  "Part {0}: {1}": { es: "Parte {0}: {1}" },
  "Part {0}": { es: "Parte {0}" },
  "Back to the top": { es: "Volver al inicio" },
  "Open {0}|document": { es: "Abrir {0}" },
  "Only the people who approve supply requests can decide this.": { es: "Solo las personas que aprueban los pedidos de suministros pueden decidir esto." },
  "Sign and order": { es: "Firmar y pedir" },
  "Choose a vendor...": { es: "Elija un proveedor..." },
  "Add a vendor under Vendors and set it to approved first.": { es: "Primero agregue un proveedor en Proveedores y m\u00e1rquelo como aprobado." },
  "Edit this vendor": { es: "Editar este proveedor" },
  "Deliver to": { es: "Entregar en" },
  "Your signature": { es: "Su firma" },
  "Purchase order": { es: "Orden de compra" },
  "Signed by {0}": { es: "Firmado por {0}" },
  "Ordered {0}, sent to {1}": { es: "Pedido el {0}, enviado a {1}" },
  "Open the purchase order": { es: "Abrir la orden de compra" },
  "Send to {0}": { es: "Enviar a {0}" },
  "Ordered {0}": { es: "Pedido el {0}" },
  "A supply order fills in the vendor's name, contact, phone, email and address from here, and offers only an approved vendor.": { es: "Una orden de suministros toma de aqu\u00ed el nombre, el contacto, el tel\u00e9fono, el correo y la direcci\u00f3n del proveedor, y solo ofrece proveedores aprobados." },
  "Include the safety walk": { es: "Incluir el recorrido de seguridad" },
  "The inspector fills in the safety inspection (OCSA-FRM-015) in the same walk and signs both once. Each is kept as its own record.": { es: "Quien inspecciona llena la inspecci\u00f3n de seguridad (OCSA-FRM-015) en el mismo recorrido y firma ambas una sola vez. Cada una se guarda como su propio registro." },
  "Safety walk": { es: "Recorrido de seguridad" },
  "Included|safety walk": { es: "Incluido" },
  "Not included|safety walk": { es: "No incluido" },
  "Open the safety inspection record": { es: "Abrir el registro de la inspecci\u00f3n de seguridad" },
  "Filed as the safety walk of a scheduled inspection.": { es: "Presentado como el recorrido de seguridad de una inspecci\u00f3n programada." },
  "Open the inspection": { es: "Abrir la inspecci\u00f3n" },
  "Schedule by shift": { es: "Horario por turno" },
  "Each block's window and kind, with its steps. A block with days shows on those days only.": { es: "La franja y el tipo de cada bloque, con sus pasos. Un bloque con d\u00edas se muestra solo esos d\u00edas." },
  "Work|block kind": { es: "Trabajo" },
  "Critical|block kind": { es: "Cr\u00edtico" },
  "Residents' meal": { es: "Comida de los residentes" },
  "Empty, full access": { es: "Vac\u00edo, acceso total" },
  "Check-in": { es: "Entrada" },
  "Check-out": { es: "Salida" },
  "When there is free time": { es: "Cuando haya tiempo libre" },
  "Every day": { es: "Todos los d\u00edas" },
  "{0} steps|block": {
    en: { one: "{0} step", other: "{0} steps" },
    es: { one: "{0} paso", other: "{0} pasos" },
  },
  "No steps": { es: "Sin pasos" },
  "Edit the block": { es: "Editar el bloque" },
  "Ends at": { es: "Termina a las" },
  "On a night shift that runs past midnight, the end can be before the start.": { es: "En un turno de noche que pasa de la medianoche, el fin puede ser antes del inicio." },
  "None ticked means every day.": { es: "Si no marca ninguno, son todos los d\u00edas." },
  "You asked for these supplies, so someone else decides them.": { es: "Usted pidi\u00f3 estos suministros, as\u00ed que otra persona decide." },
  "Add people, approve registrations, change employment and roles": { es: "Agregar personas, aprobar registros, cambiar el empleo y los roles" },
  "Read HR records": { es: "Leer los expedientes de personal" },
  "Open and work on cases": { es: "Abrir y trabajar en casos" },
  "Start a case with this report linked.": { es: "Inicie un caso con este reporte vinculado." },
  "From the filed report: {0}": { es: "Del reporte presentado: {0}" },
  "The filed report is linked to this case.": { es: "El reporte presentado queda vinculado a este caso." },
  "New notice": { es: "Aviso nuevo" },
  "Alerts on this computer": { es: "Alertas en esta computadora" },
  "Turn on alerts on this computer": { es: "Activar las alertas en esta computadora" },
  "Turn off|computer alerts": { es: "Desactivar" },
  "Alerts are on for this computer. A notice shows here even when the dashboard is closed.": { es: "Las alertas est\u00e1n activadas en esta computadora. Un aviso aparece aqu\u00ed aunque el panel est\u00e9 cerrado." },
  "Your browser asks once whether the dashboard may show alerts.": { es: "Su navegador pregunta una vez si el panel puede mostrar alertas." },
  "This browser blocks alerts from the dashboard. Allow them in the browser's site settings, then try again.": { es: "Este navegador bloquea las alertas del panel. Perm\u00edtalas en la configuraci\u00f3n del sitio del navegador y vuelva a intentarlo." },
  "This browser cannot show alerts.": { es: "Este navegador no puede mostrar alertas." },
  "Alerts did not turn on. Try again.": { es: "Las alertas no se activaron. Vuelva a intentarlo." },
  "Alerts are off here. The office may still list this computer until you turn alerts on and off again.": { es: "Las alertas est\u00e1n desactivadas aqu\u00ed. La oficina puede seguir mostrando esta computadora hasta que active y desactive las alertas otra vez." },
  "These decide what alerts your phone in the staff app and this computer. The bell here keeps everything.": { es: "Esto decide qu\u00e9 alertas recibe su tel\u00e9fono en la aplicaci\u00f3n del personal y esta computadora. La campana de aqu\u00ed guarda todo." },
  "Off|time off": { es: "Libre" },
  "Time off requested": { es: "Tiempo libre solicitado" },
  "Off, needs cover": { es: "Libre, necesita cubrirse" },
  "{0} shift under time off|count": {
    en: { one: "{0} shift under time off", other: "{0} shifts under time off" },
    es: { one: "{0} turno con tiempo libre", other: "{0} turnos con tiempo libre" },
  },
  "Choose what happens to each shift. A shift left as it is stays under this person.": { es: "Elija qu\u00e9 pasa con cada turno. Un turno que se deja como est\u00e1 sigue a nombre de esta persona." },
  "Leave it as it is": { es: "Dejarlo como est\u00e1" },
  "Reassign to": { es: "Reasignar a" },
  "Post to Shift Pickup": { es: "Publicar en Turnos disponibles" },
  "Cancel the shift": { es: "Cancelar el turno" },
  "Overtime": { es: "Horas extra" },
  "This shift takes them past 40 hours this week.": { es: "Este turno le hace pasar de 40 horas esta semana." },
  "What happens to this shift": { es: "Qu\u00e9 pasa con este turno" },
  "Choose who takes this shift.": { es: "Elija qui\u00e9n toma este turno." },
  "Shifts updated.": { es: "Turnos actualizados." },
  "Shifts to cover": { es: "Turnos por cubrir" },
  "Shifts this affects": { es: "Turnos afectados" },
  "This person is off for these shifts. Choose who covers each one, or post or cancel it.": { es: "Esta persona est\u00e1 libre en estos turnos. Elija qui\u00e9n cubre cada uno, o publ\u00edquelo o canc\u00e9lelo." },
  "{0} hour this week|count": {
    en: { one: "{0} hour this week", other: "{0} hours this week" },
    es: { one: "{0} hora esta semana", other: "{0} horas esta semana" },
  },
  "Not tracked": { es: "Sin seguimiento" },
  "Staff do not check off tasks at this site. They can read its task list as a reference.": { es: "El personal no marca tareas en este sitio. Puede leer su lista de tareas como referencia." },
  "Staff check off tasks here": { es: "El personal marca las tareas aqu\u00ed" },
  "Off: staff can read the task list as a reference, and nothing is checked off.": { es: "Apagado: el personal puede leer la lista de tareas como referencia, y no se marca nada." },
  "Staff no longer check off tasks here.": { es: "El personal ya no marca tareas aqu\u00ed." },
  "Staff check off tasks here again.": { es: "El personal vuelve a marcar las tareas aqu\u00ed." },
  // Part 341, changing a hire date.
  "To fix a wrong date, pick the right one.": { es: "Para corregir una fecha equivocada, elija la correcta." },
  // Round 1 (part 347): clearances dates only, time off for admins, a call's deadline, notices, the update bar.
  "Completed on {0}": { es: "Hecha el {0}" },
  "An admin approves or denies time off.": { es: "Un administrador aprueba o deniega el tiempo libre." },
  "The connection timed out. Try again.": { es: "La conexi\u00f3n tard\u00f3 demasiado. Intente de nuevo." },
  "This notice has nothing to open on the dashboard.": { es: "Este aviso no tiene nada que abrir en el panel." },
  "A new version is ready": { es: "Hay una versi\u00f3n nueva lista" },
  // Round 2 and who opened each HR file (part 353): the access log, the report and its grant, floor plans, and the Round 2 items against the API's part 346.
  "Who opened this": { es: "Qui\u00e9n abri\u00f3 esto" },
  "Nobody has opened this file yet.": { es: "Nadie ha abierto este archivo todav\u00eda." },
  "Opened|file": { es: "Abierto" },
  "Refused|file": { es: "Rechazado" },
  "The file did not open": { es: "El archivo no se abri\u00f3" },
  "HR file access report": { es: "Informe de acceso a archivos de RR. HH." },
  "Every time an HR file was opened or refused, newest first. Downloading the CSV is recorded too.": { es: "Cada vez que se abri\u00f3 o se rechaz\u00f3 un archivo de RR. HH., del m\u00e1s reciente al m\u00e1s antiguo. Descargar el CSV tambi\u00e9n queda registrado." },
  "Who opened it": { es: "Qui\u00e9n lo abri\u00f3" },
  "Whose file": { es: "De qui\u00e9n es el archivo" },
  "Opened or refused": { es: "Abierto o rechazado" },
  "No entries match these filters.": { es: "Ning\u00fan registro coincide con estos filtros." },
  "Show more": { es: "Mostrar m\u00e1s" },
  "Anyone": { es: "Cualquier persona" },
  "Only the super admin and people given the HR file access report can open this page.": { es: "Solo el superadministrador y las personas a quienes se dio el informe de acceso a archivos de RR. HH. pueden abrir esta p\u00e1gina." },
  "HR document": { es: "Documento de RR. HH." },
  "Clearance file": { es: "Archivo de autorizaci\u00f3n escolar" },
  "Certificate|file": { es: "Certificado" },
  "Case file": { es: "Archivo de un caso" },
  "Warning PDF": { es: "PDF de amonestaci\u00f3n" },
  "Jotform PDF": { es: "PDF de Jotform" },
  "This report's CSV": { es: "CSV de este informe" },
  "Read chat records": { es: "Leer los registros del chat" },
  "An admin account holds the rest by role. As the super admin, you can grant or take away chat records and the HR file access report.": { es: "Una cuenta de administrador tiene el resto por su rol. Como superadministrador, usted puede dar o quitar el acceso a los registros del chat y al informe de acceso a archivos de RR. HH." },
  "This floor plan could not be opened.": { es: "No se pudo abrir este plano." },
  "Picking someone in the office opens your direct chat with them.": { es: "Al elegir a alguien de la oficina se abre su chat directo con esa persona." },
  "PIN must be exactly 4 digits": { es: "El PIN debe tener exactamente 4 d\u00edgitos" },
  "Choose a PIN that is not repeated digits, a sequence, or your badge number": { es: "Elija un PIN que no sea un mismo d\u00edgito repetido, una secuencia ni su n\u00famero de empleado" },
  "Four digits, not all the same, not a run such as 1234 or 4321, and not the person's badge number.": { es: "Cuatro d\u00edgitos, no todos iguales, sin una serie como 1234 o 4321, y distintos del n\u00famero de empleado de la persona." },
  "Locked since {0} until the office unlocks it": { es: "Bloqueado desde {0} hasta que la oficina lo desbloquee" },
  "Employment ends after {0}. It takes effect on {1}.": { es: "El empleo termina despu\u00e9s del {0}. Entra en vigor el {1}." },
  "Takes effect on {0}": { es: "Entra en vigor el {0}" },
  "Recorded. The employment ends after {0}.": { es: "Registrado. El empleo termina despu\u00e9s del {0}." },
  "End the employment of {0} after {1}? They stay as they are until then, and every site they are assigned to ends with it.": { es: "\u00bfTerminar el empleo de {0} despu\u00e9s del {1}? Sigue como est\u00e1 hasta entonces, y cada sitio al que est\u00e1 asignado termina con \u00e9l." },
  "on approved time off": { es: "con tiempo libre aprobado" },
  "{0} dates were skipped because this person has approved time off:": { es: "Se omitieron {0} fechas porque esta persona tiene tiempo libre aprobado:" },
  "Checklist Results": { es: "Resultados de la lista" },
  "Checklist history": { es: "Historial de la lista de verificaci\u00f3n" },
  "See the checklist": { es: "Ver la lista de verificaci\u00f3n" },
  "No checks were made on this shift.": { es: "No se hizo ninguna marca en este turno." },
  "This shift has no end time on its schedule, so its last 30 minutes are not counted.": { es: "Este turno no tiene hora de fin en su horario, as\u00ed que sus \u00faltimos 30 minutos no se cuentan." },
  "{0} of {1} checks ({2}%) were made in the shift's last 30 minutes, from {3} to {4}.": { es: "De {3} a {4}, los \u00faltimos 30 minutos del turno, se hicieron {0} de {1} marcas ({2}%)." },
  "Whole site": { es: "Todo el sitio" },
  "Any shift": { es: "Cualquier turno" },
  "No zone": { es: "Sin zona" },
  "When the checks happened": { es: "Cu\u00e1ndo se hicieron las marcas" },
  "Last 30 minutes": { es: "\u00daltimos 30 minutos" },
  "Done each day": { es: "Hecho cada d\u00eda" },
  "Not done": { es: "No se hizo" },
  "Printed on {0} at {1} by {2}": { es: "Impreso el {0} a las {1} por {2}" },
  "{0} of {1} steps done": { es: "{0} de {1} pasos hechos" },
  "First check {0}, last check {1}": { es: "Primera marca {0}, \u00faltima marca {1}" },
  "Who worked: {0}": { es: "Qui\u00e9n trabaj\u00f3: {0}" },
  "Each bar is 15 minutes, with its number of checks over it. The hatched bars are the shift's last 30 minutes.": { es: "Cada barra es de 15 minutos, con su n\u00famero de marcas encima. Las barras rayadas son los \u00faltimos 30 minutos del turno." },
  "Each bar is one day, with its share of steps done over it and the day of the month under it.": { es: "Cada barra es un d\u00eda, con su porcentaje de pasos hechos encima y el d\u00eda del mes debajo." },
  "Every step due was done.": { es: "Se hizo cada paso que tocaba." },
  "Every task": { es: "Todas las tareas" },
  "Who": { es: "Qui\u00e9n" },
  "As needed": { es: "Seg\u00fan se necesite" },
  "Page {0} of {1}": { es: "P\u00e1gina {0} de {1}" },
  "Yesterday": { es: "Ayer" },
  "Last 7 days": { es: "\u00daltimos 7 d\u00edas" },
  "This month": { es: "Este mes" },
  "All shifts": { es: "Todos los turnos" },
  "Pick dates": { es: "Elegir fechas" },
  "One day, or up to 31 days.": { es: "Un d\u00eda, o hasta 31 d\u00edas." },
  "Live": { es: "En vivo" },
  "Read at {0}. Read again every 30 seconds.": { es: "Se ley\u00f3 a las {0} y se vuelve a leer cada 30 segundos." },
  "Read at {0}.": { es: "Se ley\u00f3 a las {0}" },
  "Each site's steps done on {0}, and the 7 days to it. Click a site to open it.": { es: "Los pasos hechos en cada sitio el {0}, y los 7 d\u00edas hasta ese d\u00eda. Haga clic en un sitio para abrirlo." },
  "Each site's steps done over these days, and each day's share. Click a site to open it.": { es: "Los pasos hechos en cada sitio en estos d\u00edas, y el porcentaje de cada d\u00eda. Haga clic en un sitio para abrirlo." },
  "{0} of {1} steps": { es: "{0} de {1} pasos" },
  "The 7 days to {0}": { es: "Los 7 d\u00edas hasta el {0}" },
  "Each day": { es: "Cada d\u00eda" },
  "Open this site": { es: "Abrir este sitio" },
  "Allow pop-ups for the dashboard, then try again.": { es: "Permita las ventanas emergentes para el panel y vuelva a intentarlo." },
  "Print report": { es: "Imprimir el informe" },
  "Nothing was due or checked at this site on this day.": { es: "Nada tocaba ni se marc\u00f3 en este sitio este d\u00eda." },
  "Nothing was due or checked at this site on these days.": { es: "Nada tocaba ni se marc\u00f3 en este sitio en estos d\u00edas." },
  "{0} of {1} ({2})": { es: "{0} de {1} ({2})" },
  "Each shift's share of steps done. Click a day to open it.": { es: "El porcentaje de pasos hechos de cada turno. Haga clic en un d\u00eda para abrirlo." },
  "On this day": { es: "Ese d\u00eda" },
  "Not due on this day": { es: "No tocaba ese d\u00eda" },
  "Done at {0} by {1}": { es: "Hecho a las {0} por {1}" },
  "Due, not done": { es: "Tocaba, no se hizo" },
  "Checks by person": { es: "Marcas por persona" },
  "Checks|check-offs": { es: "Marcas" },
  "{0}: {1}": { es: "{0}: {1}" },
  "By zone": { es: "Por zona" },
  "Steps done of steps due in each zone. Click a zone to show only its tasks.": { es: "Pasos hechos de los pasos que tocaban en cada zona. Haga clic en una zona para mostrar solo sus tareas." },
  "All zones": { es: "Todas las zonas" },
  "{0}: {1} of {2}": { es: "{0}: {1} de {2}" },
  "Repeating work": { es: "Trabajo que se repite" },
  "Not done only": { es: "Solo lo que no se hizo" },
  "Search tasks": { es: "Buscar tareas" },
  "Zone: {0}. Show every zone": { es: "Zona: {0}. Mostrar todas las zonas" },
  "No tasks match.": { es: "Ninguna tarea coincide." },
  "What was done at a site on any day, with charts, and a report that prints clearly.": { es: "Lo que se hizo en un sitio cualquier d\u00eda, con gr\u00e1ficas, y un informe que se imprime con claridad." },
  "{0} check|count": {
    en: { one: "{0} check", other: "{0} checks" },
    es: { one: "{0} marca", other: "{0} marcas" },
  },
  "This shift ends at {0}. Its last 30 minutes are counted once it has ended.": { es: "Los \u00faltimos 30 minutos de este turno se cuentan cuando termine, a las {0}" },
  "Send welcome emails": { es: "Enviar correos de bienvenida" },
  "Sending {0} of {1}": { es: "Enviando {0} de {1}" },
  "Welcome emails: {0}": { es: "Correos de bienvenida: {0}" },
  "{0} sent|welcome emails": {
    en: { one: "{0} sent", other: "{0} sent" },
    es: { one: "{0} enviado", other: "{0} enviados" },
  },
  "{0} not sent|welcome emails": {
    en: { one: "{0} not sent", other: "{0} not sent" },
    es: { one: "{0} no enviado", other: "{0} no enviados" },
  },
  "Sent|welcome email": { es: "Enviado" },
  "Waiting|welcome email": { es: "En espera" },
  "Send the welcome email to {0} people now?|count": {
    en: { one: "Send the welcome email to {0} person now?", other: "Send the welcome email to {0} people now?" },
    es: { one: "\u00bfEnviar ahora el correo de bienvenida a {0} persona?", other: "\u00bfEnviar ahora el correo de bienvenida a {0} personas?" },
  },
  "Anyone sent one before gets a new link, and the last one stops working.": { es: "Quien ya recibi\u00f3 uno recibe un enlace nuevo, y el anterior deja de funcionar." },
  "Everyone active who has never signed in and has a working email.": { es: "Todas las personas activas que nunca han iniciado sesi\u00f3n y tienen un correo que funciona." },
  "Will get the email ({0})": { es: "Recibir\u00e1n el correo ({0})" },
  "Nobody to send to.": { es: "No hay nadie a quien enviarlo." },
  "Left out": { es: "Quedan fuera" },
  "No email on file": { es: "No tiene correo registrado" },
  "Placeholder email": { es: "Correo provisional" },
  "Sent one in the last 7 days": { es: "Recibi\u00f3 uno en los \u00faltimos 7 d\u00edas" },
  "Accounts you may not change": { es: "Cuentas que usted no puede cambiar" },
  "{0} people have already signed in.|count": {
    en: { one: "{0} person has already signed in.", other: "{0} people have already signed in." },
    es: { one: "{0} persona ya inici\u00f3 sesi\u00f3n.", other: "{0} personas ya iniciaron sesi\u00f3n." },
  },
  "Include people sent one in the last 7 days": { es: "Incluir a quienes recibieron uno en los \u00faltimos 7 d\u00edas" },
  "A new email stops their last link from working.": { es: "Un correo nuevo hace que su \u00faltimo enlace deje de funcionar." },
  "Send to {0} people|count": {
    en: { one: "Send to {0} person", other: "Send to {0} people" },
    es: { one: "Enviar a {0} persona", other: "Enviar a {0} personas" },
  },
  "You can close this window. The emails keep going.": { es: "Puede cerrar esta ventana. Los correos se siguen enviando." },
  "Try the ones that failed": { es: "Volver a intentar los que fallaron" },
  "Entered with no file.": { es: "Se registr\u00f3 sin archivo." },
  "Open the file": { es: "Abrir el archivo" },
  "End taken back": { es: "Fin anulado" },
  "Take back": { es: "Anular" },
  "Taken back by {0} on {1}": { es: "Anulado por {0} el {1}" },
  "Taken back on {0}": { es: "Anulado el {0}" },
  "The end after {0} was taken back and never took effect": { es: "El fin despu\u00e9s del {0} se anul\u00f3 y nunca tuvo efecto" },
  "The end was taken back and never took effect": { es: "El fin se anul\u00f3 y nunca tuvo efecto" },
  "The end was taken back.": { es: "Se anul\u00f3 el fin." },
  "Take back the end": { es: "Anular el fin" },
  "Take back the end of the employment of {0} after {1}? They stay as they are, and the end never takes effect. The history keeps it, marked taken back.": { es: "\u00bfAnular el fin del empleo de {0} despu\u00e9s del {1}? Sigue como est\u00e1, y el fin nunca tiene efecto. El historial lo guarda, marcado como anulado." },
  "Take back the end of the employment of {0}? They stay as they are, and the end never takes effect. The history keeps it, marked taken back.": { es: "\u00bfAnular el fin del empleo de {0}? Sigue como est\u00e1, y el fin nunca tiene efecto. El historial lo guarda, marcado como anulado." },
  "This person's birthday, address, emergency contact and personal notes are not shown to you.": { es: "El cumplea\u00f1os, la direcci\u00f3n, el contacto de emergencia y las notas personales de esta persona no se le muestran." },
  "This person's birthday, address, emergency contact and personal notes are not shown to you, so they cannot be changed here.": { es: "El cumplea\u00f1os, la direcci\u00f3n, el contacto de emergencia y las notas personales de esta persona no se le muestran, as\u00ed que no se pueden cambiar aqu\u00ed." },
  "Opened from a filed report": { es: "Abierto desde un reporte enviado" },
  "Open the report": { es: "Abrir el reporte" },
  "The case will be opened without the report.": { es: "El caso se abrir\u00e1 sin el reporte." },
  "Open it without the report": { es: "Abrirlo sin el reporte" },
  "This is taking longer than usual and may still be going out. Check before you send it again.": { es: "Esto est\u00e1 tardando m\u00e1s de lo normal y puede que todav\u00eda se est\u00e9 enviando. Revise antes de volver a enviarlo." },
  "The alias list names people with the names and personal emails on their paperwork, so it is shown only to people with HR Records access.": { es: "La lista de alias nombra a las personas con los nombres y correos personales de sus documentos, as\u00ed que solo se muestra a quienes tienen acceso a Expedientes de personal." },
  "This shift is already posted for pickup or has a drop request waiting. Open it on Shift Pickup to change or cancel that posting first.": { es: "Este turno ya est\u00e1 publicado para tomar o tiene una solicitud de dejarlo pendiente. \u00c1bralo en Turnos disponibles para cambiar o cancelar esa publicaci\u00f3n primero." },
  "This client request is waiting for approval or was declined. Approve or decline it on the Client requests tab first. A declined request stays declined.": { es: "Esta solicitud del cliente espera aprobaci\u00f3n o fue rechazada. Apru\u00e9bela o rech\u00e1cela primero en la pesta\u00f1a Solicitudes de clientes. Una solicitud rechazada se queda rechazada." },
  "This issue is closed, declined or waiting for approval, so it cannot be assigned. Reopen it, or approve the request first.": { es: "Este problema est\u00e1 cerrado, rechazado o espera aprobaci\u00f3n, as\u00ed que no se puede asignar. Vuelva a abrirlo, o apruebe la solicitud primero." },
  "Choose an active staff member. A client contact or someone who has left cannot take an issue.": { es: "Elija a un miembro activo del personal. Un contacto de cliente o alguien que ya se fue no puede tomar un problema." },
  "Choose an active staff member to take this task. A client contact or someone who has left cannot take it.": { es: "Elija a un miembro activo del personal para que tome esta tarea. Un contacto de cliente o alguien que ya se fue no puede tomarla." },
  "This inspection is completed, so it cannot be cancelled or reopened, and its safety walk stays as it is. Schedule a new inspection if another visit is needed.": { es: "Esta inspecci\u00f3n est\u00e1 completa, as\u00ed que no se puede cancelar ni volver a abrir, y su recorrido de seguridad se queda como est\u00e1. Programe una nueva inspecci\u00f3n si se necesita otra visita." },
  "This request was already fulfilled, so it cannot change. Make a new request for anything more.": { es: "Esta solicitud ya se surti\u00f3, as\u00ed que no puede cambiar. Haga una nueva solicitud para algo m\u00e1s." },
  "What was sent is too large. Send less at once, or a smaller file.": { es: "Lo que se envi\u00f3 es demasiado grande. Env\u00ede menos a la vez, o un archivo m\u00e1s peque\u00f1o." },
  "What was sent could not be read. Try again, and if it happens again, reload the page.": { es: "No se pudo leer lo que se envi\u00f3. Int\u00e9ntelo de nuevo, y si vuelve a pasar, recargue la p\u00e1gina." },
  "Type a working email address to send it to.": { es: "Escriba una direcci\u00f3n de correo que funcione para enviarlo." },
  "Choose an active vendor for this order.": { es: "Elija un proveedor activo para este pedido." },
  "Fill in where the order is delivered, in 500 characters or fewer.": { es: "Escriba d\u00f3nde se entrega el pedido, en 500 caracteres o menos." },
  "Sign in the box, then try again.": { es: "Firme en el recuadro y vuelva a intentarlo." },
  "Choose Approved or Denied.": { es: "Elija Aprobada o Denegada." },
  "Check each item: approve or deny it, with an approved quantity of at least 1.": { es: "Revise cada art\u00edculo: apru\u00e9belo o rech\u00e1zelo, con una cantidad aprobada de al menos 1." },
  "Some details are missing or not valid. Check what you entered and try again.": { es: "Faltan algunos datos o no son v\u00e1lidos. Revise lo que escribi\u00f3 y vuelva a intentarlo." },
  "This file is over {0} MB. Choose a smaller file.": { es: "Este archivo pasa de {0} MB. Elija un archivo m\u00e1s peque\u00f1o." },
  "This file is too large. Choose a smaller file.": { es: "Este archivo es demasiado grande. Elija un archivo m\u00e1s peque\u00f1o." },
  "Changing shifts on Shift Pickup needs permission to manage the schedule. Ask an admin to give it to you.": { es: "Cambiar turnos en Turnos disponibles necesita permiso para administrar el horario. Pida a un administrador que se lo d\u00e9." },
  "{0} is over 25 MB.": { es: "{0} pasa de 25 MB." },
  "Other|category": { es: "Otra" },
  "Changing the schedule needs permission to manage the schedule. Ask an admin to give it to you.": { es: "Cambiar el horario necesita permiso para administrar el horario. Pida a un administrador que se lo d\u00e9." },
  "The dashboard could not reach the server. Check the connection and try again.": { es: "El panel no pudo comunicarse con el servidor. Revise la conexi\u00f3n e intente de nuevo." },
  "Close this window? What you entered here will be lost.": { es: "\u00bfCerrar esta ventana? Lo que ingres\u00f3 aqu\u00ed se perder\u00e1." },
  "Reading the shifts this request affects...": { es: "Leyendo los turnos que afecta esta solicitud..." },
  "The shifts this request affects did not load, so Approve waits.": { es: "Los turnos que afecta esta solicitud no se cargaron, as\u00ed que Aprobar espera." },
  "The document is saved. Its link to the Jotform submission did not save.": { es: "El documento qued\u00f3 guardado. Su enlace al env\u00edo de Jotform no se guard\u00f3." },
  "The document is saved. Its link to the Jotform submission did not save ({0}). Press Save to try the link again.": { es: "El documento qued\u00f3 guardado. Su enlace al env\u00edo de Jotform no se guard\u00f3 ({0}). Presione Guardar para intentar el enlace de nuevo." },
  "Who is this warning for?": { es: "\u00bfPara qui\u00e9n es esta advertencia?" },
  "Maintenance requests": { es: "Solicitudes de mantenimiento" },
  "Maintenance requests open for the people who file them. Ask an admin for access.": { es: "Las solicitudes de mantenimiento se abren para quienes las env\u00edan. Pida acceso a un administrador." },
  "Maintenance|site tab": { es: "Mantenimiento" },
  "This issue was already sent to the building as {0}.": { es: "Este problema ya se envi\u00f3 al edificio como {0}." },
  "Building maintenance": { es: "Mantenimiento del edificio" },
  "Sent as {0}": { es: "Enviado como {0}" },
  "Open the request": { es: "Abrir la solicitud" },
  "Send to building maintenance": { es: "Enviar a mantenimiento del edificio" },
  "Sent to building maintenance as {0}": { es: "Enviado a mantenimiento del edificio como {0}" },
  "This photo did not load.": { es: "Esta foto no se carg\u00f3." },
  "Up to {0} more photos, 10 MB each.": { es: "Hasta {0} fotos m\u00e1s, de 10 MB cada una." },
  "No more photos fit on this request.": { es: "Ya no caben m\u00e1s fotos en esta solicitud." },
  "Fill in the site, the category, the urgency and what is wrong.": { es: "Complete el sitio, la categor\u00eda, la urgencia y qu\u00e9 est\u00e1 mal." },
  "The request is saved, and its email did not go. Send it again from the request.": { es: "La solicitud se guard\u00f3 y su correo no se envi\u00f3. Env\u00edelo de nuevo desde la solicitud." },
  "New maintenance request": { es: "Nueva solicitud de mantenimiento" },
  "This emails the building's own maintenance team, with the office copied. Their answers are recorded on the request.": { es: "Esto env\u00eda un correo al equipo de mantenimiento del propio edificio, con copia a la oficina. Sus respuestas se registran en la solicitud." },
  "Choose a site...": { es: "Elija un sitio..." },
  "Area or room": { es: "\u00c1rea o sala" },
  "Choose...": { es: "Elija..." },
  "Urgency *": { es: "Urgencia *" },
  "What is wrong *": { es: "Qu\u00e9 est\u00e1 mal *" },
  "Where it is, what you see, and since when.": { es: "D\u00f3nde est\u00e1, qu\u00e9 ve y desde cu\u00e1ndo." },
  "The issue's photos to send": { es: "Las fotos del problema que se env\u00edan" },
  "Send photo {0}": { es: "Enviar la foto {0}" },
  "Photos": { es: "Fotos" },
  "It goes to": { es: "Se env\u00eda a" },
  "Choose a site to see who it goes to.": { es: "Elija un sitio para ver a qui\u00e9n se env\u00eda." },
  "The site's maintenance contacts did not load.": { es: "Los contactos de mantenimiento del sitio no se cargaron." },
  "Primary": { es: "Principal" },
  "The office is copied, and replies come to the office.": { es: "La oficina va en copia y las respuestas llegan a la oficina." },
  "This site has no active maintenance contact. Add one on the site's page before sending.": { es: "Este sitio no tiene un contacto de mantenimiento activo. Agregue uno en la p\u00e1gina del sitio antes de enviar." },
  "Add a contact": { es: "Agregar un contacto" },
  "Ask an admin to add one.": { es: "Pida a un administrador que agregue uno." },
  "Open it": { es: "Abrirla" },
  "Say how the building answered, or add a note, a status, a date or a photo.": { es: "Indique c\u00f3mo respondi\u00f3 el edificio, o agregue una nota, un estado, una fecha o una foto." },
  "Choose the day the work is scheduled for.": { es: "Elija el d\u00eda para el que est\u00e1 programado el trabajo." },
  "Saved on the request": { es: "Guardado en la solicitud" },
  "Part of this was saved, and the rest was not: {0}": { es: "Una parte se guard\u00f3 y el resto no: {0}" },
  "Email the building's maintenance contacts again about {0}?": { es: "\u00bfEnviar otro correo a los contactos de mantenimiento del edificio sobre {0}?" },
  "The follow-up did not go.": { es: "El seguimiento no se envi\u00f3." },
  "Follow-up sent": { es: "Seguimiento enviado" },
  "The email did not go.": { es: "El correo no se envi\u00f3." },
  "Email sent": { es: "Correo enviado" },
  "Emailed {0}.": { es: "Enviado por correo el {0}." },
  "Not emailed yet.": { es: "Todav\u00eda no se envi\u00f3 por correo." },
  "Follow-up emailed": { es: "Seguimiento enviado por correo" },
  "Request emailed": { es: "Solicitud enviada por correo" },
  "{0}: it did not go": { es: "{0}: no se envi\u00f3" },
  "Response received {0}": { es: "Respuesta recibida {0}" },
  "Status: {0}": { es: "Estado: {0}" },
  "Scheduled for {0}": { es: "Programada para el {0}" },
  "Maintenance request": { es: "Solicitud de mantenimiento" },
  "No response from the building yet. It was due by {0}.": { es: "Todav\u00eda no hay respuesta del edificio. Venc\u00eda el {0}." },
  "The building has until {0} to answer before the filer is reminded.": { es: "El edificio tiene hasta el {0} para responder antes de que se le recuerde a quien la envi\u00f3." },
  "Building, floor and area": { es: "Edificio, piso y \u00e1rea" },
  "None yet": { es: "Ninguna todav\u00eda" },
  "Scheduled for": { es: "Programada para" },
  "Fixed": { es: "Reparada" },
  "Open the issue it came from": { es: "Abrir el problema del que sali\u00f3" },
  "Who it went to": { es: "A qui\u00e9n se envi\u00f3" },
  "The office was copied.": { es: "La oficina fue en copia." },
  "Send the email again": { es: "Enviar el correo de nuevo" },
  "Record a response": { es: "Registrar una respuesta" },
  "Send a follow-up": { es: "Enviar un seguimiento" },
  "This request is closed.": { es: "Esta solicitud est\u00e1 cerrada." },
  "This request is cancelled.": { es: "Esta solicitud est\u00e1 cancelada." },
  "How it came": { es: "C\u00f3mo lleg\u00f3" },
  "No response, an update only": { es: "Sin respuesta, solo una actualizaci\u00f3n" },
  "New status": { es: "Nuevo estado" },
  "Keep it {0}": { es: "Dejarla {0}" },
  "Scheduled date": { es: "Fecha programada" },
  "What the building said.": { es: "Lo que dijo el edificio." },
  "This stays in the office's record. The building does not see it.": { es: "Esto queda en el registro de la oficina. El edificio no lo ve." },
  "Record, newest first": { es: "Registro, lo m\u00e1s reciente primero" },
  "To {0}": { es: "Para {0}" },
  "All|maintenance": { es: "Todas" },
  "Where and what": { es: "D\u00f3nde y qu\u00e9" },
  "Half of the requests fixed took {0} days or fewer.": { es: "La mitad de las solicitudes reparadas tard\u00f3 {0} d\u00edas o menos." },
  "Any urgency": { es: "Cualquier urgencia" },
  "Any category": { es: "Cualquier categor\u00eda" },
  "No maintenance requests match.": { es: "Ninguna solicitud de mantenimiento coincide." },
  "Building problems sent to each building's own maintenance team, and how they answered.": { es: "Problemas del edificio enviados al equipo de mantenimiento de cada edificio, y c\u00f3mo respondieron." },
  "Fill in the name and the email.": { es: "Complete el nombre y el correo." },
  "Edit maintenance contact": { es: "Editar el contacto de mantenimiento" },
  "Add a maintenance contact": { es: "Agregar un contacto de mantenimiento" },
  "Building or area it covers": { es: "Edificio o \u00e1rea que cubre" },
  "Leave it blank when this contact covers the whole site.": { es: "D\u00e9jelo en blanco cuando este contacto cubre todo el sitio." },
  "Primary contact": { es: "Contacto principal" },
  "Turn off {0}? Requests stop going to them.": { es: "\u00bfDesactivar a {0}? Las solicitudes dejan de envi\u00e1rsele." },
  "Contact turned on": { es: "Contacto activado" },
  "Contact turned off": { es: "Contacto desactivado" },
  "Email and phone": { es: "Correo y tel\u00e9fono" },
  "Covers": { es: "Cubre" },
  "The whole site": { es: "Todo el sitio" },
  "Turned off": { es: "Desactivado" },
  "Maintenance contacts": { es: "Contactos de mantenimiento" },
  "A request about a building goes to the contacts for that building and those covering the whole site. The primary contact is listed first.": { es: "Una solicitud sobre un edificio se env\u00eda a los contactos de ese edificio y a los que cubren todo el sitio. El contacto principal aparece primero." },
  "No maintenance contacts yet. Requests cannot be sent until one is added.": { es: "Todav\u00eda no hay contactos de mantenimiento. No se pueden enviar solicitudes hasta que se agregue uno." },
  "No contact is turned on, so requests cannot be sent.": { es: "Ning\u00fan contacto est\u00e1 activado, as\u00ed que no se pueden enviar solicitudes." },
  "Maintenance requests at this site": { es: "Solicitudes de mantenimiento en este sitio" },
  "Contact saved": { es: "Contacto guardado" },
  "Type a whole number of business days from 1 to 30.": { es: "Escriba un n\u00famero entero de d\u00edas h\u00e1biles del 1 al 30." },
  "When the building has not answered a maintenance request after this many business days, the person who sent it is reminded.": { es: "Cuando el edificio no ha respondido una solicitud de mantenimiento despu\u00e9s de esta cantidad de d\u00edas h\u00e1biles, se le recuerda a quien la envi\u00f3." },
  "Business days before a reminder": { es: "D\u00edas h\u00e1biles antes de un recordatorio" },
  "1 to 30. Weekends and company holidays do not count.": { es: "Del 1 al 30. Los fines de semana y los feriados de la empresa no cuentan." },
  "Sent|maintenance": { es: "Enviada" },
  "Acknowledged|maintenance": { es: "Recibida por el edificio" },
  "Scheduled|maintenance": { es: "Programada" },
  "Fixed|maintenance": { es: "Reparada" },
  "Closed|maintenance": { es: "Cerrada" },
  "Cancelled|maintenance": { es: "Cancelada" },
  "Plumbing": { es: "Plomer\u00eda" },
  "Electrical": { es: "El\u00e9ctrico" },
  "Lighting": { es: "Iluminaci\u00f3n" },
  "HVAC": { es: "Calefacci\u00f3n y aire acondicionado" },
  "Doors and locks": { es: "Puertas y cerraduras" },
  "Leak or water": { es: "Fuga o agua" },
  "Pests": { es: "Plagas" },
  "Elevator": { es: "Ascensor" },
  "Safety|maintenance": { es: "Seguridad" },
  "Other|maintenance": { es: "Otro" },
  "Routine|maintenance": { es: "De rutina" },
  "Soon|maintenance": { es: "Pronto" },
  "Urgent, safety": { es: "Urgente, seguridad" },
  "By phone": { es: "Por tel\u00e9fono" },
  "By email": { es: "Por correo" },
  "In person|maintenance": { es: "En persona" },
  "Response received": { es: "Respuesta recibida" },
  "Note|maintenance": { es: "Nota" },
  "Photos|maintenance": { es: "Fotos" },
  "Email|maintenance": { es: "Correo" },
  "Any age": { es: "Cualquier antig\u00fcedad" },
  "Filed in the last 7 days": { es: "Enviadas en los \u00faltimos 7 d\u00edas" },
  "8 to 30 days old": { es: "De 8 a 30 d\u00edas" },
  "Over 30 days old": { es: "De m\u00e1s de 30 d\u00edas" },
  "{0} follow-ups sent, the last {1}.|count": {
    en: { one: "{0} follow-up sent, the last {1}.", other: "{0} follow-ups sent, the last {1}." },
    es: { one: "{0} seguimiento enviado, el \u00faltimo {1}.", other: "{0} seguimientos enviados, el \u00faltimo {1}." },
  },
  "{0} days ago|count": {
    en: { one: "{0} day ago", other: "{0} days ago" },
    es: { one: "hace {0} d\u00eda", other: "hace {0} d\u00edas" },
  },
  "Turn off|maintenance contact": { es: "Desactivar" },
  "Turn on|maintenance contact": { es: "Activar" },
  "No response yet|maintenance": { es: "Sin respuesta todav\u00eda" },
  "Leave balances": { es: "Saldos de permisos" },
  "Leave balances open for HR Records and for the people who keep the balances. Ask an admin for access.": { es: "Los saldos de permisos se abren para Expedientes de personal y para quienes llevan los saldos. Pida acceso a un administrador." },
  "{0} h": { es: "{0} h" },
  "{0} days ({1})": { es: "{0} d\u00edas ({1})" },
  "Usable from {0}": { es: "Se puede usar desde el {0}" },
  "Start of year": { es: "Inicio del a\u00f1o" },
  "Granted": { es: "Asignado" },
  "Given back": { es: "Devuelto" },
  "Expired": { es: "Vencido" },
  "Adjusted": { es: "Ajustado" },
  "Balance": { es: "Saldo" },
  "No balance is kept for this person.": { es: "No se lleva saldo para esta persona." },
  "Choose the kind of leave.": { es: "Elija el tipo de permiso." },
  "Type the hours to add, or a minus sign and the hours to take away, up to two decimals.": { es: "Escriba las horas que se suman, o un signo menos y las horas que se restan, con hasta dos decimales." },
  "Say why the balance changes.": { es: "Indique por qu\u00e9 cambia el saldo." },
  "Change {0}'s {1} balance by {2}?": { es: "\u00bfCambiar el saldo de {1} de {0} en {2}?" },
  "Adjust a balance": { es: "Ajustar un saldo" },
  "Hours, plus or minus": { es: "Horas, m\u00e1s o menos" },
  "For example 8 or -4": { es: "Por ejemplo 8 o -4" },
  "Reason *": { es: "Motivo *" },
  "It is dated today and names you as the one who made it.": { es: "Queda con la fecha de hoy y lo registra a usted como quien lo hizo." },
  "Choose the day it starts.": { es: "Elija el d\u00eda en que empieza." },
  "The company default": { es: "La pol\u00edtica predeterminada de la empresa" },
  "Their site's policy": { es: "La pol\u00edtica de su sitio" },
  "Their own policy": { es: "Su propia pol\u00edtica" },
  "Starts on": { es: "Empieza el" },
  "{0}, {1} worked from {2} to {3}": { es: "{0}, {1} trabajadas del {2} al {3}" },
  "What": { es: "Qu\u00e9" },
  "The system": { es: "El sistema" },
  "Year {0}": { es: "A\u00f1o {0}" },
  "Policy: {0}": { es: "Pol\u00edtica: {0}" },
  "since {0}": { es: "desde el {0}" },
  "No hire date is on file, so a grant that depends on it does not post.": { es: "No hay fecha de contrataci\u00f3n registrada, as\u00ed que no se registra ninguna asignaci\u00f3n que dependa de ella." },
  "Open the profile": { es: "Abrir el perfil" },
  "Adjust": { es: "Ajustar" },
  "Set their own policy": { es: "Darle su propia pol\u00edtica" },
  "Adjustment saved": { es: "Ajuste guardado" },
  "Policy saved": { es: "Pol\u00edtica guardada" },
  "Balances": { es: "Saldos" },
  "Ledger, newest first": { es: "Registro, lo m\u00e1s reciente primero" },
  "Nothing has posted this year.": { es: "No se ha registrado nada este a\u00f1o." },
  "Policy history": { es: "Historial de pol\u00edticas" },
  "{0} from {1}": { es: "{0} desde el {1}" },
  "their site": { es: "su sitio" },
  "set by {0}": { es: "fijada por {0}" },
  "Open the ledger": { es: "Abrir el registro" },
  "Give the policy a name.": { es: "Dele un nombre a la pol\u00edtica." },
  "Another kind...": { es: "Otro tipo..." },
  "Edit leave policy": { es: "Editar la pol\u00edtica de permisos" },
  "New leave policy": { es: "Nueva pol\u00edtica de permisos" },
  "Grants start on": { es: "Las asignaciones empiezan el" },
  "Paid holidays": { es: "D\u00edas feriados pagados" },
  "The company's holidays, as the handbook lists them": { es: "Los feriados de la empresa, como los indica el manual" },
  "Days this policy names": { es: "Los d\u00edas que indica esta pol\u00edtica" },
  "Holiday date": { es: "Fecha del feriado" },
  "Holiday name": { es: "Nombre del feriado" },
  "Kinds of leave": { es: "Tipos de permiso" },
  "Its code": { es: "Su c\u00f3digo" },
  "Its name": { es: "Su nombre" },
  "How it is given": { es: "C\u00f3mo se asigna" },
  "Amount": { es: "Cantidad" },
  "Counted in": { es: "Se cuenta en" },
  "Hours in a day": { es: "Horas por d\u00eda" },
  "For every this many hours worked": { es: "Por cada esta cantidad de horas trabajadas" },
  "Waiting period, in days": { es: "Per\u00edodo de espera, en d\u00edas" },
  "Carryover cap": { es: "L\u00edmite para pasar al a\u00f1o siguiente" },
  "No cap": { es: "Sin l\u00edmite" },
  "Maximum balance": { es: "Saldo m\u00e1ximo" },
  "No maximum": { es: "Sin m\u00e1ximo" },
  "A new hire also gets it on the hire date": { es: "Una persona nueva tambi\u00e9n lo recibe en su fecha de contrataci\u00f3n" },
  "Seniority tiers: from a number of years of service, a different amount.": { es: "Niveles por antig\u00fcedad: a partir de cierta cantidad de a\u00f1os de servicio, otra cantidad." },
  "Years of service": { es: "A\u00f1os de servicio" },
  "years:": { es: "a\u00f1os:" },
  "Amount from those years": { es: "Cantidad desde esos a\u00f1os" },
  "Remove this tier": { es: "Quitar este nivel" },
  "Add a tier": { es: "Agregar un nivel" },
  "Remove this kind": { es: "Quitar este tipo" },
  "Add a kind of leave": { es: "Agregar un tipo de permiso" },
  "Leave policy": { es: "Pol\u00edtica de permisos" },
  "Company default": { es: "Predeterminada de la empresa" },
  "Read only": { es: "Solo lectura" },
  "This is the handbook's policy. It stays as the handbook sets it; make a new policy for anything else.": { es: "Esta es la pol\u00edtica del manual. Se queda como la fija el manual; haga una pol\u00edtica nueva para cualquier otra cosa." },
  "Grants start on {0}.": { es: "Las asignaciones empiezan el {0}." },
  "Sites that use it": { es: "Sitios que la usan" },
  "No site uses it.": { es: "Ning\u00fan sitio la usa." },
  "by the company default": { es: "por ser la predeterminada de la empresa" },
  "Who follows it": { es: "Qui\u00e9n la sigue" },
  "Nobody follows it today.": { es: "Hoy nadie la sigue." },
  "Line": { es: "L\u00ednea" },
  "Hours worked": { es: "Horas trabajadas" },
  "What posted": { es: "Qu\u00e9 se registr\u00f3" },
  "Upload an hours file": { es: "Cargar un archivo de horas" },
  "The payroll system's hours export, as a CSV: a column person (badge number or employee ID), period_start and period_end (or period), and hours. Hours worked post leave for policies given per hours worked.": { es: "La exportaci\u00f3n de horas del sistema de n\u00f3mina, en CSV: una columna person (n\u00famero de credencial o ID de empleado), period_start y period_end (o period), y hours. Las horas trabajadas registran permisos en las pol\u00edticas que los dan por horas trabajadas." },
  "Choose the CSV": { es: "Elegir el CSV" },
  "Choose another file": { es: "Elegir otro archivo" },
  "The file has no lines of hours.": { es: "El archivo no tiene l\u00edneas de horas." },
  "Posting...": { es: "Registrando..." },
  "Post the hours": { es: "Registrar las horas" },
  "This file was posted before, on {0}. Nothing new posted.": { es: "Este archivo ya se registr\u00f3 el {0}. No se registr\u00f3 nada nuevo." },
  "Posted. {0} lines posted leave, {1} posted none.": { es: "Registrado. {0} l\u00edneas registraron permisos y {1} no registraron ninguno." },
  "Fill in at least one box.": { es: "Complete al menos una casilla." },
  "Type the hours left, from 0, up to two decimals.": { es: "Escriba las horas que quedan, desde 0, con hasta dos decimales." },
  "Already at {0}.": { es: "Ya est\u00e1 en {0}." },
  "Posted {0}. The balance is {1}.": { es: "Se registr\u00f3 {0}. El saldo es {1}." },
  "{0} posted, {1} refused, {2} already at that balance.": { es: "{0} registrados, {1} rechazados, {2} ya ten\u00edan ese saldo." },
  "Paid sick now": { es: "Enfermedad pagada ahora" },
  "Paid sick hours left today": { es: "Horas de enfermedad pagada que quedan hoy" },
  "Paid sick hours left today for {0}": { es: "Horas de enfermedad pagada que le quedan hoy a {0}" },
  "Use this once, when leave balances start: type the paid sick hours each person has left today. Save posts one adjustment for each filled box, with the reason Starting balance. A box left empty posts nothing.": { es: "Use esto una vez, cuando empiezan los saldos de permisos: escriba las horas de enfermedad pagada que le quedan hoy a cada persona. Guardar registra un ajuste por cada casilla llena, con el motivo Starting balance. Una casilla vac\u00eda no registra nada." },
  "Nobody is active.": { es: "No hay nadie activo." },
  "Save the starting balances": { es: "Guardar los saldos iniciales" },
  "This site's own default, since {0}.": { es: "La predeterminada propia de este sitio, desde el {0}." },
  "The company default. People here follow it unless they have their own.": { es: "La predeterminada de la empresa. Las personas de aqu\u00ed la siguen salvo que tengan la suya." },
  "Set the site's policy": { es: "Fijar la pol\u00edtica del sitio" },
  "None kept": { es: "No se lleva ninguno" },
  "Every policy": { es: "Todas las pol\u00edticas" },
  "Every role": { es: "Todos los puestos" },
  "Every status": { es: "Todos los estados" },
  "Inactive": { es: "Inactivo" },
  "Terminated": { es: "Dado de baja" },
  "Hours files posted: {0}, the last through {1}.": { es: "Archivos de horas registrados: {0}, el \u00faltimo hasta el {1}." },
  "No hours file has been posted yet.": { es: "Todav\u00eda no se ha registrado ning\u00fan archivo de horas." },
  "Nobody matches.": { es: "Nadie coincide." },
  "{0} sites, {1} people": { es: "{0} sitios, {1} personas" },
  "No active site.": { es: "No hay ning\u00fan sitio activo." },
  "Periods": { es: "Per\u00edodos" },
  "Lines": { es: "L\u00edneas" },
  "Posted|hours file": { es: "Registradas" },
  "Posted none": { es: "Sin registro" },
  "Active people with no hire date. A grant on the hire date, a seniority tier or a waiting period cannot count for them until one is on their profile.": { es: "Personas activas sin fecha de contrataci\u00f3n. Una asignaci\u00f3n en la fecha de contrataci\u00f3n, un nivel por antig\u00fcedad o un per\u00edodo de espera no pueden contarse para ellas hasta que tengan una en su perfil." },
  "Everyone active has a hire date.": { es: "Todas las personas activas tienen fecha de contrataci\u00f3n." },
  "What waits on it": { es: "Qu\u00e9 depende de ella" },
  "Take back the approval of this time off? {0} go back to the {1} balance.": { es: "\u00bfRetirar la aprobaci\u00f3n de este tiempo libre? {0} vuelven al saldo de {1}." },
  "Take back the approval of this time off?": { es: "\u00bfRetirar la aprobaci\u00f3n de este tiempo libre?" },
  "Approval taken back. {0} given back to the balance.": { es: "Aprobaci\u00f3n retirada. Se devolvieron {0} al saldo." },
  "Approval taken back. They get a notice in the app.": { es: "Aprobaci\u00f3n retirada. La persona recibe un aviso en la aplicaci\u00f3n." },
  "No balance is kept for {0}.": { es: "No se lleva saldo de {0}." },
  "{0}: {1} now, {2} after this request of {3}.": { es: "{0}: {1} ahora, {2} despu\u00e9s de esta solicitud de {3}." },
  "{0}: {1} given back, {2} now.": { es: "{0}: {1} devueltas, {2} ahora." },
  "{0}: {1} taken, {2} now.": { es: "{0}: {1} tomadas, {2} ahora." },
  "{0}: {1} now.": { es: "{0}: {1} ahora." },
  "Take back approval": { es: "Retirar la aprobaci\u00f3n" },
  "The request becomes cancelled and its hours go back to the balance. The shifts its approval changed stay as they are.": { es: "La solicitud queda cancelada y sus horas vuelven al saldo. Los turnos que cambi\u00f3 su aprobaci\u00f3n se quedan como est\u00e1n." },
  "Approved by": { es: "Aprobado por" },
  "Taken back": { es: "Retirada" },
  "Policies": { es: "Pol\u00edticas" },
  "Hours files": { es: "Archivos de horas" },
  "Missing hire dates": { es: "Sin fecha de contrataci\u00f3n" },
  "Enter starting balances": { es: "Registrar los saldos iniciales" },
  "Grant|leave": { es: "Asignaci\u00f3n" },
  "Hours worked|leave": { es: "Horas trabajadas" },
  "Time off taken": { es: "Tiempo libre tomado" },
  "Time off given back": { es: "Tiempo libre devuelto" },
  "Expired at the year's end": { es: "Venci\u00f3 al final del a\u00f1o" },
  "Policy changed": { es: "Cambio de pol\u00edtica" },
  "Adjustment by HR": { es: "Ajuste de RR. HH." },
  "On January 1 each year": { es: "El 1 de enero de cada a\u00f1o" },
  "On each anniversary of the hire date": { es: "En cada aniversario de la fecha de contrataci\u00f3n" },
  "On the hire date": { es: "En la fecha de contrataci\u00f3n" },
  "On the first of each month": { es: "El primero de cada mes" },
  "For hours worked": { es: "Por horas trabajadas" },
  "Given by HR|leave method": { es: "Lo asigna RR. HH." },
  "No balance kept": { es: "Sin saldo" },
  "Already posted": { es: "Ya registrado" },
  "No hours rule in their policy": { es: "Su pol\u00edtica no da permisos por horas" },
  "Before their policy starts": { es: "Antes de que empiece su pol\u00edtica" },
  "No hire date on file": { es: "Sin fecha de contrataci\u00f3n registrada" },
  "The first line names the columns: person, period_start, period_end and hours (or person, period and hours).": { es: "La primera l\u00ednea nombra las columnas: person, period_start, period_end y hours (o person, period y hours)." },
  "Name the person by badge number or employee ID.": { es: "Indique a la persona por su n\u00famero de credencial o su ID de empleado." },
  "The period needs a first and a last day, YYYY-MM-DD, the first on or before the last.": { es: "El per\u00edodo necesita un primer y un \u00faltimo d\u00eda, AAAA-MM-DD, el primero igual o anterior al \u00faltimo." },
  "The period ends after today.": { es: "El per\u00edodo termina despu\u00e9s de hoy." },
  "A period is 62 days at most.": { es: "Un per\u00edodo tiene como m\u00e1ximo 62 d\u00edas." },
  "Hours are a number from 0 to 400.": { es: "Las horas son un n\u00famero de 0 a 400." },
  "{0} line cannot be read. Fix the file and choose it again.|count": {
    en: { one: "{0} line cannot be read. Fix the file and choose it again.", other: "{0} lines cannot be read. Fix the file and choose it again." },
    es: { one: "{0} l\u00ednea no se puede leer. Corrija el archivo y el\u00edjalo de nuevo.", other: "{0} l\u00edneas no se pueden leer. Corrija el archivo y el\u00edjalo de nuevo." },
  },
  "{0} line read. Post the hours to match each person and post their leave.|count": {
    en: { one: "{0} line read. Post the hours to match each person and post their leave.", other: "{0} lines read. Post the hours to match each person and post their leave." },
    es: { one: "{0} l\u00ednea le\u00edda. Registre las horas para encontrar a cada persona y registrar sus permisos.", other: "{0} l\u00edneas le\u00eddas. Registre las horas para encontrar a cada persona y registrar sus permisos." },
  },
  "Weekly, monthly, quarterly and seasonal tasks.": { es: "Tareas semanales, mensuales, trimestrales y de temporada." },
  "A card counts every step due at the site, weekly, monthly, quarterly and seasonal work included.": { es: "Una tarjeta cuenta cada paso que tocaba en el sitio, incluido el trabajo semanal, mensual, trimestral y de temporada." },
  "A PDF, a JPEG, PNG or HEIC photo, or a Word file, up to 25 MB.": { es: "Un PDF, una foto JPEG, PNG o HEIC, o un archivo de Word, de hasta 25 MB." },
  "{0} is not a PDF, a JPEG, PNG or HEIC photo, or a Word file.": { es: "{0} no es un PDF, una foto JPEG, PNG o HEIC, ni un archivo de Word." },
  "This is taking longer than usual and may still be going out. Press Send again to check: it will not go out twice.": { es: "Esto est\u00e1 tardando m\u00e1s de lo normal y puede que todav\u00eda se est\u00e9 enviando. Presione Enviar otra vez para comprobarlo: no saldr\u00e1 dos veces." },
  "This changed since you opened it": { es: "Esto cambi\u00f3 desde que lo abri\u00f3" },
  "Someone else changed what you edited since you opened it. Nothing is saved until you choose.": { es: "Otra persona cambi\u00f3 lo que usted edit\u00f3 desde que lo abri\u00f3. No se guarda nada hasta que usted elija." },
  "Field": { es: "Campo" },
  "What it says now": { es: "Lo que dice ahora" },
  "What you typed": { es: "Lo que usted escribi\u00f3" },
  "Keep what it says now": { es: "Dejar lo que dice ahora" },
  "Save what I typed": { es: "Guardar lo que escrib\u00ed" },
  "so far": { es: "hasta ahora" },
};

// The language the screen is being drawn in. The app sets it before anything renders and again on
// every change, the way it sets the theme, so tr() reads one value rather than taking one.
let current = "en";
export function setLang(id) { current = LOCALES.indexOf(id) >= 0 ? id : "en"; }
export function getLang() { return current; }
export function localeTag(lang) { return LOCALE_TAGS[lang || current] || LOCALE_TAGS.en; }

// What a browser says it wants, when nothing is stored.
export function browserLang() {
  try {
    const want = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) || [];
    for (let i = 0; i < want.length; i += 1) {
      const two = String(want[i] || "").slice(0, 2).toLowerCase();
      if (LOCALES.indexOf(two) >= 0) return two;
    }
  } catch (e) { /* a browser that will not say is an English one */ }
  return "en";
}

const fill = (text, values) => String(text).replace(/\{(\d+)\}/g, (m, i) => {
  const v = values[Number(i)];
  return v === undefined || v === null ? "" : String(v);
});
// The text before the bar, which is the English a key falls back to.
const baseOf = (key) => { const i = String(key).lastIndexOf("|"); return i > 0 ? String(key).slice(0, i) : String(key); };

export function tr(key, ...values) {
  const entry = WORDS[key];
  const said = entry && typeof entry[current] === "string" && entry[current] !== "" ? entry[current] : null;
  return fill(said === null ? baseOf(key) : said, values);
}

const RULES = {};
function rulesFor(lang) {
  if (!RULES[lang]) {
    try { RULES[lang] = new Intl.PluralRules(localeTag(lang)); }
    catch (e) { RULES[lang] = { select: (n) => (Number(n) === 1 ? "one" : "other") }; }
  }
  return RULES[lang];
}

// A count and its word, in whichever form the language uses for that number. {0} is the count.
export function trn(key, count, ...values) {
  const entry = WORDS[key] || {};
  const pick = (forms, lang) => {
    if (!forms || typeof forms !== "object") return null;
    const form = rulesFor(lang).select(Number(count));
    return forms[form] !== undefined ? forms[form] : (forms.other !== undefined ? forms.other : null);
  };
  const said = pick(entry[current], current);
  const english = said === null ? pick(entry.en, "en") : said;
  return fill(english === null ? baseOf(key) : english, [count].concat(values));
}
